
;/* ==== _bootstrap.jsx ==== */
// SUPERSYS bootstrap — runs first in the bundle.
// Provides: API helpers, the window.claude.complete() proxy shim (→ /api/ai),
// and the password AuthGate that wraps <App/> at mount.
// (In the prototype these did not exist: AI ran via the Claude Design host and
// there was no auth. Everything else in the bundle is unchanged.)

const API_BASE = "/api";

async function apiFetch(path, opts = {}) {
  const res = await fetch(API_BASE + path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  return res;
}

async function apiJSON(path, opts = {}) {
  const res = await apiFetch(path, opts);
  if (res.status === 401) {
    // Session expired — drop back to the login gate.
    window.dispatchEvent(new CustomEvent("supersys:unauthorized"));
    throw new Error("UNAUTHORIZED");
  }
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

// ── window.claude.complete shim ──────────────────────────────────────────────
// The prototype called window.claude.complete(promptOrMessages) → string.
// We forward to the Cloudflare Function /api/ai which holds the API key.
window.claude = {
  complete: async (arg) => {
    const body = typeof arg === "string" ? { prompt: arg } : arg;
    const data = await apiJSON("/ai", { method: "POST", body: JSON.stringify(body) });
    return data && typeof data.text === "string" ? data.text : "";
  },
};

window.__supersys_api = { apiFetch, apiJSON, API_BASE };

// ── AuthGate ─────────────────────────────────────────────────────────────────
const AuthGate = ({ children }) => {
  const [status, setStatus] = React.useState("checking"); // checking | locked | open
  const [pw, setPw] = React.useState("");
  const [err, setErr] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const check = React.useCallback(async () => {
    try {
      const res = await apiFetch("/session");
      const data = await res.json().catch(() => ({}));
      setStatus(data && data.authed ? "open" : "locked");
    } catch {
      setStatus("locked");
    }
  }, []);

  React.useEffect(() => { check(); }, [check]);

  React.useEffect(() => {
    const onUnauth = () => setStatus("locked");
    window.addEventListener("supersys:unauthorized", onUnauth);
    return () => window.removeEventListener("supersys:unauthorized", onUnauth);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const res = await apiFetch("/login", { method: "POST", body: JSON.stringify({ password: pw }) });
      if (res.ok) { setPw(""); setStatus("open"); }
      else {
        const d = await res.json().catch(() => ({}));
        setErr(d.error || "パスワードが違います。");
      }
    } catch {
      setErr("接続に失敗しました。");
    } finally { setBusy(false); }
  };

  if (status === "open") return children;

  // Canvas starfield ships later in the same bundle; by render time it exists.
  // Keep the CSS-layer fallback in case it ever doesn't.
  const SF = window.Starfield;
  return (
    <div className="auth-gate">
      {SF ? (
        <SF twinkle="subtle" density={1} meteors="rare" />
      ) : (
        <div className="starfield" aria-hidden="true">
          <div className="starfield-deep" /><div className="starfield-mid" /><div className="starfield-close" />
        </div>
      )}
      <form className="auth-card" onSubmit={submit}>
        <div className="auth-shield">{typeof Shield === "function" ? <Shield size={64} /> : null}</div>
        <div className="auth-title">SUPERSYS</div>
        <div className="auth-sub">SEIMU · COMMAND SYSTEM</div>
        {status === "checking" ? (
          <div className="auth-checking">認証状態を確認しています…</div>
        ) : (
          <>
            <input
              type="password"
              className="auth-input"
              placeholder="パスワード"
              value={pw}
              autoFocus
              onChange={(e) => setPw(e.target.value)}
            />
            {err && <div className="auth-err">{err}</div>}
            <button type="submit" className="auth-btn" disabled={busy || !pw}>
              {busy ? "認証中…" : "アクセス"}
            </button>
            <div className="auth-note">社外秘 — 人事評価・応募者情報を含みます。許可された担当者のみ。</div>
          </>
        )}
      </form>
    </div>
  );
};

window.AuthGate = AuthGate;


;/* ==== data.jsx ==== */
// Real task data — imported from Notion CSV (Tasks DB)
// Fields per item: title, notes, date, time, status, owner, area, priority, dur, tag, registered

// 進行中プロジェクト 2026-06-12 全面差し替え（竹内さん指定の8件）。
// 進捗・マイルストーンは不明のため 0 初期化 — 値の創作はしない。編集モーダルの
// スライダーで設定する前提。storage.jsx の __projectsResetV2 移行もこのリストを使う。
const PROJECTS_RESET_V2 = [
  { id: "p101", name: "マーケマネージャー採用", area: "HR · Hiring", pct: 0, status: "In Flight", next: "", owner: "竹内", milestones: 0, done: 0 },
  { id: "p102", name: "不動産仕入営業スカウト送り続けてみる", area: "HR · Hiring", pct: 0, status: "In Flight", next: "", owner: "竹内", milestones: 0, done: 0 },
  { id: "p103", name: "研修プログラムと評価制度", area: "HR · Strategy", pct: 0, status: "In Flight", next: "", owner: "竹内", milestones: 0, done: 0 },
  { id: "p104", name: "生成AI活用推進PJ", area: "IT", pct: 0, status: "In Flight", next: "", owner: "竹内", milestones: 0, done: 0 },
  { id: "p105", name: "9Fリフォームと移動", area: "General Affairs", pct: 0, status: "In Flight", next: "", owner: "竹内", milestones: 0, done: 0 },
  { id: "p106", name: "2027/3月の式典", area: "General Affairs", pct: 0, status: "In Flight", next: "", owner: "竹内", milestones: 0, done: 0 },
  { id: "p107", name: "中小企業成長加速化補助金", area: "General Affairs", pct: 0, status: "In Flight", next: "", owner: "竹内", milestones: 0, done: 0 },
  { id: "p108", name: "総務採用", area: "HR · Hiring", pct: 0, status: "In Flight", next: "", owner: "竹内", milestones: 0, done: 0 },
];

const initialState = {
  __dataVersion: 2,  // Bumped 2026-05-27: Notion CSV import (42 real tasks)
  items: [
    // __DATA_VERSION__: 2 — Notion CSV import 2026-05-27 (42 items)
    { id: "n4", title: "エンビルド個別契約書確認", notes: "2026-05-19 監査室・顧問室レビュー実施。重大論点3点(①第2条「実利益」定義の無限定／②第4条 実利益1/2配分の関連当事者性／③第6条 損害折半が建物瑕疵に及ぶ書きぶり)あり。電子署名は本日見送り推奨。エンビルド様に48時間延長を依頼し、3点修正＋基本契約原本確認後に判断。", status: "done", date: "2026-05-19", time: null, owner: "竹内", area: "GA", priority: "high", dur: null, tag: null, registered: "2026-05-15" },
    { id: "n8", title: "部門長会議で評価制度資料と社内研修プログラムの共有", notes: "", status: "todo", date: "2026-06-05", time: null, owner: "大志万", area: "HR", priority: "med", dur: null, tag: null, registered: "2026-05-18" },
    { id: "n13", title: "第3期終わりまでに歩合の給与化どうするかを進めていく", notes: "2026-05-19: 期限日を2026-12-31に設定。第3期(年末)終わりまでに歩合の給与化方針を確定する。", status: "todo", date: "2026-09-30", time: null, owner: "竹内", area: "HR", priority: "high", dur: null, tag: null, registered: "2026-03-25" },
    { id: "n2", title: "車両・安全運転管理規程 20260312確認", notes: "2026-05-19: 期限を2026-12-31に変更(竹内判断)。元期限3/25。", status: "todo", date: "2026-12-31", time: null, owner: "竹内", area: "GA", priority: "low", dur: null, tag: null, registered: "2026-03-24" },
    { id: "n5", title: "部門長会議前に決裁権の案とテーマシート改版", notes: "テーマシート改版は内製Webアプリ「テーマボード」v0.1.0 として再構築完了(2026-05-19)。決裁権の案=議題ごとの希望決裁レベル(社長決裁／部門長合議／持ち帰り検討／情報共有のみ)として実装。", status: "done", date: "2026-05-19", time: null, owner: "竹内", area: "Exec", priority: "high", dur: null, tag: null, registered: "2026-05-15" },
    { id: "n11", title: "ヘッドハント会社の調査", notes: "", status: "todo", date: "2026-06-01", time: null, owner: "大志万", area: "Hiring", priority: "med", dur: null, tag: null, registered: "2026-05-14" },
    { id: "n9", title: "マーケティング事業部マネージャー候補のビズリーチ候補チェック", notes: "✅️スカウトメール30通", status: "todo", date: "2026-11-20", time: null, owner: "竹内", area: "Hiring", priority: "med", dur: null, tag: null, registered: "2026-04-24" },
    { id: "n1", title: "オフィス賃貸借契約のルール作成", notes: "2026-05-19: 期限を2026-12-31に変更(竹内判断)。元期限3/17。法務系ルール整備として年内完了目標。", status: "todo", date: "2026-12-31", time: null, owner: "竹内", area: "GA", priority: "low", dur: null, tag: null, registered: "2026-03-13" },
    { id: "n10", title: "役割定義用 サプライチェーン強化に向けたセキュリティ対策評価制度に対応する", notes: "✅️2027年になるか?", status: "todo", date: "2026-12-24", time: null, owner: "山本", area: "IT", priority: "low", dur: null, tag: null, registered: "2026-02-19" },
    { id: "n12", title: "委託料の計算、最後の確認だけ黒田さん体制に変わった時に経理でやってもらえないか黒田さんに相談する", notes: "", status: "todo", date: "2026-06-30", time: null, owner: "竹内", area: "GA", priority: "med", dur: null, tag: null, registered: "2026-03-19" },
    { id: "n7", title: "評価制度資料と社内研修プログラムを部門長会議までに提出", notes: "", status: "todo", date: "2026-06-01", time: null, owner: "大志万", area: "HR", priority: "high", dur: null, tag: null, registered: "2026-05-18" },
    { id: "n6", title: "尾古貴さんの業務自動化検討ヒアリング", notes: "", status: "done", date: "2026-05-22", time: null, owner: "山本", area: "IT", priority: "med", dur: null, tag: null, registered: "2026-05-15" },
    { id: "n3", title: "第2期役割定義の会社全体バランス調整", notes: "", status: "todo", date: "2026-05-30", time: null, owner: "大志万", area: "HR", priority: "high", dur: null, tag: null, registered: "2026-04-16" },
    { id: "n15", title: "テーマボード廃止判断(6ヶ月点検)", notes: "web-app-build スキル規定の6ヶ月点検。利用率と定着度を点検し、未使用なら廃止判断。使われていれば Cloudflare KV/D1 へのデータ層移行検討。", status: "todo", date: "2026-11-19", time: null, owner: "竹内", area: "IT", priority: "low", dur: null, tag: null, registered: "2026-05-19" },
    { id: "n16", title: "5/25(月)の生成AIPJ用資料作成", notes: "", status: "done", date: "2026-05-22", time: null, owner: "山本", area: "IT", priority: "med", dur: null, tag: null, registered: "2026-05-20" },
    { id: "n21", title: "5/21(木)15:00 面接(谷澤さん)", notes: "", status: "done", date: "2026-05-21", time: "15:00", owner: "竹内", area: "Hiring", priority: "high", dur: "60 min", tag: "Project", registered: "2026-05-21" },
    { id: "n22", title: "砂坂さん復帰フローについて社長に相談しておく", notes: "", status: "done", date: "2026-05-25", time: null, owner: "竹内", area: "HR", priority: "high", dur: null, tag: null, registered: "2026-05-20" },
    { id: "n24", title: "5/22(金)夜 さっちゃん飲み会 — 19時には帰宅", notes: "", status: "done", date: "2026-05-22", time: "19:00", owner: "竹内", area: "Personal", priority: "low", dur: null, tag: "Personal", registered: "2026-05-20" },
    { id: "n25", title: "5/22(金)15:00 面接(石田さん)", notes: "", status: "done", date: "2026-05-22", time: "15:00", owner: "竹内", area: "Hiring", priority: "high", dur: "60 min", tag: "Project", registered: "2026-05-20" },
    { id: "n26", title: "5/25(月)16:00 生成AI活用推進PJミーティング", notes: "", status: "done", date: "2026-05-25", time: "16:00", owner: "竹内", area: "IT", priority: "high", dur: "60 min", tag: "Project", registered: "2026-05-20" },
    { id: "n27", title: "6/1(月)13:00 面接(浜辺さん)", notes: "", status: "todo", date: "2026-06-01", time: "13:00", owner: "竹内", area: "Hiring", priority: "high", dur: "60 min", tag: "Project", registered: "2026-05-20" },
    { id: "n28", title: "6/2(火)11:00 社長定例打合せ", notes: "", status: "todo", date: "2026-06-02", time: "11:00", owner: "竹内", area: "Exec", priority: "high", dur: "60 min", tag: "Exec", registered: "2026-05-20" },
    { id: "n29", title: "6/5(金)15:00 部門長会議", notes: "", status: "todo", date: "2026-06-05", time: "15:00", owner: "竹内", area: "Exec", priority: "high", dur: "120 min", tag: "Exec", registered: "2026-05-20" },
    { id: "n30", title: "6/7(日)参考: 社長 他社の総会見学", notes: "", status: "todo", date: "2026-06-07", time: null, owner: "竹内", area: "Exec", priority: "low", dur: null, tag: null, registered: "2026-05-20" },
    { id: "n31", title: "6/11(木)10:00 全体ミーティング 司会", notes: "", status: "todo", date: "2026-06-11", time: "10:00", owner: "竹内", area: "Exec", priority: "high", dur: "60 min", tag: "Internal", registered: "2026-05-20" },
    { id: "n32", title: "6/11(木)15:00 数値会議", notes: "", status: "todo", date: "2026-06-11", time: "15:00", owner: "竹内", area: "Exec", priority: "high", dur: "90 min", tag: "Exec", registered: "2026-05-20" },
    { id: "n33", title: "7/18(土) 夏祭り", notes: "", status: "todo", date: "2026-07-18", time: null, owner: "松川", area: "GA", priority: "med", dur: "All day", tag: "Internal", registered: "2026-05-20" },
    { id: "n34", title: "7/19(日) 夏祭り", notes: "", status: "todo", date: "2026-07-19", time: null, owner: "松川", area: "GA", priority: "med", dur: "All day", tag: "Internal", registered: "2026-05-20" },
    { id: "n35", title: "7/20(月) カレンダーで休み", notes: "", status: "todo", date: "2026-07-20", time: null, owner: "竹内", area: "Personal", priority: "low", dur: "All day", tag: "Personal", registered: "2026-05-20" },
    { id: "n36", title: "5/21(木)13:30 打合せ(黒田さん)", notes: "", status: "done", date: "2026-05-21", time: "13:30", owner: "竹内", area: "GA", priority: "med", dur: "60 min", tag: "Internal", registered: "2026-05-21" },
    { id: "n37", title: "エコマート契約書チェック(密原さん依頼)", notes: "", status: "todo", date: "2026-05-27", time: null, owner: "竹内", area: "GA", priority: "high", dur: null, tag: null, registered: "2026-05-21" },
    { id: "n38", title: "6/3(水)13:00 WEB面談(堀野昭洋様)", notes: "", status: "todo", date: "2026-06-03", time: "13:00", owner: "竹内", area: "Hiring", priority: "high", dur: "60 min", tag: "Project", registered: "2026-05-21" },
    { id: "n39", title: "谷澤知哉さんの面接と黒瀬さんの部長職(部門長)について社長と相談する", notes: "", status: "done", date: "2026-05-22", time: null, owner: "竹内", area: "Exec", priority: "high", dur: null, tag: null, registered: "2026-05-21" },
    { id: "n40", title: "5/26(火)10:00 ondo説明", notes: "", status: "done", date: "2026-05-26", time: "10:00", owner: "竹内", area: "Exec", priority: "med", dur: "60 min", tag: "Project", registered: "2026-05-22" },
    { id: "n41", title: "石田さん落選連絡", notes: "", status: "todo", date: "2026-05-27", time: null, owner: "竹内", area: "Hiring", priority: "high", dur: null, tag: null, registered: "2026-05-22" },
    { id: "n42", title: "5/26(火)13:30 打合せ(山本さん)", notes: "", status: "done", date: "2026-05-26", time: "13:30", owner: "竹内", area: "IT", priority: "med", dur: "60 min", tag: "Internal", registered: "2026-05-22" },
    { id: "n43", title: "5/27(水)AM 家でエアコン設置", notes: "", status: "todo", date: "2026-05-27", time: "09:00", owner: "竹内", area: "Personal", priority: "low", dur: null, tag: "Personal", registered: "2026-05-23" },
    { id: "n44", title: "藤原さんにキーボード渡す", notes: "", status: "done", date: "2026-05-29", time: null, owner: "山本", area: "IT", priority: "low", dur: null, tag: null, registered: "2026-05-24" },
    { id: "n45", title: "砂坂さん戻る目処ついたらランチのお誘いと晩御飯も", notes: "", status: "todo", date: "2026-06-05", time: null, owner: "竹内", area: "HR", priority: "med", dur: null, tag: null, registered: "2026-05-24" },
    { id: "n46", title: "6/4(木)11:00 面接(谷沢さん)", notes: "✅️社長・黒瀬さん同席", status: "todo", date: "2026-06-04", time: "11:00", owner: "竹内", area: "Hiring", priority: "high", dur: "60 min", tag: "Project", registered: "2026-05-26" },
    { id: "n47", title: "工事下請基本契約書チェック", notes: "", status: "todo", date: "2026-06-02", time: null, owner: "竹内", area: "GA", priority: "high", dur: null, tag: null, registered: "2026-05-26" },
    { id: "n48", title: "尾古貴さんの業務自動化検討", notes: "", status: "todo", date: "2026-06-12", time: null, owner: "山本", area: "IT", priority: "med", dur: null, tag: null, registered: "2026-05-26" },
  ],

  // 2026-06-12 進行中PJに全面置き換え（storage.jsx の __projectsResetV2 移行と同一リスト）
  projects: PROJECTS_RESET_V2.map(p => ({ ...p })),

  recordings: [],
  ideas: [],

  kpi: {
    funnel: [
      { stage: "Applied", count: 184, target: 220, rate: "—" },
      { stage: "Resume passed", count: 96, target: 120, rate: "52%" },
      { stage: "1st interview", count: 58, target: 72, rate: "60%" },
      { stage: "Final interview", count: 22, target: 28, rate: "38%" },
      { stage: "Offer", count: 9, target: 12, rate: "41%" },
      { stage: "Accepted", count: 6, target: 10, rate: "67%" },
    ],
    leaveRate: { now: 20.4, target2028: 10.0, industryAvg: 13.5 },
    headcount: { current: 94, plan2031: 124, plan2025: 98 },
    sales: { plan: 67, ondo: 40, ondoEx: 26, broker: 4, unit: "¥100M" },
  },

  contacts: [],
  books: [],

  alerts: [
    { id: "a1", title: "5/27(水) エコマート契約書チェック(密原さん依頼) — 期日", meta: "Due today", severity: "high" },
    { id: "a2", title: "5/27(水) 石田さん落選連絡 — 期日", meta: "Due today", severity: "high" },
    { id: "a3", title: "5/30(土) 第2期役割定義の会社全体バランス調整 — 大志万", meta: "3 days remaining", severity: "med" },
  ],
};

Object.assign(window, { initialState, PROJECTS_RESET_V2 });


;/* ==== holidays.jsx ==== */
// Japanese national holidays — 2025-2028 (hard-coded; substitutes & people's holidays included)

const JP_HOLIDAYS = {
  // 2025
  "2025-01-01": "元日",
  "2025-01-13": "成人の日",
  "2025-02-11": "建国記念の日",
  "2025-02-23": "天皇誕生日",
  "2025-02-24": "振替休日",
  "2025-03-20": "春分の日",
  "2025-04-29": "昭和の日",
  "2025-05-03": "憲法記念日",
  "2025-05-04": "みどりの日",
  "2025-05-05": "こどもの日",
  "2025-05-06": "振替休日",
  "2025-07-21": "海の日",
  "2025-08-11": "山の日",
  "2025-09-15": "敬老の日",
  "2025-09-23": "秋分の日",
  "2025-10-13": "スポーツの日",
  "2025-11-03": "文化の日",
  "2025-11-23": "勤労感謝の日",
  "2025-11-24": "振替休日",

  // 2026
  "2026-01-01": "元日",
  "2026-01-12": "成人の日",
  "2026-02-11": "建国記念の日",
  "2026-02-23": "天皇誕生日",
  "2026-03-20": "春分の日",
  "2026-04-29": "昭和の日",
  "2026-05-03": "憲法記念日",
  "2026-05-04": "みどりの日",
  "2026-05-05": "こどもの日",
  "2026-05-06": "振替休日",
  "2026-07-20": "海の日",
  "2026-08-11": "山の日",
  "2026-09-21": "敬老の日",
  "2026-09-22": "国民の休日",
  "2026-09-23": "秋分の日",
  "2026-10-12": "スポーツの日",
  "2026-11-03": "文化の日",
  "2026-11-23": "勤労感謝の日",

  // 2027
  "2027-01-01": "元日",
  "2027-01-11": "成人の日",
  "2027-02-11": "建国記念の日",
  "2027-02-23": "天皇誕生日",
  "2027-03-21": "春分の日",
  "2027-03-22": "振替休日",
  "2027-04-29": "昭和の日",
  "2027-05-03": "憲法記念日",
  "2027-05-04": "みどりの日",
  "2027-05-05": "こどもの日",
  "2027-07-19": "海の日",
  "2027-08-11": "山の日",
  "2027-09-20": "敬老の日",
  "2027-09-23": "秋分の日",
  "2027-10-11": "スポーツの日",
  "2027-11-03": "文化の日",
  "2027-11-23": "勤労感謝の日",

  // 2028 — 暫定。春分・秋分は天文計算ベース(官報告示は2027年2月頃に確定)
  "2028-01-01": "元日",
  "2028-01-10": "成人の日",
  "2028-02-11": "建国記念の日",
  "2028-02-23": "天皇誕生日",
  "2028-03-20": "春分の日",
  "2028-04-29": "昭和の日",
  "2028-05-03": "憲法記念日",
  "2028-05-04": "みどりの日",
  "2028-05-05": "こどもの日",
  "2028-07-17": "海の日",
  "2028-08-11": "山の日",
  "2028-09-18": "敬老の日",
  "2028-09-22": "秋分の日",
  "2028-10-09": "スポーツの日",
  "2028-11-03": "文化の日",
  "2028-11-23": "勤労感謝の日",
};

// Helpers for date strings (YYYY-MM-DD)
const pad2 = (n) => String(n).padStart(2, "0");
const fmtYMD = (d) => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
const parseYMD = (s) => {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
const todayYMD = () => fmtYMD(new Date());

// Format: returns "5/26 (火)" etc. — respects window.__supersys_settings (dateFormat, lang)
const JP_WD = ["日", "月", "火", "水", "木", "金", "土"];
const EN_WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const _settings = () => (typeof window !== "undefined" && window.__supersys_settings) || {};
const fmtMD = (s, lang) => {
  const d = parseYMD(s);
  if (!d) return s || "—";
  const ss = _settings();
  const useLang = lang || (ss.lang === "en" ? "en" : "jp");
  const wd = (useLang === "jp" ? JP_WD : EN_WD)[d.getDay()];
  const fmt = ss.dateFormat || "md-wd";
  switch (fmt) {
    case "md":      return `${d.getMonth()+1}/${d.getDate()}`;
    case "full":    return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
    case "full-wd": return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())} (${wd})`;
    case "md-wd":
    default:        return `${d.getMonth()+1}/${d.getDate()} (${wd})`;
  }
};
const fmtFullDate = (s, lang) => {
  const d = parseYMD(s);
  if (!d) return s || "—";
  const ss = _settings();
  const useLang = lang || (ss.lang === "en" ? "en" : "jp");
  const wd = (useLang === "jp" ? JP_WD : EN_WD)[d.getDay()];
  return useLang === "jp"
    ? `${d.getFullYear()}年${d.getMonth()+1}月${d.getDate()}日 (${wd})`
    : `${wd}, ${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
};
// Format "HH:MM" respecting settings.timeFormat (12h/24h)
const fmtHM = (hm) => {
  if (!hm || typeof hm !== "string") return hm || "";
  const m = hm.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return hm;
  const h = parseInt(m[1], 10), mm = m[2];
  if ((_settings().timeFormat || "24h") === "12h") {
    const ampm = h < 12 ? "AM" : "PM";
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${mm} ${ampm}`;
  }
  return `${pad2(h)}:${mm}`;
};
const isWeekend = (s) => {
  const d = parseYMD(s);
  return d && (d.getDay() === 0 || d.getDay() === 6);
};
const isSaturday = (s) => parseYMD(s)?.getDay() === 6;
const isSunday = (s) => parseYMD(s)?.getDay() === 0;
const getHoliday = (s) => JP_HOLIDAYS[s] || null;

Object.assign(window, {
  JP_HOLIDAYS, JP_WD, EN_WD,
  pad2, fmtYMD, parseYMD, todayYMD,
  fmtMD, fmtFullDate, fmtHM,
  isWeekend, isSaturday, isSunday, getHoliday,
});


;/* ==== recurrence.jsx ==== */
// Recurrence — registration-time fan-out engine for repeating tasks (2026-06-11).
//
// Design (approved by Takeuchi-san):
//   案A: on save, all occurrences are materialized as ordinary `items` rows that
//   share a `seriesId` + a denormalized copy of the rule (`recur`). Every view
//   (Board/Owners/Calendar/Table/Today/Dashboard) keeps reading the items array
//   unchanged — no virtual expansion anywhere.
//
// rule = {
//   freq: "daily" | "weekday" | "weekly" | "biweekly" | "monthly" | "monthlyNth" | "custom",
//   weekdays:   [0..6]            (weekly/biweekly; 0=Sun)
//   customDays: N >= 2            (custom: every N days)
//   nth:        1..4 | -1 (last)  (monthlyNth)
//   nthWeekday: 0..6              (monthlyNth)
//   end:  { type: "date"|"count", date?: "YYYY-MM-DD", count?: N }   // REQUIRED — no endless series
//   holidayShift: bool            (default true: occurrence on a JP holiday slides to next business day)
// }
//
// Hard caps (guard against KV bloat / typo'd end conditions):
const RECUR_MAX_COUNT = 120;   // instances per series
const RECUR_MAX_DAYS  = 366;   // horizon from the start date

let __seriesUid = 0;
const newSeriesId = () => `ser${Date.now().toString(36)}${(__seriesUid++).toString(36)}`;

// ── date helpers (parseYMD/fmtYMD/getHoliday/JP_WD come from holidays.jsx) ──
const __rcAddDays = (d, n) => { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); x.setDate(x.getDate() + n); return x; };
const __rcIsBizDay = (d) => d.getDay() !== 0 && d.getDay() !== 6 && !getHoliday(fmtYMD(d));
const __rcNextBizDay = (d) => { let x = __rcAddDays(d, 1); for (let i = 0; i < 14 && !__rcIsBizDay(x); i++) x = __rcAddDays(x, 1); return x; };
const __rcMondayOf = (d) => __rcAddDays(d, -((d.getDay() + 6) % 7));
const __rcClampToMonth = (y, mIdx, dom) => {
  const last = new Date(y, mIdx + 1, 0).getDate();
  return new Date(y, mIdx, Math.min(dom, last));
};
// nth weekday of a month; nth = 1..4, or -1 for "last". Always exists for 1..4? No —
// the 4th always exists, a literal 5th doesn't; we only ever emit 1..4 / -1.
const __rcNthWeekday = (y, mIdx, weekday, nth) => {
  if (nth === -1) {
    const last = new Date(y, mIdx + 1, 0);
    return __rcAddDays(last, -((last.getDay() - weekday + 7) % 7));
  }
  const first = new Date(y, mIdx, 1);
  const day = 1 + ((weekday - first.getDay() + 7) % 7) + (nth - 1) * 7;
  if (day > new Date(y, mIdx + 1, 0).getDate()) return null;
  return new Date(y, mIdx, day);
};
const __rcNthOfDate = (d) => (d.getDate() > 28 ? -1 : Math.min(Math.ceil(d.getDate() / 7), 4));
const __rcAddMonthsClamp = (d, n) => {
  const y = d.getFullYear(), m = d.getMonth() + n;
  return __rcClampToMonth(new Date(y, m, 1).getFullYear(), ((m % 12) + 12) % 12, d.getDate());
};

const __rcRuleOf = (v) => (v && v.freq && v.freq !== "none" ? v : null);

// Expand a rule into sorted YYYY-MM-DD strings (start date inclusive when it matches).
// Holiday shift happens per-occurrence; duplicates created by shifting are merged,
// and when end.type === "count" the walk keeps going until the FINAL count is met
// (so a merged duplicate doesn't silently shrink the series).
const buildRecurrenceDates = (startYmd, rule) => {
  const d0 = parseYMD(startYmd);
  rule = __rcRuleOf(rule);
  if (!d0) return [];
  if (!rule) return [startYmd];

  const end = rule.end || {};
  const wantCount = end.type === "count"
    ? Math.max(1, Math.min(parseInt(end.count, 10) || 1, RECUR_MAX_COUNT))
    : RECUR_MAX_COUNT;
  const hardEnd = __rcAddDays(d0, RECUR_MAX_DAYS);
  const endDate = end.type === "date" ? parseYMD(end.date) : null;
  const lastDate = endDate && endDate < hardEnd ? endDate : hardEnd;

  const seen = new Set();
  const out = [];
  const push = (d) => {
    let x = d;
    if (rule.holidayShift !== false && getHoliday(fmtYMD(x))) x = __rcNextBizDay(x);
    const s = fmtYMD(x);
    if (!seen.has(s)) { seen.add(s); out.push(s); }
  };
  const full = () => out.length >= wantCount;

  if (rule.freq === "daily" || rule.freq === "custom") {
    const step = rule.freq === "custom" ? Math.max(2, Math.min(parseInt(rule.customDays, 10) || 2, 365)) : 1;
    for (let d = d0; d <= lastDate && !full(); d = __rcAddDays(d, step)) push(d);
  } else if (rule.freq === "weekday") {
    for (let d = d0; d <= lastDate && !full(); d = __rcAddDays(d, 1)) {
      if (d.getDay() >= 1 && d.getDay() <= 5) {
        // 平日のみ: a holiday weekday is skipped outright — shifting it would just
        // collide with the next weekday's own occurrence.
        if (rule.holidayShift !== false && getHoliday(fmtYMD(d))) continue;
        push(d);
      }
    }
  } else if (rule.freq === "weekly" || rule.freq === "biweekly") {
    const wds = Array.isArray(rule.weekdays) && rule.weekdays.length ? rule.weekdays : [d0.getDay()];
    const iv = rule.freq === "biweekly" ? 2 : 1;
    const anchor = __rcMondayOf(d0).getTime();
    for (let d = d0; d <= lastDate && !full(); d = __rcAddDays(d, 1)) {
      const weekIdx = Math.round((__rcMondayOf(d).getTime() - anchor) / (7 * 864e5));
      if (wds.includes(d.getDay()) && weekIdx % iv === 0) push(d);
    }
  } else if (rule.freq === "monthly") {
    const dom = d0.getDate();
    for (let m = 0; m < 13 && !full(); m++) {
      const d = __rcClampToMonth(d0.getFullYear(), d0.getMonth() + m, dom);
      if (d < d0) continue;
      if (d > lastDate) break;
      push(d);
    }
  } else if (rule.freq === "monthlyNth") {
    const wd = rule.nthWeekday ?? d0.getDay();
    const nth = rule.nth ?? __rcNthOfDate(d0);
    for (let m = 0; m < 13 && !full(); m++) {
      // new Date() normalizes month overflow (month 13 → Jan next year)
      const ref = new Date(d0.getFullYear(), d0.getMonth() + m, 1);
      const dd = __rcNthWeekday(ref.getFullYear(), ref.getMonth(), wd, nth);
      if (!dd || dd < d0) continue;
      if (dd > lastDate) break;
      push(dd);
    }
  }

  out.sort();
  return out;
};

// Human label, e.g. 「毎週 月・水 ・9/30まで」「毎月第2火曜日 ・全12回」
const describeRecur = (rule, startYmd) => {
  rule = __rcRuleOf(rule);
  if (!rule) return "";
  const d0 = startYmd ? parseYMD(startYmd) : null;
  let head = "";
  if (rule.freq === "daily") head = "毎日";
  else if (rule.freq === "weekday") head = "平日のみ";
  else if (rule.freq === "weekly" || rule.freq === "biweekly") {
    const wds = Array.isArray(rule.weekdays) && rule.weekdays.length ? rule.weekdays : (d0 ? [d0.getDay()] : []);
    head = (rule.freq === "biweekly" ? "隔週 " : "毎週 ") + [...wds].sort().map(w => JP_WD[w]).join("・");
  } else if (rule.freq === "monthly") head = d0 ? `毎月${d0.getDate()}日` : "毎月同日";
  else if (rule.freq === "monthlyNth") {
    const wd = rule.nthWeekday ?? (d0 ? d0.getDay() : 1);
    const nth = rule.nth ?? (d0 ? __rcNthOfDate(d0) : 1);
    head = `毎月${nth === -1 ? "最終" : "第" + nth}${JP_WD[wd]}曜`;
  } else if (rule.freq === "custom") head = `${rule.customDays || 2}日ごと`;
  const end = rule.end || {};
  const tail = end.type === "count" ? `・全${end.count}回`
    : end.type === "date" && end.date ? `・${end.date.slice(5).split("-").map(Number).join("/")}まで` : "";
  return head + tail;
};

// ── 🔁 badge shown on any series instance, in every view ────────────────────
const RecurBadge = ({ it }) => {
  if (!it || !it.seriesId) return null;
  return (
    <span
      title={"繰り返し: " + (describeRecur(it.recur, it.date) || "シリーズ")}
      style={{ marginLeft: 5, fontSize: 10, opacity: 0.8, flexShrink: 0, cursor: "default" }}
    >🔁</span>
  );
};

// ── modal form section (rendered by EntityModal for field type "recur") ─────
const RecurrenceField = ({ value, onChange, date }) => {
  const rule = __rcRuleOf(value);
  const d0 = date ? parseYMD(date) : null;
  if (!d0) {
    return <div className="form-hint" style={{ marginTop: 2 }}>期限日（Date）を設定すると繰り返しを指定できます</div>;
  }

  const defEnd = { type: "date", date: fmtYMD(__rcAddMonthsClamp(d0, 3)), count: 10 };
  const onFreq = (f) => {
    if (f === "none") { onChange(null); return; }
    const next = { freq: f, holidayShift: rule ? rule.holidayShift !== false : true, end: (rule && rule.end) || defEnd };
    if (f === "weekly" || f === "biweekly") next.weekdays = (rule && rule.weekdays && rule.weekdays.length) ? rule.weekdays : [d0.getDay()];
    if (f === "custom") next.customDays = (rule && rule.customDays) || 3;
    if (f === "monthlyNth") { next.nth = __rcNthOfDate(d0); next.nthWeekday = d0.getDay(); }
    onChange(next);
  };
  const set = (patch) => onChange({ ...rule, ...patch });
  const setEnd = (patch) => set({ end: { ...((rule && rule.end) || defEnd), ...patch } });

  const nthLabel = `毎月${__rcNthOfDate(d0) === -1 ? "最終" : "第" + __rcNthOfDate(d0)}${JP_WD[d0.getDay()]}曜日`;
  const preview = rule ? buildRecurrenceDates(date, rule) : [];
  const md = (s) => s.slice(5).split("-").map(Number).join("/");
  const previewText = rule
    ? (preview.length <= 1
        ? "⚠ 生成される回がありません（終了条件を確認）"
        : `全${preview.length}回: ${preview.slice(0, 3).map(md).join(", ")}${preview.length > 3 ? ` … ${md(preview[preview.length - 1])}` : ""}`)
    : "";
  const end = (rule && rule.end) || defEnd;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <select className="form-input" value={rule ? rule.freq : "none"} onChange={e => onFreq(e.target.value)}>
        <option value="none">なし（単発）</option>
        <option value="daily">毎日</option>
        <option value="weekday">平日のみ（土日祝を除く）</option>
        <option value="weekly">毎週（曜日指定）</option>
        <option value="biweekly">隔週（曜日指定）</option>
        <option value="monthly">毎月 {d0.getDate()}日</option>
        <option value="monthlyNth">{nthLabel}</option>
        <option value="custom">カスタム（N日ごと）</option>
      </select>

      {rule && (rule.freq === "weekly" || rule.freq === "biweekly") && (
        <div className="chip-picker">
          {JP_WD.map((w, i) => {
            const active = (rule.weekdays || []).includes(i);
            return (
              <button key={i} type="button"
                className={"chip-btn" + (active ? " active" : "")}
                onClick={() => {
                  const cur = rule.weekdays || [];
                  const next = active ? cur.filter(x => x !== i) : [...cur, i];
                  set({ weekdays: next.length ? next : [d0.getDay()] });
                }}
              >{w}</button>
            );
          })}
        </div>
      )}

      {rule && rule.freq === "custom" && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input className="form-input" type="number" min={2} max={365} style={{ width: 90 }}
            value={rule.customDays || 3}
            onChange={e => set({ customDays: Math.max(2, Math.min(365, parseInt(e.target.value, 10) || 2)) })}/>
          <span style={{ fontSize: 12, color: "var(--ash)" }}>日ごと</span>
        </div>
      )}

      {rule && (
        <React.Fragment>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <select className="form-input" style={{ width: 110, flexShrink: 0 }} value={end.type}
              onChange={e => setEnd({ type: e.target.value })}>
              <option value="date">終了日</option>
              <option value="count">回数</option>
            </select>
            {end.type === "date" ? (
              <input className="form-input" type="date" min={date} value={end.date || ""}
                onChange={e => setEnd({ date: e.target.value })}/>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input className="form-input" type="number" min={1} max={RECUR_MAX_COUNT} style={{ width: 90 }}
                  value={end.count || 10}
                  onChange={e => setEnd({ count: Math.max(1, Math.min(RECUR_MAX_COUNT, parseInt(e.target.value, 10) || 1)) })}/>
                <span style={{ fontSize: 12, color: "var(--ash)" }}>回</span>
              </div>
            )}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--ivory)", cursor: "pointer", userSelect: "none" }}>
            <input type="checkbox" checked={rule.holidayShift !== false}
              onChange={e => set({ holidayShift: e.target.checked })}/>
            祝日に当たる回は翌営業日へずらす
          </label>
          <div className="form-hint" style={{ marginTop: 0 }}>
            🔁 {previewText}（上限: {RECUR_MAX_COUNT}回・1年先まで）
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

// ── scope chooser shown when editing/deleting a series instance ─────────────
const SeriesScopeDialog = ({ open, kind, onPick, onClose }) => {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const isDelete = kind === "delete";
  const Btn = ({ label, sub, scope, danger }) => (
    <button
      className="btn ghost"
      style={{ width: "100%", justifyContent: "flex-start", padding: "10px 14px", flexDirection: "column", alignItems: "flex-start", gap: 2,
        ...(danger ? { color: "var(--crimson-hi)", borderColor: "rgba(196,30,58,0.32)" } : {}) }}
      onClick={() => onPick(scope)}
    >
      <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 11, color: "var(--ash)", fontWeight: 400 }}>{sub}</span>
    </button>
  );
  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={(e) => { if (e.target.classList.contains("modal-overlay")) onClose(); }}>
      <div className="modal-stage" style={{ maxWidth: 440 }}>
        <div className="modal-head">
          <div style={{ flex: 1 }}>
            <span className="eyebrow">🔁 SERIES</span>
            <h2 className="modal-title">{isDelete ? "繰り返しタスクの削除" : "繰り返しタスクの変更"}</h2>
          </div>
          <button className="icon-btn" onClick={onClose} title="閉じる (Esc)" aria-label="閉じる"><Icon name="x" size={18}/></button>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Btn scope="one" label="この回のみ" danger={isDelete}
            sub={isDelete ? "この1件だけ削除します" : "この1件だけ変更します（シリーズの他の回はそのまま）"}/>
          <Btn scope="future" label="この回以降すべて" danger={isDelete}
            sub={isDelete ? "この回と、これより後の回をすべて削除します" : "この回と、これより後の回に変更を適用します（完了済みは保持）"}/>
          {isDelete && (
            <Btn scope="all" label="シリーズ全体" danger
              sub="過去の回・完了済みも含めて、このシリーズをすべて削除します"/>
          )}
        </div>
        <div className="modal-foot">
          <div style={{ flex: 1 }}/>
          <button className="btn ghost" onClick={onClose}>キャンセル</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

Object.assign(window, {
  RECUR_MAX_COUNT, RECUR_MAX_DAYS,
  newSeriesId, buildRecurrenceDates, describeRecur,
  RecurBadge, RecurrenceField, SeriesScopeDialog,
});


;/* ==== icons.jsx ==== */
// SVG icons — line style, weighty stroke
const Icon = ({ name, size = 18, color = "currentColor", className = "" }) => {
  const props = {
    width: size, height: size, viewBox: "0 0 24 24",
    fill: "none", stroke: color, strokeWidth: 1.6,
    strokeLinecap: "round", strokeLinejoin: "round",
    className,
  };
  const paths = {
    dashboard: <><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></>,
    tasks: <><polyline points="3 7 5 9 9 5"/><polyline points="3 14 5 16 9 12"/><line x1="13" y1="7" x2="21" y2="7"/><line x1="13" y1="14" x2="21" y2="14"/><line x1="3" y1="20" x2="21" y2="20"/></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="3" x2="8" y2="7"/><line x1="16" y1="3" x2="16" y2="7"/></>,
    folder: <><path d="M3 7a2 2 0 0 1 2-2h4l2 3h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z"/></>,
    note: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="14 3 14 9 20 9"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></>,
    chart: <><path d="M3 3v18h18"/><polyline points="7 14 11 10 14 13 20 7"/></>,
    users: <><circle cx="9" cy="8" r="3"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><circle cx="17" cy="6" r="2.5"/><path d="M21 18c0-2.5-1.8-4-4-4"/></>,
    book: <><path d="M4 5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 1-2-2V5z"/><path d="M19 21H6a2 2 0 0 0-2-2"/></>,
    star: <><polygon points="12 2 14.7 8.6 22 9.4 16.5 14.1 18.2 21.4 12 17.5 5.8 21.4 7.5 14.1 2 9.4 9.3 8.6 12 2"/></>,
    search: <><circle cx="11" cy="11" r="7"/><line x1="20" y1="20" x2="16.5" y2="16.5"/></>,
    bell: <><path d="M18 16v-6a6 6 0 0 0-12 0v6l-2 2h16l-2-2z"/><path d="M10 20a2 2 0 0 0 4 0"/></>,
    mic: <><rect x="9" y="2" width="6" height="13" rx="3"/><path d="M5 11a7 7 0 0 0 14 0"/><line x1="12" y1="18" x2="12" y2="22"/></>,
    micOff: <><line x1="3" y1="3" x2="21" y2="21"/><path d="M9 5a3 3 0 0 1 6 0v6m-6 0V8"/><path d="M5 11a7 7 0 0 0 11 5"/><line x1="12" y1="18" x2="12" y2="22"/></>,
    stop: <><rect x="6" y="6" width="12" height="12" rx="2"/></>,
    play: <><polygon points="6 4 20 12 6 20 6 4"/></>,
    pause: <><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></>,
    plus: <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>,
    minus: <><line x1="5" y1="12" x2="19" y2="12"/></>,
    x: <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></>,
    menu: <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>,
    check: <><polyline points="4 12 10 18 20 6"/></>,
    chevronRight: <><polyline points="9 6 15 12 9 18"/></>,
    chevronLeft: <><polyline points="15 6 9 12 15 18"/></>,
    chevronDown: <><polyline points="6 9 12 15 18 9"/></>,
    clock: <><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 16 14"/></>,
    flame: <><path d="M12 2c2 4 6 6 6 11a6 6 0 0 1-12 0c0-3 1.5-4 3-7 2 2 2 4 3-4z"/></>,
    target: <><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5" fill={color}/></>,
    sparkle: <><path d="M12 2v6m0 8v6M2 12h6m8 0h6M5 5l3.5 3.5M15.5 15.5L19 19M19 5l-3.5 3.5M8.5 15.5L5 19"/></>,
    waveform: <><line x1="3" y1="12" x2="3" y2="12"/><line x1="7" y1="8" x2="7" y2="16"/><line x1="11" y1="4" x2="11" y2="20"/><line x1="15" y1="9" x2="15" y2="15"/><line x1="19" y1="6" x2="19" y2="18"/></>,
    edit: <><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></>,
    trash: <><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></>,
    download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
    copy: <><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>,
    sun: <><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/><line x1="5" y1="5" x2="6.5" y2="6.5"/><line x1="17.5" y1="17.5" x2="19" y2="19"/><line x1="5" y1="19" x2="6.5" y2="17.5"/><line x1="17.5" y1="6.5" x2="19" y2="5"/></>,
    settings: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>,
    arrow: <><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></>,
    refresh: <><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></>,
    send: <><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>,
    eye: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    eyeOff: <><path d="M9.9 5a10.7 10.7 0 0 1 2.1-.2c7 0 11 7.2 11 7.2a18 18 0 0 1-3 3.6M6.6 6.6A18 18 0 0 0 1 12s4 7.2 11 7.2a10.7 10.7 0 0 0 4-.7"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/><line x1="2" y1="2" x2="22" y2="22"/></>,
    building: <><rect x="4" y="3" width="16" height="18" rx="1"/></>,
    cog: <><circle cx="12" cy="12" r="2"/><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/></>,
    alert: <><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    review: <><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 15"/></>,
    image: <><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></>,
    camera: <><path d="M4 8a2 2 0 0 1 2-2h2.5l1.5-2h4l1.5 2H18a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8z"/><circle cx="12" cy="13" r="4"/></>,
    cameraFlip: <><path d="M4 8a2 2 0 0 1 2-2h2.5l1.5-2h4l1.5 2H18a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8z"/><path d="M9 13a3 3 0 0 1 5.2-2M15 13a3 3 0 0 1-5.2 2"/><polyline points="14.2 11 14.2 13 12.2 13"/><polyline points="9.8 15 9.8 13 11.8 13"/></>,
    paste: <><path d="M9 2h6v3H9z"/><path d="M5 5h14v16H5z"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/></>,
  };
  return <svg {...props}>{paths[name] || null}</svg>;
};

// SUPERSYS original emblem — a faceted crystal hexagon badge ("fortress / HQ")
// with a four-point command star at its core. Built from simple geometry; no
// third-party / trademarked artwork. Inherits brand palette (navy / gold / crimson).
const Shield = ({ size = 46 }) => {
  const w = Math.round(size * 0.9);
  return (
    <svg
      width={w}
      height={size}
      viewBox="0 0 64 72"
      fill="none"
      role="img"
      aria-label="SUPERSYS"
      style={{
        flexShrink: 0,
        filter: "drop-shadow(0 4px 12px rgba(196,30,58,0.40)) drop-shadow(0 1px 2px rgba(0,0,0,0.55))",
      }}
    >
      <defs>
        <linearGradient id="sys-badge" x1="32" y1="2" x2="32" y2="70" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#1F3370"/>
          <stop offset="0.55" stopColor="#13205A"/>
          <stop offset="1" stopColor="#0A1030"/>
        </linearGradient>
        <linearGradient id="sys-rim" x1="32" y1="2" x2="32" y2="70" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FFE066"/>
          <stop offset="0.5" stopColor="#F5C518"/>
          <stop offset="1" stopColor="#B8860B"/>
        </linearGradient>
        <linearGradient id="sys-star" x1="32" y1="12" x2="32" y2="60" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FFE066"/>
          <stop offset="0.5" stopColor="#F5C518"/>
          <stop offset="1" stopColor="#C99A12"/>
        </linearGradient>
      </defs>
      {/* hexagon badge */}
      <path d="M32 2 L60 17.5 V54.5 L32 70 L4 54.5 V17.5 Z" fill="url(#sys-badge)" stroke="url(#sys-rim)" strokeWidth="2.4" strokeLinejoin="round"/>
      {/* inner faceted rim */}
      <path d="M32 9 L54 21.6 V50.4 L32 63 L10 50.4 V21.6 Z" fill="none" stroke="#5B7BC4" strokeOpacity="0.45" strokeWidth="1"/>
      {/* four-point command star */}
      <path d="M32 13 L39 29 L56 36 L39 43 L32 59 L25 43 L8 36 L25 29 Z" fill="url(#sys-star)"/>
      {/* crystalline facet highlight */}
      <path d="M32 13 L32 59 M8 36 L56 36" stroke="#0A1030" strokeOpacity="0.35" strokeWidth="0.8"/>
      <path d="M32 13 L39 29 L32 36 L25 29 Z" fill="#FFFFFF" fillOpacity="0.22"/>
      {/* crimson core node */}
      <circle cx="32" cy="36" r="3.4" fill="#C41E3A" stroke="#FFE066" strokeWidth="0.8"/>
    </svg>
  );
};

Object.assign(window, { Icon, Shield });


;/* ==== storage.jsx ==== */
// Persistence — SERVER-BACKED (Cloudflare KV for state, R2 for audio/screenshots).
//
// This file replaces the prototype's localStorage + IndexedDB + File System Access
// implementation. It keeps the EXACT same exported function names so app.jsx and
// every view work unchanged. The whole app state is a single JSON document stored
// in KV (revision-versioned, last-write-wins); audio and full-res screenshots are
// binary objects in R2, served back as /api/blobs/<kind>/<id> URLs that survive
// reloads (no blob:/idb: rehydration needed).

// apiFetch / apiJSON are global functions defined in _bootstrap.jsx (same bundle scope).

function blobUrl(kind, id) { return `/api/blobs/${kind}/${id}`; }

async function urlToBlob(url) {
  try { const r = await fetch(url); return await r.blob(); } catch (e) { return null; }
}

async function uploadBlob(kind, id, blob) {
  await apiFetch(`/blobs/${kind}/${id}`, {
    method: "PUT",
    headers: { "Content-Type": blob.type || "application/octet-stream" },
    body: blob,
  });
}

// ── State (de)serialization ──────────────────────────────────────────────────
// Replace live blob: audio URLs with their durable server path before saving.
function serializeState(state) {
  const recordings = (state.recordings || []).map((r) => ({
    ...r,
    audioUrl:
      r.audioUrl && r.audioUrl.startsWith && r.audioUrl.startsWith("blob:")
        ? blobUrl("audio", r.id)
        : r.audioUrl,
  }));
  return { ...state, recordings, __version: 1, __savedAt: Date.now() };
}

// ── Contact note → dedicated fields (2026-06-12) ────────────────────────────
// The card scanner used to pack extras into the note as
// "部署: X / 携帯: Y / URL: Z / かな: W" (and the AI sometimes wrote FAX there).
// Those now have dedicated contact fields — pull recognizable labelled values
// out of the note. A value is only promoted into an EMPTY field; if the field
// already holds something different, the note segment is kept (never lose data).
const noteLabelToField = (label) => {
  const u = String(label).toUpperCase();
  if (u === "URL") return "website";
  if (u === "FAX") return "fax";
  if (label === "部署") return "department";
  if (label === "携帯") return "mobile";
  if (label === "かな" || label === "ふりがな") return "kana";
  return null;
};

function promoteContactNoteFields(c) {
  if (!c || typeof c.note !== "string" || !c.note.trim()) return c;
  const out = { ...c };
  const keptLines = [];
  for (const line of c.note.split("\n")) {
    const keptSegs = [];
    for (const seg of line.split(" / ")) {
      const m = seg.match(/^\s*(部署|携帯|かな|ふりがな|URL|FAX)\s*[:：]\s*(.+?)\s*$/i);
      const key = m ? noteLabelToField(m[1]) : null;
      if (key && m[2]) {
        if (!out[key]) { out[key] = m[2]; continue; } // promoted — drop from note
        if (out[key] === m[2]) continue;              // already there — drop duplicate
      }
      if (seg.trim()) keptSegs.push(seg.trim());
    }
    if (keptSegs.length) keptLines.push(keptSegs.join(" / "));
  }
  out.note = keptLines.join("\n");
  return out;
}

// Defensive: ensure every collection exists and items carry ids (ported from the
// prototype's migrateState, minus the legacy local-only migrations).
function migrateCollections(parsed) {
  if (!parsed) return parsed;
  const arr = (x) => (Array.isArray(x) ? x : []);
  parsed.items = arr(parsed.items);
  parsed.projects = arr(parsed.projects);
  parsed.recordings = arr(parsed.recordings);
  parsed.ideas = arr(parsed.ideas);
  parsed.contacts = arr(parsed.contacts);
  parsed.books = arr(parsed.books);
  parsed.applicants = arr(parsed.applicants);
  parsed.roles = arr(parsed.roles);
  parsed.templates = arr(parsed.templates);
  parsed.youtube = arr(parsed.youtube);
  parsed.alerts = arr(parsed.alerts);
  parsed.screenshots = arr(parsed.screenshots);
  parsed.quickMemos = arr(parsed.quickMemos);
  // 2026-06-12 one-time replacement of the projects collection with the current
  // 8 in-flight PJs (explicit request: 「Projectsを下記のプロジェクトで置き換えて」).
  // Runs once per state document — the flag persists with the next save, so later
  // user edits to projects are never clobbered. PROJECTS_RESET_V2 lives in data.jsx
  // (same concatenated scope, loaded before this file).
  if (!parsed.__projectsResetV2) {
    parsed.projects = PROJECTS_RESET_V2.map((p) => ({ ...p }));
    parsed.__projectsResetV2 = true;
  }
  // 2026-06-12 one-time promotion of note-packed contact extras (部署/携帯/
  // かな/URL/FAX) into their new dedicated fields. Same flag pattern as above.
  if (!parsed.__contactsFieldsV2) {
    parsed.contacts = parsed.contacts.map(promoteContactNoteFields);
    parsed.__contactsFieldsV2 = true;
  }
  // Ensure every item has a NON-EMPTY, UNIQUE id. Missing ids are filled; ids that
  // collide with an earlier item are reassigned. (Colliding keys make React fail to
  // reconcile a row's removal until a full reload — the "delete doesn't show until
  // refresh" bug. The old AI bulk-import generated the same `n<ms>` id for every
  // record in a tight loop, so a whole batch shared one id.)
  const ensureIds = (a, prefix) => {
    if (!Array.isArray(a)) return a;
    const seen = new Set();
    const base = parsed.__savedAt || 0;
    return a.map((it, i) => {
      let id = it && it.id;
      if (!id || seen.has(id)) {
        id = `${prefix}${i}_${base}_${Math.random().toString(36).slice(2, 8)}`;
      }
      seen.add(id);
      return it && it.id === id ? it : { ...it, id };
    });
  };
  parsed.items = ensureIds(parsed.items, "i");
  parsed.projects = ensureIds(parsed.projects, "p");
  parsed.recordings = ensureIds(parsed.recordings, "r");
  parsed.ideas = ensureIds(parsed.ideas, "idea");
  parsed.contacts = ensureIds(parsed.contacts, "c");
  parsed.books = ensureIds(parsed.books, "b");
  parsed.applicants = ensureIds(parsed.applicants, "a");
  parsed.roles = ensureIds(parsed.roles, "role");
  parsed.templates = ensureIds(parsed.templates, "tpl");
  parsed.youtube = ensureIds(parsed.youtube, "yt");
  parsed.alerts = ensureIds(parsed.alerts, "al");
  parsed.screenshots = ensureIds(parsed.screenshots, "shot");
  parsed.quickMemos = ensureIds(parsed.quickMemos, "qm");
  return parsed;
}

// ── Load / Save against /api/state ─────────────────────────────────────────────
let __rev = 0;
let __saving = false;
let __pending = null;

async function loadState() {
  try {
    const data = await apiJSON("/state");
    if (!data || !data.state) return null;
    __rev = data.rev || 0;
    return migrateCollections(data.state);
  } catch (e) {
    if (e && e.message === "UNAUTHORIZED") return null;
    console.warn("loadState failed", e);
    return null;
  }
}

async function doPut(payload) {
  __saving = true;
  try {
    // NOTE: no `keepalive` — it caps the body at 64KB and the full state document
    // is larger, which made the browser throw "Failed to fetch". The 400ms debounced
    // saves during use cover persistence; the unload flush is best-effort.
    const data = await apiJSON("/state", {
      method: "PUT",
      body: JSON.stringify({ state: payload, rev: __rev }),
    });
    if (data && typeof data.rev === "number") __rev = data.rev;
  } catch (e) {
    if (e && e.message === "stale") {
      // 409 — another device saved a newer document while this tab was
      // suspended. Adopt the server copy instead of overwriting it.
      try {
        const fresh = await loadState();
        if (fresh) window.dispatchEvent(new CustomEvent("supersys:state-adopted", { detail: fresh }));
      } catch {}
    } else if (!(e && e.message === "UNAUTHORIZED")) console.warn("saveState PUT failed", e);
  } finally {
    __saving = false;
    if (__pending) { const p = __pending; __pending = null; doPut(p); }
  }
}

// Foreground re-sync: returns the migrated server state only when it is NEWER
// than what this tab last loaded/saved, null otherwise. Skipped while a local
// save is in flight so an in-progress edit is never thrown away.
async function fetchStateIfNewer() {
  if (__saving || __pending) return null;
  try {
    const data = await apiJSON("/state");
    if (!data || !data.state) return null;
    if ((data.rev || 0) <= __rev) return null;
    __rev = data.rev || 0;
    return migrateCollections(data.state);
  } catch (e) {
    return null;
  }
}

// Optimistic: returns true synchronously (app.jsx uses the bool to stamp savedAt).
// Coalesces rapid edits so only one PUT is in flight at a time.
function saveState(state) {
  const payload = serializeState(state);
  if (__saving) __pending = payload;
  else doPut(payload);
  return true;
}

// ── Audio (R2) ────────────────────────────────────────────────────────────────
async function persistAudio(id, blobUrlStr) {
  if (!blobUrlStr || !blobUrlStr.startsWith || !blobUrlStr.startsWith("blob:")) return;
  const blob = await urlToBlob(blobUrlStr);
  if (blob) await uploadBlob("audio", id, blob);
}
async function idbDelete(id) {
  try { await apiFetch(`/blobs/audio/${id}`, { method: "DELETE" }); } catch (e) {}
}

// ── Screenshots (R2) ────────────────────────────────────────────────────────────
async function persistScreenshot(id, dataUrlOrBlob) {
  let blob = dataUrlOrBlob;
  if (typeof dataUrlOrBlob === "string") {
    const r = await fetch(dataUrlOrBlob);
    blob = await r.blob();
  }
  if (!blob) return null;
  await uploadBlob("shot", id, blob);
  return blobUrl("shot", id);
}
async function loadScreenshotUrl(id) { return blobUrl("shot", id); }
async function idbDeleteScreenshot(id) {
  try { await apiFetch(`/blobs/shot/${id}`, { method: "DELETE" }); } catch (e) {}
}

// ── Reset / Export / Import ─────────────────────────────────────────────────────
async function clearAllData() {
  try { await apiFetch("/state", { method: "DELETE" }); } catch (e) { console.warn("clearAllData failed", e); }
}

function exportData(state) {
  const ser = serializeState(state);
  const json = JSON.stringify(ser, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `supersys-backup-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function importData(file) {
  const text = await file.text();
  return JSON.parse(text);
}

async function storageStats() {
  try {
    const data = await apiJSON("/state");
    const bytes = data && data.state ? JSON.stringify(data.state).length * 2 : 0;
    const audioCount = (data && data.state && data.state.recordings ? data.state.recordings : []).length;
    return { localBytes: bytes, audioCount, audioBytes: 0 };
  } catch (e) {
    return { localBytes: 0, audioCount: 0, audioBytes: 0 };
  }
}

// ── Folder sync — not applicable in server mode (data is already shared). Stubs
// keep the Tweaks/Settings "Sync Folder" section from crashing; it renders an
// "unsupported" message. ────────────────────────────────────────────────────────
const FS_SUPPORTED = false;
async function connectFolder() { throw new Error("サーバ共有モードではフォルダ同期は不要です。データは自動的に全端末で共有されます。"); }
async function disconnectFolder() {}
async function getFolderHandle() { return null; }
async function folderStatus() { return { supported: false, connected: false, name: null }; }
async function writeStateToFolder() { return false; }
async function readStateFromFolder() { return null; }
async function writeAudioToFolder() { return false; }
async function deleteAudioFromFolder() {}
async function writeScreenshotToFolder() { return false; }
async function deleteScreenshotFromFolder() {}

Object.assign(window, {
  saveState, loadState, fetchStateIfNewer, persistAudio, idbDelete, migrateCollections, promoteContactNoteFields,
  clearAllData, exportData, importData, storageStats,
  persistScreenshot, loadScreenshotUrl, idbDeleteScreenshot,
  writeScreenshotToFolder, deleteScreenshotFromFolder,
  FS_SUPPORTED, connectFolder, disconnectFolder, getFolderHandle,
  folderStatus, writeStateToFolder, writeAudioToFolder,
  deleteAudioFromFolder, readStateFromFolder,
});


;/* ==== modals.jsx ==== */
// Generic entity edit modal — handles add/edit forms with delete
// Usage:
//   <EntityModal open schema={...} initial={...} onSave={vals=>...} onDelete={()=>...} onClose={...} />
//
// Schema:
//   {
//     title: "Edit Task" | "新規タスク",
//     eyebrow: "TASK",
//     fields: [
//       { key, label, type: 'text'|'textarea'|'select'|'number'|'chips', options?, required?, placeholder?, span?: 1|2 }
//     ]
//   }

// Compact time picker — two dropdowns (hour + 10-min steps) instead of the
// scroll-heavy native spinner. Stores value as "HH:MM" (or "" for all-day).
const TIME_HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const TIME_MINS = ["00", "10", "20", "30", "40", "50"];

const TimeSelect = ({ value, onChange }) => {
  const hasVal = value && value.includes(":");
  const [h, m] = hasVal ? value.split(":") : ["", ""];
  // If an imported value lands off a 10-min boundary, keep it as an extra option
  const minOptions = (!m || TIME_MINS.includes(m)) ? TIME_MINS : [...TIME_MINS, m].sort();

  const update = (nh, nm) => {
    if (!nh && !nm) { onChange(""); return; }
    onChange(`${(nh || "00").padStart(2, "0")}:${(nm || "00").padStart(2, "0")}`);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <select
        className="form-input"
        style={{ flex: 1, minWidth: 0 }}
        value={h}
        onChange={e => update(e.target.value, e.target.value ? (m || "00") : "")}
      >
        <option value="">時 --</option>
        {TIME_HOURS.map(hh => <option key={hh} value={hh}>{hh} 時</option>)}
      </select>
      <span style={{ color: "var(--smoke)", fontFamily: "var(--f-mono)", fontSize: 13 }}>:</span>
      <select
        className="form-input"
        style={{ flex: 1, minWidth: 0 }}
        value={m}
        onChange={e => update(h || "00", e.target.value)}
      >
        <option value="">分 --</option>
        {minOptions.map(mm => <option key={mm} value={mm}>{mm} 分</option>)}
      </select>
      {hasVal && (
        <button
          type="button"
          className="btn ghost"
          style={{ padding: "8px 10px", fontSize: 10 }}
          onClick={() => onChange("")}
          title="時刻をクリア（終日）"
        >クリア</button>
      )}
    </div>
  );
};

// Datetime entry that's far easier than a raw <input type="datetime-local">: a
// date picker (with the weekday/holiday badge) stacked over the same 10-min
// TimeSelect used for plain time fields. Stores "YYYY-MM-DDTHH:MM" when a time is
// set, or just "YYYY-MM-DD" when only a date is chosen — fmtNext/isPast handle
// either form, so a date-only interview shows cleanly without a stray 00:00.
const DateTimeSelect = ({ value, onChange }) => {
  const v = value || "";
  const datePart = /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : "";
  const timePart = v.length >= 16 && v[10] === "T" ? v.slice(11, 16) : "";
  const setDate = (d) => { if (!d) { onChange(""); return; } onChange(timePart ? `${d}T${timePart}` : d); };
  const setTime = (t) => { if (!datePart) { onChange(""); return; } onChange(t ? `${datePart}T${t}` : datePart); };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} role="group" aria-label="日時（日付と時刻）">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input
          className="form-input"
          type="date"
          value={datePart}
          onChange={e => setDate(e.target.value)}
          style={{ flex: 1 }}
          aria-label="日付"
        />
        {datePart && (() => {
          const d = parseYMD(datePart);
          if (!d) return null;
          const wd = JP_WD[d.getDay()];
          const hol = getHoliday(datePart);
          const isSat = d.getDay() === 6, isSun = d.getDay() === 0;
          return (
            <div className="date-badge">
              <span style={{ color: isSun || hol ? "var(--crimson-hi)" : isSat ? "var(--crystal)" : "var(--ivory)", fontWeight: 600 }}>{wd}</span>
              {hol && <span style={{ color: "var(--crimson-hi)", fontSize: 10, marginLeft: 6 }}>{hol}</span>}
            </div>
          );
        })()}
      </div>
      <TimeSelect value={timePart} onChange={setTime} />
    </div>
  );
};

const EntityModal = ({ open, schema, initial, onSave, onDelete, onClose }) => {
  const [values, setValues] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const [zoomSrc, setZoomSrc] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      const init = {};
      schema?.fields?.forEach(f => {
        let v = initial?.[f.key] !== undefined ? initial[f.key] : (f.default !== undefined ? f.default : (f.type === "number" ? 0 : ""));
        // A controlled <select> must hold a value that matches one of its
        // options. If the value is empty the browser still shows (and lets you
        // "keep") the first option, while React state stays "" — so required
        // validation fails and Save appears dead. Seed the first option instead.
        if ((f.type === "select" || f.type === "radio") && (v === "" || v === undefined || v === null) && Array.isArray(f.options) && f.options.length) {
          const first = f.options[0];
          v = typeof first === "object" ? first.value : first;
        }
        init[f.key] = v;
      });
      setValues(init);
      setErrors({});
    }
  }, [open, schema, initial]);

  // Escape closes (consistent with search overlay / lightbox), and the first
  // text field gets focus so typing can start without a click.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !zoomSrc) onClose(); };
    window.addEventListener("keydown", onKey);
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(".modal-overlay .modal-body input.form-input, .modal-overlay .modal-body textarea.form-input");
      if (el) el.focus();
    });
    return () => { window.removeEventListener("keydown", onKey); cancelAnimationFrame(raf); };
  }, [open, zoomSrc, onClose]);

  if (!open || !schema) return null;

  const set = (k, v) => setValues(s => ({ ...s, [k]: v }));

  const handleSave = () => {
    const errs = {};
    schema.fields.forEach(f => {
      if (f.required && (!values[f.key] || (typeof values[f.key] === "string" && !values[f.key].trim()))) {
        errs[f.key] = "Required";
      }
    });
    if (Object.keys(errs).length) {
      setErrors(errs);
      // Surface the (often scrolled-out-of-view) error so Save doesn't feel dead.
      requestAnimationFrame(() => {
        const body = document.querySelector('.modal-overlay .modal-body');
        const firstErrField = document.querySelector('.modal-overlay .form-error')?.closest('.form-field');
        if (body && firstErrField) {
          body.scrollTop = Math.max(0, firstErrField.offsetTop - 12);
        }
      });
      return;
    }
    // Preserve any non-schema keys on `initial` (e.g. cardImage on contacts)
    onSave({ ...(initial || {}), ...values });
  };

  const isEdit = !!initial;

  return ReactDOM.createPortal(
    <React.Fragment>
    <div className="modal-overlay" onClick={(e) => { if (e.target.classList.contains("modal-overlay")) onClose(); }}>
      <div className="modal-stage">
        <div className="modal-head">
          <div style={{flex:1}}>
            {schema.eyebrow && <span className="eyebrow">{schema.eyebrow}</span>}
            <h2 className="modal-title">{isEdit ? (schema.titleEdit || schema.title) : (schema.titleNew || schema.title)}</h2>
          </div>
          <button className="icon-btn" onClick={onClose} title="閉じる (Esc)" aria-label="閉じる">
            <Icon name="x" size={18} />
          </button>
        </div>

        <div className="modal-body">
          {schema.headerImageKey && initial?.[schema.headerImageKey] && (
            <div style={{marginBottom:16, textAlign:"center"}}>
              <button
                type="button"
                onClick={() => setZoomSrc(initial[schema.headerImageKey])}
                title="クリックで拡大"
                style={{display:"inline-block", maxWidth:"100%", background:"#0a0e1a", borderRadius:6, overflow:"hidden", border:"1px solid var(--hairline)", padding:0, cursor:"zoom-in"}}
              >
                <img src={initial[schema.headerImageKey]} alt="" style={{display:"block", maxWidth:"100%", maxHeight:200, objectFit:"contain"}}/>
              </button>
              <div style={{fontFamily:"var(--f-mono)", fontSize:9, letterSpacing:"0.10em", color:"var(--ash)", marginTop:6}}>{schema.headerImageLabel || "IMAGE"} · クリックで拡大</div>
            </div>
          )}
          <div
            className="form-grid"
            onKeyDown={(e) => {
              // Enter on any single-line input saves (textarea keeps Enter for
              // newlines, selects keep native behavior).
              if (e.key === "Enter" && e.target.tagName === "INPUT" && !e.nativeEvent.isComposing) { e.preventDefault(); handleSave(); }
            }}
          >
            {schema.fields.map(f => (
              <div key={f.key} className={"form-field" + (f.span === 2 ? " span-2" : "")}>
                <label className="form-label">
                  {f.label}
                  {f.required && <span style={{color:"var(--crimson-hi)"}}>*</span>}
                </label>
                {f.type === "text" && (
                  <input
                    className="form-input"
                    type="text"
                    value={values[f.key] || ""}
                    onChange={e => set(f.key, e.target.value)}
                    placeholder={f.placeholder}
                  />
                )}
                {f.type === "datalist" && (
                  <React.Fragment>
                    <input
                      className="form-input"
                      type="text"
                      list={`__dl_${f.key}`}
                      value={values[f.key] || ""}
                      onChange={e => set(f.key, e.target.value)}
                      placeholder={f.placeholder || "リストから選択 or 自由入力"}
                    />
                    <datalist id={`__dl_${f.key}`}>
                      {(f.options || []).map(o => (
                        <option key={o} value={o}/>
                      ))}
                    </datalist>
                  </React.Fragment>
                )}
                {f.type === "textarea" && (
                  <textarea
                    className="form-input form-textarea"
                    value={values[f.key] || ""}
                    onChange={e => set(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    rows={f.rows}
                    style={f.minHeight ? {minHeight: f.minHeight} : undefined}
                  />
                )}
                {f.type === "select" && (
                  <select
                    className="form-input"
                    value={values[f.key] || ""}
                    onChange={e => set(f.key, e.target.value)}
                  >
                    {(() => {
                      const opts = f.options || [];
                      const valuesList = opts.map(o => typeof o === "object" ? o.value : o);
                      const current = values[f.key];
                      const missing = current && !valuesList.includes(current);
                      return (
                        <React.Fragment>
                          {missing && <option value={current}>{current} (マスタ未登録)</option>}
                          {opts.map(o => {
                            const v = typeof o === "object" ? o.value : o;
                            const l = typeof o === "object" ? o.label : o;
                            return <option key={v} value={v}>{l}</option>;
                          })}
                        </React.Fragment>
                      );
                    })()}
                  </select>
                )}
                {f.type === "date" && (
                  <div style={{display:"flex", alignItems:"center", gap:10}}>
                    <input
                      className="form-input"
                      type="date"
                      value={values[f.key] || ""}
                      onChange={e => set(f.key, e.target.value)}
                      style={{flex:1}}
                    />
                    {values[f.key] && (
                      <div className="date-badge">
                        {(() => {
                          const d = parseYMD(values[f.key]);
                          if (!d) return null;
                          const wd = JP_WD[d.getDay()];
                          const hol = getHoliday(values[f.key]);
                          const isSat = d.getDay() === 6;
                          const isSun = d.getDay() === 0;
                          return (
                            <>
                              <span style={{color: isSun || hol ? "var(--crimson-hi)" : isSat ? "var(--crystal)" : "var(--ivory)", fontWeight: 600}}>{wd}</span>
                              {hol && <span style={{color: "var(--crimson-hi)", fontSize: 10, marginLeft: 6}}>{hol}</span>}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
                {f.type === "time" && (
                  <TimeSelect
                    value={values[f.key] || ""}
                    onChange={v => set(f.key, v)}
                  />
                )}
                {f.type === "datetime" && (
                  <DateTimeSelect
                    value={values[f.key] || ""}
                    onChange={v => set(f.key, v)}
                  />
                )}
                {f.type === "number" && (() => {
                  // Slider + numeric box, both driving the same value. The box stays
                  // for precision (e.g. KPI counts up to 100,000 where a bar drag is
                  // coarse) and for keyboard entry.
                  const min = f.min ?? 0;
                  const max = f.max ?? 100;
                  const num = Number(values[f.key]) || 0;
                  const fill = max > min ? Math.max(0, Math.min(100, ((num - min) / (max - min)) * 100)) : 0;
                  return (
                    <div className="num-slider">
                      <input
                        type="range"
                        className="num-slider-bar"
                        style={{ "--fill": fill + "%" }}
                        value={num}
                        min={min}
                        max={max}
                        step={f.step ?? 1}
                        onChange={e => set(f.key, Number(e.target.value))}
                      />
                      <input
                        className="form-input num-slider-box"
                        type="number"
                        value={values[f.key] ?? 0}
                        min={f.min}
                        max={f.max}
                        step={f.step}
                        onChange={e => set(f.key, Number(e.target.value))}
                      />
                    </div>
                  );
                })()}
                {f.type === "radio" && (
                  <div className="radio-picker">
                    {f.options.map(o => {
                      const v = typeof o === "object" ? o.value : o;
                      const l = typeof o === "object" ? o.label : o;
                      const active = values[f.key] === v;
                      return (
                        <label
                          key={v}
                          className={"radio-opt" + (active ? " active" : "")}
                        >
                          <input
                            type="radio"
                            name={`__radio_${f.key}`}
                            checked={active}
                            onChange={() => set(f.key, v)}
                          />
                          <span className="radio-dot"/>
                          <span className="radio-text">{l}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {f.type === "recur" && (
                  <RecurrenceField
                    value={values[f.key]}
                    onChange={v => set(f.key, v)}
                    date={values.date}
                  />
                )}
                {f.type === "chips" && (
                  <div className="chip-picker">
                    {f.options.map(o => {
                      const v = typeof o === "object" ? o.value : o;
                      const l = typeof o === "object" ? o.label : o;
                      const active = values[f.key] === v;
                      return (
                        <button
                          key={v}
                          type="button"
                          className={"chip-btn" + (active ? " active" : "") + (f.colors?.[v] ? " " + f.colors[v] : "")}
                          onClick={() => set(f.key, v)}
                        >{l}</button>
                      );
                    })}
                  </div>
                )}
                {errors[f.key] && <div className="form-error">{errors[f.key]}</div>}
                {f.hint && <div className="form-hint">{f.hint}</div>}
              </div>
            ))}
          </div>
        </div>

        <div className="modal-foot">
          {isEdit && onDelete && (
            <button
              className="btn ghost"
              style={{color:"var(--crimson-hi)", borderColor:"rgba(196,30,58,0.32)"}}
              onClick={() => {
                // Series instances get their own scope dialog (この回のみ/以降すべて/全体)
                // from the caller — a browser confirm() here would double-prompt.
                if (initial?.seriesId || confirm("Delete this item?")) { onDelete(); }
              }}
            >
              <Icon name="trash" size={12}/>
              <span>Delete</span>
            </button>
          )}
          <div style={{flex:1}}/>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={handleSave}>
            <Icon name="check" size={12}/>
            <span>{isEdit ? "Save" : "Create"}</span>
          </button>
        </div>
      </div>
    </div>
    <ImageLightbox src={zoomSrc} caption={schema.headerImageLabel || ""} onClose={() => setZoomSrc(null)}/>
    </React.Fragment>,
    document.body
  );
};

// Confirm delete (used when row delete button clicked outside modal)
const ConfirmDelete = ({ open, message, onConfirm, onClose }) => {
  if (!open) return null;
  return (
    ReactDOM.createPortal(
    <div className="modal-overlay" onClick={(e) => { if (e.target.classList.contains("modal-overlay")) onClose(); }}>
      <div className="modal-stage" style={{maxWidth:420}}>
        <div className="modal-head">
          <div style={{flex:1}}>
            <span className="eyebrow">CONFIRM</span>
            <h2 className="modal-title">Confirm deletion</h2>
          </div>
        </div>
        <div className="modal-body">
          <div style={{padding:"8px 0", fontSize:14, color:"var(--ivory)", lineHeight:1.6}}>
            {message || "Delete this item? This action cannot be undone."}
          </div>
        </div>
        <div className="modal-foot">
          <div style={{flex:1}}/>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn" style={{background:"linear-gradient(180deg, var(--crimson-hi), var(--crimson) 70%, var(--crimson-lo))", borderColor:"var(--crimson)", color:"var(--ivory)"}} onClick={onConfirm}>
            <Icon name="trash" size={12}/>
            <span>Delete</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
    )
  );
};

// Monotonic counter so ids stay unique even when add() is called many times in the
// same millisecond (e.g. a forEach bulk insert). `Date.now()` alone collides there.
let __crudUid = 0;
const newCrudId = () => `n${Date.now().toString(36)}${(__crudUid++).toString(36)}`;

// Hook: useCrud(state, setState, key) — returns { items, add, update, remove }
function useCrud(state, setState, key) {
  return {
    items: state[key],
    add: (item) => setState(s => ({ ...s, [key]: [{ ...item, id: item.id || newCrudId() }, ...s[key]] })),
    // Insert many rows in one setState (recurring-task fan-out)
    addMany: (list) => setState(s => ({ ...s, [key]: [...list.map(it => ({ ...it, id: it.id || newCrudId() })), ...s[key]] })),
    update: (id, patch) => setState(s => ({ ...s, [key]: s[key].map(it => it.id === id ? { ...it, ...patch } : it) })),
    // Apply many patches in a single setState — use for reordering rows
    updateMany: (patches) => setState(s => ({
      ...s,
      [key]: s[key].map(it => patches[it.id] ? { ...it, ...patches[it.id] } : it)
    })),
    remove: (id) => setState(s => ({ ...s, [key]: s[key].filter(it => it.id !== id) })),
    removeMany: (ids) => { const drop = new Set(ids); setState(s => ({ ...s, [key]: s[key].filter(it => !drop.has(it.id)) })); },
  };
}

// Fullscreen image lightbox — click outside or press ESC to close
const ImageLightbox = ({ src, caption, onClose }) => {
  React.useEffect(() => {
    if (!src) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [src, onClose]);

  if (!src) return null;

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(2,4,10,0.92)",
        backdropFilter: "blur(8px)",
        zIndex: 9999,
        display: "grid", placeItems: "center",
        padding: "5vh 5vw",
        cursor: "zoom-out",
        animation: "fade-in 0.18s ease-out",
      }}
    >
      <button
        onClick={onClose}
        title="Close"
        style={{
          position: "absolute", top: 18, right: 18,
          width: 38, height: 38, borderRadius: "50%",
          background: "rgba(255,255,255,0.06)",
          border: "1px solid var(--hairline)",
          color: "var(--ivory)",
          display: "grid", placeItems: "center",
          cursor: "pointer", fontSize: 18, lineHeight: 1,
          fontFamily: "var(--f-display)",
          zIndex: 2,
        }}
      >✕</button>
      <img
        src={src}
        alt=""
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: "90vw",
          maxHeight: "82vh",
          objectFit: "contain",
          borderRadius: 6,
          boxShadow: "0 24px 80px rgba(0,0,0,0.65), 0 0 0 1px rgba(245,197,24,0.18)",
          background: "#0a0e1a",
          cursor: "default",
        }}
      />
      {caption && (
        <div
          onClick={e => e.stopPropagation()}
          style={{position:"absolute", bottom:24, left:0, right:0, textAlign:"center", fontFamily:"var(--f-mono)", fontSize:10, letterSpacing:"0.12em", color:"var(--ash)", pointerEvents:"none"}}
        >{caption}</div>
      )}
    </div>,
    document.body
  );
};

Object.assign(window, { EntityModal, ConfirmDelete, useCrud, ImageLightbox });


;/* ==== warp.jsx ==== */
// WarpStars — Superman 1978 opening warp effect (canvas-based)
// Radial streaking stars from center outward, with hyperspace feel.

const WarpStars = ({ active = true, intensity = 1, color = "#FFFFFF", tint = true }) => {
  const canvasRef = React.useRef(null);
  const rafRef = React.useRef(null);
  const starsRef = React.useRef([]);
  const lastTimeRef = React.useRef(0);

  React.useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let W = 0, H = 0, cx = 0, cy = 0;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx = W / 2;
      cy = H / 2;
    };
    resize();
    window.addEventListener("resize", resize);

    // Initialize stars
    const STAR_COUNT = Math.floor(280 * intensity);
    const makeStar = (atOrigin = false) => {
      // Polar position
      const angle = Math.random() * Math.PI * 2;
      // distance from center — exponential distribution so most start close
      const dist = atOrigin
        ? Math.random() * 30 + 1
        : Math.random() * Math.max(W, H) * 0.55;
      return {
        a: angle,
        r: dist,
        speed: 0.2 + Math.random() * 0.5, // base radial speed multiplier
        size: 0.6 + Math.random() * 1.4,
        // Hue: most white, some pale blue, some pale gold
        hue: tint
          ? (Math.random() < 0.7 ? "white" : Math.random() < 0.5 ? "blue" : "gold")
          : "white",
        trail: 4 + Math.random() * 10,
      };
    };

    starsRef.current = Array.from({ length: STAR_COUNT }, () => makeStar(false));

    const tick = (t) => {
      if (!lastTimeRef.current) lastTimeRef.current = t;
      const dt = Math.min(50, t - lastTimeRef.current);
      lastTimeRef.current = t;

      // Clear with slight trail effect (semi-transparent black)
      ctx.fillStyle = "rgba(3, 6, 15, 0.35)";
      ctx.fillRect(0, 0, W, H);

      const maxR = Math.hypot(W, H) * 0.7;

      for (let i = 0; i < starsRef.current.length; i++) {
        const s = starsRef.current[i];

        // Acceleration: stars accelerate as they go outward
        const accel = 0.0003 * dt;
        s.r += (s.r * 0.0004 * dt + s.speed * 0.2) * (dt * 0.18);
        s.speed += accel;

        // Position
        const x = cx + Math.cos(s.a) * s.r;
        const y = cy + Math.sin(s.a) * s.r;

        // Trail tail position (closer to center)
        const tailR = Math.max(0, s.r - s.trail * (1 + s.r / 200));
        const tx = cx + Math.cos(s.a) * tailR;
        const ty = cy + Math.sin(s.a) * tailR;

        // Color
        let col;
        if (s.hue === "blue") col = "rgba(184,212,255,";
        else if (s.hue === "gold") col = "rgba(255,229,128,";
        else col = "rgba(255,255,255,";

        // Alpha brightens with distance from center, fades near edge
        const distFrac = s.r / maxR;
        const alpha = Math.min(1, 0.15 + s.r / 60) * (1 - Math.max(0, distFrac - 0.85) * 6);

        // Draw streak: gradient line from tail to head
        ctx.strokeStyle = col + alpha.toFixed(3) + ")";
        ctx.lineWidth = s.size * (1 + s.r / 250);
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(x, y);
        ctx.stroke();

        // Bright head dot for closer stars
        if (s.r > 30) {
          ctx.fillStyle = col + (alpha * 0.9).toFixed(3) + ")";
          ctx.beginPath();
          ctx.arc(x, y, s.size * (0.7 + s.r / 400), 0, Math.PI * 2);
          ctx.fill();
        }

        // Recycle stars that have passed off-screen
        if (s.r > maxR || x < -50 || x > W + 50 || y < -50 || y > H + 50) {
          starsRef.current[i] = makeStar(true);
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      lastTimeRef.current = 0;
    };
  }, [active, intensity, tint]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      className="warp-canvas"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        pointerEvents: "none",
        mixBlendMode: "screen",
      }}
    />
  );
};

Object.assign(window, { WarpStars });


;/* ==== starfield.jsx ==== */
// starfield.jsx — realistic canvas night sky (stars + meteors).
// Replaces the CSS box-shadow starfield + looping CSS shooting star.
//
// Realism model:
//   • Stars get spectral-class colors (O/B blue-white → M reddish) with
//     realistic weights, and a power-law magnitude distribution (many faint,
//     few bright). The brightest few percent get a halo + diffraction spikes.
//   • Twinkle is atmospheric scintillation: every star has its own phase and
//     frequency mix (two incommensurate sines), so stars never blink in sync
//     the way CSS keyframes did. Intensity levels map to the existing
//     settings values (off/subtle/normal/medium/strong/vivid).
//   • Meteors arrive on a Poisson schedule (exponential gaps — sometimes two
//     in quick succession, sometimes a long lull), each with random entry
//     point, direction, speed, trail length, brightness envelope and a fading
//     afterglow. ~12% are fireballs: brighter, tinted, terminal flash.
//
// The component renders a single fixed canvas at the same z-index/blend mode
// as the old CSS layers, so page layering is unchanged. prefers-reduced-motion
// forces a static frame (no loop, no meteors).

const SF_TWINKLE_LEVELS = {
  off:    { amp: 0,    speed: 0   },
  subtle: { amp: 0.22, speed: 0.6 },
  normal: { amp: 0.38, speed: 0.9 },
  medium: { amp: 0.52, speed: 1.15 },
  strong: { amp: 0.68, speed: 1.45 },
  vivid:  { amp: 0.85, speed: 1.9 },
};

// Mean milliseconds between meteors per frequency setting.
const SF_METEOR_MEAN_MS = {
  off: 0, rare: 48000, normal: 20000, frequent: 8000, shower: 3200,
};

// Spectral classes: weight, core RGB. Weights roughly follow what a dark-sky
// naked-eye field looks like (white/yellow dominate; blue giants rare).
const SF_SPECTRA = [
  { w: 0.06, rgb: [168, 196, 255] }, // O/B — blue-white
  { w: 0.16, rgb: [202, 216, 255] }, // A   — pale blue-white
  { w: 0.26, rgb: [248, 247, 255] }, // F   — white
  { w: 0.28, rgb: [255, 243, 212] }, // G   — yellow-white (Sun-like)
  { w: 0.16, rgb: [255, 219, 178] }, // K   — orange
  { w: 0.08, rgb: [255, 190, 152] }, // M   — reddish
];

function __sf_pickSpectrum(rng = Math.random) {
  let r = rng(), acc = 0;
  for (let i = 0; i < SF_SPECTRA.length; i++) {
    acc += SF_SPECTRA[i].w;
    if (r < acc) return i;
  }
  return SF_SPECTRA.length - 1;
}

// Generate the star catalog for a given viewport. Pure — unit-testable.
function __sf_makeStars(w, h, density = 1, rng = Math.random) {
  // Floor keeps tiny viewports starry; cap keeps 4K × "満天" within the
  // per-frame drawImage budget (~1200 sprites ≈ 2-3ms on integrated GPUs).
  const count = Math.min(1200, Math.max(40, Math.round(w * h * 0.00016 * density)));
  const stars = new Array(count);
  for (let i = 0; i < count; i++) {
    const m = Math.pow(rng(), 2.4);              // 0..1, heavily biased faint
    const bright = m > 0.82;                     // top ~few % get halo+spikes
    stars[i] = {
      x: rng() * w,
      y: rng() * h,
      r: 0.35 + m * 1.45,                        // CSS px radius
      base: 0.22 + m * 0.78,                     // base alpha
      sp: __sf_pickSpectrum(rng),
      bright,
      // scintillation parameters — unique per star
      ph: rng() * Math.PI * 2,
      f1: 0.55 + rng() * 1.5,                    // Hz-ish
      f2: 1.4 + rng() * 2.2,
      amp: 0.55 + rng() * 0.75,                  // per-star amplitude jitter
    };
  }
  return stars;
}

// Twinkle multiplier 0..1 for a star at time tSec under a given level.
function __sf_twinkleFactor(star, tSec, level) {
  const lv = SF_TWINKLE_LEVELS[level] || SF_TWINKLE_LEVELS.subtle;
  if (!lv.amp) return 1;
  const t = tSec * lv.speed;
  // Two incommensurate sines → quasi-random flicker, normalized to 0..1
  const n =
    0.5 +
    0.32 * Math.sin(star.f1 * t * 2.4 + star.ph) +
    0.18 * Math.sin(star.f2 * t * 2.4 + star.ph * 1.7);
  const a = Math.min(1, lv.amp * star.amp);
  return Math.max(0.05, 1 - a + a * Math.max(0, Math.min(1, n)));
}

// Exponential (Poisson) gap until the next meteor, clamped to sane bounds.
function __sf_meteorDelay(meanMs, rng = Math.random) {
  if (!meanMs) return Infinity;
  const u = Math.max(1e-6, 1 - rng());
  return Math.min(meanMs * 4, Math.max(meanMs * 0.22, -Math.log(u) * meanMs));
}

function __sf_spawnMeteor(W, H, rng = Math.random) {
  const ltr = rng() < 0.5;                          // direction: L→R or R→L
  const slope = (18 + rng() * 42) * (Math.PI / 180); // 18°–60° below horizontal
  const speed = 650 + rng() * 750;                  // px/s
  const fireball = rng() < 0.12;
  return {
    x: rng() * W,
    y: rng() * H * 0.55,
    dx: (ltr ? 1 : -1) * Math.cos(slope) * speed,
    dy: Math.sin(slope) * speed,
    len: (fireball ? 1.6 : 1) * (80 + speed * 0.11 + rng() * 60),
    age: 0,
    life: 0.65 + rng() * 0.85,                      // seconds of active flight
    glow: 0.45,                                     // afterglow seconds
    fireball,
    rgb: fireball
      ? (rng() < 0.5 ? [180, 255, 216] : [255, 211, 160]) // green-ish / warm
      : [255, 255, 255],
  };
}

// ── Component ────────────────────────────────────────────────────────────────
const Starfield = ({ twinkle = "subtle", density = 1, meteors = "normal" }) => {
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduceMotion =
      typeof matchMedia === "function" &&
      matchMedia("(prefers-reduced-motion: reduce)").matches;

    const dpr = Math.min(2, window.devicePixelRatio || 1);
    let W = 0, H = 0;
    let stars = [];
    let activeMeteors = [];
    let nextMeteorIn = 0;
    let raf = 0;
    let last = 0;
    let elapsed = 0;

    const meanGap = reduceMotion ? 0 : (SF_METEOR_MEAN_MS[meteors] ?? SF_METEOR_MEAN_MS.normal);
    const twinkleLevel = reduceMotion ? "off" : (SF_TWINKLE_LEVELS[twinkle] ? twinkle : "subtle");
    const animated = twinkleLevel !== "off" || meanGap > 0;

    // Pre-rendered sprites: per spectrum a soft dot, plus a halo+spikes sprite
    // for bright stars. drawImage of cached sprites keeps ~400 stars cheap.
    const SPR = 32;            // sprite size (device px) for normal stars
    const SPR_B = 96;          // sprite size for bright halo stars
    const dots = [], halos = [];
    SF_SPECTRA.forEach((s) => {
      const [r, g, b] = s.rgb;
      const c = document.createElement("canvas");
      c.width = c.height = SPR;
      const x = c.getContext("2d");
      const gr = x.createRadialGradient(SPR/2, SPR/2, 0, SPR/2, SPR/2, SPR/2);
      gr.addColorStop(0, "rgba(255,255,255,1)");
      gr.addColorStop(0.22, `rgba(${r},${g},${b},0.9)`);
      gr.addColorStop(0.55, `rgba(${r},${g},${b},0.22)`);
      gr.addColorStop(1, "rgba(0,0,0,0)");
      x.fillStyle = gr;
      x.fillRect(0, 0, SPR, SPR);
      dots.push(c);

      const hb = document.createElement("canvas");
      hb.width = hb.height = SPR_B;
      const hx = hb.getContext("2d");
      const cgr = hx.createRadialGradient(SPR_B/2, SPR_B/2, 0, SPR_B/2, SPR_B/2, SPR_B/2);
      cgr.addColorStop(0, "rgba(255,255,255,1)");
      cgr.addColorStop(0.12, `rgba(${r},${g},${b},0.85)`);
      cgr.addColorStop(0.4, `rgba(${r},${g},${b},0.16)`);
      cgr.addColorStop(1, "rgba(0,0,0,0)");
      hx.fillStyle = cgr;
      hx.fillRect(0, 0, SPR_B, SPR_B);
      // 4-point diffraction spikes
      hx.strokeStyle = `rgba(${r},${g},${b},0.55)`;
      hx.lineWidth = 1;
      const cgrad = (x0, y0, x1, y1) => {
        const lg = hx.createLinearGradient(x0, y0, x1, y1);
        lg.addColorStop(0, "rgba(255,255,255,0.7)");
        lg.addColorStop(1, "rgba(255,255,255,0)");
        return lg;
      };
      const mid = SPR_B / 2, arm = SPR_B * 0.46;
      [[mid, mid, mid, mid - arm], [mid, mid, mid, mid + arm],
       [mid, mid, mid - arm, mid], [mid, mid, mid + arm, mid]].forEach(([x0, y0, x1, y1]) => {
        hx.strokeStyle = cgrad(x0, y0, x1, y1);
        hx.beginPath(); hx.moveTo(x0, y0); hx.lineTo(x1, y1); hx.stroke();
      });
      halos.push(hb);
    });

    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      stars = __sf_makeStars(W, H, density);
      if (!animated) drawFrame(0); // static mode: repaint once per resize
    };

    const drawStars = (tSec) => {
      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        const f = __sf_twinkleFactor(s, tSec, twinkleLevel);
        const alpha = Math.min(1, s.base * f);
        ctx.globalAlpha = alpha;
        if (s.bright) {
          const k = s.r * 7 * (0.85 + 0.15 * f); // halo breathes slightly
          ctx.drawImage(halos[s.sp], s.x - k, s.y - k, k * 2, k * 2);
        } else {
          const k = s.r * 3.2;
          ctx.drawImage(dots[s.sp], s.x - k, s.y - k, k * 2, k * 2);
        }
      }
      ctx.globalAlpha = 1;
    };

    const drawMeteor = (m) => {
      const over = Math.max(0, m.age - m.life);          // afterglow phase
      const env = over > 0
        ? Math.max(0, 1 - over / m.glow) * 0.5           // fading wake
        : Math.min(1, m.age / (m.life * 0.15), (m.life - m.age) / (m.life * 0.3) + 0.35);
      if (env <= 0) return;
      const sp = Math.hypot(m.dx, m.dy);
      const ux = m.dx / sp, uy = m.dy / sp;
      const hx = m.x, hy = m.y;
      const tx = hx - ux * m.len, ty = hy - uy * m.len;
      const [r, g, b] = m.rgb;
      const grad = ctx.createLinearGradient(tx, ty, hx, hy);
      grad.addColorStop(0, "rgba(0,0,0,0)");
      grad.addColorStop(0.6, `rgba(${r},${g},${b},${(0.35 * env).toFixed(3)})`);
      grad.addColorStop(1, `rgba(255,255,255,${(0.95 * env).toFixed(3)})`);
      ctx.strokeStyle = grad;
      ctx.lineWidth = m.fireball ? 3.2 : 1.8;
      ctx.lineCap = "round";
      ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(hx, hy); ctx.stroke();
      if (over === 0) {
        // bright head
        const hr = m.fireball ? 4.2 : 2.4;
        const hg = ctx.createRadialGradient(hx, hy, 0, hx, hy, hr * 3);
        hg.addColorStop(0, `rgba(255,255,255,${(0.95 * env).toFixed(3)})`);
        hg.addColorStop(0.35, `rgba(${r},${g},${b},${(0.5 * env).toFixed(3)})`);
        hg.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = hg;
        ctx.beginPath(); ctx.arc(hx, hy, hr * 3, 0, Math.PI * 2); ctx.fill();
        // fireball terminal flash
        if (m.fireball && m.age > m.life * 0.88) {
          const fa = (m.age / m.life - 0.88) / 0.12;
          ctx.fillStyle = `rgba(255,255,255,${(0.5 * (1 - fa) * env).toFixed(3)})`;
          ctx.beginPath(); ctx.arc(hx, hy, 10 + fa * 18, 0, Math.PI * 2); ctx.fill();
        }
      }
    };

    const drawFrame = (tSec) => {
      ctx.clearRect(0, 0, W, H);
      drawStars(tSec);
      for (let i = 0; i < activeMeteors.length; i++) drawMeteor(activeMeteors[i]);
    };

    const tick = (t) => {
      if (!last) last = t;
      const dt = Math.min(100, t - last);
      last = t;
      elapsed += dt;

      if (meanGap > 0) {
        nextMeteorIn -= dt;
        if (nextMeteorIn <= 0) {
          activeMeteors.push(__sf_spawnMeteor(W, H));
          nextMeteorIn = __sf_meteorDelay(meanGap);
        }
        const dts = dt / 1000;
        activeMeteors = activeMeteors.filter((m) => {
          m.age += dts;
          if (m.age <= m.life) { m.x += m.dx * dts; m.y += m.dy * dts; }
          const gone = m.age > m.life + m.glow ||
            (m.x < -300 || m.x > W + 300 || m.y > H + 300);
          return !gone;
        });
      }

      drawFrame(elapsed / 1000);
      raf = requestAnimationFrame(tick);
    };

    resize();
    window.addEventListener("resize", resize);
    // Test hook: lets e2e force meteors instead of waiting out the Poisson gap.
    canvas.__sf_spawnMeteorNow = (n = 1) => {
      for (let i = 0; i < n; i++) activeMeteors.push(__sf_spawnMeteor(W, H));
    };
    // Paint the first frame synchronously — rAF doesn't fire while the tab is
    // hidden, and without this the sky stays blank until the first frame.
    drawFrame(0);
    if (animated) {
      nextMeteorIn = meanGap > 0 ? __sf_meteorDelay(meanGap) * 0.4 : Infinity;
      raf = requestAnimationFrame(tick);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      delete canvas.__sf_spawnMeteorNow;
    };
  }, [twinkle, density, meteors]);

  return (
    <canvas
      ref={canvasRef}
      className="starfield-canvas"
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        pointerEvents: "none",
        mixBlendMode: "screen",
      }}
    />
  );
};

Object.assign(window, {
  Starfield,
  __sf_makeStars, __sf_pickSpectrum, __sf_twinkleFactor,
  __sf_meteorDelay, __sf_spawnMeteor,
  SF_TWINKLE_LEVELS, SF_METEOR_MEAN_MS, SF_SPECTRA,
});


;/* ==== shell.jsx ==== */
// Shell: sidebar + topbar with global record button
const { useState, useEffect, useRef, useMemo, useCallback } = React;

const NAV = [
  { group: "Operations", items: [
    { id: "dashboard", label: "Command", icon: "dashboard" },
    { id: "tasks", label: "Tasks", icon: "tasks" },
    { id: "quickmemo", label: "Quick Memo", icon: "note" },
    { id: "calendar", label: "Calendar", icon: "calendar" },
    { id: "projects", label: "Projects", icon: "folder" },
  ]},
  { group: "Knowledge", items: [
    { id: "memos", label: "Minutes / Ideas", icon: "note" },
    { id: "screenshots", label: "Screenshots", icon: "image" },
    { id: "applicants", label: "Applicants", icon: "users" },
    { id: "roles", label: "Role Definitions", icon: "star" },
    { id: "templates", label: "Templates", icon: "copy" },
    { id: "youtube", label: "YouTube Learning", icon: "play" },
    { id: "kpi", label: "Metrics", icon: "chart" },
    { id: "contacts", label: "Network", icon: "users" },
    { id: "learning", label: "Library", icon: "book" },
  ]},
  { group: "Intelligence", items: [
    { id: "review", label: "Weekly Review", icon: "star" },
    { id: "sparring", label: "AI Sparring", icon: "sparkle" },
  ]},
];

// Resolve the sidebar nav from the canonical NAV + the user's saved navOrder.
// Robust to drift: items/groups removed from code are ignored; items/groups added
// to code are appended at the end so they never silently disappear. Pass
// { includeHidden:true } to get the full layout (with a .hidden flag per item)
// for the settings editor; without it, hidden items and empty groups are dropped.
function getNav(state, opts) {
  const includeHidden = !!(opts && opts.includeHidden);
  const ord = (state && state.navOrder) || {};
  const hidden = new Set(Array.isArray(ord.hidden) ? ord.hidden : []);

  const byName = {};
  NAV.forEach(g => { byName[g.group] = g; });

  // Group order: saved groups that still exist, then any new canonical groups.
  const groupOrder = (Array.isArray(ord.groups) ? ord.groups : []).filter(n => byName[n]);
  NAV.forEach(g => { if (!groupOrder.includes(g.group)) groupOrder.push(g.group); });

  const out = groupOrder.map(name => {
    const def = byName[name];
    const defById = {};
    def.items.forEach(it => { defById[it.id] = it; });
    const savedIds = (ord.items && Array.isArray(ord.items[name]) ? ord.items[name] : []).filter(id => defById[id]);
    const itemIds = [...savedIds];
    def.items.forEach(it => { if (!itemIds.includes(it.id)) itemIds.push(it.id); });
    const items = itemIds
      .map(id => defById[id])
      .filter(it => includeHidden || !hidden.has(it.id))
      .map(it => includeHidden ? { ...it, hidden: hidden.has(it.id) } : it);
    return { group: name, items };
  });

  return includeHidden ? out : out.filter(g => g.items.length > 0);
}

const VIEW_TITLES = {
  dashboard: { eyebrow: "Command Center", title: "Headquarters" },
  tasks: { eyebrow: "Workspace", title: "Tasks" },
  quickmemo: { eyebrow: "Workspace", title: "Quick Memo" },
  calendar: { eyebrow: "Workspace", title: "Calendar" },
  projects: { eyebrow: "Initiatives", title: "Projects" },
  memos: { eyebrow: "Archive", title: "Minutes & Ideas" },
  screenshots: { eyebrow: "Archive", title: "Screenshots" },
  applicants: { eyebrow: "Recruitment", title: "Applicants" },
  roles: { eyebrow: "Performance", title: "Role Definitions" },
  templates: { eyebrow: "Library", title: "Templates" },
  youtube: { eyebrow: "Library", title: "YouTube Learning" },
  kpi: { eyebrow: "Metrics", title: "KPI & Numbers" },
  contacts: { eyebrow: "Network", title: "Contacts" },
  learning: { eyebrow: "Library", title: "Learning" },
  review: { eyebrow: "Reflection", title: "Weekly Review" },
  sparring: { eyebrow: "Brainstorm", title: "AI Sparring" },
};

const Sidebar = ({ view, setView, recordingCount, savedAt, openSettings, userName, userRole, avatar, nav, open, onClose }) => {
  const navGroups = nav || NAV;
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, []);
  const savedLabel = React.useMemo(() => {
    if (!savedAt) return "—";
    const s = Math.max(0, Math.floor((Date.now() - savedAt) / 1000));
    if (s < 5) return "just now";
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
    // tick is just to trigger re-render
    // eslint-disable-next-line
  }, [savedAt, tick]);

  return (
  <aside className={"sidebar" + (open ? " open" : "")}>
    <div className="brand">
      <Shield size={38} />
      <div className="brand-text">
        <div className="brand-name">SUPERSYS</div>
        <div className="brand-sub">SEIMU · HQ</div>
      </div>
      <button type="button" className="sidebar-close" onClick={onClose} aria-label="メニューを閉じる" title="閉じる">
        <Icon name="x" size={20} />
      </button>
    </div>
    <nav className="nav">
      {navGroups.map(group => (
        <div key={group.group} className="nav-section">
          <div className="nav-section-title">{group.group}</div>
          {group.items.map(item => (
            <button
              type="button"
              key={item.id}
              className={"nav-item" + (view === item.id ? " active" : "")}
              aria-current={view === item.id ? "page" : undefined}
              onClick={() => setView(item.id)}
            >
              <Icon name={item.icon} size={16} className="nav-icon" />
              <span>{item.label}</span>
              {item.badge && <span className="nav-badge">{item.badge}</span>}
              {item.id === "memos" && recordingCount > 0 && <span className="nav-badge" style={{background: "var(--gold)", color: "var(--void)"}}>{recordingCount}</span>}
            </button>
          ))}
        </div>
      ))}
    </nav>
    <div className="sidebar-foot">
      <div className="avatar" onClick={openSettings} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openSettings(); } }}
        style={{cursor:"pointer"}} title="設定を開く" aria-label="設定を開く">{avatar || "M"}</div>
      <div style={{flex:1, minWidth:0}}>
        <div className="user-name">{userName || "Masaaki"}</div>
        <div className="user-role">{userRole || "HR · GA · IT Lead"}</div>
      </div>
      <div className="save-pill" title={savedAt ? `Last saved: ${new Date(savedAt).toLocaleString()}` : "Not yet saved"}>
        <span className="save-dot"/>
        <span>{savedLabel}</span>
      </div>
    </div>
  </aside>
  );
};

const Topbar = ({ view, openRecord, openSearch, recording, recordingSeconds, fmtTime, alerts, setView, onMenu }) => {
  const meta = VIEW_TITLES[view] || VIEW_TITLES.dashboard;
  const [notifOpen, setNotifOpen] = React.useState(false);
  const notifRef = React.useRef(null);
  React.useEffect(() => {
    const h = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    if (notifOpen) { document.addEventListener("mousedown", h); }
    return () => document.removeEventListener("mousedown", h);
  }, [notifOpen]);

  return (
    <div className="topbar">
      <button type="button" className="topbar-hamburger icon-btn" onClick={onMenu} aria-label="メニューを開く" title="メニュー">
        <Icon name="menu" size={20} />
      </button>
      <div className="crumbs">
        <div className="crumbs-block">
          <div className="crumb-eyebrow">{meta.eyebrow}</div>
          <div className="crumb-title">{meta.title}</div>
        </div>
      </div>
      <div className="topbar-spacer"></div>

      <div className="search-bar" onClick={openSearch}>
        <Icon name="search" size={16} />
        <input placeholder="Search tasks, minutes, people, KPI…" readOnly />
        <span className="kbd">⌘K</span>
      </div>

      <div ref={notifRef} style={{position:"relative"}}>
        <button className="icon-btn" title="Notifications" onClick={() => setNotifOpen(o => !o)}>
          <Icon name="bell" size={17} />
          {alerts && alerts.length > 0 && (
            <span style={{position:"absolute", top:4, right:4, width:8, height:8, borderRadius:"50%", background:"var(--crimson)", boxShadow:"0 0 6px var(--crimson-glow)"}}/>
          )}
        </button>
        {notifOpen && (
          <div style={{position:"absolute", top:46, right:0, width:320, background:"linear-gradient(180deg, var(--night), var(--space))", border:"1px solid var(--border-hi)", borderRadius:8, boxShadow:"0 16px 40px rgba(0,0,0,0.5)", zIndex:100, padding:14}}>
            <div className="eyebrow" style={{marginBottom:10}}>NOTIFICATIONS</div>
            {(!alerts || alerts.length === 0) ? (
              <div style={{padding:"20px 0", textAlign:"center", color:"var(--ash)", fontSize:12}}>通知はありません</div>
            ) : (
              alerts.map(a => (
                <div key={a.id} style={{padding:"10px 8px", marginBottom:6, background:"rgba(196,30,58,0.06)", border:"1px solid rgba(196,30,58,0.20)", borderRadius:5, cursor:"pointer"}} onClick={() => { setView("tasks"); setNotifOpen(false); }}>
                  <div style={{fontSize:12, color:"var(--ivory)", fontWeight:500, lineHeight:1.4}}>{a.title}</div>
                  <div style={{fontSize:10, color:"var(--ash)", marginTop:4, fontFamily:"var(--f-mono)", letterSpacing:"0.06em"}}>{a.meta}</div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <button
        className={"record-button" + (recording ? " recording" : "")}
        onClick={openRecord}
      >
        <Icon name={recording ? "stop" : "mic"} size={16} />
        <span>{recording ? `REC ● ${fmtTime ? fmtTime(recordingSeconds || 0) : ""}` : "RECORD"}</span>
      </button>
    </div>
  );
};

Object.assign(window, { Sidebar, Topbar, VIEW_TITLES, NAV, getNav });


;/* ==== recording.jsx ==== */
// RecordingStudio — engine extracted to useRecordingEngine() hook so recording
// persists across modal open/close. A floating widget appears when recording
// continues with the modal hidden.

// ── Summary styles (shared by the live recorder and the saved-memo viewer) ──
// The user can pick a style before summarising and re-run with another style
// afterwards. Each style only changes the tone/emphasis instruction; the JSON
// shape (points/todos/decisions/questions) stays identical so the UI is stable.
// kind:"clean" = 整形のみ（要約しない／原文保持）, kind:"rules" = 掲示板ルール文章化。
// それ以外は構造化要約（points/todos/decisions/questions）。
const SUMMARY_STYLES = [
  { id: "cleanup",    label: "📝 文字起こし整形（要約しない・原文保持）", kind: "clean",
    instr: "原文の内容・言い回し・順序を変えず、誤字脱字と句読点を直し、言いよどみ(フィラー)だけを取り除いて読みやすく整える。" },
  { id: "rules",      label: "📕 ルール文章化（LINEWORKS掲示板用）", kind: "rules",
    instr: "社内掲示板の投稿ルールに従い、📕件名＋✅箇条書き・である調・文末「。」なし・#表記ゆれタグで整える。" },
  { id: "exec",       label: "経営層向け（簡潔）",       instr: "経営層への報告として簡潔・構造的に。各文は結論から入り1文で言い切る。冗長な背景は省く。文体はていねいな敬体（です・ます）。" },
  { id: "standard",   label: "標準（バランス）",         instr: "標準的な議事録の文体。事実ベースで過不足なく、ていねいな敬体（です・ます）でまとめる。" },
  { id: "detailed",   label: "詳細（網羅）",             instr: "詳細かつ網羅的に。背景・経緯・固有名詞・数値も省略せず、説明的な敬体でまとめる。" },
  { id: "minutes",    label: "正式議事録（決定・論点重視）", instr: "公式議事録の硬い記録調。体言止めや「〜の件」など簡潔な書き言葉を用い、決定事項と未解決の論点を最優先で明確にする。" },
  { id: "action",     label: "アクション重視（ToDo抽出）", instr: "実行重視。ToDoを具体的に多く抽出し、各項目を『誰が・何を・いつまでに』の能動形で書く。" },
  { id: "bullet",     label: "要点のみ箇条書き",         instr: "要点(points)だけを短い体言止めの箇条書きで列挙する。1項目20〜30字。装飾語は削る。ToDoや決定事項は本当に明確なものだけ最小限。" },
  { id: "brief",      label: "3行サマリー（超要約）",     instr: "最重要ポイントを3点以内に絞った超要約。各要点は40字以内で言い切る。" },
  { id: "casual",     label: "カジュアル（タメ口）",     instr: "友達に話すようなくだけた口調。敬語は使わず『〜だよ』『〜なんだよね』『〜って話』のようなフランクな言い回しにする。堅い言葉や専門用語は日常語に言い換える。例:『来週までに資料そろえる感じ』『この件は山本さんが進めてくれるって』。砕けても事実は正確に。" },
  { id: "friendly",   label: "やさしい言葉で（初心者向け）", instr: "予備知識ゼロの人向けに、とにかくやさしく噛み砕く。専門用語・カタカナ語・略語は必ず身近な言葉へ言い換える（例:『KPI』→『目標の数字』『コミット』→『約束』）。一文を短くし、口調はあたたかい敬体（です・ます）。" },
  { id: "emoji",      label: "😎 絵文字たっぷり",         instr: "1文ごとに内容に合った絵文字を2〜3個ちりばめ、明るくポップなテンションでまとめる。例:『来週までに資料を準備📄✅ 担当は山本さん🙋‍♂️🔥』。絵文字を盛っても事実は正確に。" },
  { id: "reiwa",      label: "🆕 令和の若者風",           instr: "令和の若者言葉で。『それな』『わかりみが深い』『〜すぎる』『エモい』『ガチで』『とりま』『〜なやつ』などを自然に使う。例:『とりま来週までに資料用意するのガチ大事すぎる』。ノリは軽くても事実は正確に。" },
  { id: "gyaru",      label: "💅 ギャル語",               instr: "ギャル語で超ノリよく。『マジ』『〜じゃん？』『ウチ』『神』『〜なんですけど！』『あげみ』などを使い語尾は高テンション。絵文字も多めでOK。例:『来週まで資料準備するの神じゃん？ウチ担当マジ頑張る💅✨』。盛っても事実は正確に。" },
  { id: "kansai",     label: "🐯 こてこて関西弁",         instr: "こてこての関西弁で。『〜やで』『〜やんか』『ほんま』『せやな』『〜してや』『あかん』などを使う。例:『来週までに資料そろえなあかんで〜 担当は山本さんやんか』。砕けても事実は正確に。" },
  { id: "sengoku",    label: "⚔️ 戦国武将風",             instr: "戦国武将のように古風で勇ましく。『〜でござる』『〜なり』『我が軍』『〜せよ』『相分かった』などを使う。例:『来週までに資料を整えよ。任は山本に与えるものなり』。荘厳に語っても事実は正確に。" },
];
function getSummaryStyle(id) {
  return SUMMARY_STYLES.find(x => x.id === id) || SUMMARY_STYLES.find(x => x.id === "exec") || SUMMARY_STYLES[0];
}
function summaryStyleInstr(id) { return getSummaryStyle(id).instr; }
function isCleanStyle(id) { return getSummaryStyle(id).kind === "clean"; }
function isRulesStyle(id) { return getSummaryStyle(id).kind === "rules"; }

// Build the prompt. "clean" → {title, cleaned}; "rules" → {title, doc};
// summary kinds → {title, points, todos?, decisions?, questions?}.
function buildSummaryPrompt(fullText, style, ss) {
  ss = ss || {};
  const text = (fullText || "").slice(0, 6000);

  if (isCleanStyle(style)) {
    return `あなたは文字起こしの整形担当です。以下の文字起こしを、内容・言い回し・話の順序を一切要約せず、できる限り原文のまま整えてください。

[行うこと（最小限）]
- 明らかな誤字脱字・音声認識の変換ミスの修正
- 句読点・改行を補って読みやすくする
- 「えーと」「あのー」「えっと」「まあ」「そのー」などの意味を持たない言いよどみ(フィラー)の削除
- 同じ内容の言い直し・重複の整理（意味が変わらない範囲のみ）

[禁止]
- 要約・短縮・言い換え・情報の追加や解釈
- 話の順序の入れ替え

以下のJSON形式のみで応答してください(コードフェンスや前置きは絶対に入れない):

{
  "title": "短いタイトル(60文字以内)",
  "cleaned": "整形後の全文。改行は \\n で表現する。"
}

文字起こし:
"""
${text}
"""

JSONのみで返答してください。`;
  }

  if (isRulesStyle(style)) {
    return `あなたはセイムグループの社内掲示板(LINE WORKS)へ掲載する「ルール・通知文」の作成担当です。以下のボイスメモ/文字起こしの内容を、社内掲示板の投稿ルールに厳密に従って整形してください。

[投稿ルール（厳守）]
- 件名(title)の先頭に 📕 を付ける
- 件名に「について」を付けない（例：×「挨拶について」 ○「挨拶」）
- まとめられる項目はできる限り1つの件名にまとめる（多すぎて見づらい場合のみ分けてよい）
- 本文(doc)は各項目の文頭に ✅ を付けた箇条書きにする
- 中項目・小項目を付けたい時は、✅ の行の中で「・」や「①②③」を使ってよい
- 箇条書きが長い時は、全角スペースのインデントや改行を入れて見やすくする
- 文末に「。」を付けない
- ですます調ではなく「である調」で書く（例：「〜とする」「〜である」「〜すること」）
- 強調したい箇所は **太字**（マークダウン）で示してよい
- 本文の最後の行に、検索の表記ゆれ対策として #○○ の形で似た語を半角スペース区切りで複数並べる（例：#見積 #見積もり #見積り）

[禁止]
- ✅ で始まる箇条書き以外の前置き・あいさつ・署名・締めの言葉
- 文末の「。」
- ですます調

以下のJSON形式のみで応答してください(コードフェンスや前置きは絶対に入れない):

{
  "title": "📕件名（「について」を付けない）",
  "doc": "✅項目1\\n✅項目2\\n　・小項目\\n\\n#タグ1 #タグ2 #タグ3"
}

文字起こし:
"""
${text}
"""

JSONのみで返答してください。`;
  }

  const aiCtx = ss.aiContext || "セイムグループ(大阪・不動産)の人事総務部部長兼情報システム統括";
  const wantDecisions = ss.summaryDecisions !== false;
  const wantTodos = ss.summaryTodos !== false;
  const wantQuestions = ss.summaryQuestions !== false;
  const fields = [`  "title": "短いタイトル(60文字以内)"`, `  "points": ["要点1", "要点2", "要点3", "要点4"]`];
  if (wantTodos) fields.push(`  "todos": [{"who": "担当者", "what": "アクション内容", "by": "期日"}]`);
  if (wantDecisions) fields.push(`  "decisions": ["決定事項1"]`);
  if (wantQuestions) fields.push(`  "questions": ["未解決の論点・要確認事項"]`);
  return `あなたは会議・ボイスメモの要約アシスタントです。文体は下の[文体・トーン]の指定に厳密に従ってください（特定の役職向けに固定しないこと）。

[コンテキスト（背景理解のためだけに使う。文体には影響させない）]
${aiCtx}

[文体・トーン（最重要・必ず守る）]
${summaryStyleInstr(style)}
※この文体は title・points・todos・decisions・questions の**すべての文**に適用すること。内容の正確さは保ったまま、言い回しだけを指定の文体にすること。

文字起こしを読み、上記の文体で **日本語で** 以下のJSON形式のみで応答してください(コードフェンスや前置きは絶対に入れない):

{
${fields.join(",\n")}
}

文字起こし:
"""
${text}
"""

JSONのみで返答してください。`;
}

// Run a summary: call Claude, extract the JSON object, return the parsed result.
async function runSummary(fullText, style, ss) {
  const prompt = buildSummaryPrompt(fullText, style, ss);
  const resp = await window.claude.complete(prompt);
  let jsonStr = resp.trim();
  const m = jsonStr.match(/\{[\s\S]*\}/);
  if (m) jsonStr = m[0];
  return JSON.parse(jsonStr);
}

// Clipboard copy with a legacy execCommand fallback. Returns true on success.
async function copyTextToClipboard(text) {
  try { await navigator.clipboard.writeText(text); return true; }
  catch (e) {
    try {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch (_) { return false; }
  }
}

// Render an AI summary object as plain text (for "copy summary only").
function summaryToText(sm) {
  if (!sm) return "";
  if (sm.cleaned) return sm.cleaned;
  if (sm.doc) return sm.doc;
  const L = [];
  if (sm.points && sm.points.length) { L.push("【要点】"); sm.points.forEach(p => L.push(`・${p}`)); }
  if (sm.todos && sm.todos.length) { L.push("", "【Next Action / ToDo】"); sm.todos.forEach(t => L.push(`・${t.who ? t.who + ": " : ""}${t.what || t}${t.by ? `（期日: ${t.by}）` : ""}`)); }
  if (sm.decisions && sm.decisions.length) { L.push("", "【決定事項】"); sm.decisions.forEach(d => L.push(`・${d}`)); }
  if (sm.questions && sm.questions.length) { L.push("", "【未解決の論点】"); sm.questions.forEach(q => L.push(`・${q}`)); }
  return L.join("\n");
}

// Pick a recording container/codec the current browser actually supports.
// Desktop Chrome → webm/opus; iOS Safari only does mp4/aac. Returns "" if we
// can't probe (let the browser choose its own default).
function pickAudioMime() {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  const cands = [
    "audio/webm;codecs=opus", "audio/webm",
    "audio/mp4;codecs=mp4a.40.2", "audio/mp4",
    "audio/aac", "audio/mpeg", "audio/ogg;codecs=opus",
  ];
  for (const c of cands) { try { if (MediaRecorder.isTypeSupported(c)) return c; } catch (e) {} }
  return "";
}

function useRecordingEngine() {
  const _ss = () => (typeof window !== "undefined" && window.__supersys_settings) || {};
  const defaultCat = _ss().recCategory || "Meeting";
  const defaultLang = _ss().recLang || "ja-JP";

  const [phase, setPhase] = React.useState("idle"); // idle | live | review | summarising | done
  const [seconds, setSeconds] = React.useState(0);
  const [transcript, setTranscript] = React.useState("");
  const [partial, setPartial] = React.useState("");
  const [audioUrl, setAudioUrl] = React.useState(null);
  const [title, setTitle] = React.useState("");
  const [category, setCategory] = React.useState(defaultCat);
  const [language, setLanguage] = React.useState(defaultLang);
  const [summary, setSummary] = React.useState(null);
  const [summaryStyle, setSummaryStyle] = React.useState(_ss().recDefaultStyle || _ss().aiStyle || "exec");
  const [summaryError, setSummaryError] = React.useState(null);
  const [waveBars, setWaveBars] = React.useState(Array(48).fill(8));
  const [permissionDenied, setPermissionDenied] = React.useState(false);
  const [recError, setRecError] = React.useState(null); // { kind, msg } — accurate, mobile-friendly error
  const [maxAlertFired, setMaxAlertFired] = React.useState(false);

  const mediaRecorderRef = React.useRef(null);
  const audioChunksRef = React.useRef([]);
  const recognitionRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const audioCtxRef = React.useRef(null);
  const rafRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const waveTimerRef = React.useRef(null);   // synthetic waveform (mobile transcript-only mode)
  const liveRef = React.useRef(false);       // are we actively recording? (drives SR auto-restart)
  const transcriptRef = React.useRef("");
  const startTimeRef = React.useRef(0);

  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  const speechAvailable = !!SR;
  const isMobileUA = typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod|IEMobile|BlackBerry|Mobile/i.test(navigator.userAgent || "");
  // DECISION (2026-06): browser live transcription is unreliable on phones
  // (Android Web Speech restarts every ~5s and duplicates; iOS Safari lacks it
  // entirely). Per operating decision, auto-transcription is PC-only. On phones
  // we record audio + accept manual text entry, and never run SpeechRecognition.
  const useLiveSpeech = speechAvailable && !isMobileUA;

  // Long-recording alert
  React.useEffect(() => {
    if (phase !== "live") { if (maxAlertFired) setMaxAlertFired(false); return; }
    const maxMin = parseInt(_ss().recMaxMin || "0", 10) || 0;
    if (maxMin > 0 && seconds >= maxMin * 60 && !maxAlertFired) {
      setMaxAlertFired(true);
      try { alert(`録音が ${maxMin} 分を超えました。長時間録音は文字起こし精度に影響します。`); } catch {}
    }
  }, [seconds, phase, maxAlertFired]);

  const stopHardware = () => {
    liveRef.current = false;   // gates SR auto-restart; do NOT null onend (it commits the last final)
    try { if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop(); } catch (e) {}
    try { if (recognitionRef.current) { recognitionRef.current.stop(); } } catch (e) {}
    try { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); } catch (e) {}
    try { if (audioCtxRef.current) audioCtxRef.current.close(); } catch (e) {}
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (waveTimerRef.current) { clearInterval(waveTimerRef.current); waveTimerRef.current = null; }
  };

  const reset = () => {
    setPhase("idle"); setSeconds(0); setTranscript(""); setPartial("");
    setAudioUrl(null); setTitle(""); setCategory("Meeting"); setSummary(null);
    setSummaryError(null); setPermissionDenied(false); setRecError(null);
    transcriptRef.current = "";
  };

  // Build a configured SpeechRecognition instance. Shared by desktop & mobile,
  // but with DIFFERENT result-handling because Android Chrome mishandles
  // `continuous` (it re-sends already-finalized results → duplicated text).
  const buildSR = () => {
    const rec = new SR();
    rec.lang = language;
    rec.interimResults = true;

    if (isMobileUA) {
      // ── Android: one utterance per session. Commit each final EXACTLY once,
      //    then auto-restart for the next phrase. No incremental `+=` (that's
      //    what duplicates on Android), no reliance on resultIndex. ──
      rec.continuous = false;
      let committed = false;
      rec.onresult = (e) => {
        let finalText = "", interim = "";
        for (let i = 0; i < e.results.length; i++) {
          const r = e.results[i];
          if (r.isFinal) finalText += r[0].transcript;
          else interim += r[0].transcript;
        }
        if (finalText && !committed) {
          committed = true;
          transcriptRef.current = (transcriptRef.current + " " + finalText).trim();
          setTranscript(transcriptRef.current);
          setPartial("");
        } else if (!finalText) {
          setPartial(interim);
        }
      };
      rec.onend = () => {
        if (liveRef.current && recognitionRef.current === rec) {
          committed = false;          // ready for the next utterance
          try { rec.start(); } catch (e) {}
        } else {
          setPartial("");
        }
      };
    } else {
      // ── Desktop Chrome: `continuous` works correctly; append finals as they
      //    arrive using the advancing resultIndex. ──
      rec.continuous = true;
      rec.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) { transcriptRef.current += t; setTranscript(transcriptRef.current); }
          else { interim += t; }
        }
        setPartial(interim);
      };
      rec.onend = () => {
        // Chrome ends the session after a few seconds of silence (and on some
        // transient errors). To keep a *continuous* desktop transcript we must
        // restart it. Calling start() synchronously inside onend can throw
        // InvalidStateError ("recognition has already started") — if we only
        // tried once and swallowed it, recognition would die after the first
        // pause and only the first few seconds would be transcribed. So retry
        // on a short delay until it actually re-arms.
        if (liveRef.current && recognitionRef.current === rec) {
          const rearm = (attempt) => {
            if (!liveRef.current || recognitionRef.current !== rec) return;
            try { rec.start(); }
            catch (e) {
              if (attempt < 8) setTimeout(() => rearm(attempt + 1), 250);
            }
          };
          rearm(0);
        } else {
          setPartial("");
        }
      };
    }

    rec.onerror = (e) => {
      const er = e && e.error;
      console.log("speech err", er);
      if (er === "not-allowed" || er === "service-not-allowed") {
        // Permission for speech refused → a real failure worth surfacing.
        stopHardware();
        setRecError({ kind: "mic", msg: "マイクの使用が許可されていません。ブラウザの🔒/サイト設定でマイクを「許可」にして、もう一度お試しください。" });
        setPermissionDenied(true);
        setPhase("idle");
      }
      // "no-speech" / "audio-capture" / "network" → let onend auto-restart.
    };
    return rec;
  };

  const startTimer = () => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 200);
  };

  const start = async () => {
    setRecError(null);
    setPermissionDenied(false);

    // ── 0. Capability check ──
    if (!window.isSecureContext) {
      setRecError({ kind: "insecure", msg: "録音には安全な接続(HTTPS)が必要です。https:// のURLで開いてください。" });
      setPermissionDenied(true);
      return;
    }

    // Phones record audio only (live transcription is PC-only by decision).
    // ── Record audio (+ live transcript on desktop) ──
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setRecError({ kind: "unsupported", msg: "この端末/ブラウザは録音に対応していません（マイク取得不可）。最新のSafari / Chromeでお試しください。" });
      setPermissionDenied(true);
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      setRecError({ kind: "unsupported", msg: "このブラウザは録音(MediaRecorder)に未対応です。iOSはSafari最新版、AndroidはChrome最新版でお試しください。" });
      setPermissionDenied(true);
      return;
    }

    // ── 1. Microphone — the ONLY step whose failure is a real "録音失敗" ──
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const n = (err && err.name) || "";
      let msg;
      if (n === "NotAllowedError" || n === "SecurityError" || n === "PermissionDeniedError") {
        msg = "マイクの使用が許可されていません。ブラウザのアドレスバーの🔒やサイト設定からマイクを「許可」にして、もう一度お試しください。";
      } else if (n === "NotFoundError" || n === "DevicesNotFoundError") {
        msg = "マイクが見つかりませんでした。端末のマイクをご確認ください。";
      } else if (n === "NotReadableError" || n === "TrackStartError") {
        msg = "マイクが他のアプリ（通話・録音アプリ等）で使用中の可能性があります。他のアプリを閉じてから再度お試しください。";
      } else {
        msg = "マイクの起動に失敗しました（" + (n || "不明なエラー") + "）。";
      }
      setRecError({ kind: "mic", msg });
      setPermissionDenied(true);
      return;
    }
    streamRef.current = stream;

    // ── 2. MediaRecorder — pick a codec the browser supports (iOS=mp4) ──
    let mr;
    const mime = pickAudioMime();
    try {
      mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch (e) {
      try { mr = new MediaRecorder(stream); } catch (e2) {
        setRecError({ kind: "unsupported", msg: "録音の初期化に失敗しました（" + ((e2 && e2.name) || "") + "）。別のブラウザでお試しください。" });
        setPermissionDenied(true);
        try { stream.getTracks().forEach(t => t.stop()); } catch (_) {}
        return;
      }
    }
    mediaRecorderRef.current = mr;
    audioChunksRef.current = [];
    const fallbackType = mr.mimeType || mime || "audio/webm";
    mr.ondataavailable = e => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
    mr.onstop = () => {
      const type = (audioChunksRef.current[0] && audioChunksRef.current[0].type) || fallbackType;
      const blob = new Blob(audioChunksRef.current, { type });
      setAudioUrl(URL.createObjectURL(blob));
    };
    try {
      mr.start();
    } catch (e) {
      setRecError({ kind: "unsupported", msg: "録音の開始に失敗しました（" + ((e && e.name) || "") + "）。別のブラウザでお試しください。" });
      setPermissionDenied(true);
      try { stream.getTracks().forEach(t => t.stop()); } catch (_) {}
      return;
    }

    // ── 3. Waveform (AudioContext) — OPTIONAL. Must never abort recording. ──
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        audioCtxRef.current = ctx;
        if (ctx.state === "suspended") { try { await ctx.resume(); } catch (_) {} }
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 128;
        src.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteFrequencyData(data);
          const bars = [];
          const N = 48;
          const step = Math.floor(data.length / N);
          for (let i = 0; i < N; i++) {
            const v = data[i * step] || 0;
            bars.push(Math.max(4, (v / 255) * 50));
          }
          setWaveBars(bars);
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      }
    } catch (e) { console.log("waveform unavailable", e); }

    // ── 4. Live transcription (Web Speech API) — desktop only (PC-only by decision). ──
    liveRef.current = true;
    if (useLiveSpeech) {
      try {
        const rec = buildSR();
        rec.start();
        recognitionRef.current = rec;
      } catch (e) { console.log("speech recognition unavailable", e); }
    }

    startTimer();
    setPhase("live");
  };

  const stop = () => {
    stopHardware();
    // Fold any not-yet-finalized interim speech into the transcript so the last
    // phrase isn't lost (common on mobile where SR finalizes lazily).
    if (partial && partial.trim() && !transcriptRef.current.endsWith(partial)) {
      transcriptRef.current = (transcriptRef.current + " " + partial).trim();
      setTranscript(transcriptRef.current);
    }
    setPartial("");
    setPhase("review");
    const txt = transcriptRef.current || partial;
    if (!title) {
      const firstLine = txt.replace(/\s+/g, " ").slice(0, 30);
      setTitle(firstLine || `Recording ${new Date().toLocaleDateString("en-US")} ${new Date().toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}`);
    }
    // 要約は自動では開始しない。review画面でスタイルを選んで「要約をスタート」を押すと開始する。
  };

  const summarise = async (styleArg) => {
    const style = (typeof styleArg === "string" && styleArg) ? styleArg : summaryStyle;
    if (typeof styleArg === "string" && styleArg) setSummaryStyle(styleArg);
    setPhase("summarising");
    setSummaryError(null);
    const fullText = transcriptRef.current || transcript;
    if (!fullText || fullText.length < 8) {
      setSummaryError("文字起こしが短すぎます。手動で議事をテキスト入力するか、再録音してください。");
      setPhase("review");
      return;
    }
    const ss = _ss();
    try {
      const parsed = await runSummary(fullText, style, ss);
      setSummary(parsed);
      // 整形/ルール文章化はタイトル(📕件名等)が本質なので常に反映。要約系は未設定時のみ。
      if (parsed.title && (isCleanStyle(style) || isRulesStyle(style) || !title)) setTitle(parsed.title);
      setPhase("done");
      // Auto-extract tasks if settings ask for it
      if (ss.autoExtractTasks && parsed.todos && parsed.todos.length > 0 && window.__superman_addTasks) {
        const today = new Date().toISOString().slice(0, 10);
        const newItems = parsed.todos.map(t => ({
          id: "tsk_rec_" + Date.now() + "_" + Math.random().toString(36).slice(2,6),
          title: t.what || t,
          notes: `録音「${parsed.title || title || "(no title)"}」から自動抽出`,
          status: "todo", area: "HR", owner: t.who || "竹内",
          date: (t.by && t.by.match(/^\d{4}-\d{2}-\d{2}/)) ? t.by.slice(0, 10) : today,
          time: null, dur: null, tag: null,
        }));
        window.__superman_addTasks(newItems);
      }
    } catch (e) {
      console.log(e);
      setSummaryError("要約生成に失敗しました。文字起こしから手動で保存できます。");
      setPhase("review");
    }
  };

  const fmtTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return (h ? String(h).padStart(2,"0") + ":" : "") + String(m).padStart(2,"0") + ":" + String(sec).padStart(2,"0");
  };

  const buildSaved = () => ({
    id: "r" + Date.now(),
    title: title || `Recording ${new Date().toLocaleString("en-US")}`,
    type: "rec",
    date: new Date().toLocaleString("en-US", { month: "short", day: "numeric", weekday: "short", hour: "2-digit", minute: "2-digit" }),
    duration: fmtTime(seconds),
    audioUrl,
    participants: ["Masaaki"],
    tags: [category],
    transcript: transcriptRef.current || transcript,
    summary: summary || {
      points: [(transcriptRef.current || transcript).slice(0, 120) + "…"],
      todos: [], decisions: [], questions: [],
    },
    summaryStyle: summary ? summaryStyle : null,
  });

  const discard = () => {
    stopHardware();
    reset();
  };

  return {
    // state
    phase, seconds, transcript, partial, audioUrl,
    title, setTitle, category, setCategory, language, setLanguage,
    summary, setSummary, summaryStyle, setSummaryStyle, summaryError, waveBars, permissionDenied, recError,
    speechAvailable, liveTranscription: useLiveSpeech, isMobileUA, transcriptRef,
    // setters
    setTranscript,
    // actions
    start, stop, summarise, discard, reset, buildSaved, fmtTime,
    setPhase,
  };
}

// =====================================================================
// Floating mini-widget — shown when recording but modal hidden
// =====================================================================
const FloatingRecBadge = ({ engine, onExpand }) => {
  const live = engine.phase === "live";
  const hasContent = engine.phase === "review" || engine.phase === "summarising" || engine.phase === "done";
  return (
    <div className="rec-floater" onClick={onExpand}>
      <div className="rec-floater-dot">
        {live ? <span className="dot-pulse"/> : <Icon name="waveform" size={14} color="var(--gold)"/>}
      </div>
      <div className="rec-floater-content">
        <div className="rec-floater-label">{live ? "RECORDING" : engine.phase === "summarising" ? "SUMMARISING" : engine.phase === "done" ? "READY TO SAVE" : "REVIEW"}</div>
        <div className="rec-floater-time">{engine.fmtTime(engine.seconds)}</div>
      </div>
      {live && (
        <button
          className="rec-floater-stop"
          onClick={(e) => { e.stopPropagation(); engine.stop(); onExpand(); }}
          title="Stop recording"
        >
          <Icon name="stop" size={14}/>
        </button>
      )}
      <button
        className="rec-floater-expand"
        onClick={(e) => { e.stopPropagation(); onExpand(); }}
        title="Open recording"
      >
        <Icon name="chevronRight" size={14}/>
      </button>
    </div>
  );
};

// =====================================================================
// RecordingStudio modal — pure UI, receives engine as prop
// =====================================================================
const RecordingStudio = ({ open, onClose, onSave, engine }) => {
  const [copied, setCopied] = React.useState("");
  const doCopy = async (text, label) => {
    if (!text) return;
    const ok = await copyTextToClipboard(text);
    if (ok) { setCopied(label); setTimeout(() => setCopied(c => c === label ? "" : c), 1500); }
    else alert("コピーに失敗しました");
  };
  if (!open) return null;

  const phase = engine.phase;

  const handleSave = () => {
    onSave(engine.buildSaved());
    engine.reset();
    onClose();
  };

  const handleStartFresh = () => {
    if (phase !== "idle") {
      if (!confirm("Discard current recording and start fresh?")) return;
      engine.discard();
    }
    engine.start();
  };

  return (
    <div className="rec-overlay" onClick={(e) => { if (e.target.classList.contains("rec-overlay")) onClose(); }}>
      <div className="rec-stage">
        <div className="rec-stage-head">
          <div style={{flex:1}}>
            <span className="eyebrow">FORTRESS OF SOLITUDE — RECORDING</span>
            <h2>Crystal Recorder</h2>
          </div>
          {phase === "live" && (
            <div className="chip crimson" style={{marginRight:10}}>
              <span className="dot-pulse" style={{marginRight:6}}/>
              REC {engine.fmtTime(engine.seconds)}
            </div>
          )}
          <button className="icon-btn" onClick={onClose} title="Hide (recording continues)">
            <Icon name="chevronRight" size={18} />
          </button>
        </div>

        <div className="rec-stage-body">
          {phase === "idle" && (
            <>
              <div className="rec-bigbutton-wrap">
                <button className="rec-bigbutton" onClick={engine.start}>
                  <Icon name="mic" size={56} />
                </button>
              </div>
              <div className="rec-status-text">Ready</div>
              <div className="rec-timer">00:00</div>

              <div style={{display:"flex", justifyContent:"center", gap:10, marginTop:18}}>
                <label style={{display:"flex", alignItems:"center", gap:8, fontSize:12, color:"var(--ash)"}}>
                  <span className="label-mono">LANGUAGE</span>
                  <select value={engine.language} onChange={e => engine.setLanguage(e.target.value)} style={{padding:"6px 10px", background:"rgba(0,0,0,0.4)", border:"1px solid var(--border)", borderRadius:4, color:"var(--ivory)", fontFamily:"var(--f-body)", fontSize:12, outline:"none"}}>
                    <option value="ja-JP">日本語</option>
                    <option value="en-US">English (US)</option>
                    <option value="en-GB">English (UK)</option>
                  </select>
                </label>
              </div>

              <div style={{textAlign:"center", marginTop:16, color:"var(--ash)", fontSize:13, lineHeight:1.7}}>
                {engine.liveTranscription
                  ? "ボタンを押すと録音と自動文字起こしを開始します。"
                  : "ボタンを押すと音声を録音します（このスマホでは自動文字起こしは行いません）。"}<br/>
                録音中にこのパネルを閉じても、バックグラウンドで録音は続きます。<span className="gold-text">他の画面に移動して大丈夫です。</span>
              </div>
              {!engine.liveTranscription && (
                <div style={{marginTop:16, padding:"10px 14px", background:"rgba(245,197,24,0.08)", border:"1px solid rgba(245,197,24,0.24)", borderRadius:6, fontSize:12, color:"var(--gold)", textAlign:"left", lineHeight:1.7}}>
                  ⓘ <strong>このスマホでは自動文字起こしは行いません。</strong><br/>
                  {engine.isMobileUA
                    ? <>スマホのブラウザ音声認識は途切れ・重複が多く不安定なため、<strong>自動文字起こしはPC（Chrome / Edge）専用</strong>としています。<br/>スマホでは <strong>①音声を録音</strong> →（停止後の画面で）<strong>②文字を手入力</strong> ができます。文字起こしを自動で行いたい議事は<strong>PCで録音</strong>してください。<br/><span style={{color:"var(--ash)"}}>ヒント：キーボード（Gboard）のマイクを使うと、文字入力欄に音声でディクテーションできます。</span></>
                    : <>このブラウザは自動文字起こしに非対応です。<strong>PCのChrome / Edge</strong>でお使いください（音声の録音・手入力は可能です）。</>}
                </div>
              )}
              {engine.recError && (
                <div style={{marginTop:16, padding:"10px 14px", background:"rgba(196,30,58,0.10)", border:"1px solid rgba(196,30,58,0.32)", borderRadius:6, fontSize:12.5, color:"var(--crimson-hi)", textAlign:"left", lineHeight:1.7}}>
                  ⚠️ {engine.recError.msg}
                </div>
              )}
              {!engine.recError && engine.permissionDenied && (
                <div style={{marginTop:16, padding:"10px 14px", background:"rgba(196,30,58,0.10)", border:"1px solid rgba(196,30,58,0.32)", borderRadius:6, fontSize:12, color:"var(--crimson-hi)", textAlign:"center"}}>
                  マイクの使用が許可されていません。ブラウザの設定でマイクを許可してください。
                </div>
              )}
            </>
          )}

          {phase === "live" && (
            <>
              <div className="rec-bigbutton-wrap">
                <button className="rec-bigbutton live" onClick={engine.stop}>
                  <Icon name="stop" size={56} />
                </button>
              </div>
              <div className="rec-status-text crimson-text">Recording ●</div>
              <div className="rec-timer">{engine.fmtTime(engine.seconds)}</div>
              <div className="waveform">
                {engine.waveBars.map((h, i) => (
                  <div key={i} className="bar" style={{
                    height: h + "px",
                    background: i % 7 === 0 ? "var(--gold)" : "var(--crystal)",
                    opacity: 0.5 + (h / 100)
                  }}/>
                ))}
              </div>
              <div className="transcript-stage">
                {engine.transcript}
                {engine.partial && <span className="partial"> {engine.partial}</span>}
              </div>
              <div style={{marginTop:14, textAlign:"center", color:"var(--ash)", fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", fontFamily:"var(--f-mono)"}}>
                ● Press × to hide — recording continues in background
              </div>
            </>
          )}

          {(phase === "review" || phase === "summarising" || phase === "done") && (
            <>
              <div style={{display:"flex", alignItems:"center", gap:18, padding:"14px 18px", background:"rgba(91,123,196,0.06)", border:"1px solid var(--hairline)", borderRadius:8}}>
                <div style={{width:48, height:48, borderRadius:"50%", background:"linear-gradient(135deg, var(--gold), var(--gold-lo))", display:"grid", placeItems:"center", color:"var(--void)"}}>
                  <Icon name="check" size={22} color="currentColor"/>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontFamily:"var(--f-display)", fontSize:16, letterSpacing:"0.10em", color:"var(--ivory)"}}>Recording complete — {engine.fmtTime(engine.seconds)}</div>
                  <div style={{fontSize:12, color:"var(--ash)", marginTop:2}}>{(engine.transcriptRef.current || engine.transcript).length} chars transcribed</div>
                </div>
                {engine.audioUrl && <audio src={engine.audioUrl} controls style={{height:36}}/>}
              </div>

              <div className="rec-meta">
                <input
                  placeholder="Title (e.g. CEO sync — hiring discussion)"
                  value={engine.title}
                  onChange={e => engine.setTitle(e.target.value)}
                />
                <select value={engine.category} onChange={e => engine.setCategory(e.target.value)}>
                  <option>Meeting</option>
                  <option>1:1 / Interview</option>
                  <option>Dept-Head Mtg</option>
                  <option>CEO Sync</option>
                  <option>Voice Memo</option>
                </select>
              </div>

              <div style={{marginTop:18}}>
                <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:4}}>
                  <span className="label-mono">Transcript（原文）</span>
                  <button
                    className="btn ghost sm"
                    style={{marginLeft:"auto", fontSize:10, padding:"3px 8px"}}
                    type="button"
                    onClick={() => doCopy(engine.transcript, "transcript")}
                    title="原文をコピー"
                  >
                    <Icon name={copied === "transcript" ? "check" : "copy"} size={11}/>
                    <span>{copied === "transcript" ? "コピーしました" : "原文をコピー"}</span>
                  </button>
                </div>
                <textarea
                  className="transcript-stage"
                  style={{width:"100%", minHeight:140, resize:"vertical", color:"var(--ivory)", outline:"none"}}
                  value={engine.transcript}
                  onChange={e => { engine.transcriptRef.current = e.target.value; engine.setTranscript(e.target.value); }}
                />
              </div>

              {phase === "review" && (
                <div className="summary-stage" style={{marginTop:18}}>
                  <h4 style={{marginTop:0}}>★ AI要約 / 整形</h4>
                  <div style={{fontSize:12, color:"var(--ash)", marginBottom:12, lineHeight:1.7}}>
                    スタイルを選んで「スタート」を押すと生成を開始します。完了後に別スタイルで再実行もできます。
                  </div>
                  <div style={{display:"flex", alignItems:"center", gap:10, flexWrap:"wrap"}}>
                    <select
                      value={engine.summaryStyle}
                      onChange={e => engine.setSummaryStyle(e.target.value)}
                      style={{padding:"8px 10px", background:"rgba(0,0,0,0.4)", border:"1px solid var(--border)", borderRadius:4, color:"var(--ivory)", fontFamily:"var(--f-body)", fontSize:13, outline:"none", minWidth:260}}
                    >
                      {SUMMARY_STYLES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                    <button className="btn gold" onClick={() => engine.summarise()}>
                      <Icon name="sparkle" size={14}/>
                      <span>スタート</span>
                    </button>
                  </div>
                  <div style={{fontSize:11, color:"var(--smoke)", marginTop:8, lineHeight:1.6}}>
                    {(SUMMARY_STYLES.find(s => s.id === engine.summaryStyle) || {}).instr}
                  </div>
                </div>
              )}

              {phase === "summarising" && (
                <div className="summary-stage">
                  <h4>{isCleanStyle(engine.summaryStyle) ? "整形中…" : isRulesStyle(engine.summaryStyle) ? "ルール文章を作成中…" : "要約生成中…"}</h4>
                  <div style={{display:"flex", alignItems:"center", gap:14, padding:"10px 0"}}>
                    <div style={{width:28, height:28, border:"2px solid var(--gold)", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite"}}/>
                    <span style={{color:"var(--ash)", fontSize:13}}>
                      「{(SUMMARY_STYLES.find(s => s.id === engine.summaryStyle) || {}).label || engine.summaryStyle}」スタイルでClaudeが処理しています…
                    </span>
                  </div>
                </div>
              )}

              {engine.summaryError && (
                <div style={{marginTop:14, padding:"10px 14px", background:"rgba(196,30,58,0.10)", border:"1px solid rgba(196,30,58,0.32)", borderRadius:6, fontSize:12, color:"var(--crimson-hi)"}}>
                  {engine.summaryError}
                </div>
              )}

              {phase === "done" && engine.summary && (
                <div className="summary-stage">
                  <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:8}}>
                    <h4 style={{margin:0}}>{engine.summary.doc ? "★ ルール文章（掲示板用）" : engine.summary.cleaned ? "★ 整形済みテキスト" : "★ AI 要約"}</h4>
                    <button
                      className="btn ghost sm"
                      style={{fontSize:10, padding:"3px 8px"}}
                      type="button"
                      onClick={() => doCopy(summaryToText(engine.summary), "summary")}
                      title="結果をコピー"
                    >
                      <Icon name={copied === "summary" ? "check" : "copy"} size={11}/>
                      <span>{copied === "summary" ? "コピーしました" : (engine.summary.doc ? "本文をコピー" : engine.summary.cleaned ? "整形文をコピー" : "要約をコピー")}</span>
                    </button>
                    <div style={{flex:1}}/>
                    <select
                      value={engine.summaryStyle}
                      onChange={e => engine.setSummaryStyle(e.target.value)}
                      title="再実行のスタイル"
                      style={{padding:"5px 8px", background:"rgba(0,0,0,0.4)", border:"1px solid var(--border)", borderRadius:4, color:"var(--ivory)", fontFamily:"var(--f-body)", fontSize:11, outline:"none"}}
                    >
                      {SUMMARY_STYLES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                    <button
                      className="btn gold sm"
                      style={{fontSize:10, padding:"4px 10px"}}
                      type="button"
                      onClick={() => engine.summarise(engine.summaryStyle)}
                      title="選んだスタイルでやり直す"
                    >
                      <Icon name="refresh" size={11}/>
                      <span>別スタイルで再実行</span>
                    </button>
                  </div>
                  {(engine.summary.cleaned || engine.summary.doc) && (
                    <div className="summary-section">
                      <div style={{whiteSpace:"pre-wrap", lineHeight:1.9, color:"var(--ivory)", fontSize:13}}>{engine.summary.cleaned || engine.summary.doc}</div>
                    </div>
                  )}
                  {engine.summary.doc && (
                    <div style={{marginTop:6, padding:"8px 12px", background:"rgba(245,197,24,0.06)", border:"1px solid rgba(245,197,24,0.22)", borderRadius:6, fontSize:11, color:"var(--gold)", lineHeight:1.6}}>
                      ⓘ 掲示板への投稿時：件名は📕始まり／「コメントを許可」を外す／「編集を許可」はチェックしない
                    </div>
                  )}
                  {engine.summary.points && engine.summary.points.length > 0 && (
                    <div className="summary-section">
                      <h5>要点</h5>
                      <ul>{engine.summary.points.map((p, i) => <li key={i}>{p}</li>)}</ul>
                    </div>
                  )}
                  {engine.summary.todos && engine.summary.todos.length > 0 && (
                    <div className="summary-section">
                      <h5>Next Action / ToDo</h5>
                      <ul>{engine.summary.todos.map((t, i) => (
                        <li key={i}>
                          <span style={{color:"var(--gold)"}}>{t.who}</span> — {t.what}
                          {t.by && <span style={{color:"var(--ash)", marginLeft:8, fontSize:11}}>(期日: {t.by})</span>}
                        </li>
                      ))}</ul>
                    </div>
                  )}
                  {engine.summary.decisions && engine.summary.decisions.length > 0 && (
                    <div className="summary-section">
                      <h5>決定事項</h5>
                      <ul>{engine.summary.decisions.map((d, i) => <li key={i}>{d}</li>)}</ul>
                    </div>
                  )}
                  {engine.summary.questions && engine.summary.questions.length > 0 && (
                    <div className="summary-section">
                      <h5>未解決の論点</h5>
                      <ul>{engine.summary.questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="rec-stage-foot">
          {phase === "idle" && (
            <>
              <div style={{flex:1}}/>
              <button className="btn ghost" onClick={onClose}>Close</button>
            </>
          )}
          {phase === "live" && (
            <>
              <button className="btn ghost" onClick={onClose}>Hide (keep recording)</button>
              <div style={{flex:1}}/>
              <button className="btn primary" onClick={engine.stop}>
                <Icon name="stop" size={12}/>
                <span>Stop</span>
              </button>
            </>
          )}
          {phase === "review" && (
            <>
              <button className="btn ghost" onClick={() => { if (confirm("Discard this recording?")) { engine.discard(); onClose(); } }}>
                <Icon name="trash" size={12}/>
                <span>Discard</span>
              </button>
              <div style={{flex:1}}/>
              <button className="btn primary" onClick={handleSave}>
                <Icon name="check" size={14}/>
                <span>要約せず保存</span>
              </button>
            </>
          )}
          {phase === "done" && (
            <>
              <button className="btn ghost" onClick={() => { engine.setSummary(null); engine.setPhase("review"); }}>Back</button>
              <div style={{flex:1}}/>
              <button className="btn primary" onClick={handleSave}>
                <Icon name="check" size={14}/>
                <span>Save to Archive</span>
              </button>
            </>
          )}
          {phase === "summarising" && (
            <>
              <div style={{flex:1}}/>
              <button className="btn ghost" disabled>Working…</button>
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

Object.assign(window, {
  useRecordingEngine, RecordingStudio, FloatingRecBadge,
  SUMMARY_STYLES, runSummary, copyTextToClipboard, summaryToText,
  isCleanStyle, isRulesStyle, getSummaryStyle,
});


;/* ==== dashboard.jsx ==== */
// Dashboard — single-screen command center
// Designed to fit 1080p without scrolling: compact hero strip + dense 3-col grid.
// Each panel is filled with today + upcoming groups so vertical space is utilized.
const Dashboard = ({ state, setState, openRecord, setView, openRecording, effectiveAlerts }) => {
  const alerts = effectiveAlerts || state.alerts || [];
  const today = new Date();
  const dateStr = today.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const hour = today.getHours();
  const greeting = hour < 5 ? "Burning the midnight oil" : hour < 11 ? "Good morning" : hour < 17 ? "Good afternoon" : hour < 22 ? "Good evening" : "Late night";

  // Task quick-add modal (uses same schema as Workspace)
  const [taskModalOpen, setTaskModalOpen] = React.useState(false);
  const [taskInitial, setTaskInitial] = React.useState(null);
  const alertsListRef = React.useRef(null);
  const taskCrud = window.useCrud ? window.useCrud(state, setState, "items") : null;
  const openNewTask = (defaults = {}) => {
    const def = window.ITEM_DEFAULT || { status: "todo", area: "HR", owner: "竹内" };
    const todayStr = window.fmtYMD ? window.fmtYMD(today) : today.toISOString().slice(0, 10);
    // 「新規タスクの期限日は今日（呼び出し側が日付を指定していない場合のみ）」のルールは
    // workspace.jsx の seedNewItemDefaults に集約し、ここと二重持ちにしない（drift防止）。
    // window 経由で参照（Workspace と同じ）。最後に spread するので明示指定の date が勝つ。
    setTaskInitial({ __isNew: true, ...def, ...window.seedNewItemDefaults(defaults, todayStr) });
    setTaskModalOpen(true);
  };
  // 既存タスクをその場で編集（Command画面から離脱せずに開く）
  const openEditTask = (task) => {
    setTaskInitial({ ...task });
    setTaskModalOpen(true);
  };
  const saveTask = (vals) => {
    const { __isNew, id, ...clean } = vals;
    if (taskCrud) {
      if (__isNew || !id) taskCrud.add(clean);
      else taskCrud.update(id, clean);
    }
    setTaskModalOpen(false);
  };

  // 「今日」をヘッダー(dateStr/greeting)と同じ実際の現在日に統一する
  const todayYmd = window.fmtYMD ? window.fmtYMD(today) : today.toISOString().slice(0, 10);
  const todayDate = window.parseYMD ? window.parseYMD(todayYmd) : new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const tomorrowYmd = (() => { const d = new Date(todayDate); d.setDate(d.getDate() + 1); return window.fmtYMD ? window.fmtYMD(d) : d.toISOString().slice(0,10); })();
  const weekEndDate = (() => { const d = new Date(todayDate); d.setDate(d.getDate() + 7); return d; })();

  // Group items
  const items = state.items || [];
  const grouped = React.useMemo(() => {
    const overdue = [], today = [], todone = [], tmw = [], week = [];
    const todayE = [], tmwE = [], weekE = [];
    items.forEach(it => {
      const d = it.date ? (window.parseYMD ? window.parseYMD(it.date) : null) : null;
      if (it.time) {
        // event
        if (it.date === todayYmd) todayE.push(it);
        else if (it.date === tomorrowYmd) tmwE.push(it);
        else if (d && d > todayDate && d <= weekEndDate) weekE.push(it);
      } else {
        // task
        if (it.status === "done" && it.date === todayYmd) todone.push(it);
        else if (!d) return;
        else if (d < todayDate && it.status !== "done") overdue.push(it);
        else if (it.date === todayYmd && it.status !== "done") today.push(it);
        else if (it.date === tomorrowYmd && it.status !== "done") tmw.push(it);
        else if (d <= weekEndDate && it.status !== "done") week.push(it);
      }
    });
    const sortByTime = (a,b) => (a.time || "99:99").localeCompare(b.time || "99:99");
    const sortByDateTime = (a,b) => ((a.date||"")+(a.time||"")).localeCompare((b.date||"")+(b.time||""));
    return {
      overdue, today: today.sort(sortByTime), todone, tomorrow: tmw.sort(sortByTime), thisWeek: week.sort((a,b) => (a.date||"").localeCompare(b.date||"")),
      eventsToday: todayE.sort(sortByTime), eventsTomorrow: tmwE.sort(sortByTime), eventsThisWeek: weekE.sort(sortByDateTime),
    };
    // eslint-disable-next-line
  }, [items, todayYmd, tomorrowYmd]);

  const recentRecordings = (state.recordings || []).slice(0, 3);
  const ownerColors = state.ownerColors || (window.DEFAULT_OWNER_COLORS || {});

  // Applicant pipeline — next-action queue
  const applicants = state.applicants || [];
  const statuses = window.getApplicantStatuses ? window.getApplicantStatuses(state) : (window.DEFAULT_APPLICANT_STATUSES || []);
  const applicantPipeline = React.useMemo(() => {
    const out = [];
    applicants.forEach(a => {
      const s = statuses.find(x => x.id === a.status);
      if (!s || s.terminal) return;
      if (!s.nextLabel) return;
      const v = s.nextDateKey ? a[s.nextDateKey] : null;
      out.push({ id: a.id, name: a.name, position: a.position, action: s.nextLabel, date: v, color: s.color });
    });
    out.sort((x, y) => (x.date || "9999").localeCompare(y.date || "9999"));
    return out.slice(0, 6);
  }, [applicants, statuses]);

  const toggleStatus = (id) => {
    const it = items.find(x => x.id === id);
    if (!it) return;
    const next = it.status === "done" ? "todo" : "done";
    setState(s => ({ ...s, items: s.items.map(x => x.id === id ? { ...x, status: next } : x) }));
  };

  // Format "YYYY-MM-DD" -> "M/D(曜)" with weekend coloring info
  const JP_WD = window.JP_WD || ["日","月","火","水","木","金","土"];
  const fmtMD_WD = (ymd) => {
    if (!ymd) return "";
    const d = window.parseYMD ? window.parseYMD(ymd) : null;
    if (!d) return ymd;
    return `${d.getMonth() + 1}/${d.getDate()}(${JP_WD[d.getDay()]})`;
  };
  const dateColor = (ymd) => {
    if (!ymd) return null;
    const d = window.parseYMD ? window.parseYMD(ymd) : null;
    if (!d) return null;
    if (d.getDay() === 0) return "var(--crimson-hi)";  // 日
    if (d.getDay() === 6) return "var(--crystal)";     // 土
    return null;
  };

  // Reusable task row
  const TaskRow = ({ task, done = false }) => {
    const oc = window.getOwnerColor ? window.getOwnerColor(task.owner, ownerColors) : null;
    return (
      <div className={"dash-task-row" + (done ? " done" : "")} onClick={() => openEditTask(task)}>
        <div className={"priority-mark " + (task.priority || "low")}/>
        <div className={"task-check" + (done ? " checked" : "")} onClick={(e) => { e.stopPropagation(); toggleStatus(task.id); }}>
          {done && <Icon name="check" size={10}/>}
        </div>
        <div className="dash-task-content">
          <div className="dash-task-title">{task.title}</div>
          <div className="dash-task-meta">
            {task.date && (
              <span className="dash-task-date" style={dateColor(task.date) ? { color: dateColor(task.date), background: dateColor(task.date) + "22" } : undefined}>
                {fmtMD_WD(task.date)}
              </span>
            )}
            <span className="chip xs">{task.area}</span>
            {task.owner && <span className="chip xs" style={oc ? { color: oc, borderColor: oc + "55", background: oc + "22" } : undefined}>{task.owner}</span>}
            {task.status === "important" && <span className="chip xs crimson">重要</span>}
            {task.status === "doing" && <span className="chip xs gold">In Progress</span>}
          </div>
        </div>
      </div>
    );
  };

  const GroupHeading = ({ label, count, color }) => count > 0 ? (
    <div className="dash-group-head" style={color ? { color } : undefined}>
      <span>{label}</span><span className="dash-group-count">{count}</span>
    </div>
  ) : null;

  return (
    <div className="view-fade dash-condensed">
      {/* HERO STRIP */}
      <div className="dash-hero-strip">
        <div className="dash-hero-left">
          <div className="dash-hero-eyebrow">★ COMMAND · {dateStr}</div>
          <div className="dash-hero-greet">{greeting}, <span className="accent">Masaaki</span></div>
        </div>
        <div className="dash-hero-stats">
          <div className="dash-stat">
            <span className="dash-stat-label">Tasks</span>
            <span className="dash-stat-value">{grouped.today.length}<span className="dash-stat-tot">/{grouped.today.length + grouped.todone.length}</span></span>
            <span className="dash-stat-delta">{grouped.todone.length} done</span>
          </div>
          <div className="dash-stat">
            <span className="dash-stat-label">Meetings</span>
            <span className="dash-stat-value">{grouped.eventsToday.length}</span>
          </div>
          <div className="dash-stat">
            <span className="dash-stat-label">Alerts</span>
            <span className="dash-stat-value">{alerts.length}</span>
          </div>
          <div className="dash-stat">
            <span className="dash-stat-label">Projects</span>
            <span className="dash-stat-value">{(state.projects || []).length}</span>
          </div>
        </div>
        <div className="dash-hero-actions">
          <button className="btn sm" onClick={() => openNewTask()} title="新しいタスクを追加">
            <Icon name="plus" size={12}/>
            <span>Task</span>
          </button>
          <button className="btn primary sm" onClick={openRecord}>
            <Icon name="mic" size={12}/>
            <span>Record</span>
          </button>
        </div>
      </div>

      {/* ALERTS STRIP */}
      {alerts.length > 0 && (
        <div className="dash-alerts-strip">
          <Icon name="alert" size={13} color="var(--crimson-hi)"/>
          <span className="dash-alerts-label">PRIORITY</span>
          <button className="dash-alerts-nav" aria-label="前へ" onClick={() => alertsListRef.current && alertsListRef.current.scrollBy({left: -240, behavior: "smooth"})}>
            <Icon name="chevronLeft" size={13}/>
          </button>
          <div className="dash-alerts-list" ref={alertsListRef}>
            {alerts.slice(0, 5).map(a => (
              <div key={a.id} className={"dash-alert-pill " + (a.severity === "high" ? "high" : "med")}>
                <span className="dash-alert-title">{a.title}</span>
                <span className="dash-alert-meta">{a.meta}</span>
              </div>
            ))}
            {alerts.length > 5 && (
              <button className="dash-alert-pill more" onClick={() => setView("tasks")}>
                +{alerts.length - 5} more
              </button>
            )}
          </div>
          <button className="dash-alerts-nav" aria-label="次へ" onClick={() => alertsListRef.current && alertsListRef.current.scrollBy({left: 240, behavior: "smooth"})}>
            <Icon name="chevronRight" size={13}/>
          </button>
        </div>
      )}

      {/* MAIN GRID */}
      <div className="dash-cols">
        {/* COLUMN 1 — Tasks (Today + Upcoming + Overdue) */}
        <div className="panel dash-panel">
          <div className="panel-head compact">
            <div>
              <span className="eyebrow">MISSION</span>
              <h3>Tasks</h3>
            </div>
            <div style={{display:"flex", gap:6}}>
              <button className="btn sm" onClick={() => openNewTask()} title="タスク追加">
                <Icon name="plus" size={11}/>
                <span>Add</span>
              </button>
              <button className="btn sm ghost" onClick={() => setView("tasks")}>
                <span>All</span><Icon name="chevronRight" size={11}/>
              </button>
            </div>
          </div>
          <div className="panel-body dash-scroll" style={{paddingTop:4}}>
            {grouped.overdue.length > 0 && (
              <>
                <GroupHeading label="OVERDUE" count={grouped.overdue.length} color="var(--crimson-hi)"/>
                {grouped.overdue.map(t => <TaskRow key={t.id} task={t}/>)}
              </>
            )}
            <GroupHeading label="TODAY" count={grouped.today.length + grouped.todone.length} color="var(--gold)"/>
            {grouped.today.map(t => <TaskRow key={t.id} task={t}/>)}
            {grouped.todone.map(t => <TaskRow key={t.id} task={t} done/>)}
            {grouped.tomorrow.length > 0 && (
              <>
                <GroupHeading label="TOMORROW" count={grouped.tomorrow.length} color="var(--crystal)"/>
                {grouped.tomorrow.map(t => <TaskRow key={t.id} task={t}/>)}
              </>
            )}
            {grouped.thisWeek.length > 0 && (
              <>
                <GroupHeading label="THIS WEEK" count={grouped.thisWeek.length} color="var(--smoke)"/>
                {grouped.thisWeek.map(t => <TaskRow key={t.id} task={t}/>)}
              </>
            )}
            {grouped.overdue.length + grouped.today.length + grouped.todone.length + grouped.tomorrow.length + grouped.thisWeek.length === 0 && (
              <div className="dash-empty">No tasks scheduled.</div>
            )}
          </div>
        </div>

        {/* COLUMN 2 — Schedule (Today + Tomorrow) */}
        <div className="panel dash-panel">
          <div className="panel-head compact">
            <div>
              <span className="eyebrow">TIMELINE</span>
              <h3>Schedule</h3>
            </div>
            <button className="btn sm ghost" onClick={() => setView("calendar")}>
              <Icon name="calendar" size={11}/>
              <span>Cal</span>
            </button>
          </div>
          <div className="panel-body dash-scroll">
            <GroupHeading label="TODAY" count={grouped.eventsToday.length} color="var(--gold)"/>
            {grouped.eventsToday.map(s => (
              <div key={s.id} className="dash-schedule-row" style={{cursor:"pointer"}} onClick={() => openEditTask(s)}>
                <div className="dash-sched-time">
                  <div>{s.time}</div>
                  {s.dur && <div className="dash-sched-dur">{s.dur}</div>}
                </div>
                <div className="dash-sched-content">
                  <div className="dash-sched-title" style={{textDecoration: s.status === "done" ? "line-through" : "none", opacity: s.status === "done" ? 0.5 : 1}}>{s.title}</div>
                  {s.notes && <div className="dash-sched-notes">{s.notes}</div>}
                </div>
                <span className={"chip xs " + (s.area === "Exec" || s.tag === "Exec" ? "crimson" : s.tag === "1:1" ? "gold" : "")}>{s.tag || s.area}</span>
              </div>
            ))}
            {grouped.eventsTomorrow.length > 0 && (
              <>
                <GroupHeading label="TOMORROW" count={grouped.eventsTomorrow.length} color="var(--crystal)"/>
                {grouped.eventsTomorrow.map(s => (
                  <div key={s.id} className="dash-schedule-row" style={{cursor:"pointer"}} onClick={() => openEditTask(s)}>
                    <div className="dash-sched-time">
                      <div>{s.time}</div>
                      {s.dur && <div className="dash-sched-dur">{s.dur}</div>}
                    </div>
                    <div className="dash-sched-content">
                      <div className="dash-sched-title">{s.title}</div>
                      {s.notes && <div className="dash-sched-notes">{s.notes}</div>}
                    </div>
                    <span className={"chip xs " + (s.area === "Exec" || s.tag === "Exec" ? "crimson" : s.tag === "1:1" ? "gold" : "")}>{s.tag || s.area}</span>
                  </div>
                ))}
              </>
            )}
            {grouped.eventsThisWeek.length > 0 && (
              <>
                <GroupHeading label="THIS WEEK" count={grouped.eventsThisWeek.length} color="var(--smoke)"/>
                {grouped.eventsThisWeek.map(s => (
                  <div key={s.id} className="dash-schedule-row" style={{cursor:"pointer"}} onClick={() => openEditTask(s)}>
                    <div className="dash-sched-time">
                      <div className="dash-sched-date" style={dateColor(s.date) ? { color: dateColor(s.date) } : undefined}>{fmtMD_WD(s.date)}</div>
                      <div>{s.time}</div>
                    </div>
                    <div className="dash-sched-content">
                      <div className="dash-sched-title">{s.title}</div>
                      {s.notes && <div className="dash-sched-notes">{s.notes}</div>}
                    </div>
                    <span className={"chip xs " + (s.area === "Exec" || s.tag === "Exec" ? "crimson" : s.tag === "1:1" ? "gold" : "")}>{s.tag || s.area}</span>
                  </div>
                ))}
              </>
            )}
            {grouped.eventsToday.length + grouped.eventsTomorrow.length + grouped.eventsThisWeek.length === 0 && (
              <div className="dash-empty">No events scheduled.</div>
            )}
          </div>
        </div>

        {/* COLUMN 3 — Projects + Applicants Pipeline + Recordings */}
        <div className="dash-stack">
          <div className="panel dash-panel">
            <div className="panel-head compact">
              <div>
                <span className="eyebrow">INITIATIVES</span>
                <h3>Projects</h3>
              </div>
              <button className="btn sm ghost" onClick={() => setView("projects")}>
                <span>All</span><Icon name="chevronRight" size={11}/>
              </button>
            </div>
            <div className="panel-body dash-scroll">
              {(state.projects || []).slice(0, 5).map(p => (
                <div key={p.id} className="dash-project-row">
                  <div className="dash-project-head">
                    <span className="dash-project-name">{p.name}</span>
                    <span className="chip xs">{p.area}</span>
                    <span className="dash-project-pct">{p.pct}%</span>
                  </div>
                  <div className="progress-bar"><div className="progress-fill" style={{width: p.pct + "%"}}/></div>
                  <div className="dash-project-meta">{p.done}/{p.milestones} milestones</div>
                </div>
              ))}
              {(state.projects || []).length === 0 && <div className="dash-empty">No projects.</div>}
            </div>
          </div>
          <div className="panel dash-panel">
            <div className="panel-head compact">
              <div>
                <span className="eyebrow">HIRING PIPELINE</span>
                <h3>Next Actions</h3>
              </div>
              <button className="btn sm ghost" onClick={() => setView("applicants")}>
                <span>All</span><Icon name="chevronRight" size={11}/>
              </button>
            </div>
            <div className="panel-body dash-scroll">
              {applicantPipeline.map(a => (
                <div key={a.id} className="dash-appl-row" onClick={() => setView("applicants")}>
                  <div className="dash-appl-dot" style={{background: a.color}}/>
                  <div className="dash-appl-content">
                    <div className="dash-appl-name">{a.name}<span className="dash-appl-pos"> · {a.position}</span></div>
                    <div className="dash-appl-action" style={{color: a.color}}>{a.action}</div>
                  </div>
                  <div className="dash-appl-date" style={a.date && dateColor(a.date.slice(0,10)) ? { color: dateColor(a.date.slice(0,10)) } : undefined}>
                    {a.date ? fmtMD_WD(a.date.slice(0, 10)) : "未設定"}
                  </div>
                </div>
              ))}
              {applicantPipeline.length === 0 && <div className="dash-empty">選考中の応募者はいません</div>}
            </div>
          </div>
          {recentRecordings.length > 0 && (
            <div className="panel dash-panel">
              <div className="panel-head compact">
                <div>
                  <span className="eyebrow">ARCHIVES</span>
                  <h3>Recent Recordings</h3>
                </div>
                <button className="btn sm ghost" onClick={() => setView("memos")}>
                  <span>All</span><Icon name="chevronRight" size={11}/>
                </button>
              </div>
              <div className="panel-body dash-scroll">
                {recentRecordings.map(r => (
                  <div key={r.id} className="dash-rec-row" onClick={() => openRecording(r)}>
                    <div className="dash-rec-icon"><Icon name="waveform" size={14}/></div>
                    <div className="dash-rec-content">
                      <div className="dash-rec-title">{r.title}</div>
                      <div className="dash-rec-sub">{r.date} · {r.participants.join(", ")}</div>
                    </div>
                    <div className="dash-rec-duration">{r.duration}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {window.EntityModal && window.ITEM_SCHEMA && (
        <window.EntityModal
          open={taskModalOpen}
          schema={window.ITEM_SCHEMA}
          initial={taskInitial}
          onSave={saveTask}
          onClose={() => setTaskModalOpen(false)}
        />
      )}
    </div>
  );
};

Object.assign(window, { Dashboard });


;/* ==== workspace.jsx ==== */
// Workspace — Notion-style unified view of tasks + events
// Same data shown across Board / Calendar / Table / Today views

const AREA_OPTIONS = ["HR", "Hiring", "GA", "IT", "Exec", "Personal"];
const OWNER_OPTIONS = ["竹内", "大志万", "山本", "松川", "楠田", "妹尾", "その他"];
const TAG_OPTIONS = ["Exec", "1:1", "Project", "Internal", "GA", "Personal"];
const DUR_OPTIONS = ["", "15 min", "30 min", "45 min", "60 min", "90 min", "120 min", "180 min", "All day"];

// Owner color system — palette is editable in Settings → リスト管理.
// Hue-distinct, equal-saturation palette derived from oklch(0.75 0.13 H).
const DEFAULT_OWNER_COLORS = {
  "竹内":   "#f5c518", // gold
  "大志万": "#5b9bd5", // blue
  "山本":   "#5ac994", // green
  "松川":   "#b58aff", // violet
  "楠田":   "#ed7aa6", // pink
  "妹尾":   "#ed8c4a", // orange
  "その他": "#8a93a6", // slate
};
const FALLBACK_OWNER_COLORS = ["#5b9bd5", "#5ac994", "#ed8c4a", "#b58aff", "#ed7aa6", "#f5c518", "#8a93a6"];
let _hashCounter = 0;
const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};
const getOwnerColor = (owner, ownerColors) => {
  if (!owner) return null;
  if (ownerColors && ownerColors[owner]) return ownerColors[owner];
  if (DEFAULT_OWNER_COLORS[owner]) return DEFAULT_OWNER_COLORS[owner];
  return FALLBACK_OWNER_COLORS[hashStr(owner) % FALLBACK_OWNER_COLORS.length];
};
// Build a tinted background (low-alpha) from a hex color for soft pills/cards.
const tint = (hex, alpha = 0.16) => {
  if (!hex) return null;
  const m = hex.replace("#", "").match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return null;
  const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
Object.assign(window, { DEFAULT_OWNER_COLORS, getOwnerColor, ownerColorTint: tint });

const ITEM_SCHEMA = {
  eyebrow: "ITEM",
  title: "Item",
  titleNew: "New Item",
  titleEdit: "Edit Item",
  fields: [
    { key: "title", label: "Title / タスク名", type: "text", required: true, span: 2, placeholder: "What needs to happen?" },
    { key: "date", label: "Date / 期限日", type: "date", hint: "Leave blank for backlog" },
    { key: "dateEnd", label: "End / 終了日（連日登録）", type: "date", hint: "設定すると開始日〜終了日の毎日分を一括登録（繰り返し設定がある場合はそちらを優先）" },
    { key: "time", label: "Time / 時刻", type: "time", hint: "Leave blank = all-day / undated" },
    { key: "recur", label: "Repeat / 繰り返し", type: "recur", span: 2 },
    { key: "status", label: "Status / ステータス", type: "chips", options: [
      { value: "todo", label: "To Do" }, { value: "doing", label: "In Progress" }, { value: "important", label: "重要" }, { value: "done", label: "Done" }
    ], colors: { important: "crimson", doing: "gold", done: "crystal" } },
    { key: "owner", label: "Owner / 担当者", type: "select", options: OWNER_OPTIONS },
    { key: "area", label: "Area / 領域", type: "radio", options: AREA_OPTIONS },
    { key: "notes", label: "Notes / メモ", type: "textarea", span: 2 },
  ],
};

const ITEM_DEFAULT = { status: "todo", area: "HR", owner: "竹内" };

// A brand-new task defaults its 期限日 to today UNLESS the caller already chose a
// date. The calendar paths pass an explicit `date` (the clicked/selected day), so
// the presence of a "date" key — even null/"" — means "the caller decided the
// date, respect it". Everywhere else (New button, Board/Owners column add) omits
// it → seed today. Pure + exported so the rule is unit-testable in isolation.
function seedNewItemDefaults(defaults, today) {
  return defaults && "date" in defaults ? defaults : { ...defaults, date: today };
}

// Workspace tabs — defs + persisted order (drag-droppable)
const WS_TAB_DEFS = {
  board:    { label: "Board",    icon: "tasks",    defaultFilter: "all" },
  owners:   { label: "By Owner", icon: "users",    defaultFilter: "active" },
  calendar: { label: "Calendar", icon: "calendar", defaultFilter: "all" },
  table:    { label: "Table",    icon: "dashboard", defaultFilter: "active" },
  today:    { label: "Today",    icon: "clock",    defaultFilter: "all" },
};
const WS_TAB_DEFAULT_ORDER = ["board", "owners", "calendar", "table", "today"];
const WS_TABS_KEY = "__supersys_ws_tabs_v1";

// ── Workspace top — filter + view-mode + add ─────────────────────────────
const WorkspaceShell = ({ state, setState, defaultMode = "board" }) => {
  const [mode, setMode] = React.useState(defaultMode);
  const [tabOrder, setTabOrder] = React.useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(WS_TABS_KEY) || "null");
      if (Array.isArray(saved) && saved.length === WS_TAB_DEFAULT_ORDER.length && saved.every(k => WS_TAB_DEFS[k])) return saved;
    } catch {}
    return WS_TAB_DEFAULT_ORDER;
  });
  React.useEffect(() => { try { localStorage.setItem(WS_TABS_KEY, JSON.stringify(tabOrder)); } catch {} }, [tabOrder]);
  const [draggedTab, setDraggedTab] = React.useState(null);
  const [dropTargetTab, setDropTargetTab] = React.useState(null);
  const [filterArea, setFilterArea] = React.useState("all");
  const [filterStatus, setFilterStatus] = React.useState("all"); // default: ALL
  const [filterOwner, setFilterOwner] = React.useState("all");
  const [sortKey, setSortKey] = React.useState("datetime");
  const [sortDir, setSortDir] = React.useState("asc");
  const [search, setSearch] = React.useState("");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const crud = useCrud(state, setState, "items");
  const ownerColors = state.ownerColors || DEFAULT_OWNER_COLORS;

  React.useEffect(() => { setMode(defaultMode); }, [defaultMode]);

  // Per-mode default filter (By Owner defaults to Active, others to All)
  React.useEffect(() => {
    const def = WS_TAB_DEFS[mode]?.defaultFilter;
    if (def) setFilterStatus(def);
  }, [mode]);

  // Apply filters
  const filtered = React.useMemo(() => {
    return state.items.filter(it => {
      if (filterArea !== "all" && it.area !== filterArea) return false;
      if (filterStatus === "active" && it.status === "done") return false;
      if (filterStatus === "done" && it.status !== "done") return false;
      if (filterOwner !== "all" && it.owner !== filterOwner) return false;
      if (search && !(it.title + " " + (it.notes || "")).toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [state.items, filterArea, filterStatus, filterOwner, search]);

  const openNew = (defaults = {}) => {
    setEditing({ __isNew: true, ...ITEM_DEFAULT, ...seedNewItemDefaults(defaults, todayYMD()) });
    setModalOpen(true);
  };
  const openEdit = (it) => { setEditing(it); setModalOpen(true); };
  // ── recurring-series save/delete (rule lives in vals.recur, link in seriesId) ──
  const [seriesAsk, setSeriesAsk] = React.useState(null); // { kind: "save"|"delete", vals? }
  const ruleOf = (v) => (v && v.freq && v.freq !== "none" ? v : null);

  const createSeries = (vals) => {
    const { __isNew, id, seriesId: _sid, ...clean } = vals;
    const recur = ruleOf(clean.recur);
    const dates = buildRecurrenceDates(clean.date, recur);
    const seriesId = newSeriesId();
    crud.addMany(dates.map(d => ({ ...clean, recur, seriesId, date: d, status: clean.status || "todo" })));
  };

  const handleSave = (rawVals) => {
    // 連日登録: an end date (and no explicit rule) becomes a daily series over the
    // span — every calendar day inclusive, no holiday shifting (a 3-day training
    // covers its weekend/holiday days too). dateEnd itself never lands on items.
    const vals = { ...rawVals };
    if (!ruleOf(vals.recur) && vals.date && vals.dateEnd && vals.dateEnd > vals.date) {
      vals.recur = { freq: "daily", holidayShift: false, end: { type: "date", date: vals.dateEnd } };
    }
    delete vals.dateEnd;
    const recur = ruleOf(vals.recur);
    if (editing && !editing.__isNew) {
      if (editing.seriesId) { setSeriesAsk({ kind: "save", vals }); return; } // scope dialog
      if (recur && vals.date) {
        // plain item gained a recurrence rule → becomes the anchor of a new series
        crud.remove(editing.id);
        createSeries(vals);
      } else {
        crud.update(editing.id, { ...vals, recur: null });
      }
    } else {
      if (recur && vals.date) createSeries(vals);
      else { const { __isNew, id, ...clean } = vals; crud.add({ ...clean, recur: null }); }
    }
    setModalOpen(false);
  };

  const applySeriesSave = (scope, vals) => {
    const sid = editing.seriesId;
    const fromDate = editing.date || "";
    const newRule = ruleOf(vals.recur);
    if (scope === "one") {
      // The rule belongs to the series — この回のみ edits never touch it.
      const { recur, ...rest } = vals;
      crud.update(editing.id, rest);
    } else {
      const ruleChanged = JSON.stringify(newRule) !== JSON.stringify(ruleOf(editing.recur));
      const dateChanged = (vals.date || "") !== fromDate;
      if (!newRule) {
        // recurrence switched off: keep this row as a plain item, drop future undone siblings
        const ids = state.items
          .filter(it => it.seriesId === sid && it.id !== editing.id && (it.date || "") >= fromDate && it.status !== "done")
          .map(it => it.id);
        crud.removeMany(ids);
        crud.update(editing.id, { ...vals, recur: null, seriesId: null });
      } else if (ruleChanged || dateChanged) {
        // re-anchor: regenerate this-and-future from the (possibly new) date; done rows stay as 履歴
        const ids = state.items
          .filter(it => it.seriesId === sid && (it.date || "") >= fromDate && it.status !== "done")
          .map(it => it.id);
        crud.removeMany(ids);
        const { __isNew, id, ...clean } = vals;
        const dates = buildRecurrenceDates(vals.date || fromDate, newRule);
        crud.addMany(dates.map(d => ({ ...clean, recur: newRule, seriesId: sid, date: d, status: "todo" })));
      } else {
        // field-only change → patch this and future (date/status stay per-instance)
        const { date, status, recur, __isNew, id, ...fieldPatch } = vals;
        const patches = {};
        state.items.forEach(it => {
          if (it.seriesId === sid && (it.date || "") >= fromDate) patches[it.id] = fieldPatch;
        });
        patches[editing.id] = { ...fieldPatch, status: vals.status };
        crud.updateMany(patches);
      }
    }
    setSeriesAsk(null);
    setModalOpen(false);
  };

  const applySeriesDelete = (scope) => {
    const sid = editing.seriesId;
    const fromDate = editing.date || "";
    if (scope === "one") crud.remove(editing.id);
    else if (scope === "future") crud.removeMany(state.items.filter(it => it.seriesId === sid && (it.date || "") >= fromDate).map(it => it.id));
    else crud.removeMany(state.items.filter(it => it.seriesId === sid).map(it => it.id));
    setSeriesAsk(null);
    setModalOpen(false);
  };

  const handleDelete = () => {
    if (editing && editing.id && !editing.__isNew) {
      if (editing.seriesId) { setSeriesAsk({ kind: "delete" }); return; }
      crud.remove(editing.id);
    }
    setModalOpen(false);
  };
  const cycleStatus = (it) => {
    crud.update(it.id, { status: nextStatus(it.status) });
  };

  return (
    <div className="view-fade">
      {/* View toggle + actions */}
      <div className="ws-toolbar">
        <div className="ws-tabs">
          {tabOrder.map(k => {
            const def = WS_TAB_DEFS[k];
            const isDragging = draggedTab === k;
            const isDropTarget = dropTargetTab === k && draggedTab && draggedTab !== k;
            return (
              <div
                key={k}
                className={"ws-tab" + (mode === k ? " active" : "") + (isDragging ? " dragging" : "") + (isDropTarget ? " drop-target" : "")}
                draggable
                onDragStart={(e) => {
                  setDraggedTab(k);
                  try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", k); } catch {}
                }}
                onDragEnd={() => { setDraggedTab(null); setDropTargetTab(null); }}
                onDragOver={(e) => {
                  if (!draggedTab || draggedTab === k) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDropTargetTab(k);
                }}
                onDragLeave={() => { if (dropTargetTab === k) setDropTargetTab(null); }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (!draggedTab || draggedTab === k) { setDraggedTab(null); setDropTargetTab(null); return; }
                  setTabOrder(o => {
                    const next = o.filter(t => t !== draggedTab);
                    const idx = next.indexOf(k);
                    next.splice(idx, 0, draggedTab);
                    return next;
                  });
                  setDraggedTab(null);
                  setDropTargetTab(null);
                }}
                onClick={() => setMode(k)}
                title="クリックで切替 / ドラッグで並び替え"
              >
                <Icon name={def.icon} size={13}/>
                <span>{def.label}</span>
              </div>
            );
          })}
        </div>

        <div className="ws-spacer"/>

        <div className="ws-filters">
          {(mode === "board" || mode === "owners") && (
            <div className="ws-sort" style={{display:"flex", alignItems:"center", gap:4}}>
              <select value={sortKey} onChange={e => setSortKey(e.target.value)} className="ws-filter" title="ソート項目">
                <option value="datetime">日時順</option>
                <option value="title">タイトル順</option>
                <option value="owner">担当者順</option>
                <option value="area">領域順</option>
                <option value="status">ステータス順</option>
                <option value="priority">優先度順</option>
                <option value="created">作成順</option>
              </select>
              <button
                className="ws-filter"
                onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
                title={sortDir === "asc" ? "昇順 (クリックで降順)" : "降順 (クリックで昇順)"}
                style={{cursor:"pointer", padding:"5px 8px", fontFamily:"var(--f-mono)"}}
              >{sortDir === "asc" ? "▲" : "▼"}</button>
            </div>
          )}
          <select value={filterOwner} onChange={e => setFilterOwner(e.target.value)} className="ws-filter">
            <option value="all">All owners</option>
            {OWNER_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <select value={filterArea} onChange={e => setFilterArea(e.target.value)} className="ws-filter">
            <option value="all">All areas</option>
            {AREA_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="ws-filter">
            <option value="active">Active</option>
            <option value="all">All</option>
            <option value="done">Done</option>
          </select>
          <div className="ws-search">
            <Icon name="search" size={12}/>
            <input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}/>
          </div>
        </div>

        <button className="btn gold sm" onClick={() => openNew()}>
          <Icon name="plus" size={12}/>
          <span>New</span>
        </button>
      </div>

      {/* Mode body */}
      {mode === "board" && <BoardView items={filtered} openEdit={openEdit} openNew={openNew} crud={crud} cycleStatus={cycleStatus} ownerColors={ownerColors} sortKey={sortKey} sortDir={sortDir}/>}
      {mode === "owners" && <OwnersView items={filtered} openEdit={openEdit} openNew={openNew} crud={crud} ownerColors={ownerColors} state={state} sortKey={sortKey} sortDir={sortDir}/>}
      {mode === "calendar" && <CalendarMode items={filtered} allItems={state.items} openEdit={openEdit} openNew={openNew} crud={crud} ownerColors={ownerColors}/>}
      {mode === "table" && <TableView items={filtered} openEdit={openEdit} crud={crud} cycleStatus={cycleStatus} ownerColors={ownerColors}/>}
      {mode === "today" && <TodayView items={filtered} allItems={state.items} openEdit={openEdit} openNew={openNew} crud={crud} cycleStatus={cycleStatus} ownerColors={ownerColors}/>}

      {/* Quick add */}
      <QuickAdd onCreate={(title, date) => crud.add({ ...ITEM_DEFAULT, title, date: date || null, time: null, dur: null, tag: null, notes: "" })}/>

      <EntityModal
        open={modalOpen}
        schema={ITEM_SCHEMA}
        initial={editing}
        onSave={handleSave}
        onDelete={editing && editing.id && !editing.__isNew ? handleDelete : null}
        onClose={() => setModalOpen(false)}
      />

      <SeriesScopeDialog
        open={!!seriesAsk}
        kind={seriesAsk?.kind}
        onPick={(scope) => seriesAsk?.kind === "delete" ? applySeriesDelete(scope) : applySeriesSave(scope, seriesAsk.vals)}
        onClose={() => setSeriesAsk(null)}
      />
    </div>
  );
};

// ── Quick add at bottom ───────────────────────────────────────────────────
const QuickAdd = ({ onCreate }) => {
  const [val, setVal] = React.useState("");
  // Default the date to today (editable / clearable for a backlog item). After a
  // submit, reset back to today rather than blank so the next quick-add keeps the
  // same convenient default.
  const [date, setDate] = React.useState(todayYMD);
  const submit = () => {
    if (!val.trim()) return;
    onCreate(val.trim(), date || null);
    setVal(""); setDate(todayYMD());
  };
  return (
    <div className="ws-quick">
      <Icon name="plus" size={14} color="var(--gold)"/>
      <input
        placeholder="Quick add — type a task title and press Enter"
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => e.key === "Enter" && !e.nativeEvent.isComposing && submit()}
      />
      <input
        type="date"
        value={date}
        onChange={e => setDate(e.target.value)}
        className="ws-quick-date"
        title="Date (optional)"
      />
      <button className="btn gold sm" onClick={submit}>Add</button>
    </div>
  );
};

// ── STATUS chip helper ────────────────────────────────────────────────────
// Declared in Board lane order: To Do → In Progress → 重要 → Done.
const STATUS_META = {
  todo: { label: "TO DO", cls: "" },
  doing: { label: "IN PROGRESS", cls: "gold" },
  important: { label: "重要", cls: "crimson" },
  done: { label: "DONE", cls: "crystal" },
};

// Click-to-cycle walks the WORKING states only: todo → doing → done → todo.
// 「重要」 (2026-07-09) is a deliberate designation — set in the modal chips or by
// dragging onto the board's 重要 column — so it is never cycled INTO by accident.
// Cycling OUT of 重要 goes to doing, NOT done: an accidental pill click must never
// silently complete a task the user flagged as important. Pure + exported so the
// rule is unit-testable without rendering the workspace.
function nextStatus(status) {
  switch (status) {
    case "important": return "doing";
    case "todo": return "doing";
    case "doing": return "done";
    default: return "todo";   // done (and any unknown/missing) → todo
  }
}
const PRIO_DOT = { high: "var(--crimson)", med: "var(--gold)", low: "var(--crystal)" };

const StatusButton = ({ status, onClick }) => {
  const m = STATUS_META[status] || STATUS_META.todo;
  return (
    <button
      className={"status-pill " + status}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title="Click to cycle status"
    >
      <span className="status-pill-dot"/>
      <span>{m.label}</span>
    </button>
  );
};

const DateBadge = ({ date, time, compact }) => {
  if (!date && !time) return <span className="muted" style={{fontSize:11, fontFamily:"var(--f-mono)"}}>—</span>;
  const settings = (typeof window !== "undefined" && window.__supersys_settings) || {};
  const showHolidays = settings.showHolidays !== false;
  const lang = settings.lang === "en" ? "en" : "jp";
  const fmt = settings.dateFormat || "md-wd";
  const showWd = fmt === "md-wd" || fmt === "full-wd";
  const showFullYear = fmt === "full" || fmt === "full-wd";

  if (!date) return <span style={{fontSize:11, fontFamily:"var(--f-mono)", color:"var(--gold)"}}>{fmtHM(time)}</span>;
  const d = parseYMD(date);
  if (!d) return <span style={{fontSize:11, fontFamily:"var(--f-mono)"}}>{date}</span>;
  const wdArr = lang === "en" ? EN_WD : JP_WD;
  const wd = wdArr[d.getDay()];
  const hol = showHolidays ? getHoliday(date) : null;
  const isSun = d.getDay() === 0;
  const isSat = d.getDay() === 6;
  const wdColor = isSun || hol ? "var(--crimson-hi)" : isSat ? "var(--crystal)" : "var(--ash)";
  const isPast = parseYMD(todayYMD()) > d;
  const isToday = todayYMD() === date;
  const dateLabel = showFullYear
    ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`
    : `${d.getMonth()+1}/${d.getDate()}`;
  return (
    <span className={"date-badge-inline" + (isToday ? " today" : "") + (isPast ? " past" : "")}>
      <span style={{color:"var(--gold)", fontFamily:"var(--f-mono)", fontWeight:600}}>{dateLabel}</span>
      {showWd && <span style={{color: wdColor, marginLeft:4, fontWeight:600}}>({wd})</span>}
      {time && <span style={{marginLeft:6, color:"var(--ivory)", fontFamily:"var(--f-mono)"}}>{fmtHM(time)}</span>}
      {!compact && hol && <span style={{marginLeft:6, color:"var(--crimson-hi)", fontSize:10}}>{hol}</span>}
    </span>
  );
};

// Format an item as "M/D(曜)HH:MM タイトル" — used by the row copy buttons.
// No space between date / weekday / time; single space before title.
const formatItemAsText = (it) => {
  let datetime = "";
  if (it.date) {
    const d = (typeof window !== "undefined" && window.parseYMD) ? window.parseYMD(it.date) : null;
    if (d) {
      datetime = `${d.getMonth() + 1}/${d.getDate()}(${JP_WD[d.getDay()]})`;
    } else {
      datetime = it.date;
    }
  }
  if (it.time) datetime += it.time;
  return [datetime, it.title].filter(Boolean).join(" ");
};

// Robust clipboard copy with execCommand fallback (sandbox-safe).
const wsCopyText = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed"; ta.style.opacity = "0"; ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
};

// Floating toast for clipboard feedback
let __wsToastTimer = null;
const wsShowToast = (msg, kind = "ok") => {
  let el = document.getElementById("__ws_toast");
  if (!el) {
    el = document.createElement("div");
    el.id = "__ws_toast";
    el.className = "ws-toast";
    document.body.appendChild(el);
  }
  el.textContent = msg;
  el.dataset.kind = kind;
  el.classList.add("visible");
  if (__wsToastTimer) clearTimeout(__wsToastTimer);
  __wsToastTimer = setTimeout(() => el.classList.remove("visible"), 1600);
};

// Single source for the copy button + handler used by Board / Owners / Table / Today
const CopyItemButton = ({ item, small = false }) => {
  const onClick = async (e) => {
    e.stopPropagation();
    const text = formatItemAsText(item);
    const ok = await wsCopyText(text);
    wsShowToast(ok ? `コピー: ${text.slice(0, 40)}${text.length > 40 ? "…" : ""}` : "コピーに失敗", ok ? "ok" : "err");
  };
  return (
    <button
      className={"row-action-btn copy" + (small ? " sm" : "")}
      onClick={onClick}
      title={`「${formatItemAsText(item)}」をコピー`}
      type="button"
    >
      <Icon name="copy" size={small ? 10 : 12}/>
    </button>
  );
};
// keys: date, time, title, owner, area, status, priority, created
const PRIORITY_ORDER = { high: 0, med: 1, low: 2 };
// Mirrors the Board lane order (重要 sits right of In Progress — 竹内さん 2026-07-09)
// so sorting a table by status walks the same sequence the board shows.
const STATUS_ORDER = { todo: 0, doing: 1, important: 2, done: 3 };
const sortItems = (arr, key, dir) => {
  const mul = dir === "desc" ? -1 : 1;
  const ranked = (k) => k === "priority" ? PRIORITY_ORDER : k === "status" ? STATUS_ORDER : null;
  const ranks = ranked(key);
  return [...arr].sort((a, b) => {
    let va = a[key], vb = b[key];
    if (key === "created") { va = a.id; vb = b.id; }
    // 日時順: combine date + time into one comparable value so same-day tasks
    // order by time. Tasks with no date sink to the bottom (handled by empty check).
    if (key === "datetime") {
      va = a.date ? `${a.date} ${a.time || "00:00"}` : "";
      vb = b.date ? `${b.date} ${b.time || "00:00"}` : "";
    }
    if (ranks) { va = ranks[va] ?? 99; vb = ranks[vb] ?? 99; }
    const aE = va === undefined || va === null || va === "";
    const bE = vb === undefined || vb === null || vb === "";
    // Empties always go to bottom regardless of dir
    if (aE && bE) return 0;
    if (aE) return 1;
    if (bE) return -1;
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * mul;
    return String(va).localeCompare(String(vb), "ja") * mul;
  });
};

// Helper: given a list of items in a column and a drop target (cardId, position),
// produce patches { [id]: { [orderField]: number, ...extraPatch } } for the dragged item
// + every item whose order changes.
const computeReorder = ({ items, draggedId, targetGroupKey, beforeId, getGroup, orderField, extraPatch }) => {
  const draggedItem = items.find(it => it.id === draggedId);
  if (!draggedItem) return {};
  const dest = items
    .filter(it => getGroup(it) === targetGroupKey && it.id !== draggedId)
    .sort((a, b) => (a[orderField] ?? 9999) - (b[orderField] ?? 9999));
  let idx = beforeId == null ? dest.length : dest.findIndex(it => it.id === beforeId);
  if (idx < 0) idx = dest.length;
  dest.splice(idx, 0, draggedItem);
  const patches = {};
  dest.forEach((it, i) => {
    const patch = { [orderField]: i * 10 };
    if (it.id === draggedId && extraPatch) Object.assign(patch, extraPatch);
    patches[it.id] = patch;
  });
  return patches;
};

// A column-body that handles drag-reorder. Renders insertion-line hints between
// cards. Children must be already-sorted card elements; `itemIds` is the
// matching array of item ids in render order.
const KanbanColumnBody = ({ children, itemIds, dragId, columnKey, onDrop, emptyHint }) => {
  const [hoverIdx, setHoverIdx] = React.useState(null);
  // Compute beforeId from hoverIdx: hoverIdx === itemIds.length means "at end"
  const onColDragOver = (e) => {
    e.preventDefault();
  };
  const onColDragLeave = (e) => {
    // Only clear if we're really leaving the column (not just moving to a child)
    if (!e.currentTarget.contains(e.relatedTarget)) setHoverIdx(null);
  };
  const onColDrop = (e) => {
    e.preventDefault();
    if (!dragId) { setHoverIdx(null); return; }
    const idx = hoverIdx ?? itemIds.length;
    const beforeId = idx < itemIds.length ? itemIds[idx] : null;
    onDrop(columnKey, beforeId);
    setHoverIdx(null);
  };
  const childrenArr = React.Children.toArray(children);
  return (
    <div
      className="board-col-body"
      onDragOver={onColDragOver}
      onDragLeave={onColDragLeave}
      onDrop={onColDrop}
    >
      {childrenArr.length === 0 && (
        <div
          className="board-col-empty"
          onDragOver={(e) => { e.preventDefault(); setHoverIdx(0); }}
        >{dragId ? "ここにドロップ" : (emptyHint || "—")}</div>
      )}
      {childrenArr.map((child, i) => (
        <React.Fragment key={itemIds[i]}>
          {dragId && itemIds[i] !== dragId && (
            <div
              className={"kanban-drop-line" + (hoverIdx === i ? " active" : "")}
              onDragOver={(e) => { e.preventDefault(); setHoverIdx(i); }}
            />
          )}
          {React.cloneElement(child, {
            onDragOver: (e) => {
              if (!dragId || itemIds[i] === dragId) return;
              e.preventDefault();
              const rect = e.currentTarget.getBoundingClientRect();
              const isTopHalf = (e.clientY - rect.top) < rect.height / 2;
              setHoverIdx(isTopHalf ? i : i + 1);
            },
          })}
        </React.Fragment>
      ))}
      {/* Tail drop zone */}
      {dragId && childrenArr.length > 0 && (
        <div
          className={"kanban-drop-line tail" + (hoverIdx === childrenArr.length ? " active" : "")}
          onDragOver={(e) => { e.preventDefault(); setHoverIdx(childrenArr.length); }}
        />
      )}
    </div>
  );
};

const BoardView = ({ items, openEdit, openNew, crud, cycleStatus, ownerColors, sortKey = "datetime", sortDir = "asc" }) => {
  const cols = [
    { key: "todo", label: "To Do" },
    { key: "doing", label: "In Progress" },
    { key: "important", label: "重要" },
    { key: "done", label: "Done" },
  ];
  const [dragId, setDragId] = React.useState(null);

  const itemsByStatus = React.useMemo(() => {
    const out = {};
    cols.forEach(c => {
      out[c.key] = sortItems(items.filter(it => it.status === c.key), sortKey, sortDir);
    });
    return out;
    // eslint-disable-next-line
  }, [items, sortKey, sortDir]);

  // Drag between columns only — changes status, not in-column order
  const handleDrop = (colKey) => {
    if (!dragId) return;
    const dragged = items.find(it => it.id === dragId);
    if (!dragged) { setDragId(null); return; }
    if (dragged.status !== colKey) crud.update(dragId, { status: colKey });
    setDragId(null);
  };

  return (
    <div className="board-grid">
      {cols.map(col => {
        const list = itemsByStatus[col.key];
        return (
          <div
            key={col.key}
            className="board-col"
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("dragover"); }}
            onDragLeave={(e) => e.currentTarget.classList.remove("dragover")}
            onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("dragover"); handleDrop(col.key); }}
          >
            <div className="board-col-head">
              <div className={"status-pill " + col.key} style={{cursor:"default"}}>
                <span className="status-pill-dot"/>
                <span>{col.label.toUpperCase()}</span>
                <span style={{marginLeft:6, color:"var(--ash)"}}>{list.length}</span>
              </div>
              <button className="row-action-btn" onClick={() => openNew({ status: col.key })} title="Add to this column">
                <Icon name="plus" size={13}/>
              </button>
            </div>
            <div className="board-col-body">
              {list.map(it => {
                const oc = getOwnerColor(it.owner, ownerColors);
                return (
                  <div
                    key={it.id}
                    className={"board-card" + (it.status === "done" ? " done" : "") + (dragId === it.id ? " dragging" : "")}
                    draggable
                    onDragStart={() => setDragId(it.id)}
                    onDragEnd={() => setDragId(null)}
                    onClick={() => openEdit(it)}
                    style={oc ? { borderLeft: `3px solid ${oc}`, paddingLeft: 11 } : undefined}
                  >
                    <div style={{display:"flex", alignItems:"flex-start", gap:8, marginBottom:8}}>
                      <div className="board-card-title">{it.title}<RecurBadge it={it}/></div>
                      <CopyItemButton item={it} small/>
                    </div>
                    <div className="board-card-meta">
                      <DateBadge date={it.date} time={it.time} compact/>
                      {it.owner && (
                        <span className="chip" style={oc ? { background: tint(oc, 0.18), borderColor: tint(oc, 0.45), color: oc } : undefined}>{it.owner}</span>
                      )}
                      {it.area && <span className="chip">{it.area}</span>}
                    </div>
                  </div>
                );
              })}
              {list.length === 0 && <div className="board-col-empty">—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ====================================================================
//  OWNERS VIEW (Kanban grouped by owner, drag to reassign + reorder)
// ====================================================================
const OwnersView = ({ items, openEdit, openNew, crud, ownerColors, state, sortKey = "datetime", sortDir = "asc" }) => {
  const [dragId, setDragId] = React.useState(null);
  const knownOwners = state?.owners || ["竹内", "大志万", "山本", "松川", "楠田", "妹尾", "その他"];
  const seenOwners = Array.from(new Set(items.map(it => it.owner).filter(Boolean)));
  const colOwners = [
    ...knownOwners,
    ...seenOwners.filter(o => !knownOwners.includes(o)),
  ];
  const cols = [
    ...colOwners.map(o => ({ key: o, label: o, color: getOwnerColor(o, ownerColors) })),
    { key: "__unassigned__", label: "未割当", color: "#666" },
  ];

  const groupKey = (it) => it.owner || "__unassigned__";
  const itemsByOwner = React.useMemo(() => {
    const out = {};
    cols.forEach(c => {
      out[c.key] = sortItems(items.filter(it => groupKey(it) === c.key), sortKey, sortDir);
    });
    return out;
    // eslint-disable-next-line
  }, [items, colOwners.join(","), sortKey, sortDir]);

  const handleDrop = (colKey) => {
    if (!dragId) return;
    const dragged = items.find(it => it.id === dragId);
    if (!dragged) { setDragId(null); return; }
    const newOwner = colKey === "__unassigned__" ? "" : colKey;
    if ((dragged.owner || "") !== newOwner) crud.update(dragId, { owner: newOwner });
    setDragId(null);
  };

  return (
    <div className="board-grid owners-grid">
      {cols.map(col => {
        const list = itemsByOwner[col.key] || [];
        const c = col.color;
        return (
          <div
            key={col.key}
            className="board-col"
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("dragover"); }}
            onDragLeave={(e) => e.currentTarget.classList.remove("dragover")}
            onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("dragover"); handleDrop(col.key); }}
          >
            <div className="board-col-head">
              <div
                className="status-pill"
                style={{
                  cursor: "default",
                  background: c ? tint(c, 0.18) : undefined,
                  borderColor: c ? tint(c, 0.5) : undefined,
                  color: c || "var(--ivory)",
                }}
              >
                <span className="status-pill-dot" style={c ? { background: c } : undefined}/>
                <span>{col.label}</span>
                <span style={{marginLeft:6, opacity: 0.7}}>{list.length}</span>
              </div>
              <button className="row-action-btn" onClick={() => openNew({ owner: col.key === "__unassigned__" ? "" : col.key })} title="このオーナーに追加">
                <Icon name="plus" size={13}/>
              </button>
            </div>
            <div className="board-col-body">
              {list.map(it => (
                <div
                  key={it.id}
                  className={"board-card" + (it.status === "done" ? " done" : "") + (dragId === it.id ? " dragging" : "")}
                  draggable
                  onDragStart={() => setDragId(it.id)}
                  onDragEnd={() => setDragId(null)}
                  onClick={() => openEdit(it)}
                  style={c ? { borderLeft: `3px solid ${c}`, paddingLeft: 11 } : undefined}
                >
                  <div style={{display:"flex", alignItems:"flex-start", gap:8, marginBottom:8}}>
                    <div className="board-card-title">{it.title}</div>
                    <CopyItemButton item={it} small/>
                  </div>
                  <div className="board-card-meta">
                    <span className={"status-pill " + it.status} style={{padding:"2px 6px", fontSize:9}}>
                      <span className="status-pill-dot"/>
                      <span>{(STATUS_META[it.status] || STATUS_META.todo).label}</span>
                    </span>
                    <DateBadge date={it.date} time={it.time} compact/>
                    {it.area && <span className="chip">{it.area}</span>}
                  </div>
                </div>
              ))}
              {list.length === 0 && <div className="board-col-empty">—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ====================================================================
//  TABLE VIEW (Notion-like table) — reorderable + resizable columns
// ====================================================================
const TABLE_COLUMNS = {
  status: { label: "Status",  width: 130, sortable: true,  align: "left" },
  title:  { label: "Title",   width: 360, sortable: true,  align: "left" },
  date:   { label: "Date",    width: 160, sortable: true,  align: "left" },
  time:   { label: "Time",    width: 80,  sortable: true,  align: "left" },
  owner:  { label: "Owner",   width: 100, sortable: true,  align: "left" },
  area:   { label: "Area",    width: 100, sortable: true,  align: "left" },
  notes:  { label: "Notes",   width: 280, sortable: false, align: "left" },
  actions:{ label: "",        width: 56,  sortable: false, align: "right" },
};
const TABLE_DEFAULT_ORDER = ["status", "title", "date", "time", "owner", "area", "actions"];
const TABLE_CONFIG_KEY = "__supersys_tasks_table_v1";

const loadTableConfig = () => {
  try {
    const raw = localStorage.getItem(TABLE_CONFIG_KEY);
    if (!raw) return null;
    const cfg = JSON.parse(raw);
    if (!Array.isArray(cfg.order) || typeof cfg.widths !== "object") return null;
    // Drop unknown column ids; append any new ones not yet ordered
    const known = cfg.order.filter(id => TABLE_COLUMNS[id]);
    for (const id of TABLE_DEFAULT_ORDER) if (!known.includes(id)) known.push(id);
    return { order: known, widths: { ...cfg.widths } };
  } catch (e) { return null; }
};
const saveTableConfig = (cfg) => {
  try { localStorage.setItem(TABLE_CONFIG_KEY, JSON.stringify(cfg)); } catch (e) {}
};

const TableView = ({ items, openEdit, crud, cycleStatus, ownerColors }) => {
  const [sort, setSort] = React.useState({ key: "date", dir: "asc" });
  const [config, setConfig] = React.useState(() => {
    const saved = loadTableConfig();
    if (saved) return saved;
    return {
      order: TABLE_DEFAULT_ORDER,
      widths: Object.fromEntries(Object.entries(TABLE_COLUMNS).map(([k, v]) => [k, v.width])),
    };
  });
  const [dragColId, setDragColId] = React.useState(null);
  const [dropTargetId, setDropTargetId] = React.useState(null);
  const resizeRef = React.useRef(null);

  // Persist whenever config changes
  React.useEffect(() => { saveTableConfig(config); }, [config]);

  const sortedItems = React.useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      const va = a[sort.key] || "";
      const vb = b[sort.key] || "";
      const cmp = String(va).localeCompare(String(vb));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [items, sort]);

  const setSortKey = (k) => {
    if (!TABLE_COLUMNS[k]?.sortable) return;
    setSort(s => s.key === k ? { ...s, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" });
  };
  const arrow = (k) => sort.key === k ? (sort.dir === "asc" ? " ▲" : " ▼") : "";

  // Drag to reorder
  const onColDragStart = (id) => (e) => {
    setDragColId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch {}
  };
  const onColDragOver = (id) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragColId && dragColId !== id) setDropTargetId(id);
  };
  const onColDragLeave = (id) => () => {
    if (dropTargetId === id) setDropTargetId(null);
  };
  const onColDrop = (id) => (e) => {
    e.preventDefault();
    if (!dragColId || dragColId === id) { setDragColId(null); setDropTargetId(null); return; }
    setConfig(c => {
      const order = c.order.filter(x => x !== dragColId);
      const idx = order.indexOf(id);
      order.splice(idx, 0, dragColId);
      return { ...c, order };
    });
    setDragColId(null);
    setDropTargetId(null);
  };
  const onColDragEnd = () => { setDragColId(null); setDropTargetId(null); };

  // Resize column
  const startResize = (id) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = config.widths[id] || TABLE_COLUMNS[id].width;
    resizeRef.current = id;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev) => {
      const next = Math.max(48, startW + (ev.clientX - startX));
      setConfig(c => ({ ...c, widths: { ...c.widths, [id]: next } }));
    };
    const onUp = () => {
      resizeRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const resetConfig = () => {
    if (!confirm("テーブル列の順序と幅をリセットしますか?")) return;
    setConfig({
      order: TABLE_DEFAULT_ORDER,
      widths: Object.fromEntries(Object.entries(TABLE_COLUMNS).map(([k, v]) => [k, v.width])),
    });
  };

  // Cell renderers per column
  const renderCell = (id, it, oc, isFirst) => {
    const accent = (oc && isFirst) ? { boxShadow: `inset 4px 0 0 ${oc}` } : undefined;
    switch (id) {
      case "status":
        return (
          <td key={id} onClick={(e) => e.stopPropagation()} style={accent}>
            <StatusButton status={it.status} onClick={() => cycleStatus(it)}/>
          </td>
        );
      case "title":
        return (
          <td key={id} className="ws-table-title-cell" style={accent}>
            <div className="ws-table-title">{it.title}<RecurBadge it={it}/></div>
            {it.notes && <div className="ws-table-notes">{it.notes.length > 160 ? it.notes.slice(0, 160) + "…" : it.notes}</div>}
          </td>
        );
      case "date":
        return <td key={id} style={accent}><DateBadge date={it.date} compact/></td>;
      case "time":
        return <td key={id} style={{...accent, fontFamily:"var(--f-mono)", fontSize:11, color:"var(--gold)"}}>{it.time || "—"}</td>;
      case "owner":
        return (
          <td key={id} style={accent}>
            <span className="chip" style={oc ? { background: tint(oc, 0.18), borderColor: tint(oc, 0.45), color: oc } : undefined}>{it.owner || "—"}</span>
          </td>
        );
      case "area":
        return <td key={id} style={accent}><span className="chip">{it.area || "—"}</span></td>;
      case "notes":
        return <td key={id} className="ws-table-notes-cell" style={accent}><div className="ws-table-notes-full">{it.notes || "—"}</div></td>;
      case "actions":
        return (
          <td key={id} onClick={(e) => e.stopPropagation()} style={accent}>
            <div className="row-actions" style={{opacity:1, display:"flex", gap:4}}>
              <CopyItemButton item={it} small/>
              <button className="row-action-btn danger" title="削除" onClick={() => { if (confirm(`「${it.title || "(無題)"}」を削除しますか?`)) crud.remove(it.id); }}><Icon name="trash" size={12}/></button>
            </div>
          </td>
        );
      default:
        return <td key={id} style={accent}/>;
    }
  };

  return (
    <div className="ws-table-wrap">
      <div className="ws-table-toolbar">
        <div className="label-mono">⇆ 列ヘッダーをドラッグで並び替え · 右端をドラッグで幅調整</div>
        <div style={{flex:1}}/>
        <button className="btn ghost sm" onClick={resetConfig}>
          <Icon name="refresh" size={11}/>
          <span>列をリセット</span>
        </button>
      </div>
      <div style={{overflowX:"auto"}}>
        <table className="ws-table" style={{tableLayout:"fixed", minWidth: config.order.reduce((s, id) => s + (config.widths[id] || TABLE_COLUMNS[id].width), 0)}}>
          <colgroup>
            {config.order.map(id => (
              <col key={id} style={{width: (config.widths[id] || TABLE_COLUMNS[id].width) + "px"}}/>
            ))}
          </colgroup>
          <thead>
            <tr>
              {config.order.map(id => {
                const col = TABLE_COLUMNS[id];
                if (!col) return null;
                const isDragging = dragColId === id;
                const isDropTarget = dropTargetId === id;
                return (
                  <th
                    key={id}
                    draggable
                    onDragStart={onColDragStart(id)}
                    onDragOver={onColDragOver(id)}
                    onDragLeave={onColDragLeave(id)}
                    onDrop={onColDrop(id)}
                    onDragEnd={onColDragEnd}
                    onClick={() => col.sortable && setSortKey(id)}
                    style={{
                      cursor: col.sortable ? "pointer" : "grab",
                      position: "relative",
                      opacity: isDragging ? 0.4 : 1,
                      boxShadow: isDropTarget ? "inset 3px 0 0 var(--gold)" : "none",
                      textAlign: col.align,
                      userSelect: "none",
                    }}
                    title={col.sortable ? "クリックでソート / ドラッグで並び替え" : "ドラッグで並び替え"}
                  >
                    <span style={{display:"inline-flex", alignItems:"center", gap:4}}>
                      <span style={{opacity:0.4, fontSize:10, marginRight:2, cursor:"grab"}}>⋮⋮</span>
                      {col.label}{arrow(id)}
                    </span>
                    <span
                      onMouseDown={startResize(id)}
                      onClick={(e) => e.stopPropagation()}
                      className="ws-table-resizer"
                      title="ドラッグで幅を変更"
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(it => {
              const oc = getOwnerColor(it.owner, ownerColors);
              return (
                <tr key={it.id} className={it.status === "done" ? "done" : ""} onClick={() => openEdit(it)}>
                  {config.order.map((id, idx) => renderCell(id, it, oc, idx === 0))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {sortedItems.length === 0 && <div className="empty-state"><h4>No items</h4><div>No items match the current filters.</div></div>}
    </div>
  );
};

// ====================================================================
//  TODAY VIEW (focused on today + overdue + upcoming)
// ====================================================================
const TodayView = ({ items, openEdit, openNew, crud, cycleStatus }) => {
  const tStr = todayYMD();
  const today = parseYMD(tStr);
  const groups = {
    overdue: [],
    today: [],
    tomorrow: [],
    thisWeek: [],
    later: [],
    noDate: [],
  };
  const tomorrow = new Date(today.getTime() + 86400000);
  const weekEnd = new Date(today.getTime() + 7 * 86400000);
  items.forEach(it => {
    if (it.status === "done") return;  // skip done in today view
    if (!it.date) { groups.noDate.push(it); return; }
    const d = parseYMD(it.date);
    if (!d) { groups.noDate.push(it); return; }
    if (d < today) groups.overdue.push(it);
    else if (d.getTime() === today.getTime()) groups.today.push(it);
    else if (d.getTime() === tomorrow.getTime()) groups.tomorrow.push(it);
    else if (d < weekEnd) groups.thisWeek.push(it);
    else groups.later.push(it);
  });

  // sort by time within each group
  Object.values(groups).forEach(arr => arr.sort((a, b) => {
    const dc = (a.date || "9999").localeCompare(b.date || "9999");
    if (dc !== 0) return dc;
    return (a.time || "99:99").localeCompare(b.time || "99:99");
  }));

  const Section = ({ title, items: arr, accent }) => arr.length > 0 && (
    <div className="today-section">
      <div className="today-section-head" style={{color: accent || "var(--gold)"}}>
        {title}<span style={{marginLeft:8, color:"var(--ash)", fontSize:11, letterSpacing:"normal"}}>· {arr.length}</span>
      </div>
      <div>
        {arr.map(it => (
          <div key={it.id} className="today-row" onClick={() => openEdit(it)}>
            <StatusButton status={it.status} onClick={() => cycleStatus(it)}/>
            <div style={{flex:1, minWidth:0}}>
              <div className="today-row-title">{it.title}<RecurBadge it={it}/></div>
                <div className="today-row-meta">
                <DateBadge date={it.date} time={it.time} compact/>
                {it.owner && <span className="chip">{it.owner}</span>}
                {it.area && <span className="chip">{it.area}</span>}
                {it.notes && <span className="muted" style={{fontSize:11}}>{it.notes.length > 80 ? it.notes.slice(0, 80) + "…" : it.notes}</span>}
              </div>
            </div>
            <div className="row-actions" style={{opacity:0.7, display:"flex", gap:4}}>
              <CopyItemButton item={it} small/>
              <button className="row-action-btn" onClick={(e) => { e.stopPropagation(); openEdit(it); }}><Icon name="edit" size={12}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const total = Object.values(groups).reduce((s, a) => s + a.length, 0);

  return (
    <div className="today-shell">
      {total === 0 && (
        <div className="empty-state"><h4>You're caught up</h4><div>No active items. Add a new one to get going.</div></div>
      )}
      <Section title="OVERDUE" items={groups.overdue} accent="var(--crimson-hi)"/>
      <Section title="TODAY" items={groups.today} accent="var(--gold)"/>
      <Section title="TOMORROW" items={groups.tomorrow} accent="var(--crystal)"/>
      <Section title="THIS WEEK" items={groups.thisWeek} accent="var(--crystal)"/>
      <Section title="LATER" items={groups.later} accent="var(--ash)"/>
      <Section title="NO DATE (BACKLOG)" items={groups.noDate} accent="var(--smoke)"/>
    </div>
  );
};

// ====================================================================
//  CALENDAR MODE (month / week / agenda — events + tasks on same grid)
// ====================================================================
const CalendarMode = ({ items, allItems, openEdit, openNew, crud, ownerColors }) => {
  const todayDemo = parseYMD(todayYMD()) || new Date();
  const todayStr = fmtYMD(todayDemo);
  const settings = (typeof window !== "undefined" && window.__supersys_settings) || {};
  const weekStartsOnMon = settings.weekStart === "mon";
  const showHolidays = settings.showHolidays !== false;
  // Local override of getHoliday — respects showHolidays setting
  const getHoliday = showHolidays ? window.getHoliday : () => null;
  // dayOffset: shift such that the column index of weekday `wd` is `(wd - startDay + 7) % 7`
  const startDayIdx = weekStartsOnMon ? 1 : 0;
  const colIndexFor = (jsDay) => (jsDay - startDayIdx + 7) % 7;
  const [cursor, setCursor] = React.useState(new Date(todayDemo.getFullYear(), todayDemo.getMonth(), 1));
  const [selectedDate, setSelectedDate] = React.useState(todayStr);
  const [subMode, setSubMode] = React.useState("month"); // month | week | agenda

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const leadingBlanks = colIndexFor(firstDay);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysPrev = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = 0; i < leadingBlanks; i++) {
    const day = daysPrev - leadingBlanks + 1 + i;
    const date = new Date(year, month - 1, day);
    cells.push({ day, other: true, key: "p" + i, ymd: fmtYMD(date), date });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    cells.push({ day: d, other: false, key: "c" + d, ymd: fmtYMD(date), date });
  }
  let n = 1;
  while (cells.length % 7 !== 0) {
    const date = new Date(year, month + 1, n);
    cells.push({ day: n, other: true, key: "n" + n, ymd: fmtYMD(date), date });
    n++;
  }

  // Use filtered items for the body of the calendar
  const eventsByDate = React.useMemo(() => {
    const map = {};
    items.forEach(it => {
      if (!it.date) return;
      if (!map[it.date]) map[it.date] = [];
      map[it.date].push(it);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => (a.time || "99:99").localeCompare(b.time || "99:99")));
    return map;
  }, [items]);

  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const weekdaysFull = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const weekdaysFullJp = ["日","月","火","水","木","金","土"];
  const weekdays = Array.from({length:7}, (_, i) => weekdaysFull[(i + startDayIdx) % 7]);
  const weekdaysJp = Array.from({length:7}, (_, i) => weekdaysFullJp[(i + startDayIdx) % 7]);

  const weekStart = React.useMemo(() => {
    const d = parseYMD(selectedDate) || todayDemo;
    const day = d.getDay();
    const offset = colIndexFor(day);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() - offset);
    // eslint-disable-next-line
  }, [selectedDate, weekStartsOnMon]);
  const weekDays = Array.from({length:7}, (_, i) => {
    const d = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i);
    return { date: d, ymd: fmtYMD(d) };
  });

  const selectedDayEvents = eventsByDate[selectedDate] || [];
  const selectedHoliday = getHoliday(selectedDate);

  // Owner-color based event style. Falls back to subtle defaults for unassigned.
  const eventStyle = (it) => {
    if (it.status === "done") return undefined;
    const c = getOwnerColor(it.owner, ownerColors);
    if (!c) return undefined;
    return { background: tint(c, 0.16), borderLeftColor: c, color: c };
  };

  return (
    // Month mode: two-column grid — calendar (flexible) + selected-day panel
    // (fixed 360px). The side panel OCCUPIES its column (no overlay), so the
    // calendar's Saturday column can never end up underneath it.
    <div className={subMode === "month" ? "cal-layout" : undefined}>
      <div className="panel cinematic mb-3 cal-main">
        <div className="panel-head">
          <div style={{display:"flex", alignItems:"center", gap:14}}>
            <button className="icon-btn" onClick={() => setCursor(new Date(year, month - 1, 1))}><Icon name="chevronLeft" size={16}/></button>
            <div style={{minWidth:200}}>
              <span className="eyebrow">{year}</span>
              <h3 style={{fontFamily:"var(--f-display)", fontSize:26, letterSpacing:"0.10em", color:"var(--ivory)", marginTop:4, lineHeight:1}}>
                {monthNames[month]}
              </h3>
            </div>
            <button className="icon-btn" onClick={() => setCursor(new Date(year, month + 1, 1))}><Icon name="chevronRight" size={16}/></button>
          </div>
          <div className="panel-head-meta">
            <div className="tabs" style={{margin:0, border:"none"}}>
              {[["month","Month"],["week","Week"],["agenda","Agenda"]].map(([k,l]) => (
                <div key={k} className={"tab" + (subMode === k ? " active" : "")} onClick={() => setSubMode(k)} style={{padding:"5px 11px", fontSize:10}}>{l}</div>
              ))}
            </div>
            <button className="btn ghost sm" onClick={() => { setCursor(new Date(todayDemo.getFullYear(), todayDemo.getMonth(), 1)); setSelectedDate(todayStr); }}>Today</button>
          </div>
        </div>

        {subMode === "month" && (
          <div className="panel-body">
            <div className="cal-grid">
              {weekdays.map((w, i) => {
                const realDay = (i + startDayIdx) % 7; // 0=Sun
                return (
                  <div key={i} className="cal-head" style={{color: realDay === 0 ? "var(--crimson-hi)" : realDay === 6 ? "var(--crystal)" : "var(--gold)"}}>
                    {w}<span style={{marginLeft:4, color:"var(--smoke)", fontSize:9}}>{weekdaysJp[i]}</span>
                  </div>
                );
              })}
              {cells.map((c) => {
                const isToday = c.ymd === todayStr;
                const isSelected = c.ymd === selectedDate;
                const holiday = getHoliday(c.ymd);
                const wday = c.date.getDay();
                const isSun = wday === 0;
                const isSat = wday === 6;
                const dayEvents = eventsByDate[c.ymd] || [];
                const classes = [
                  "cal-day",
                  c.other ? "other" : "",
                  isToday ? "today" : "",
                  isSelected && !isToday ? "selected" : "",
                  holiday ? "holiday" : (isSun ? "sunday" : isSat ? "saturday" : ""),
                ].filter(Boolean).join(" ");
                return (
                  <div key={c.key} className={classes} onClick={() => setSelectedDate(c.ymd)} onDoubleClick={() => openNew({ date: c.ymd })}>
                    <div style={{display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                      <div className="cal-num">{c.day}</div>
                      {holiday && <div className="cal-holiday-name" title={holiday}>{holiday}</div>}
                    </div>
                    {dayEvents.slice(0, 3).map((e) => (
                      <div key={e.id} className={"cal-event" + (e.status === "done" ? " done" : "")} style={eventStyle(e)} onClick={(ev) => { ev.stopPropagation(); openEdit(e); }} title={`${e.time || ""} ${e.title}${e.owner ? " (" + e.owner + ")" : ""}`}>
                        {e.time && <span style={{fontFamily:"var(--f-mono)", fontSize:9, opacity:0.8, marginRight:4}}>{e.time}</span>}
                        {e.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && <div className="cal-event-more">+{dayEvents.length - 3} more</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {subMode === "week" && (
          <div className="panel-body">
            <div className="week-grid">
              {weekDays.map((w, i) => {
                const isToday = w.ymd === todayStr;
                const holiday = getHoliday(w.ymd);
                const isSun = w.date.getDay() === 0;
                const isSat = w.date.getDay() === 6;
                const events = eventsByDate[w.ymd] || [];
                return (
                  <div key={i} className={"week-col" + (isToday ? " today" : "") + (holiday ? " holiday" : isSun ? " sunday" : isSat ? " saturday" : "")}>
                    <div className="week-col-head">
                      <div className="week-col-day" style={{color: isSun || holiday ? "var(--crimson-hi)" : isSat ? "var(--crystal)" : "var(--gold)"}}>{weekdaysFullJp[w.date.getDay()]}</div>
                      <div className="week-col-num">{w.date.getDate()}</div>
                      {holiday && <div className="week-col-holiday">{holiday}</div>}
                    </div>
                    <div className="week-col-body">
                      {events.length === 0 && <div style={{padding:"20px 8px", textAlign:"center", color:"var(--smoke)", fontSize:10, fontFamily:"var(--f-mono)"}}>—</div>}
                      {events.map(e => (
                        <div key={e.id} className={"week-event" + (e.status === "done" ? " done" : "")} style={eventStyle(e)} onClick={() => openEdit(e)}>
                          {e.time && <div className="week-event-time">{e.time}{e.dur ? ` · ${e.dur}` : ""}</div>}
                          <div className="week-event-title">{e.title}</div>
                        </div>
                      ))}
                      <button className="week-add" onClick={() => openNew({ date: w.ymd })}><Icon name="plus" size={12}/></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {subMode === "agenda" && (
          <div className="panel-body">
            {(() => {
              const sorted = [...items]
                .filter(i => i.date)
                .sort((a, b) => (a.date + " " + (a.time || "")).localeCompare(b.date + " " + (b.time || "")));
              const groups = {};
              sorted.forEach(e => { (groups[e.date] = groups[e.date] || []).push(e); });
              return Object.entries(groups).map(([d, evts]) => {
                const holiday = getHoliday(d);
                const isSun = isSunday(d);
                const isSat = isSaturday(d);
                return (
                  <div key={d} className="agenda-group">
                    <div className="agenda-date" style={{color: isSun || holiday ? "var(--crimson-hi)" : isSat ? "var(--crystal)" : "var(--gold)"}}>
                      {fmtFullDate(d, "jp")}
                      {holiday && <span style={{marginLeft:10, padding:"1px 8px", background:"rgba(196,30,58,0.16)", border:"1px solid rgba(196,30,58,0.32)", borderRadius:3, fontSize:10}}>{holiday}</span>}
                      {d === todayStr && <span style={{marginLeft:10, padding:"1px 8px", background:"var(--crimson)", color:"var(--ivory)", borderRadius:3, fontSize:10, fontFamily:"var(--f-mono)", letterSpacing:"0.08em"}}>TODAY</span>}
                    </div>
                    {evts.map(e => (
                      <div key={e.id} className="schedule-row" onClick={() => openEdit(e)} style={{cursor:"pointer"}}>
                        <div className="schedule-time">{e.time || "—"}<span className="schedule-time-dur">{e.dur || ""}</span></div>
                        <div>
                          <div className="schedule-title" style={{textDecoration: e.status === "done" ? "line-through" : "none", opacity: e.status === "done" ? 0.5 : 1}}>{e.title}<RecurBadge it={e}/></div>
                          <div className="schedule-sub">{e.notes}</div>
                        </div>
                        <span className={"chip " + (e.area === "Exec" ? "crimson" : e.priority === "high" ? "crimson" : "")}>{e.area}</span>
                      </div>
                    ))}
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>

      {/* Selected day detail (month sub-mode) — right-hand column */}
      {subMode === "month" && (
        <div className="panel cal-side">
          <div className="panel-head" style={{flexWrap:"wrap", gap:8}}>
            <div style={{minWidth:0}}>
              <span className="eyebrow">{selectedDate === todayStr ? "TODAY" : "SELECTED"}</span>
              <h3 style={{fontSize:17}}>{fmtFullDate(selectedDate, "jp")}</h3>
              {selectedHoliday && <div style={{marginTop:6}}><span style={{padding:"3px 10px", background:"rgba(196,30,58,0.16)", border:"1px solid rgba(196,30,58,0.32)", borderRadius:4, fontSize:11, color:"var(--crimson-hi)", letterSpacing:"0.08em", fontFamily:"var(--f-title)"}}>🏛 {selectedHoliday}</span></div>}
            </div>
            <div className="panel-head-meta">
              <button className="btn gold sm" onClick={() => openNew({ date: selectedDate })}><Icon name="plus" size={12}/><span>New item</span></button>
            </div>
          </div>
          <div className="panel-body">
            {selectedDayEvents.length === 0 && (
              <div className="empty-state"><h4>Nothing on this day</h4><div>カレンダーの日付をダブルクリックするか、「New item」で予定を追加できます。</div></div>
            )}
            {selectedDayEvents.map(s => {
              const oc = getOwnerColor(s.owner, ownerColors);
              return (
                <div key={s.id} className="schedule-row cal-side-row" onClick={() => openEdit(s)} style={{cursor:"pointer"}}>
                  <div className="schedule-time">{s.time || "—"}<span className="schedule-time-dur">{s.dur || ""}</span></div>
                  <div style={{minWidth:0}}>
                    <div className="schedule-title" style={{textDecoration: s.status === "done" ? "line-through" : "none", opacity: s.status === "done" ? 0.5 : 1}}>{s.title}<RecurBadge it={s}/></div>
                    <div className="schedule-sub">{s.notes}</div>
                  </div>
                  <div style={{display:"flex", alignItems:"center", gap:8}}>
                    <div style={{display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4}}>
                      <span className={"chip " + (s.area === "Exec" ? "crimson" : s.priority === "high" ? "crimson" : "")}>{s.area}</span>
                      {s.owner && (
                        <span className="chip" style={oc ? { background: tint(oc, 0.18), borderColor: tint(oc, 0.45), color: oc } : undefined}>
                          {s.owner}
                        </span>
                      )}
                    </div>
                    <div onClick={(e) => e.stopPropagation()} style={{display:"flex", alignItems:"center"}}>
                      <CopyItemButton item={s} small/>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

Object.assign(window, { WorkspaceShell, ITEM_SCHEMA, ITEM_DEFAULT, seedNewItemDefaults, nextStatus, STATUS_META, STATUS_ORDER, sortItems });


;/* ==== projects.jsx ==== */
// Projects view — full CRUD
const PROJECT_SCHEMA = {
  eyebrow: "PROJECT",
  title: "Project",
  titleNew: "New Project",
  titleEdit: "Edit Project",
  fields: [
    { key: "name", label: "Project name", type: "text", required: true, span: 2, placeholder: "e.g. Recruitment Funnel Reform" },
    { key: "area", label: "Area", type: "select", options: ["HR · Hiring", "HR · Comp", "HR · Strategy", "General Affairs", "IT", "GA · IT"] },
    { key: "status", label: "Status", type: "chips", options: [
      { value: "Kickoff", label: "Kickoff" }, { value: "In Flight", label: "In Flight" }, { value: "At Risk", label: "At Risk" }, { value: "Done", label: "Done" }
    ], colors: { "In Flight": "gold", "At Risk": "crimson", Done: "crystal", Kickoff: "crystal" } },
    { key: "pct", label: "Progress (%)", type: "number", min: 0, max: 100 },
    { key: "milestones", label: "Total milestones", type: "number", min: 0, max: 10 },
    { key: "done", label: "Milestones done", type: "number", min: 0, max: 10 },
    { key: "owner", label: "Owner", type: "text" },
    { key: "next", label: "Next action", type: "textarea", span: 2, placeholder: "What's the next concrete step?" },
  ],
};

// Pure reorder — exposed for unit tests. Moves `fromId` so it lands at `toId`'s slot,
// pushing the target down (or up) accordingly. Returns the original list when the
// move is a no-op (same id, missing id, etc).
function reorderProjects(list, fromId, toId) {
  if (!Array.isArray(list)) return list;
  if (!fromId || !toId || fromId === toId) return list;
  const fromIdx = list.findIndex(x => x && x.id === fromId);
  const toIdx = list.findIndex(x => x && x.id === toId);
  if (fromIdx < 0 || toIdx < 0) return list;
  const next = list.slice();
  const [moved] = next.splice(fromIdx, 1);
  // splice indices shift after removal — use the original toIdx because we want
  // the moved item to land in the slot where `toId` currently sits, regardless
  // of direction.
  const insertAt = fromIdx < toIdx ? toIdx : toIdx;
  next.splice(insertAt, 0, moved);
  return next;
}

const ProjectsView = ({ state, setState }) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [dragId, setDragId] = React.useState(null);
  const [overId, setOverId] = React.useState(null);
  const crud = useCrud(state, setState, "projects");

  const openEdit = (p) => { setEditing(p); setModalOpen(true); };
  const openNew = () => { setEditing(null); setModalOpen(true); };
  const handleSave = (vals) => {
    if (editing) crud.update(editing.id, vals);
    else crud.add({ owner: "Masaaki", milestones: 0, done: 0, pct: 0, status: "Kickoff", ...vals });
    setModalOpen(false);
  };
  const handleDelete = () => {
    if (editing) crud.remove(editing.id);
    setModalOpen(false);
  };

  const moveBy = (id, delta) => {
    setState(prev => {
      const list = prev.projects || [];
      const idx = list.findIndex(x => x && x.id === id);
      if (idx < 0) return prev;
      const targetIdx = idx + delta;
      if (targetIdx < 0 || targetIdx >= list.length) return prev;
      const targetId = list[targetIdx].id;
      return { ...prev, projects: reorderProjects(list, id, targetId) };
    });
  };

  const onDragStart = (id) => (e) => {
    setDragId(id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      try { e.dataTransfer.setData("text/plain", id); } catch (_) {}
    }
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    if (id !== overId) setOverId(id);
  };
  const onDrop = (id) => (e) => {
    e.preventDefault();
    const from = dragId;
    setDragId(null);
    setOverId(null);
    if (!from || from === id) return;
    setState(prev => ({ ...prev, projects: reorderProjects(prev.projects || [], from, id) }));
  };
  const onDragEnd = () => { setDragId(null); setOverId(null); };

  return (
    <div className="view-fade">
      <div className="panel cinematic mb-3" style={{padding:"10px 18px", display:"flex", alignItems:"center"}}>
        <div style={{flex:1}}>
          <span className="eyebrow">INITIATIVES · {state.projects.length} active</span>
          <h3 style={{fontFamily:"var(--f-display)", fontSize:16, letterSpacing:"0.10em", color:"var(--ivory)", marginTop:2}}>All Projects</h3>
        </div>
        <span style={{fontSize:10, color:"var(--smoke)", marginRight:14, fontFamily:"var(--f-mono)", letterSpacing:"0.06em"}}>
          ⋮⋮ ドラッグ / ▲▼ で並び替え
        </span>
        <button className="btn gold sm" onClick={openNew}>
          <Icon name="plus" size={12}/>
          <span>New Project</span>
        </button>
      </div>

      <div className="projects-grid">
        {state.projects.map((p, idx) => (
          <div
            key={p.id}
            className={
              "project-card" +
              (dragId === p.id ? " dragging" : "") +
              (overId === p.id && dragId && dragId !== p.id ? " drop-target" : "")
            }
            onClick={() => openEdit(p)}
            style={{cursor:"pointer"}}
            draggable
            onDragStart={onDragStart(p.id)}
            onDragOver={onDragOver(p.id)}
            onDrop={onDrop(p.id)}
            onDragEnd={onDragEnd}
          >
            <div className="project-drag-grip" title="ドラッグして並び替え" aria-hidden="true">⋮⋮</div>
            <div className="row-actions">
              <button
                className="row-action-btn"
                onClick={(e) => { e.stopPropagation(); moveBy(p.id, -1); }}
                disabled={idx === 0}
                title="上へ"
              >▲</button>
              <button
                className="row-action-btn"
                onClick={(e) => { e.stopPropagation(); moveBy(p.id, 1); }}
                disabled={idx === state.projects.length - 1}
                title="下へ"
              >▼</button>
              <button className="row-action-btn" onClick={(e) => { e.stopPropagation(); openEdit(p); }} title="Edit">
                <Icon name="edit" size={13}/>
              </button>
              <button className="row-action-btn danger" onClick={(e) => { e.stopPropagation(); if (confirm("Delete this project?")) crud.remove(p.id); }} title="Delete">
                <Icon name="trash" size={13}/>
              </button>
            </div>
            <div className="project-card-top">
              <div className="project-area">{p.area}</div>
              {p.next ? (
                <div className="project-next" title={p.next}>
                  <span className="label-mono">NEXT </span>
                  <span style={{color:"var(--ivory)"}}>{p.next}</span>
                </div>
              ) : null}
              <span className={"chip " + (p.status === "In Flight" ? "gold" : p.status === "At Risk" ? "crimson" : "crystal")}>{p.status}</span>
            </div>
            <div className="project-name" title={p.name}>{p.name}</div>
            <div className="progress-bar"><div className="progress-fill" style={{width: Math.max(0, Math.min(100, Number(p.pct) || 0)) + "%"}}/></div>
            <div className="progress-meta">
              <span>{p.done} / {p.milestones} MS · {p.owner}</span>
              <span className="progress-pct">{p.pct}%</span>
            </div>
          </div>
        ))}
      </div>

      {state.projects.length === 0 && (
        <div className="empty-state">
          <h4>No projects yet</h4>
          <div>Click "New Project" above to add your first initiative.</div>
        </div>
      )}

      <EntityModal
        open={modalOpen}
        schema={PROJECT_SCHEMA}
        initial={editing}
        onSave={handleSave}
        onDelete={editing ? handleDelete : null}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

Object.assign(window, { ProjectsView, reorderProjects });


;/* ==== memos.jsx ==== */
// Memos / recordings library
const IDEA_SCHEMA = {
  eyebrow: "IDEA",
  title: "Idea",
  titleNew: "New Idea",
  titleEdit: "Edit Idea",
  fields: [
    { key: "title", label: "Title", type: "text", required: true, span: 2 },
    { key: "tag", label: "Tag", type: "select", options: ["HR", "Hiring", "GA", "IT", "Cross-cut", "Strategy"] },
    { key: "date", label: "Date", type: "text", placeholder: "MM/DD" },
    { key: "body", label: "Body", type: "textarea", span: 2, required: true, placeholder: "Sketch the idea — what, why, how, KPIs…" },
  ],
};

const MemosView = ({ state, setState, openRecording, openRecord }) => {
  const [tab, setTab] = React.useState("rec");
  const [q, setQ] = React.useState("");
  const [ideaModal, setIdeaModal] = React.useState(false);
  const [editingIdea, setEditingIdea] = React.useState(null);
  const ideaCrud = useCrud(state, setState, "ideas");
  const recCrud = useCrud(state, setState, "recordings");

  const recItems = state.recordings.filter(r => !q || ((r.title || "") + (r.transcript||"") + (r.tags||[]).join(" ")).toLowerCase().includes(q.toLowerCase()));
  const ideas = state.ideas.filter(i => !q || ((i.title || "") + (i.body || "")).toLowerCase().includes(q.toLowerCase()));

  const openEditIdea = (i) => { setEditingIdea(i); setIdeaModal(true); };
  const openNewIdea = () => {
    setEditingIdea(null);
    setIdeaModal(true);
  };
  const saveIdea = (vals) => {
    if (editingIdea) ideaCrud.update(editingIdea.id, vals);
    else ideaCrud.add({ date: new Date().toLocaleDateString("en-US",{month:"numeric",day:"numeric"}), tag: "HR", ...vals });
    setIdeaModal(false);
  };
  const deleteIdea = () => {
    if (editingIdea) ideaCrud.remove(editingIdea.id);
    setIdeaModal(false);
  };

  return (
    <div className="view-fade">
      <div className="panel cinematic mb-3" style={{padding: "16px 22px", display:"flex", gap:14, alignItems:"center"}}>
        <Icon name="search" size={16} color="var(--gold)"/>
        <input
          placeholder="Search memos & minutes…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{flex:1, background:"transparent", border:"none", outline:"none", color:"var(--ivory)", fontSize:14}}
        />
        {tab === "rec" ? (
          <button className="btn primary sm" onClick={openRecord}>
            <Icon name="mic" size={12}/>
            <span>New Record</span>
          </button>
        ) : (
          <button className="btn gold sm" onClick={openNewIdea}>
            <Icon name="plus" size={12}/>
            <span>New Idea</span>
          </button>
        )}
      </div>

      <div className="tabs">
        <div className={"tab" + (tab === "rec" ? " active" : "")} onClick={() => setTab("rec")}>
          Recordings / Minutes<span className="tab-count">{state.recordings.length}</span>
        </div>
        <div className={"tab" + (tab === "ideas" ? " active" : "")} onClick={() => setTab("ideas")}>
          Ideas<span className="tab-count">{state.ideas.length}</span>
        </div>
      </div>

      {tab === "rec" && (
        <>
          <div className="memo-grid">
            {recItems.map(r => (
              <div key={r.id} className="memo-card" onClick={() => openRecording(r)}>
                <div className="row-actions">
                  <button className="row-action-btn danger" onClick={(e) => { e.stopPropagation(); if (confirm("Delete this recording?")) recCrud.remove(r.id); }}><Icon name="trash" size={12}/></button>
                </div>
                <div className="memo-card-head">
                  <div className="memo-type-icon rec"><Icon name="waveform" size={14}/></div>
                  <span className="chip">{r.duration}</span>
                </div>
                <div className="memo-title">{r.title}</div>
                <div className="memo-body">
                  {r.summary?.points?.[0] || r.transcript?.slice(0, 140) || "—"}
                </div>
                <div className="memo-foot">
                  <span className="memo-date">{r.date}</span>
                  <div style={{display:"flex", gap:4, marginLeft:"auto"}}>
                    {r.tags.slice(0, 2).map(t => <span key={t} className="chip" style={{fontSize:9}}>{t}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {recItems.length === 0 && (
            <div className="empty-state"><h4>No recordings</h4><div>{q ? "No matches." : "Press the RECORD button in the top bar to start."}</div></div>
          )}
        </>
      )}

      {tab === "ideas" && (
        <>
          <div className="memo-grid">
            {ideas.map(i => (
              <div key={i.id} className="memo-card" onClick={() => openEditIdea(i)}>
                <div className="row-actions">
                  <button className="row-action-btn" onClick={(e) => { e.stopPropagation(); openEditIdea(i); }}><Icon name="edit" size={12}/></button>
                  <button className="row-action-btn danger" onClick={(e) => { e.stopPropagation(); if (confirm("Delete this idea?")) ideaCrud.remove(i.id); }}><Icon name="trash" size={12}/></button>
                </div>
                <div className="memo-card-head">
                  <div className="memo-type-icon idea"><Icon name="sparkle" size={14}/></div>
                  <span className="chip gold">{i.tag}</span>
                </div>
                <div className="memo-title">{i.title}</div>
                <div className="memo-body">{i.body}</div>
                <div className="memo-foot">
                  <span className="memo-date">{i.date}</span>
                </div>
              </div>
            ))}
          </div>
          {ideas.length === 0 && (
            <div className="empty-state"><h4>No ideas yet</h4><div>Click 'New Idea' to capture a flash of genius.</div></div>
          )}
        </>
      )}

      <EntityModal
        open={ideaModal}
        schema={IDEA_SCHEMA}
        initial={editingIdea}
        onSave={saveIdea}
        onDelete={editingIdea ? deleteIdea : null}
        onClose={() => setIdeaModal(false)}
      />
    </div>
  );
};

// Detail viewer for a recording. Supports per-section copy (原文 / AI要約) and
// re-running the AI summary in a different style (persisted via onUpdate).
const RecordingDetail = ({ rec, onClose, onUpdate }) => {
  const defStyle = () => (rec && rec.summaryStyle) || (window.__supersys_settings && (window.__supersys_settings.recDefaultStyle || window.__supersys_settings.aiStyle)) || "exec";
  const [summary, setSummary] = React.useState(rec ? rec.summary : null);
  const [style, setStyle] = React.useState(defStyle());
  const [busy, setBusy] = React.useState(false);
  const [reErr, setReErr] = React.useState(null);
  const [copied, setCopied] = React.useState("");
  React.useEffect(() => {
    setSummary(rec ? rec.summary : null);
    setStyle(defStyle());
    setReErr(null); setBusy(false);
    // eslint-disable-next-line
  }, [rec]);

  const STYLES = window.SUMMARY_STYLES || [];
  const doCopy = async (text, label) => {
    if (!text) return;
    const ok = window.copyTextToClipboard ? await window.copyTextToClipboard(text) : false;
    if (ok) { setCopied(label); setTimeout(() => setCopied(c => c === label ? "" : c), 1500); }
    else alert("コピーに失敗しました");
  };
  const reSummarise = async () => {
    if (!rec.transcript || rec.transcript.length < 8) { setReErr("原文が短すぎて要約できません。"); return; }
    if (!window.runSummary) { setReErr("要約機能がロードされていません。"); return; }
    setBusy(true); setReErr(null);
    try {
      const sm = await window.runSummary(rec.transcript, style, window.__supersys_settings || {});
      setSummary(sm);
      const patch = { summary: sm, summaryStyle: style };
      // ルール文章化は📕件名がタイトルそのものなので、録音タイトルも更新する。
      if (sm.title && window.isRulesStyle && window.isRulesStyle(style)) patch.title = sm.title;
      if (onUpdate) onUpdate(rec.id, patch);
    } catch (e) { console.log(e); setReErr("再要約に失敗しました。もう一度お試しください。"); }
    finally { setBusy(false); }
  };

  if (!rec) return null;

  const buildMarkdown = () => {
    const lines = [];
    lines.push(`# ${rec.title}`);
    lines.push("");
    lines.push(`- 日付: ${rec.date || "—"}`);
    if (rec.duration) lines.push(`- 長さ: ${rec.duration}`);
    if (rec.participants?.length) lines.push(`- 参加者: ${rec.participants.join(", ")}`);
    if (rec.tags?.length) lines.push(`- タグ: ${rec.tags.join(", ")}`);
    lines.push("");
    if (summary && summary.doc) {
      lines.push("## ルール文章（LINEWORKS掲示板用）", "", summary.doc, "");
    } else if (summary && summary.cleaned) {
      lines.push("## 整形済みテキスト", "", summary.cleaned, "");
    } else if (summary) {
      lines.push("## AI 要約");
      if (summary.points?.length) {
        lines.push("", "### 要点");
        summary.points.forEach(p => lines.push(`- ${p}`));
      }
      if (summary.todos?.length) {
        lines.push("", "### Next Action / ToDo");
        summary.todos.forEach(t => lines.push(`- ${t.who ? `**${t.who}** — ` : ""}${t.what}${t.by ? ` (期日: ${t.by})` : ""}`));
      }
      if (summary.decisions?.length) {
        lines.push("", "### 決定事項");
        summary.decisions.forEach(d => lines.push(`- ${d}`));
      }
      if (summary.questions?.length) {
        lines.push("", "### 未解決の論点");
        summary.questions.forEach(q => lines.push(`- ${q}`));
      }
      lines.push("");
    }
    if (rec.transcript) {
      lines.push("## Transcript", "", rec.transcript);
    }
    return lines.join("\n");
  };

  const handleCopy = async () => {
    const md = buildMarkdown();
    try {
      await navigator.clipboard.writeText(md);
      alert("クリップボードにコピーしました");
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = md;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); alert("クリップボードにコピーしました"); }
      catch (_) { alert("コピーに失敗しました"); }
      document.body.removeChild(ta);
    }
  };

  const handleExport = () => {
    const md = buildMarkdown();
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(rec.title || "recording").replace(/[\\/:*?"<>|]/g, "_")}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rec-overlay" onClick={(e) => { if (e.target.classList.contains("rec-overlay")) onClose(); }}>
      <div className="rec-stage">
        <div className="rec-stage-head">
          <div style={{flex:1}}>
            <span className="eyebrow">ARCHIVE · {rec.date}</span>
            <h2>{rec.title}</h2>
          </div>
          <span className="chip gold">{rec.duration}</span>
          <button className="icon-btn" onClick={onClose} title="閉じる" aria-label="閉じる"><Icon name="x" size={18}/></button>
        </div>
        <div className="rec-stage-body">
          <div style={{display:"flex", gap:6, marginBottom:18, flexWrap:"wrap"}}>
            {rec.participants?.map(p => <span key={p} className="chip">👤 {p}</span>)}
            {rec.tags?.map(t => <span key={t} className="chip crystal">{t}</span>)}
          </div>

          {rec.audioUrl && (
            <div style={{marginBottom:18, padding:14, background:"rgba(91,123,196,0.06)", border:"1px solid var(--hairline)", borderRadius:6}}>
              <audio src={rec.audioUrl} controls style={{width:"100%"}}/>
            </div>
          )}

          {(summary || rec.transcript) && (
            <div className="summary-stage" style={{marginTop:0}}>
              <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:8}}>
                <h4 style={{margin:0}}>{summary?.doc ? "★ ルール文章（掲示板用）" : summary?.cleaned ? "★ 整形済みテキスト" : "★ AI 要約"}</h4>
                {summary && (
                  <button
                    className="btn ghost sm"
                    style={{fontSize:10, padding:"3px 8px"}}
                    type="button"
                    onClick={() => doCopy(window.summaryToText ? window.summaryToText(summary) : "", "summary")}
                    title="結果だけをコピー"
                  >
                    <Icon name={copied === "summary" ? "check" : "copy"} size={11}/>
                    <span>{copied === "summary" ? "コピーしました" : (summary?.doc ? "本文をコピー" : summary?.cleaned ? "整形文をコピー" : "要約をコピー")}</span>
                  </button>
                )}
                {summary?.todos?.length > 0 && (
                  <button className="btn sm gold" style={{fontSize:10, padding:"3px 8px"}} onClick={() => {
                    if (!window.__superman_addTasks) { alert("タスク追加機能がロードされていません"); return; }
                    const today = new Date().toISOString().slice(0,10);
                    const newItems = summary.todos.map(t => ({
                      id: "tsk_" + Date.now() + "_" + Math.random().toString(36).slice(2,6),
                      title: t.what + (t.who ? ` (${t.who})` : ""),
                      notes: `議事録「${rec.title}」から自動抽出。期日: ${t.by || "未定"}`,
                      status: "todo", area: "HR", owner: t.who || "竹内",
                      date: t.by && t.by.match(/^\d{4}-\d{2}-\d{2}/) ? t.by.slice(0,10) : today,
                      time: null, dur: null, tag: null,
                    }));
                    window.__superman_addTasks(newItems);
                    alert(`${newItems.length}件のタスクを追加しました`);
                  }}>
                    <Icon name="plus" size={11}/>
                    <span>ToDoをタスクに追加</span>
                  </button>
                )}
                <div style={{flex:1}}/>
                <select
                  value={style}
                  onChange={e => setStyle(e.target.value)}
                  disabled={busy}
                  title="要約のスタイル"
                  style={{padding:"5px 8px", background:"rgba(0,0,0,0.4)", border:"1px solid var(--border)", borderRadius:4, color:"var(--ivory)", fontFamily:"var(--f-body)", fontSize:11, outline:"none"}}
                >
                  {STYLES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <button
                  className="btn gold sm"
                  style={{fontSize:10, padding:"4px 10px"}}
                  type="button"
                  onClick={reSummarise}
                  disabled={busy || !rec.transcript}
                  title={summary ? "選んだスタイルで要約をやり直す" : "このスタイルでAI要約を生成する"}
                >
                  <Icon name="sparkle" size={11}/>
                  <span>{busy ? "生成中…" : (summary ? "別スタイルで再要約" : "AIで要約")}</span>
                </button>
              </div>

              {reErr && (
                <div style={{margin:"4px 0 10px", padding:"8px 12px", background:"rgba(196,30,58,0.10)", border:"1px solid rgba(196,30,58,0.32)", borderRadius:6, fontSize:12, color:"var(--crimson-hi)"}}>
                  {reErr}
                </div>
              )}
              {busy && (
                <div style={{display:"flex", alignItems:"center", gap:12, padding:"6px 0 12px"}}>
                  <div style={{width:22, height:22, border:"2px solid var(--gold)", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite"}}/>
                  <span style={{color:"var(--ash)", fontSize:12}}>「{(STYLES.find(s => s.id === style) || {}).label || style}」スタイルで要約を生成しています…</span>
                </div>
              )}

              {!summary && !busy && (
                <div style={{fontSize:12, color:"var(--ash)", padding:"4px 0 6px"}}>
                  この録音にはまだAI要約がありません。スタイルを選んで「AIで要約」を押してください。
                </div>
              )}
              {(summary?.cleaned || summary?.doc) && (
                <div className="summary-section">
                  <div style={{whiteSpace:"pre-wrap", lineHeight:1.9, color:"var(--ivory)", fontSize:13}}>{summary.cleaned || summary.doc}</div>
                </div>
              )}
              {summary?.doc && (
                <div style={{marginTop:6, padding:"8px 12px", background:"rgba(245,197,24,0.06)", border:"1px solid rgba(245,197,24,0.22)", borderRadius:6, fontSize:11, color:"var(--gold)", lineHeight:1.6}}>
                  ⓘ 掲示板への投稿時：件名は📕始まり／「コメントを許可」を外す／「編集を許可」はチェックしない
                </div>
              )}
              {summary?.points?.length > 0 && (
                <div className="summary-section">
                  <h5>要点</h5>
                  <ul>{summary.points.map((p, i) => <li key={i}>{p}</li>)}</ul>
                </div>
              )}
              {summary?.todos?.length > 0 && (
                <div className="summary-section">
                  <h5>Next Action / ToDo</h5>
                  <ul>{summary.todos.map((t, i) => (
                    <li key={i}>
                      <span style={{color:"var(--gold)"}}>{t.who}</span> — {t.what}
                      {t.by && <span style={{color:"var(--ash)", marginLeft:8, fontSize:11}}>(期日: {t.by})</span>}
                    </li>
                  ))}</ul>
                </div>
              )}
              {summary?.decisions?.length > 0 && (
                <div className="summary-section">
                  <h5>決定事項</h5>
                  <ul>{summary.decisions.map((d, i) => <li key={i}>{d}</li>)}</ul>
                </div>
              )}
              {summary?.questions?.length > 0 && (
                <div className="summary-section">
                  <h5>未解決の論点</h5>
                  <ul>{summary.questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
                </div>
              )}
            </div>
          )}

          {rec.transcript && (
            <div style={{marginTop:18}}>
              <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:4}}>
                <span className="label-mono">Transcript（原文）</span>
                <button
                  className="btn ghost sm"
                  style={{marginLeft:"auto", fontSize:10, padding:"3px 8px"}}
                  type="button"
                  onClick={() => doCopy(rec.transcript, "transcript")}
                  title="原文だけをコピー"
                >
                  <Icon name={copied === "transcript" ? "check" : "copy"} size={11}/>
                  <span>{copied === "transcript" ? "コピーしました" : "原文をコピー"}</span>
                </button>
              </div>
              <div className="transcript-stage" style={{maxHeight: 280}}>
                {rec.transcript}
              </div>
            </div>
          )}
        </div>
        <div className="rec-stage-foot">
          <button className="btn ghost" onClick={handleCopy}><Icon name="copy" size={12}/><span>コピー</span></button>
          <button className="btn ghost" onClick={handleExport}><Icon name="download" size={12}/><span>Markdownエクスポート</span></button>
          <button className="btn primary" onClick={onClose}>閉じる</button>
        </div>
      </div>
    </div>
  );
};

Object.assign(window, { MemosView, RecordingDetail });


;/* ==== quickmemo.jsx ==== */
// Quick Memo — sticky-note scratchpad with AI task extraction (2026-06-12).
//
// Spec (approved by Takeuchi-san):
//   • Sticky-card memos, auto-saved on every keystroke (rides the app's existing
//     400ms-debounced KV save — there is deliberately NO save button).
//   • ⚡タスク化: the memo text goes to window.claude.complete (own Worker → /api/ai,
//     never a third-party service) and comes back as task candidates. AI may infer
//     date/owner ONLY when the memo states them — no fabrication (null otherwise).
//   • A preview modal lists the candidates (checkbox + inline-editable fields);
//     "N件を登録" bulk-inserts the picked rows into items via addMany.
//   • Registered memos keep a "✅ タスク化済み" badge + dimmed card, and can be
//     re-extracted after appending more text.

const QM_AREAS = ["HR", "Hiring", "GA", "IT", "Exec", "Personal"];
const QM_OWNERS = ["竹内", "大志万", "山本", "松川", "楠田", "妹尾", "その他"];

// ── pure: prompt builder (today injected for testability) ───────────────────
const __qm_buildPrompt = (text, today) => `あなたは人事総務部長のアシスタントです。以下のメモから「実行すべきタスク」を抽出し、**JSON配列のみ**で返してください。コードフェンス・前置き・後置きの説明は禁止。先頭は [ 、末尾は ] 。タスクが1件も無ければ [] を返す。

今日の日付: ${today}（「明日」「金曜まで」等の相対表現はこれを基準に YYYY-MM-DD へ変換）

各要素の形式:
{
  "title": "タスク名（簡潔な行動文・40字以内）",
  "date": "期限日 YYYY-MM-DD（メモに明示、または確実に変換できる場合のみ。無ければ null）",
  "dateEnd": "終了日 YYYY-MM-DD（「6/15〜17」「15日から3日間」のような連続した複数日の予定のみ。date に開始日、dateEnd に最終日。単日や不明は null）",
  "time": "時刻 HH:MM（明示されている場合のみ。無ければ null）",
  "owner": "担当者（メモに明示されている場合のみ。候補: ${QM_OWNERS.join(" / ")}。無ければ null）",
  "area": "領域。次のいずれか: ${QM_AREAS.join(" / ")}（不明は HR）",
  "notes": "補足（メモ内の該当箇所の要点。無ければ空文字）"
}

ルール:
- 根拠の無い日付・担当者を創作しない（必ず null にする）
- 完了済みの話・単なる感想・記録だけのメモはタスクにしない
- 同じ作業を重複して出力しない

メモ:
${text}`;

// ── pure: response normalizer (fence-strip → JSON → validated rows) ─────────
const __qm_parseExtraction = (raw) => {
  let s = String(raw || "").trim().replace(/^```(?:json)?/i, "").replace(/```$/, "").trim();
  let parsed;
  try { parsed = JSON.parse(s); }
  catch {
    const a = s.indexOf("["), o = s.indexOf("{");
    const useArr = a >= 0 && (o < 0 || a < o);
    const st = useArr ? a : o;
    const en = s.lastIndexOf(useArr ? "]" : "}");
    if (st < 0 || en <= st) return [];
    try { parsed = JSON.parse(s.slice(st, en + 1)); } catch { return []; }
  }
  if (parsed && !Array.isArray(parsed)) {
    parsed = Array.isArray(parsed.tasks) ? parsed.tasks : [parsed];
  }
  return (parsed || [])
    .filter(t => t && typeof t.title === "string" && t.title.trim())
    .map(t => {
      const date = /^\d{4}-\d{2}-\d{2}$/.test(t.date || "") ? t.date : "";
      const dateEnd = /^\d{4}-\d{2}-\d{2}$/.test(t.dateEnd || "") && date && t.dateEnd > date ? t.dateEnd : "";
      return {
      pick: true,
      title: t.title.trim().slice(0, 80),
      date,
      dateEnd,
      time: /^([01]\d|2[0-3]):[0-5]\d$/.test(t.time || "") ? t.time : "",
      // Default to 竹内 (= the new-task form's ITEM_DEFAULT.owner) when the memo
      // names nobody — board/calendar colors are owner-driven, so an empty owner
      // renders a colorless task. 「担当者なし」 stays selectable in the preview.
      owner: typeof t.owner === "string" && t.owner.trim() ? t.owner.trim() : "竹内",
      area: QM_AREAS.includes(t.area) ? t.area : "HR",
      notes: typeof t.notes === "string" ? t.notes.trim() : "",
      };
    });
};

// ── pure: picked preview rows → task objects (ids assigned by the caller) ───
// A row with dateEnd > date fans out into one task per calendar day, linked as
// a daily series (seriesId + recur) so the existing scoped edit/delete applies.
const __qm_rowsToTasks = (picked) => {
  const out = [];
  (picked || []).forEach(r => {
    if (!r || !r.title || !r.title.trim()) return;
    const base = {
      title: r.title.trim(),
      date: r.date || null,
      time: r.time || null,
      dur: null, tag: null,
      status: "todo",
      owner: r.owner || "",
      area: r.area || "HR",
      notes: r.notes || "",
    };
    if (r.date && r.dateEnd && r.dateEnd > r.date) {
      const rule = { freq: "daily", holidayShift: false, end: { type: "date", date: r.dateEnd } };
      const seriesId = newSeriesId();
      buildRecurrenceDates(r.date, rule).forEach(d => out.push({ ...base, date: d, recur: rule, seriesId }));
    } else {
      out.push(base);
    }
  });
  return out;
};

// ── preview modal: pick + inline-edit before registering ────────────────────
const QmPreviewModal = ({ open, rows, setRows, onRegister, onClose, busy }) => {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const setRow = (i, patch) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const picked = rows.filter(r => r.pick);
  // 連日行は1行で複数タスクになる — ボタンには実際の登録件数を出す
  const totalTasks = picked.reduce((n, r) =>
    n + ((r.date && r.dateEnd && r.dateEnd > r.date)
      ? buildRecurrenceDates(r.date, { freq: "daily", holidayShift: false, end: { type: "date", date: r.dateEnd } }).length
      : 1), 0);
  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={(e) => { if (e.target.classList.contains("modal-overlay")) onClose(); }}>
      <div className="modal-stage" style={{ maxWidth: 780 }}>
        <div className="modal-head">
          <div style={{ flex: 1 }}>
            <span className="eyebrow">⚡ AI TASK PREVIEW</span>
            <h2 className="modal-title">登録するタスクの確認</h2>
          </div>
          <button className="icon-btn" onClick={onClose} title="閉じる (Esc)" aria-label="閉じる"><Icon name="x" size={18}/></button>
        </div>
        <div className="modal-body">
          <div className="form-hint" style={{ marginBottom: 12 }}>
            内容はここで修正できます。チェックを外した行は登録されません。日付はメモに書かれていた場合のみ、担当者はメモに無ければ「竹内」をAIが設定しています。「〜」に終了日を入れた行は、開始日から終了日までの毎日分が一括登録されます。
          </div>
          {rows.length === 0 && (
            <div style={{ padding: "24px 0", textAlign: "center", color: "var(--ash)", fontSize: 13 }}>
              タスクとして登録できる項目が見つかりませんでした
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rows.map((r, i) => (
              <div key={i} className="qm-preview-row" style={{ opacity: r.pick ? 1 : 0.45 }}>
                <input type="checkbox" checked={r.pick} onChange={e => setRow(i, { pick: e.target.checked })}
                  aria-label={`タスク${i + 1}を登録対象にする`} style={{ marginTop: 9 }}/>
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input className="form-input" type="text" value={r.title} placeholder="タスク名"
                      onChange={e => setRow(i, { title: e.target.value })} style={{ flex: 1 }}/>
                    <input className="form-input" type="date" value={r.date}
                      onChange={e => setRow(i, { date: e.target.value })} style={{ width: 132, flexShrink: 0 }}/>
                    <span style={{ alignSelf: "center", color: "var(--ash)", fontSize: 11, flexShrink: 0 }}>〜</span>
                    <input className="form-input" type="date" value={r.dateEnd || ""} title="終了日（連日登録・任意）"
                      min={r.date || undefined}
                      onChange={e => setRow(i, { dateEnd: e.target.value })} style={{ width: 132, flexShrink: 0 }}/>
                    <input className="form-input" type="time" value={r.time}
                      onChange={e => setRow(i, { time: e.target.value })} style={{ width: 92, flexShrink: 0 }}/>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <select className="form-input" value={r.owner} onChange={e => setRow(i, { owner: e.target.value })} style={{ width: 130 }}>
                      <option value="">担当者なし</option>
                      {QM_OWNERS.map(o => <option key={o} value={o}>{o}</option>)}
                      {r.owner && !QM_OWNERS.includes(r.owner) && <option value={r.owner}>{r.owner}</option>}
                    </select>
                    <select className="form-input" value={r.area} onChange={e => setRow(i, { area: e.target.value })} style={{ width: 110 }}>
                      {QM_AREAS.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <input className="form-input" type="text" value={r.notes} placeholder="メモ（任意）"
                      onChange={e => setRow(i, { notes: e.target.value })} style={{ flex: 1 }}/>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-foot">
          <div style={{ fontSize: 12, color: "var(--ash)" }}>{picked.length} / {rows.length} 件を選択中</div>
          <div style={{ flex: 1 }}/>
          <button className="btn ghost" onClick={onClose}>キャンセル</button>
          <button className="btn primary" disabled={picked.length === 0 || busy} onClick={onRegister}>
            <Icon name="check" size={12}/>
            <span>{totalTasks}件を登録</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── sticky card: free placement (drag), remembered size (resize), pin lock ──
// Per-memo persisted fields: x/y (position), w/h (manual size), z (stack order),
// pinned (true = drag disabled, position locked). Legacy memos without x/y get a
// deterministic 3-column fallback slot and are only written once actually moved.
const QM_CARD_W = 330, QM_CARD_H = 230;
const __qm_defaultPos = (idx) => ({ x: 8 + (idx % 3) * (QM_CARD_W + 20), y: 8 + Math.floor(idx / 3) * (QM_CARD_H + 20) });

const QmStickyCard = ({ m, idx, memoCrud, focus, extracting, anyExtracting, extractErr, onExtract, onExpand, bringToFront, fmtStamp, archived, isPrivate }) => {
  const ref = React.useRef(null);
  const [dragPos, setDragPos] = React.useState(null);
  const def = __qm_defaultPos(idx);
  const pos = dragPos || { x: m.x ?? def.x, y: m.y ?? def.y };

  const onGripDown = (e) => {
    if (archived || m.pinned || e.button !== 0) return;
    e.preventDefault();
    bringToFront(m);
    const start = { px: e.clientX, py: e.clientY, x: pos.x, y: pos.y };
    // clientX/Y can be fractional (sub-pixel pointers) — store whole pixels
    const at = (ev) => ({
      x: Math.max(0, Math.round(start.x + ev.clientX - start.px)),
      y: Math.max(0, Math.round(start.y + ev.clientY - start.py)),
    });
    const move = (ev) => setDragPos(at(ev));
    const up = (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setDragPos(null);
      memoCrud.update(m.id, at(ev));
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  // Remember manual resizes (CSS resize handle, bottom-right corner). The first
  // observer tick reports the initial layout — skip it so untouched cards never
  // get phantom w/h writes.
  React.useEffect(() => {
    const el = ref.current;
    if (archived || !el || typeof ResizeObserver === "undefined") return;
    let first = true, t = null;
    const ro = new ResizeObserver(() => {
      if (first) { first = false; return; }
      clearTimeout(t);
      t = setTimeout(() => {
        const w = Math.round(el.offsetWidth), h = Math.round(el.offsetHeight);
        if (Math.abs(w - (m.w || QM_CARD_W)) > 2 || Math.abs(h - (m.h || QM_CARD_H)) > 2) {
          memoCrud.update(m.id, { w, h });
        }
      }, 350);
    });
    ro.observe(el);
    return () => { ro.disconnect(); clearTimeout(t); };
    // eslint-disable-next-line
  }, [m.id, m.w, m.h]);

  const stop = (e) => e.stopPropagation();

  return (
    <div
      ref={ref}
      className={"qm-card" + (m.taskified ? " taskified" : "") + (m.pinned && !archived ? " pinned" : "") + (dragPos ? " dragging" : "") + (archived ? " archived" : "")}
      style={archived ? {
        position: "relative", width: "auto", height: m.h || QM_CARD_H,
      } : {
        position: "absolute", left: pos.x, top: pos.y,
        width: m.w || QM_CARD_W, height: m.h || QM_CARD_H,
        zIndex: dragPos ? 9999 : (m.z || 1),
      }}
    >
      <div
        className="qm-grip"
        onPointerDown={onGripDown}
        style={{ cursor: archived || m.pinned ? "default" : "grab" }}
        title={archived ? "過去のメモ" : m.pinned ? "ピン留め中（場所固定）" : "ドラッグで移動"}
      >
        <span className="qm-grip-dots" aria-hidden="true">⠿</span>
        <span style={{ fontSize: 10, color: "var(--ash)", fontFamily: "var(--f-mono)", letterSpacing: "0.06em" }}>
          {archived && m.archivedAt ? `過去送り ${fmtStamp(m.archivedAt)}` : fmtStamp(m.updatedAt || m.createdAt)}
        </span>
        <div style={{ flex: 1 }}/>
        <button
          className="row-action-btn qm-expand-btn"
          title="拡大して読み書き（Escで閉じる）"
          aria-label="メモを拡大表示"
          onPointerDown={stop}
          onClick={() => onExpand(m.id)}
        >⤢</button>
        {archived ? (
          <button
            className="row-action-btn qm-restore-btn"
            title="今に戻す（ボードへ復帰）"
            aria-label="メモを今のボードに戻す"
            onPointerDown={stop}
            onClick={() => memoCrud.update(m.id, { archived: false })}
          >↩</button>
        ) : (
          <>
            {isPrivate ? (
              <button
                className="row-action-btn qm-flag-btn"
                title="今のボードへ移動（Private解除）"
                aria-label="Privateを解除して今タブへ移動"
                onPointerDown={stop}
                onClick={() => memoCrud.update(m.id, { private: false })}
              >🔓</button>
            ) : (
              <button
                className="row-action-btn qm-flag-btn"
                title="過去へ移動（済んだメモを保管）"
                aria-label="メモを過去タブへ移動"
                onPointerDown={stop}
                onClick={() => memoCrud.update(m.id, { archived: true, archivedAt: Date.now() })}
              >🚩</button>
            )}
            <button
              className={"row-action-btn" + (m.pinned ? " qm-pin-on" : "")}
              title={m.pinned ? "ピン留め解除（移動できるようになります）" : "ピン留め（場所を固定）"}
              aria-pressed={!!m.pinned}
              onPointerDown={stop}
              onClick={() => memoCrud.update(m.id, { pinned: !m.pinned })}
            >📌</button>
          </>
        )}
        <button
          className="row-action-btn danger"
          title="メモを削除"
          onPointerDown={stop}
          onClick={() => { if (confirm("このメモを削除しますか？（登録済みタスクは残ります）")) memoCrud.remove(m.id); }}
        ><Icon name="trash" size={12}/></button>
      </div>
      <textarea
        className="qm-text"
        value={m.text}
        placeholder="走り書き、TODO、会議の宿題…"
        autoFocus={focus}
        onChange={e => memoCrud.update(m.id, { text: e.target.value, updatedAt: Date.now() })}
      />
      {extractErr && (
        <div style={{ fontSize: 11, color: "var(--crimson-hi)", padding: "2px 2px" }}>{extractErr}</div>
      )}
      <div className="qm-foot">
        {m.taskified && (
          <div className="qm-badge" title={new Date(m.taskified.at).toLocaleString("ja-JP")}>
            ✅ タスク化済み（{m.taskified.count}件・{fmtStamp(m.taskified.at)}）
          </div>
        )}
        <div style={{ flex: 1 }}/>
        <button
          className="btn sm gold"
          disabled={!m.text.trim() || anyExtracting}
          onClick={() => onExtract(m)}
          title="AIがメモからタスク候補を抽出します（登録前に確認できます）"
        >
          <span>{extracting ? "抽出中…" : m.taskified ? "⚡ 再タスク化" : "⚡ タスク化"}</span>
        </button>
      </div>
    </div>
  );
};

// ── expanded editor: one click on ⤢ opens the memo in a large overlay ───────
// Same memo, same auto-save path (state → debounced KV) — closing just returns
// to the sticky; position/size/pin of the card are untouched.
const QmExpandModal = ({ memo, memoCrud, onClose, onExtract, anyExtracting, extracting, fmtStamp }) => {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!memo) return null;
  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={(e) => { if (e.target.classList.contains("modal-overlay")) onClose(); }}>
      <div className="modal-stage qm-expand-stage">
        <div className="modal-head">
          <div style={{ flex: 1 }}>
            <span className="eyebrow">📝 QUICK MEMO</span>
            <h2 className="modal-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              メモ（拡大表示）
              <span style={{ fontSize: 11, color: "var(--ash)", fontFamily: "var(--f-mono)", fontWeight: 400, letterSpacing: "0.06em" }}>
                {fmtStamp(memo.updatedAt || memo.createdAt)} · 自動保存
              </span>
            </h2>
          </div>
          <button className="icon-btn" onClick={onClose} title="閉じる (Esc)" aria-label="閉じる"><Icon name="x" size={18}/></button>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <textarea
            className="qm-text qm-expand-text"
            value={memo.text}
            autoFocus
            placeholder="走り書き、TODO、会議の宿題…"
            onChange={e => memoCrud.update(memo.id, { text: e.target.value, updatedAt: Date.now() })}
          />
        </div>
        <div className="modal-foot">
          {memo.taskified && (
            <div className="qm-badge">✅ タスク化済み（{memo.taskified.count}件・{fmtStamp(memo.taskified.at)}）</div>
          )}
          <div style={{ flex: 1 }}/>
          <button
            className="btn sm gold"
            disabled={!memo.text.trim() || anyExtracting}
            onClick={() => onExtract(memo)}
            title="AIがメモからタスク候補を抽出します（登録前に確認できます）"
          >
            <span>{extracting ? "抽出中…" : memo.taskified ? "⚡ 再タスク化" : "⚡ タスク化"}</span>
          </button>
          <button className="btn ghost" onClick={onClose}>閉じる</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── main view ────────────────────────────────────────────────────────────────
const QuickMemoView = ({ state, setState }) => {
  const memoCrud = useCrud(state, setState, "quickMemos");
  const itemCrud = useCrud(state, setState, "items");
  const memos = state.quickMemos || [];
  const [tab, setTab] = React.useState("now");               // "now" | "past"
  const [focusId, setFocusId] = React.useState(null);
  const [expandedId, setExpandedId] = React.useState(null);
  const [extractingId, setExtractingId] = React.useState(null);
  const [extractErr, setExtractErr] = React.useState(null); // { id, msg }
  const [preview, setPreview] = React.useState(null);       // { memoId, rows }
  const [registering, setRegistering] = React.useState(false);

  // 🚩 flag moves a sticky to 過去 (archived); ↩ brings it back. The board (今)
  // only lays out active memos; archived ones render as a static tidy grid.
  const activeMemos = memos.filter(m => !m.archived && !m.private);
  const privateMemos = memos.filter(m => m.private && !m.archived);
  const pastMemos = memos.filter(m => m.archived)
    .sort((a, b) => (b.archivedAt || 0) - (a.archivedAt || 0));

  const maxZ = [...activeMemos, ...privateMemos].reduce((mx, m) => Math.max(mx, m.z || 1), 1);

  const addMemo = () => {
    const id = newCrudId();
    const isPrivateTab = tab === "private";
    const liveMemos = isPrivateTab ? privateMemos : activeMemos;
    const n = liveMemos.length;
    memoCrud.add({
      id, text: "", createdAt: Date.now(), taskified: null,
      x: 8 + (n % 6) * 34, y: 8 + (n % 6) * 30, z: maxZ + 1, pinned: false,
      ...(isPrivateTab ? { private: true } : {}),
    });
    if (tab === "past") setTab("now");
    setFocusId(id);
  };

  const bringToFront = (m) => {
    if ((m.z || 1) < maxZ || maxZ === 1) memoCrud.update(m.id, { z: maxZ + 1 });
  };

  // canvas tall enough for the lowest card (+ resize margin)
  const liveMemos = tab === "private" ? privateMemos : activeMemos;
  const canvasH = Math.max(
    480,
    ...liveMemos.map((m, i) => (m.y ?? __qm_defaultPos(i).y) + (m.h || QM_CARD_H) + 120),
  );

  const extract = async (memo) => {
    if (!memo.text.trim() || extractingId) return;
    setExtractErr(null);
    setExtractingId(memo.id);
    try {
      const prompt = __qm_buildPrompt(memo.text, todayYMD());
      let raw = await window.claude.complete(prompt);
      let rows = __qm_parseExtraction(raw);
      if (rows.length === 0 && raw && !/^\s*\[\s*\]\s*$/.test(String(raw))) {
        // One retry with a stricter reminder — same pattern as the other AI features.
        raw = await window.claude.complete(prompt + "\n\n重要: 必ず生のJSON配列だけを返してください。コードフェンスや説明文は厳禁です。");
        rows = __qm_parseExtraction(raw);
      }
      setPreview({ memoId: memo.id, rows });
    } catch (e) {
      setExtractErr({ id: memo.id, msg: "AI抽出に失敗しました。通信状態を確認してもう一度お試しください。" });
    } finally {
      setExtractingId(null);
    }
  };

  const register = () => {
    if (!preview || registering) return;
    const picked = preview.rows.filter(r => r.pick && r.title.trim());
    if (picked.length === 0) return;
    setRegistering(true);
    // Pre-assign ids so the memo can remember which tasks it produced.
    // (連日行はここで1日1タスクに展開される)
    const tasks = __qm_rowsToTasks(picked).map(t => ({ ...t, id: newCrudId() }));
    itemCrud.addMany(tasks);
    const memo = memos.find(m => m.id === preview.memoId);
    const prev = (memo && memo.taskified) || { count: 0, taskIds: [] };
    memoCrud.update(preview.memoId, {
      taskified: {
        at: Date.now(),
        count: prev.count + tasks.length,
        taskIds: [...(prev.taskIds || []), ...tasks.map(t => t.id)],
      },
    });
    setRegistering(false);
    setPreview(null);
  };

  const fmtStamp = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    const wdays = ["日", "月", "火", "水", "木", "金", "土"];
    return `${y}/${mo}/${dy}(${wdays[d.getDay()]})`;
  };

  return (
    <div className="view-fade">
      <div className="panel cinematic mb-3" style={{ padding: "16px 22px", display: "flex", gap: 14, alignItems: "center" }}>
        <Icon name="note" size={16} color="var(--gold)"/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: "var(--ivory)", fontWeight: 600 }}>Quick Memo</div>
          <div style={{ fontSize: 11, color: "var(--ash)", marginTop: 2 }}>✓ 入力は自動保存されます（保存ボタン不要）。⚡タスク化でAIがタスク候補を抽出し、確認してから登録できます。🚩で済んだメモを「過去」へ保管。</div>
        </div>
        <button className="btn gold sm" onClick={addMemo}>
          <Icon name="plus" size={12}/>
          <span>新しいメモ</span>
        </button>
      </div>

      <div className="tabs">
        <div className={"tab" + (tab === "now" ? " active" : "")} onClick={() => setTab("now")}>
          今<span className="tab-count">{activeMemos.length}</span>
        </div>
        <div className={"tab" + (tab === "past" ? " active" : "")} onClick={() => setTab("past")}>
          過去<span className="tab-count">{pastMemos.length}</span>
        </div>
        <div className={"tab" + (tab === "private" ? " active" : "")} onClick={() => setTab("private")}>
          🔒 Private<span className="tab-count">{privateMemos.length}</span>
        </div>
      </div>

      {(tab === "now" || tab === "private") && (
        <>
          {liveMemos.length === 0 && (
            <div className="empty-state">
              <h4>{tab === "private" ? "Privateメモはまだありません" : "メモはまだありません"}</h4>
              <div>{tab === "private" ? "「新しいメモ」から書き始めてください。Privateタブのメモは他のタブには表示されません。" : "「新しいメモ」から書き始めてください。書いた内容は自動で保存されます。"}</div>
            </div>
          )}
          <div className="qm-canvas" style={{ height: canvasH }}>
            {liveMemos.map((m, i) => (
              <QmStickyCard
                key={m.id}
                m={m}
                idx={i}
                memoCrud={memoCrud}
                focus={focusId === m.id}
                extracting={extractingId === m.id}
                anyExtracting={!!extractingId}
                extractErr={extractErr && extractErr.id === m.id ? extractErr.msg : null}
                onExtract={extract}
                onExpand={setExpandedId}
                bringToFront={bringToFront}
                fmtStamp={fmtStamp}
                isPrivate={tab === "private"}
              />
            ))}
          </div>
        </>
      )}

      {tab === "past" && (
        <>
          {pastMemos.length === 0 && (
            <div className="empty-state">
              <h4>過去のメモはありません</h4>
              <div>「今」のメモの 🚩 を押すと、ここに保管されます（↩でいつでも戻せます）。</div>
            </div>
          )}
          <div className="qm-archive-grid">
            {pastMemos.map(m => (
              <QmStickyCard
                key={m.id}
                m={m}
                idx={0}
                archived
                memoCrud={memoCrud}
                focus={false}
                extracting={extractingId === m.id}
                anyExtracting={!!extractingId}
                extractErr={extractErr && extractErr.id === m.id ? extractErr.msg : null}
                onExtract={extract}
                onExpand={setExpandedId}
                bringToFront={bringToFront}
                fmtStamp={fmtStamp}
              />
            ))}
          </div>
        </>
      )}

      <QmExpandModal
        memo={expandedId ? memos.find(m => m.id === expandedId) || null : null}
        memoCrud={memoCrud}
        onClose={() => setExpandedId(null)}
        onExtract={extract}
        anyExtracting={!!extractingId}
        extracting={extractingId === expandedId}
        fmtStamp={fmtStamp}
      />

      <QmPreviewModal
        open={!!preview}
        rows={preview ? preview.rows : []}
        setRows={(fn) => setPreview(p => p ? { ...p, rows: typeof fn === "function" ? fn(p.rows) : fn } : p)}
        onRegister={register}
        onClose={() => setPreview(null)}
        busy={registering}
      />
    </div>
  );
};

Object.assign(window, { QuickMemoView, __qm_buildPrompt, __qm_parseExtraction, __qm_rowsToTasks, QM_AREAS, QM_OWNERS });


;/* ==== screenshots.jsx ==== */
// Screenshots library — paste images, AI-describe them, browse & re-open
//
// State shape: state.screenshots = [{ id, title, memo, createdAt, thumbnail, w, h, analyzing? }]
// Full-res PNG is stored in IndexedDB (and mirrored to the sync folder); the
// inline thumbnail (~360px, jpeg) keeps the grid render fast and the localStorage
// payload tiny.

const SS_MAX_THUMB = 360;
const SS_MAX_FULL = 1920;
const SS_THUMB_QUALITY = 0.78;

// ── Image helpers ────────────────────────────────────────────────────────────
function _ssLoadImg(src) {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = src;
  });
}

async function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

// Down-rescale an image to maxSide on the longest side. Returns { dataUrl, w, h }.
async function rescaleImage(srcDataUrl, maxSide, mime = "image/jpeg", quality = 0.92) {
  const im = await _ssLoadImg(srcDataUrl);
  const ratio = Math.min(1, maxSide / Math.max(im.width, im.height));
  const w = Math.max(1, Math.round(im.width * ratio));
  const h = Math.max(1, Math.round(im.height * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  // For JPEG we need a non-transparent backdrop
  if (mime === "image/jpeg") {
    ctx.fillStyle = "#0B1330";
    ctx.fillRect(0, 0, w, h);
  }
  ctx.drawImage(im, 0, 0, w, h);
  return { dataUrl: canvas.toDataURL(mime, quality), w, h, ow: im.width, oh: im.height };
}

// Extract base64 payload from a data URL
function dataUrlToBase64(d) {
  const i = String(d).indexOf(",");
  return i >= 0 ? d.slice(i + 1) : d;
}

// ── AI analysis (Claude vision) ──────────────────────────────────────────────
// The claude.complete endpoint has a ~256KB request size limit. Full-res
// screenshots / camera photos blow past it, so we adaptively downscale +
// re-encode the image until the base64 payload fits comfortably under the cap
// (target ≤180KB of base64 so the prompt + JSON envelope fit too).
const AI_MAX_BASE64_BYTES = 180 * 1024;

async function prepImageForAI(srcDataUrl) {
  // Start at 1280px / q=0.82 and step down until under budget
  const attempts = [
    [1280, 0.82],
    [1024, 0.78],
    [ 896, 0.74],
    [ 768, 0.70],
    [ 640, 0.66],
    [ 512, 0.62],
  ];
  let last = null;
  for (const [side, q] of attempts) {
    const out = await rescaleImage(srcDataUrl, side, "image/jpeg", q);
    const b64Len = out.dataUrl.length - (out.dataUrl.indexOf(",") + 1);
    last = { dataUrl: out.dataUrl, b64Len, side, q };
    if (b64Len <= AI_MAX_BASE64_BYTES) return last;
  }
  return last; // best effort
}

async function analyzeScreenshotAI(dataUrl) {
  const instruction = `添付された画像を見て、内容を日本語で**観察記録 + 洞察**として説明してください。
画像はスクリーンショット(UI画面)の場合もあれば、カメラで撮影した実写写真の場合もあります。両対応してください。

【重要なルール】
- 顔の特徴から個人を推測してはいけません(顔認証は禁止)。
- ただし**画像内に明示的に書かれている文字情報**(名札・社員証・スライドの名前表示・SNSプロフィール・メールヘッダー・名刺・会議参加者リスト等)からは、人物名・肩書・会社・部署を読み取ってよい(これは「顔を見て推測」ではなく「書かれた文字を読む」だけ)。
- 写真に人物が写っているが文字情報が無い場合は、個人を特定しないこと。人数・服装・年齢層・関係性の雰囲気だけ客観的に書く。
- 必ず以下のJSONフォーマットのみで返答してください(コードフェンス・前置き・後置きなし、純粋なJSONオブジェクトのみ):

{
  "title": "短い題名(15文字以内、内容を一言で表す)",
  "summary": "画像の概要(1〜2文)",
  "details": "観察項目を箇条書きで(各行の頭に「・」)",
  "insights": "そこから読み取れる興味深い洞察・推測・気づきを箇条書きで(各行の頭に「💡」)",
  "tags": ["関連キーワード", "を3〜5個"],
  "peopleCandidates": [
    { "name": "氏名(画像内の文字から読み取れた場合のみ。漢字/カナ/英字いずれでも)",
      "kana": "ふりがな(画像に書かれていれば)",
      "role": "肩書/役職(画像内の文字から)",
      "company": "所属会社/組織(画像内の文字から)",
      "department": "部署(画像内の文字から)",
      "source": "情報源(名札/社員証/スライド/名刺/会議参加者リスト/SNSプロフィール/メールヘッダー 等)",
      "confidence": "high/medium/low (文字がはっきり読めるか・名前らしい綴りか)"
    }
  ]
}

- peopleCandidates は画像内の**文字情報から読み取れた人物**だけ。顔だけで推測した人物は絶対に入れない。
- 該当者がいなければ peopleCandidates は空配列 [] にする。
- 利用者本人(撮影者)らしき情報は除外する。

【detailsには 客観的事実 を書く】
■スクリーンショットの場合: 画面種別(ダッシュボード/チャット/メール/コード/記事/SNS等)、表示中のデータ・数値・日付・固有名詞、操作の文脈(閲覧/編集/設定/分析等)
■実写写真の場合: 場所/シーン、被写体(人数・年齢層・服装・小物)、時間帯・天気・色調、構図、目に見える出来事

【insightsには 推測・気づき・面白いことを書く】これが一番重要です。淡々とした説明だけでなく、観察から踏み込んだ次のような情報を3〜6個:
- 撮影/撮影時期の推測(季節・時間帯・地理的特徴・気候の手がかり、影の向きから方角、空の色から時刻 等)
- イベントや状況の推測(何の集まりか、初対面か仲が良いか、フォーマル/カジュアル、お祝い事/日常 等)
- 細部から読み取れること(服装やロゴ・小物から趣味/職業/ライフスタイル、背景の建築様式から地域、年式が分かる物 等)
- 気づきにくいディテール(写真の隅に映る物、UIの非表示メニュー、矛盾している点、メタ情報のヒント 等)
- 構図・ライティング・撮影者の意図についての推測
- 数値や情報の意味づけ(良い数字か悪い数字か、トレンドが読めるか、注意すべきポイント 等)
- 文化的・社会的な文脈(その国/地域ならでは、世代感、時代感 等)
- ちょっとした面白さ・ユーモア・人間らしい観察(押し付けがましくない範囲で)

【トーン】観察記録は淡々と、insightsは少し踏み込んで具体的に。ただし断定せず「〜ようだ」「〜と推測される」「おそらく〜」と表現する。`;

  // Downscale to fit within the claude.complete request budget
  const prepped = await prepImageForAI(dataUrl);
  const aiDataUrl = prepped ? prepped.dataUrl : dataUrl;

  // dataUrl should be a JPEG (re-encoded) — peel out mime + base64 cleanly
  let mediaType = "image/jpeg";
  const mt = /^data:([^;]+);base64,/.exec(aiDataUrl);
  if (mt) mediaType = mt[1];
  const base64 = dataUrlToBase64(aiDataUrl);

  if (!window.claude || !window.claude.complete) {
    throw new Error("AI is not available in this environment.");
  }
  const resp = await window.claude.complete({
    messages: [{
      role: "user",
      content: [
        { type: "image", source: { type: "base64", media_type: mediaType, data: base64 } },
        { type: "text", text: instruction },
      ],
    }],
  });
  // Parse the first JSON object from the response
  const text = String(resp || "").trim();
  const m = text.match(/\{[\s\S]*\}/);
  if (!m) {
    // Fallback: use the raw response as a free-form memo so the user still
    // gets something useful (e.g. when the model returned prose instead of JSON).
    if (text) {
      return {
        title: "",
        summary: text.split(/\n/)[0].slice(0, 120),
        details: text,
        insights: "",
        tags: [],
      };
    }
    throw new Error("AI response was empty.");
  }
  let j;
  try {
    j = JSON.parse(m[0]);
  } catch (err) {
    // Salvage as free-form text if JSON is malformed
    return {
      title: "",
      summary: text.split(/\n/)[0].slice(0, 120),
      details: text,
      insights: "",
      tags: [],
    };
  }
  return {
    title: String(j.title || "").trim(),
    summary: String(j.summary || "").trim(),
    details: String(j.details || "").trim(),
    insights: String(j.insights || "").trim(),
    tags: Array.isArray(j.tags) ? j.tags.filter(Boolean).map(String).slice(0, 6) : [],
    peopleCandidates: Array.isArray(j.peopleCandidates)
      ? j.peopleCandidates.slice(0, 12).map(p => ({
          name: String((p && p.name) || "").trim(),
          kana: String((p && p.kana) || "").trim(),
          role: String((p && p.role) || "").trim(),
          company: String((p && p.company) || "").trim(),
          department: String((p && p.department) || "").trim(),
          source: String((p && p.source) || "").trim(),
          confidence: ["high","medium","low"].includes(p && p.confidence) ? p.confidence : "low",
        })).filter(p => p.name)
      : [],
  };
}

// Normalize text for matching: NFKC, lowercase, strip whitespace/punctuation.
// Used to compare names/companies regardless of full/half width or spacing.
function _ssNormalizeForMatch(s) {
  return String(s || "")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[\s　・･.,，、。()（）\-_/／\\]+/g, "");
}

// Match peopleCandidates (extracted from image text) against the contacts list.
// Returns [{ candidate, contact, score, reasons[] }, ...] only for confident hits.
// Pure + exported so it can be unit-tested without the network.
function matchPeopleToContacts(candidates, contacts) {
  const cands = Array.isArray(candidates) ? candidates : [];
  const list = Array.isArray(contacts) ? contacts : [];
  if (!cands.length || !list.length) return [];

  const prepared = list.map(c => ({
    raw: c,
    nameN:       _ssNormalizeForMatch(c.name),
    kanaN:       _ssNormalizeForMatch(c.kana),
    companyN:    _ssNormalizeForMatch(c.company),
    roleN:       _ssNormalizeForMatch(c.role),
    departmentN: _ssNormalizeForMatch(c.department),
  })).filter(p => p.nameN);

  const out = [];
  const seenContactIds = new Set();
  for (const cand of cands) {
    const candNameN    = _ssNormalizeForMatch(cand.name);
    const candKanaN    = _ssNormalizeForMatch(cand.kana);
    const candCompanyN = _ssNormalizeForMatch(cand.company);
    const candRoleN    = _ssNormalizeForMatch(cand.role);
    const candDeptN    = _ssNormalizeForMatch(cand.department);
    if (!candNameN && !candKanaN) continue;

    let best = null;
    for (const p of prepared) {
      let score = 0;
      const reasons = [];
      // Name match (the gate — without name evidence we do NOT match)
      if (candNameN && p.nameN === candNameN)            { score += 60; reasons.push("氏名一致"); }
      else if (candNameN && (p.nameN.includes(candNameN) || candNameN.includes(p.nameN)) && Math.min(p.nameN.length, candNameN.length) >= 2) {
        score += 35; reasons.push("氏名部分一致");
      }
      // Kana-only match is treated as a name signal too — a katakana name tag
      // ("ヤマダ タロウ") still uniquely identifies a person and clears the gate.
      if (candKanaN && p.kanaN && p.kanaN === candKanaN) { score += 50; reasons.push("ふりがな一致"); }

      // Without a name signal, skip — never identify by company/role alone
      if (score === 0) continue;

      if (candCompanyN && p.companyN && (p.companyN === candCompanyN || p.companyN.includes(candCompanyN) || candCompanyN.includes(p.companyN))) {
        score += 20; reasons.push("会社一致");
      }
      if (candDeptN && p.departmentN && (p.departmentN === candDeptN || p.departmentN.includes(candDeptN))) {
        score += 8; reasons.push("部署一致");
      }
      if (candRoleN && p.roleN && (p.roleN === candRoleN || p.roleN.includes(candRoleN))) {
        score += 5; reasons.push("役職一致");
      }
      if (cand.confidence === "high")   score += 6;
      else if (cand.confidence === "low") score -= 6;

      if (!best || score > best.score) best = { contact: p.raw, score, reasons };
    }
    // Threshold: require at least 50 (= "氏名一致" alone, or 部分一致+会社一致).
    if (best && best.score >= 50 && !seenContactIds.has(best.contact.id)) {
      seenContactIds.add(best.contact.id);
      out.push({ candidate: cand, ...best });
    }
  }
  return out;
}

// Build the "identified contacts" section appended to the memo. Pure + exported.
function buildIdentifiedContactsBlock(matches) {
  if (!matches || !matches.length) return "";
  const lines = ["── 📇 識別された連絡先 ──"];
  for (const m of matches) {
    const c = m.contact;
    const head = c.name + (c.kana ? `（${c.kana}）` : "") +
      (c.company || c.role ? "  " + [c.company, c.role].filter(Boolean).join(" / ") : "");
    lines.push("👤 " + head);
    if (c.department) lines.push("   部署: " + c.department);
    const contactLines = [];
    if (c.email)    contactLines.push("📧 " + c.email);
    if (c.phone)    contactLines.push("☎ "  + c.phone);
    if (c.mobile)   contactLines.push("📱 " + c.mobile);
    if (c.fax)      contactLines.push("📠 " + c.fax);
    if (c.website)  contactLines.push("🌐 " + c.website);
    if (contactLines.length) lines.push("   " + contactLines.join(" / "));
    if (c.note)     lines.push("   📝 " + c.note.split("\n")[0].slice(0, 120));
    lines.push(`   (識別根拠: ${m.reasons.join("・")} / 信頼度: ${m.score >= 70 ? "高" : m.score >= 50 ? "中" : "低"} / 画像内の情報源: ${m.candidate.source || "—"})`);
  }
  lines.push("※ 顔ではなく画像内に書かれた文字情報から照合しています。");
  return lines.join("\n");
}

// Combine the AI fields into one editable memo blob.
// `matches` is the result of matchPeopleToContacts — when present, an
// "識別された連絡先" section is prepended so the user spots it immediately.
function buildMemoFromAI(ai, matches) {
  const parts = [];
  const identified = buildIdentifiedContactsBlock(matches);
  if (identified) parts.push(identified);
  if (ai.summary) parts.push(ai.summary);
  if (ai.details) {
    const d = ai.details.includes("\n") || ai.details.includes("・")
      ? ai.details
      : ai.details.split(/[、。]\s*/).filter(Boolean).map(s => "・" + s).join("\n");
    parts.push(d);
  }
  if (ai.insights) {
    const ins = ai.insights.includes("\n") || ai.insights.includes("💡")
      ? ai.insights
      : ai.insights.split(/[、。]\s*/).filter(Boolean).map(s => "💡 " + s).join("\n");
    parts.push("──  気づき・推測  ──\n" + ins);
  }
  if (ai.tags && ai.tags.length) parts.push("Tags: " + ai.tags.map(t => "#" + t).join(" "));
  return parts.join("\n\n").trim();
}

// ── Formatting ──
function _ssFormatDate(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${p(d.getMonth()+1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// ── Main view ────────────────────────────────────────────────────────────────
const ScreenshotsView = ({ state, setState, folderConnected }) => {
  const screenshots = state.screenshots || [];
  const [q, setQ] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [flash, setFlash] = React.useState(null);
  const [selectedId, setSelectedId] = React.useState(null);
  const [cameraOpen, setCameraOpen] = React.useState(false);
  const fileRef = React.useRef(null);

  const flashMsg = (kind, text, ms = 2400) => {
    setFlash({ kind, text });
    setTimeout(() => setFlash(null), ms);
  };

  // ── Ingest a new image (from paste or file) ──
  const ingestBlob = async (blob) => {
    if (!blob) return;
    if (!blob.type || !blob.type.startsWith("image/")) {
      flashMsg("err", "画像ファイルのみ取り込めます。");
      return;
    }
    setBusy(true);
    try {
      const rawDataUrl = await blobToDataUrl(blob);
      // Re-encode at max 1920px on long edge to keep IDB small
      const full = await rescaleImage(rawDataUrl, SS_MAX_FULL, "image/jpeg", 0.88);
      // Generate a small inline thumbnail for the grid
      const thumb = await rescaleImage(full.dataUrl, SS_MAX_THUMB, "image/jpeg", SS_THUMB_QUALITY);
      const id = `ss_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const entry = {
        id,
        title: "解析中…",
        memo: "",
        tags: [],
        createdAt: Date.now(),
        thumbnail: thumb.dataUrl,
        w: full.ow, h: full.oh,
        analyzing: true,
      };
      // Persist full image to IDB + folder
      await window.persistScreenshot(id, full.dataUrl);
      if (folderConnected && window.writeScreenshotToFolder) {
        window.writeScreenshotToFolder(id, full.dataUrl);
      }
      // Add to state
      setState(s => ({ ...s, screenshots: [entry, ...(s.screenshots || [])] }));
      setSelectedId(id);

      // AI analysis (best-effort, async)
      try {
        const ai = await analyzeScreenshotAI(full.dataUrl);
        // Match image-extracted people against the contacts master. We read
        // contacts fresh from state via the setState updater below so we don't
        // close over a stale snapshot.
        let matches = [];
        let identifiedIds = [];
        setState(s => {
          matches = matchPeopleToContacts(ai.peopleCandidates || [], s.contacts || []);
          identifiedIds = matches.map(m => m.contact.id);
          const memo = buildMemoFromAI(ai, matches);
          const extraTags = matches.length ? ["識別:" + matches.length + "名"] : [];
          return {
            ...s,
            screenshots: (s.screenshots || []).map(x => x.id === id
              ? {
                  ...x,
                  title: ai.title || "Untitled",
                  memo,
                  tags: [...(ai.tags || []), ...extraTags],
                  identifiedContactIds: identifiedIds,
                  analyzing: false,
                }
              : x),
          };
        });
        if (matches.length) {
          flashMsg("ok", `${matches.length}名の連絡先を画像内の文字情報から識別しました`);
        }
      } catch (e) {
        console.warn("AI failed:", e);
        const reason = (e && e.message) ? String(e.message).slice(0, 140) : "";
        const memoText = reason
          ? `(AI解析に失敗しました。手動でメモを記入してください)\n\n[詳細] ${reason}`
          : "(AI解析に失敗しました。手動でメモを記入してください)";
        setState(s => ({
          ...s,
          screenshots: (s.screenshots || []).map(x => x.id === id
            ? { ...x, title: "Untitled", memo: memoText, analyzing: false }
            : x),
        }));
        flashMsg("err", "AI解析に失敗しました。メモは手動で編集できます。");
      }
    } catch (e) {
      console.warn(e);
      flashMsg("err", "画像の取り込みに失敗しました。");
    } finally {
      setBusy(false);
    }
  };

  // ── Global paste handler (only while view is mounted) ──
  React.useEffect(() => {
    const onPaste = async (e) => {
      // Ignore paste into text fields — user is editing memo/etc.
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || (t.isContentEditable))) return;
      const items = e.clipboardData?.items;
      if (!items || !items.length) return;
      for (const item of items) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          e.preventDefault();
          const blob = item.getAsFile();
          await ingestBlob(blob);
          return;
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line
  }, [folderConnected]);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) await ingestBlob(f);
    e.target.value = "";
  };

  const removeOne = async (id) => {
    if (!confirm("このスクリーンショットを削除しますか?")) return;
    setState(s => ({ ...s, screenshots: (s.screenshots || []).filter(x => x.id !== id) }));
    if (window.idbDeleteScreenshot) window.idbDeleteScreenshot(id);
    if (folderConnected && window.deleteScreenshotFromFolder) window.deleteScreenshotFromFolder(id);
    if (selectedId === id) setSelectedId(null);
  };

  const updateMemo = (id, memo) => {
    setState(s => ({
      ...s,
      screenshots: (s.screenshots || []).map(x => x.id === id ? { ...x, memo } : x),
    }));
  };
  const updateTitle = (id, title) => {
    setState(s => ({
      ...s,
      screenshots: (s.screenshots || []).map(x => x.id === id ? { ...x, title } : x),
    }));
  };

  // ── Filtering ──
  const filtered = screenshots.filter(s => {
    if (!q) return true;
    const hay = ((s.title || "") + " " + (s.memo || "") + " " + (s.tags || []).join(" ")).toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const selected = screenshots.find(x => x.id === selectedId) || null;

  return (
    <div className="view-fade">
      {/* Top bar */}
      <div className="panel cinematic mb-3" style={{padding: "16px 22px", display:"flex", gap:14, alignItems:"center"}}>
        <Icon name="search" size={16} color="var(--gold)"/>
        <input
          placeholder="Search screenshots, memos, tags…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{flex:1, background:"transparent", border:"none", outline:"none", color:"var(--ivory)", fontSize:14}}
        />
        <span className="kbd" style={{fontSize:10}}>⌘V でペースト</span>
        <button className="btn ghost sm" onClick={() => setCameraOpen(true)} title="カメラで撮影">
          <Icon name="camera" size={12}/>
          <span>カメラ撮影</span>
        </button>
        <button className="btn ghost sm" onClick={() => fileRef.current?.click()}>
          <Icon name="image" size={12}/>
          <span>ファイル選択</span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{display:"none"}} onChange={onFile}/>
      </div>

      {/* Drop / paste zone */}
      <div
        className={"ss-dropzone" + (busy ? " busy" : "")}
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("over"); }}
        onDragLeave={(e) => e.currentTarget.classList.remove("over")}
        onDrop={async (e) => {
          e.preventDefault();
          e.currentTarget.classList.remove("over");
          const files = Array.from(e.dataTransfer.files || []);
          for (const f of files) await ingestBlob(f);
        }}
        onClick={() => fileRef.current?.click()}
      >
        <Icon name="image" size={26} color="var(--gold)"/>
        <div>
          <div style={{fontFamily:"var(--f-mono)", fontSize:10, letterSpacing:"0.16em", color:"var(--gold)"}}>PASTE · DROP · BROWSE</div>
          <div style={{fontSize:13, color:"var(--ivory)", marginTop:2}}>
            {busy ? "解析中… しばらくお待ちください" : "スクリーンショットをここに ⌘V でペースト / ドラッグ&ドロップ / クリックでファイル選択"}
          </div>
          <div style={{fontSize:11, color:"var(--ash)", marginTop:4}}>
            貼り付けるとAIが内容を読み取り、自動でタイトルとメモを生成します
          </div>
        </div>
        {busy && <div className="ss-spinner"/>}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="empty-state" style={{marginTop:24}}>
          <h4>{q ? "該当なし" : "まだスクリーンショットがありません"}</h4>
          <div>{q ? "別のキーワードでお試しください。" : "クリップボードに画像をコピーして ⌘V で貼り付けてください。"}</div>
        </div>
      ) : (
        <div className="ss-grid">
          {filtered.map(s => (
            <div key={s.id} className={"ss-card" + (s.analyzing ? " analyzing" : "")} onClick={() => setSelectedId(s.id)}>
              <div className="ss-thumb-wrap">
                <img src={s.thumbnail} alt="" className="ss-thumb"/>
                {s.analyzing && (
                  <div className="ss-analyzing-overlay">
                    <div className="ss-spinner"/>
                    <div style={{fontFamily:"var(--f-mono)", fontSize:9, letterSpacing:"0.14em", color:"var(--gold)", marginTop:8}}>AI ANALYZING…</div>
                  </div>
                )}
                <button className="ss-delete" onClick={(e) => { e.stopPropagation(); removeOne(s.id); }} title="削除">
                  <Icon name="trash" size={12}/>
                </button>
              </div>
              <div className="ss-meta">
                <div className="ss-title">{s.title || "Untitled"}</div>
                <div className="ss-date">{_ssFormatDate(s.createdAt)}</div>
                {s.tags && s.tags.length > 0 && (
                  <div className="ss-tag-row">
                    {s.tags.slice(0, 3).map(t => <span key={t} className="chip" style={{fontSize:9}}>{t}</span>)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      <ScreenshotDetail
        entry={selected}
        contacts={state.contacts || []}
        onClose={() => setSelectedId(null)}
        onMemoChange={(memo) => selected && updateMemo(selected.id, memo)}
        onTitleChange={(title) => selected && updateTitle(selected.id, title)}
        onDelete={() => selected && removeOne(selected.id)}
        onFlash={flashMsg}
      />

      {flash && (
        <div className={"toast " + flash.kind}>
          <Icon name={flash.kind === "err" ? "alert" : flash.kind === "ok" ? "check" : "download"} size={14}/>
          <span>{flash.text}</span>
        </div>
      )}

      {cameraOpen && (
        <CameraCapture
          onClose={() => setCameraOpen(false)}
          onCaptured={async (blob) => {
            setCameraOpen(false);
            await ingestBlob(blob);
          }}
          onFlash={flashMsg}
        />
      )}
    </div>
  );
};

// ── Detail overlay ───────────────────────────────────────────────────────────
const ScreenshotDetail = ({ entry, contacts, onClose, onMemoChange, onTitleChange, onDelete, onFlash }) => {
  const [fullUrl, setFullUrl] = React.useState(null);
  const [editingTitle, setEditingTitle] = React.useState(false);
  const [titleDraft, setTitleDraft] = React.useState("");
  const [copyFlash, setCopyFlash] = React.useState(false);
  const [zoomOpen, setZoomOpen] = React.useState(false);
  const urlRef = React.useRef(null);

  // Load full-res image lazily
  React.useEffect(() => {
    let cancelled = false;
    if (!entry) { setFullUrl(null); return; }
    (async () => {
      const url = await window.loadScreenshotUrl(entry.id);
      if (cancelled) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      // Revoke previous
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      setFullUrl(url || entry.thumbnail);
    })();
    return () => {
      cancelled = true;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, [entry?.id]);

  React.useEffect(() => {
    if (!entry) return;
    setTitleDraft(entry.title || "");
    setEditingTitle(false);
  }, [entry?.id]);

  // ESC to close
  React.useEffect(() => {
    if (!entry) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [entry, onClose]);

  if (!entry) return null;

  const copyMemo = async () => {
    try {
      await navigator.clipboard.writeText(entry.memo || "");
      setCopyFlash(true);
      setTimeout(() => setCopyFlash(false), 1400);
      onFlash && onFlash("ok", "メモをコピーしました");
    } catch (e) {
      onFlash && onFlash("err", "コピーに失敗しました");
    }
  };

  const downloadImage = async () => {
    if (!fullUrl) return;
    const a = document.createElement("a");
    a.href = fullUrl;
    a.download = `${(entry.title || "screenshot").replace(/[^\w\-]+/g, "_")}_${entry.id}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const saveTitle = () => {
    onTitleChange && onTitleChange(titleDraft.trim() || "Untitled");
    setEditingTitle(false);
  };

  return (
    <div className="rec-overlay" onClick={(e) => { if (e.target.classList.contains("rec-overlay")) onClose(); }}>
      <div className="rec-stage" style={{maxWidth: 1480}}>
        <div className="rec-stage-head">
          <div style={{flex:1, minWidth:0}}>
            <span className="eyebrow">SCREENSHOT · {_ssFormatDate(entry.createdAt)}</span>
            {editingTitle ? (
              <div style={{display:"flex", gap:8, alignItems:"center", marginTop:4}}>
                <input
                  autoFocus
                  value={titleDraft}
                  onChange={e => setTitleDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.nativeEvent.isComposing) saveTitle(); if (e.key === "Escape") setEditingTitle(false); }}
                  style={{flex:1, background:"rgba(91,123,196,0.08)", border:"1px solid var(--border-hi)", color:"var(--ivory)", padding:"6px 10px", borderRadius:5, fontSize:20, fontFamily:"var(--f-title)"}}
                />
                <button className="btn gold sm" onClick={saveTitle}><Icon name="check" size={12}/></button>
              </div>
            ) : (
              <h2 style={{display:"flex", alignItems:"center", gap:10}}>
                <span>{entry.title || "Untitled"}</span>
                <button className="icon-btn" onClick={() => setEditingTitle(true)} title="タイトル編集" style={{padding:6}}>
                  <Icon name="edit" size={13}/>
                </button>
              </h2>
            )}
          </div>
          {entry.w && entry.h && <span className="chip">{entry.w} × {entry.h}</span>}
          <button className="icon-btn" onClick={onClose} title="閉じる" aria-label="閉じる"><Icon name="x" size={18}/></button>
        </div>
        <div className="rec-stage-body" style={{padding:0}}>
          <div className="ss-detail-grid">
            <div className="ss-detail-image">
              {fullUrl ? (
                <button
                  type="button"
                  className="ss-detail-zoom-btn"
                  onClick={() => setZoomOpen(true)}
                  title="クリックで拡大表示"
                >
                  <img src={fullUrl} alt={entry.title || ""}/>
                  <span className="ss-zoom-hint">
                    <Icon name="search" size={12}/>
                    <span>クリックで拡大</span>
                  </span>
                </button>
              ) : (
                <div style={{display:"flex", alignItems:"center", justifyContent:"center", height:"100%", color:"var(--ash)", fontFamily:"var(--f-mono)", fontSize:11, letterSpacing:"0.12em"}}>LOADING…</div>
              )}
            </div>
            <div className="ss-detail-side">
              <div className="ss-side-section">
                <div className="ss-side-head">
                  <div className="label-mono">AI MEMO</div>
                  <button className={"btn sm" + (copyFlash ? " gold" : " ghost")} onClick={copyMemo} title="メモをコピー">
                    <Icon name={copyFlash ? "check" : "copy"} size={11}/>
                    <span>{copyFlash ? "Copied!" : "ワンクリックコピー"}</span>
                  </button>
                </div>
                {entry.analyzing ? (
                  <div style={{padding:"24px 12px", textAlign:"center", color:"var(--ash)"}}>
                    <div className="ss-spinner" style={{margin:"0 auto 12px"}}/>
                    <div style={{fontFamily:"var(--f-mono)", fontSize:10, letterSpacing:"0.14em", color:"var(--gold)"}}>AI ANALYZING…</div>
                    <div style={{fontSize:11, marginTop:6}}>画像の内容を読み取っています</div>
                  </div>
                ) : (
                  <textarea
                    className="ss-memo-area"
                    value={entry.memo || ""}
                    onChange={e => onMemoChange && onMemoChange(e.target.value)}
                    placeholder="メモを記入…"
                  />
                )}
              </div>

              {/* 画像内の文字情報から照合できた連絡先（顔ではなく文字ベース） */}
              {(() => {
                const ids = (entry && entry.identifiedContactIds) || [];
                if (!ids.length) return null;
                const byId = new Map((contacts || []).map(c => [c.id, c]));
                const found = ids.map(id => byId.get(id)).filter(Boolean);
                if (!found.length) return null;
                return (
                  <div className="ss-side-section">
                    <div className="label-mono mb-1">📇 識別された連絡先（{found.length}名）</div>
                    <div style={{display:"flex", flexDirection:"column", gap:8}}>
                      {found.map(c => (
                        <div key={c.id} style={{padding:"10px 12px", background:"rgba(91,123,196,0.08)", border:"1px solid var(--border-hi)", borderRadius:6}}>
                          <div style={{color:"var(--ivory)", fontWeight:600, fontSize:13}}>{c.name}{c.kana && <span style={{color:"var(--ash)", fontWeight:400, marginLeft:6, fontSize:11}}>（{c.kana}）</span>}</div>
                          {(c.company || c.role || c.department) && (
                            <div style={{color:"var(--ash)", fontSize:11, marginTop:3}}>{[c.company, c.department, c.role].filter(Boolean).join(" / ")}</div>
                          )}
                          {(c.email || c.phone || c.mobile) && (
                            <div style={{fontFamily:"var(--f-mono)", fontSize:10, color:"var(--smoke)", marginTop:4, letterSpacing:"0.02em"}}>
                              {[c.email && "📧 " + c.email, c.phone && "☎ " + c.phone, c.mobile && "📱 " + c.mobile].filter(Boolean).join(" / ")}
                            </div>
                          )}
                          {c.note && <div style={{color:"var(--ash)", fontSize:11, marginTop:4, fontStyle:"italic"}}>📝 {c.note.split("\n")[0].slice(0, 100)}</div>}
                        </div>
                      ))}
                    </div>
                    <div style={{fontSize:10, color:"var(--ash)", marginTop:6, lineHeight:1.5}}>
                      ※ 顔ではなく画像内に書かれた文字情報（名札・名刺・スライドの名前表示等）から照合しています。
                    </div>
                  </div>
                );
              })()}

              {entry.tags && entry.tags.length > 0 && (
                <div className="ss-side-section">
                  <div className="label-mono mb-1">TAGS</div>
                  <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
                    {entry.tags.map(t => <span key={t} className="chip crystal">{t}</span>)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="rec-stage-foot">
          <button className="btn ghost" onClick={downloadImage}><Icon name="download" size={12}/><span>画像をダウンロード</span></button>
          <button className="btn ghost" onClick={onDelete} style={{color:"var(--crimson-hi)", borderColor:"rgba(196,30,58,0.32)"}}>
            <Icon name="trash" size={12}/><span>削除</span>
          </button>
          <div style={{flex:1}}/>
          <button className="btn primary" onClick={onClose}>閉じる</button>
        </div>
      </div>
      {zoomOpen && fullUrl && (
        <ScreenshotLightbox
          src={fullUrl}
          title={entry.title || ""}
          w={entry.w}
          h={entry.h}
          onClose={() => setZoomOpen(false)}
        />
      )}
    </div>
  );
};

// ── Camera capture modal ────────────────────────────────────────────────────
const CameraCapture = ({ onClose, onCaptured, onFlash }) => {
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [facing, setFacing] = React.useState("environment"); // 'environment' or 'user'
  const [devices, setDevices] = React.useState([]);
  const [deviceId, setDeviceId] = React.useState(null);
  const [preview, setPreview] = React.useState(null); // { blob, url, w, h }
  const [capturing, setCapturing] = React.useState(false);
  const [hasMultiple, setHasMultiple] = React.useState(false);

  // Revoke the preview blob: URL if the modal closes (overlay click / Esc /
  // unmount) while a capture is still being reviewed — otherwise each
  // capture-then-close leaks one object URL.
  const previewUrlRef = React.useRef(null);
  previewUrlRef.current = preview ? preview.url : null;
  React.useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const stopStream = React.useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startStream = React.useCallback(async (opts = {}) => {
    stopStream();
    setReady(false);
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("このブラウザはカメラに対応していません。");
      }
      const constraints = {
        audio: false,
        video: opts.deviceId
          ? { deviceId: { exact: opts.deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
          : { facingMode: { ideal: opts.facing || facing }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setReady(true);

      // Enumerate devices now that we have permission
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const cams = all.filter(d => d.kind === "videoinput");
        setDevices(cams);
        setHasMultiple(cams.length > 1);
      } catch {}
    } catch (e) {
      console.warn("camera error:", e);
      const name = e && e.name;
      let msg = "カメラを起動できませんでした。";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        msg = "カメラへのアクセスが許可されていません。ブラウザの権限設定をご確認ください。";
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        msg = "利用可能なカメラが見つかりません。";
      } else if (name === "NotReadableError") {
        msg = "カメラが他のアプリで使用中です。";
      } else if (e && e.message) {
        msg = e.message;
      }
      setError(msg);
      setReady(false);
    }
  }, [facing, stopStream]);

  // Boot
  React.useEffect(() => {
    startStream({ facing });
    return () => stopStream();
    // eslint-disable-next-line
  }, []);

  // ESC to close
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        // From preview, Esc = retake: revoke the blob URL AND restart the
        // camera (just clearing preview left a dead, stopped camera view).
        if (preview) retake();
        else onClose();
      } else if (e.key === " " && !preview && ready) {
        e.preventDefault();
        capture();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [preview, ready]);

  const switchFacing = async () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);
    await startStream({ facing: next });
  };

  const pickDevice = async (id) => {
    setDeviceId(id);
    await startStream({ deviceId: id });
  };

  const capture = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    setCapturing(true);
    try {
      const w = v.videoWidth;
      const h = v.videoHeight;
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      // Mirror front-camera preview, but save un-mirrored? Most users expect the
      // photo to match what they saw — mirror to match preview when facing=user.
      if (facing === "user") {
        ctx.translate(w, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(v, 0, 0, w, h);
      const blob = await new Promise((res) => canvas.toBlob(b => res(b), "image/jpeg", 0.92));
      if (!blob) throw new Error("Capture failed");
      const url = URL.createObjectURL(blob);
      setPreview({ blob, url, w, h });
      // Stop the stream while user reviews — saves battery / camera light
      stopStream();
      setReady(false);
    } catch (e) {
      console.warn(e);
      onFlash && onFlash("err", "撮影に失敗しました。");
    } finally {
      setCapturing(false);
    }
  };

  const retake = async () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    await startStream({ facing, deviceId });
  };

  const usePhoto = async () => {
    if (!preview) return;
    const blob = preview.blob;
    URL.revokeObjectURL(preview.url);
    setPreview(null);
    await onCaptured(blob);
  };

  return (
    <div className="rec-overlay" onClick={(e) => { if (e.target.classList.contains("rec-overlay")) onClose(); }}>
      <div className="rec-stage cam-stage">
        <div className="rec-stage-head">
          <div style={{flex:1, minWidth:0}}>
            <span className="eyebrow">CAMERA · LIVE CAPTURE</span>
            <h2 style={{display:"flex", alignItems:"center", gap:10}}>
              <Icon name="camera" size={18} color="var(--gold)"/>
              <span>{preview ? "撮影プレビュー" : "カメラ撮影"}</span>
            </h2>
          </div>
          {!preview && ready && (
            <span className="chip" style={{fontFamily:"var(--f-mono)", fontSize:10}}>
              ● LIVE
            </span>
          )}
          {preview && <span className="chip">{preview.w} × {preview.h}</span>}
          <button className="icon-btn" onClick={onClose} title="閉じる" aria-label="閉じる"><Icon name="x" size={18}/></button>
        </div>

        <div className="rec-stage-body" style={{padding:0, background:"#02040a"}}>
          <div className="cam-viewport">
            {error ? (
              <div className="cam-error">
                <Icon name="alert" size={28} color="var(--crimson-hi)"/>
                <div style={{fontFamily:"var(--f-title)", fontSize:16, color:"var(--ivory)", marginTop:12}}>
                  カメラを起動できません
                </div>
                <div style={{fontSize:12, color:"var(--ash)", marginTop:6, maxWidth:420, textAlign:"center"}}>
                  {error}
                </div>
                <button className="btn ghost sm" onClick={() => startStream({ facing, deviceId })} style={{marginTop:18}}>
                  <Icon name="refresh" size={12}/>
                  <span>再試行</span>
                </button>
              </div>
            ) : preview ? (
              <img className="cam-preview-img" src={preview.url} alt="captured"/>
            ) : (
              <>
                <video
                  ref={videoRef}
                  playsInline
                  autoPlay
                  muted
                  className={"cam-video" + (facing === "user" ? " mirror" : "")}
                />
                {!ready && (
                  <div className="cam-loading">
                    <div className="ss-spinner"/>
                    <div style={{fontFamily:"var(--f-mono)", fontSize:10, letterSpacing:"0.16em", color:"var(--gold)", marginTop:12}}>
                      INITIALIZING CAMERA…
                    </div>
                  </div>
                )}
                {ready && (
                  <div className="cam-reticle">
                    <span className="cam-corner tl"/>
                    <span className="cam-corner tr"/>
                    <span className="cam-corner bl"/>
                    <span className="cam-corner br"/>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Device picker (only when multiple cameras detected and labels available) */}
          {!preview && !error && devices.length > 1 && devices.some(d => d.label) && (
            <div className="cam-device-row">
              <span className="label-mono" style={{flexShrink:0}}>SOURCE</span>
              <select
                value={deviceId || ""}
                onChange={e => pickDevice(e.target.value)}
                className="cam-device-select"
              >
                <option value="">— 自動 ({facing === "environment" ? "リア" : "フロント"}) —</option>
                {devices.map(d => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Camera ${d.deviceId.slice(0,6)}`}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="rec-stage-foot cam-foot">
          {preview ? (
            <>
              <button className="btn ghost" onClick={retake}>
                <Icon name="refresh" size={12}/>
                <span>撮り直す</span>
              </button>
              <div style={{flex:1}}/>
              <button className="btn ghost" onClick={onClose}>キャンセル</button>
              <button className="btn gold" onClick={usePhoto}>
                <Icon name="sparkle" size={12}/>
                <span>この写真を使用 · AI解析へ</span>
              </button>
            </>
          ) : (
            <>
              <button
                className="btn ghost"
                onClick={switchFacing}
                disabled={!ready}
                title="フロント / リア カメラを切替"
                style={{opacity: hasMultiple || !ready ? 1 : 0.5}}
              >
                <Icon name="cameraFlip" size={12}/>
                <span>{facing === "environment" ? "フロント" : "リア"} に切替</span>
              </button>
              <div style={{flex:1, textAlign:"center", fontFamily:"var(--f-mono)", fontSize:10, letterSpacing:"0.14em", color:"var(--smoke)"}}>
                {ready ? "SPACE で撮影 · ESC で閉じる" : ""}
              </div>
              <button className="btn ghost" onClick={onClose}>キャンセル</button>
              <button
                className="btn gold cam-shutter"
                onClick={capture}
                disabled={!ready || capturing}
              >
                <span className="cam-shutter-dot"/>
                <span>{capturing ? "撮影中…" : "撮影"}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Fullscreen zoomable lightbox ─────────────────────────────────────────────
// Opened from the detail overlay. Click / scroll to zoom, drag to pan.
const ZOOM_MIN = 1;
const ZOOM_MAX = 6;

const ScreenshotLightbox = ({ src, title, w, h, onClose }) => {
  const [scale, setScale] = React.useState(1);
  const [tx, setTx] = React.useState(0);
  const [ty, setTy] = React.useState(0);
  const dragRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);

  const reset = React.useCallback(() => { setScale(1); setTx(0); setTy(0); }, []);

  const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

  const zoomBy = (factor) => {
    setScale(prev => {
      const next = clampZoom(prev * factor);
      if (next === 1) { setTx(0); setTy(0); }
      return next;
    });
  };

  // ESC to close, +/- to zoom, 0 to reset
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); }
      else if (e.key === "+" || e.key === "=") { e.preventDefault(); zoomBy(1.4); }
      else if (e.key === "-" || e.key === "_") { e.preventDefault(); zoomBy(1/1.4); }
      else if (e.key === "0") { e.preventDefault(); reset(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, reset]);

  const onWheel = (e) => {
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? 1.12 : 1/1.12);
  };

  const onImgClick = (e) => {
    e.stopPropagation();
    // Toggle between fit and a comfortable 2.5x zoom
    if (scale > 1.05) { reset(); }
    else { setScale(2.5); }
  };

  const onPointerDown = (e) => {
    if (scale <= 1.02) return;
    e.preventDefault();
    dragRef.current = { x: e.clientX, y: e.clientY, tx, ty };
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setTx(dragRef.current.tx + (e.clientX - dragRef.current.x));
    setTy(dragRef.current.ty + (e.clientY - dragRef.current.y));
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    setDragging(false);
    e.currentTarget?.releasePointerCapture?.(e.pointerId);
  };

  const zoomed = scale > 1.02;

  return (
    <div className="ss-lightbox" onClick={onClose}>
      <div className="ss-lightbox-bar" onClick={e => e.stopPropagation()}>
        <span className="eyebrow" style={{margin:0}}>{title || "SCREENSHOT"}</span>
        {w && h && <span className="chip" style={{fontFamily:"var(--f-mono)", fontSize:10}}>{w} × {h}</span>}
        <div style={{flex:1}}/>
        <button className="icon-btn" onClick={() => zoomBy(1/1.4)} title="縮小 ( − )"><Icon name="minus" size={16}/></button>
        <span className="ss-zoom-pct">{Math.round(scale * 100)}%</span>
        <button className="icon-btn" onClick={() => zoomBy(1.4)} title="拡大 ( + )"><Icon name="plus" size={16}/></button>
        <button className="icon-btn" onClick={reset} title="リセット ( 0 )" disabled={!zoomed} style={{opacity: zoomed ? 1 : 0.4}}><Icon name="refresh" size={15}/></button>
        <button className="icon-btn" onClick={onClose} title="閉じる ( Esc )"><Icon name="x" size={16}/></button>
      </div>
      <div
        className={"ss-lightbox-canvas" + (zoomed ? " zoomed" : "") + (dragging ? " dragging" : "")}
        onWheel={onWheel}
        onClick={e => { if (e.target.classList.contains("ss-lightbox-canvas")) onClose(); }}
      >
        <img
          src={src}
          alt={title || ""}
          draggable={false}
          style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})` }}
          onClick={onImgClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      </div>
      <div className="ss-lightbox-help" onClick={e => e.stopPropagation()}>
        クリックで拡大 / 縮小 · ドラッグで移動 · スクロールでズーム · Esc で閉じる
      </div>
    </div>
  );
};

Object.assign(window, {
  ScreenshotsView,
  CameraCapture,
  // exported for unit tests / debugging
  _ssNormalizeForMatch,
  matchPeopleToContacts,
  buildIdentifiedContactsBlock,
  buildMemoFromAI,
});


;/* ==== kpi.jsx ==== */
// KPI view — full CRUD on metrics
const FUNNEL_SCHEMA = {
  eyebrow: "FUNNEL STAGE",
  title: "Funnel Stage",
  titleNew: "New Stage",
  titleEdit: "Edit Stage",
  fields: [
    { key: "stage", label: "Stage name / ステージ名", type: "text", required: true, span: 2, placeholder: "例: 応募 / 書類通過 / 1次面接…" },
    { key: "count", label: "Actual / 実績", type: "number", min: 0, max: 100000 },
    { key: "target", label: "Target / 目標", type: "number", min: 0, max: 100000 },
    { key: "rate", label: "Pass rate / 通過率(任意)", type: "text", placeholder: "例: 52% / —" },
  ],
};

const KpiView = ({ state, setState }) => {
  const k = state.kpi;
  const maxFunnel = Math.max(...k.funnel.map(f => f.count), 1);

  const [funnelModal, setFunnelModal] = React.useState(false);
  const [editingStage, setEditingStage] = React.useState(null);
  const [editScalars, setEditScalars] = React.useState(false);
  const [scalars, setScalars] = React.useState({});

  const setKpi = (path, val) => {
    setState(s => {
      const next = JSON.parse(JSON.stringify(s.kpi));
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = val;
      return { ...s, kpi: next };
    });
  };

  // Funnel CRUD
  const openNewStage = () => { setEditingStage(null); setFunnelModal(true); };
  const openEditStage = (idx) => { setEditingStage({ ...k.funnel[idx], __idx: idx }); setFunnelModal(true); };
  const saveStage = (vals) => {
    setState(s => {
      const nf = [...s.kpi.funnel];
      if (editingStage && editingStage.__idx !== undefined) {
        nf[editingStage.__idx] = { stage: vals.stage, count: Number(vals.count) || 0, target: Number(vals.target) || 0, rate: vals.rate || "—" };
      } else {
        nf.push({ stage: vals.stage, count: Number(vals.count) || 0, target: Number(vals.target) || 0, rate: vals.rate || "—" });
      }
      return { ...s, kpi: { ...s.kpi, funnel: nf } };
    });
    setFunnelModal(false);
  };
  const deleteStage = () => {
    if (!editingStage || editingStage.__idx === undefined) return;
    setState(s => ({ ...s, kpi: { ...s.kpi, funnel: s.kpi.funnel.filter((_, i) => i !== editingStage.__idx) } }));
    setFunnelModal(false);
  };

  // Edit all scalar values at once
  const openScalarEditor = () => {
    setScalars({
      leaveRateNow: k.leaveRate.now,
      leaveRateTarget: k.leaveRate.target2028,
      leaveRateIndustry: k.leaveRate.industryAvg,
      headcountCurrent: k.headcount.current,
      headcountPlan2025: k.headcount.plan2025,
      headcountPlan2031: k.headcount.plan2031,
      salesPlan: k.sales.plan,
      salesOndo: k.sales.ondo,
      salesOndoEx: k.sales.ondoEx,
      salesBroker: k.sales.broker,
    });
    setEditScalars(true);
  };
  const saveScalars = () => {
    setState(s => ({
      ...s,
      kpi: {
        ...s.kpi,
        leaveRate: {
          now: Number(scalars.leaveRateNow) || 0,
          target2028: Number(scalars.leaveRateTarget) || 0,
          industryAvg: Number(scalars.leaveRateIndustry) || 0,
        },
        headcount: {
          current: Number(scalars.headcountCurrent) || 0,
          plan2025: Number(scalars.headcountPlan2025) || 0,
          plan2031: Number(scalars.headcountPlan2031) || 0,
        },
        sales: {
          plan: Number(scalars.salesPlan) || 0,
          ondo: Number(scalars.salesOndo) || 0,
          ondoEx: Number(scalars.salesOndoEx) || 0,
          broker: Number(scalars.salesBroker) || 0,
          unit: s.kpi.sales.unit,
        },
      },
    }));
    setEditScalars(false);
  };

  return (
    <div className="view-fade">
      <div className="dash-grid-3 mb-3">
        <div className="panel kpi-tile" style={{padding: 22, cursor:"pointer"}} onClick={openScalarEditor}>
          <div className="kpi-tile-head">
            <span className="kpi-tile-label">Attrition (proj.)</span>
            <Icon name="flame" size={16} color="var(--crimson-hi)"/>
          </div>
          <div className="kpi-tile-value">{k.leaveRate.now}<span className="kpi-tile-unit">%</span></div>
          <div style={{display:"flex", gap:8, marginTop:10, fontSize:11, color:"var(--ash)"}}>
            <span>Industry avg: {k.leaveRate.industryAvg}%</span>
            <span style={{color:"var(--crimson-hi)"}}>{k.leaveRate.now > k.leaveRate.industryAvg ? "+" : ""}{(k.leaveRate.now - k.leaveRate.industryAvg).toFixed(1)}pt</span>
          </div>
          <svg viewBox="0 0 200 36" className="kpi-spark">
            <polyline points="0,28 30,24 60,18 90,22 120,14 150,10 180,12 200,8"
              fill="none" stroke="var(--crimson)" strokeWidth="2"/>
            <polyline points="0,28 30,24 60,18 90,22 120,14 150,10 180,12 200,8"
              fill="none" stroke="var(--crimson)" strokeWidth="6" opacity="0.2"/>
          </svg>
          <div style={{fontSize:11, color:"var(--smoke)", marginTop:6, fontFamily:"var(--f-mono)", letterSpacing:"0.08em"}}>2028 Target: {k.leaveRate.target2028}% ↓</div>
        </div>

        <div className="panel kpi-tile" style={{padding: 22, cursor:"pointer"}} onClick={openScalarEditor}>
          <div className="kpi-tile-head">
            <span className="kpi-tile-label">Headcount</span>
            <Icon name="users" size={16} color="var(--crystal)"/>
          </div>
          <div className="kpi-tile-value">{k.headcount.current}<span className="kpi-tile-unit">staff</span></div>
          <div style={{display:"flex", gap:8, marginTop:10, fontSize:11, color:"var(--ash)"}}>
            <span>2025 plan: {k.headcount.plan2025}</span>
            <span style={{color:"var(--crimson-hi)"}}>{k.headcount.current < k.headcount.plan2025 ? "−" : "+"}{Math.abs(k.headcount.plan2025 - k.headcount.current)}</span>
          </div>
          <div style={{marginTop:14, padding:10, background:"rgba(91,123,196,0.06)", border:"1px solid var(--hairline)", borderRadius:6}}>
            <div style={{fontSize:10, color:"var(--smoke)", letterSpacing:"0.08em", fontFamily:"var(--f-mono)"}}>2031 PLAN</div>
            <div style={{fontFamily:"var(--f-display)", fontSize:18, color:"var(--gold)", marginTop:2, letterSpacing:"0.04em"}}>{k.headcount.plan2031} staff</div>
          </div>
        </div>

        <div className="panel kpi-tile" style={{padding: 22, cursor:"pointer"}} onClick={openScalarEditor}>
          <div className="kpi-tile-head">
            <span className="kpi-tile-label">Revenue Plan (annual)</span>
            <Icon name="chart" size={16} color="var(--gold)"/>
          </div>
          <div className="kpi-tile-value">¥{k.sales.plan}<span className="kpi-tile-unit">00M</span></div>
          <div style={{marginTop:14, display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8}}>
            <div style={{padding:8, background:"rgba(245,197,24,0.06)", border:"1px solid rgba(245,197,24,0.20)", borderRadius:4, textAlign:"center"}}>
              <div style={{fontFamily:"var(--f-mono)", fontSize:9, color:"var(--gold)", letterSpacing:"0.12em"}}>ONDO</div>
              <div style={{fontFamily:"var(--f-display)", fontSize:18, color:"var(--ivory)", marginTop:4}}>¥{k.sales.ondo}00M</div>
            </div>
            <div style={{padding:8, background:"rgba(91,123,196,0.06)", border:"1px solid var(--hairline)", borderRadius:4, textAlign:"center"}}>
              <div style={{fontFamily:"var(--f-mono)", fontSize:9, color:"var(--crystal)", letterSpacing:"0.12em"}}>OTHER</div>
              <div style={{fontFamily:"var(--f-display)", fontSize:18, color:"var(--ivory)", marginTop:4}}>¥{k.sales.ondoEx}00M</div>
            </div>
            <div style={{padding:8, background:"rgba(91,123,196,0.06)", border:"1px solid var(--hairline)", borderRadius:4, textAlign:"center"}}>
              <div style={{fontFamily:"var(--f-mono)", fontSize:9, color:"var(--crystal)", letterSpacing:"0.12em"}}>BROKER</div>
              <div style={{fontFamily:"var(--f-display)", fontSize:18, color:"var(--ivory)", marginTop:4}}>¥{k.sales.broker}00M</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{textAlign:"center", marginBottom: 18, color:"var(--smoke)", fontSize:11, fontStyle:"italic"}}>
        ↑ KPIタイルをクリックすると数値を編集できます
      </div>

      {/* Recruitment funnel */}
      <div className="panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">RECRUITMENT FUNNEL · This Month</span>
            <h3>Hiring Funnel</h3>
          </div>
          <div className="panel-head-meta">
            <button className="btn gold sm" onClick={openNewStage}><Icon name="plus" size={12}/><span>Add Stage</span></button>
            <button className="btn ghost sm" onClick={() => {
              const rows = [["Stage","Actual","Target","Rate"], ...k.funnel.map(f => [f.stage, f.count, f.target, f.rate])];
              const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
              const blob = new Blob([new Uint8Array([0xEF,0xBB,0xBF]), csv], { type: "text/csv;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = `hiring-funnel-${new Date().toISOString().slice(0,10)}.csv`;
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }}><Icon name="download" size={12}/><span>Export CSV</span></button>
          </div>
        </div>
        <div className="panel-body">
          {k.funnel.map((f, i) => (
            <div key={i} className="funnel-stage" onClick={() => openEditStage(i)} style={{cursor:"pointer"}}>
              <div className="funnel-stage-label">{f.stage}</div>
              <div className="funnel-bar-wrap">
                <div className="funnel-bar" style={{width: (f.count / maxFunnel * 100) + "%"}}>
                  {f.count}
                </div>
              </div>
              <div className="funnel-rate">{f.rate}</div>
              <div className="funnel-target">/ {f.target}<br/><span style={{color:"var(--ash)", fontSize:9}}>target</span></div>
            </div>
          ))}
          {k.funnel.length === 0 && (
            <div className="empty-state"><h4>No funnel stages</h4><div>Click "Add Stage" to create one.</div></div>
          )}
        </div>
      </div>

      <EntityModal
        open={funnelModal}
        schema={FUNNEL_SCHEMA}
        initial={editingStage}
        onSave={saveStage}
        onDelete={editingStage && editingStage.__idx !== undefined ? deleteStage : null}
        onClose={() => setFunnelModal(false)}
      />

      {editScalars && (
        <div className="modal-overlay" onClick={(e) => { if (e.target.classList.contains("modal-overlay")) setEditScalars(false); }}>
          <div className="modal-stage">
            <div className="modal-head">
              <div style={{flex:1}}>
                <span className="eyebrow">METRICS</span>
                <h2 className="modal-title">Edit KPI Values</h2>
              </div>
              <button className="icon-btn" onClick={() => setEditScalars(false)} title="閉じる" aria-label="閉じる"><Icon name="x" size={18}/></button>
            </div>
            <div className="modal-body">
              <div style={{fontFamily:"var(--f-title)", fontSize:11, letterSpacing:"0.20em", color:"var(--gold)", marginBottom:10, fontWeight:600}}>ATTRITION / 離職率 (%)</div>
              <div className="form-grid" style={{marginBottom:24}}>
                <div className="form-field">
                  <label className="form-label">Now</label>
                  <input type="number" step="0.1" className="form-input" value={scalars.leaveRateNow} onChange={e => setScalars(s => ({ ...s, leaveRateNow: e.target.value }))}/>
                </div>
                <div className="form-field">
                  <label className="form-label">2028 Target</label>
                  <input type="number" step="0.1" className="form-input" value={scalars.leaveRateTarget} onChange={e => setScalars(s => ({ ...s, leaveRateTarget: e.target.value }))}/>
                </div>
                <div className="form-field span-2">
                  <label className="form-label">Industry Average</label>
                  <input type="number" step="0.1" className="form-input" value={scalars.leaveRateIndustry} onChange={e => setScalars(s => ({ ...s, leaveRateIndustry: e.target.value }))}/>
                </div>
              </div>

              <div style={{fontFamily:"var(--f-title)", fontSize:11, letterSpacing:"0.20em", color:"var(--gold)", marginBottom:10, fontWeight:600}}>HEADCOUNT / 人員 (人)</div>
              <div className="form-grid" style={{marginBottom:24}}>
                <div className="form-field">
                  <label className="form-label">Current / 現在</label>
                  <input type="number" className="form-input" value={scalars.headcountCurrent} onChange={e => setScalars(s => ({ ...s, headcountCurrent: e.target.value }))}/>
                </div>
                <div className="form-field">
                  <label className="form-label">2025 Plan</label>
                  <input type="number" className="form-input" value={scalars.headcountPlan2025} onChange={e => setScalars(s => ({ ...s, headcountPlan2025: e.target.value }))}/>
                </div>
                <div className="form-field span-2">
                  <label className="form-label">2031 Plan</label>
                  <input type="number" className="form-input" value={scalars.headcountPlan2031} onChange={e => setScalars(s => ({ ...s, headcountPlan2031: e.target.value }))}/>
                </div>
              </div>

              <div style={{fontFamily:"var(--f-title)", fontSize:11, letterSpacing:"0.20em", color:"var(--gold)", marginBottom:10, fontWeight:600}}>REVENUE / 売上 (億)</div>
              <div className="form-grid">
                <div className="form-field span-2">
                  <label className="form-label">Total annual plan</label>
                  <input type="number" className="form-input" value={scalars.salesPlan} onChange={e => setScalars(s => ({ ...s, salesPlan: e.target.value }))}/>
                </div>
                <div className="form-field">
                  <label className="form-label">ondo</label>
                  <input type="number" className="form-input" value={scalars.salesOndo} onChange={e => setScalars(s => ({ ...s, salesOndo: e.target.value }))}/>
                </div>
                <div className="form-field">
                  <label className="form-label">ondo以外</label>
                  <input type="number" className="form-input" value={scalars.salesOndoEx} onChange={e => setScalars(s => ({ ...s, salesOndoEx: e.target.value }))}/>
                </div>
                <div className="form-field span-2">
                  <label className="form-label">仲介</label>
                  <input type="number" className="form-input" value={scalars.salesBroker} onChange={e => setScalars(s => ({ ...s, salesBroker: e.target.value }))}/>
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <div style={{flex:1}}/>
              <button className="btn ghost" onClick={() => setEditScalars(false)}>Cancel</button>
              <button className="btn primary" onClick={saveScalars}>
                <Icon name="check" size={12}/>
                <span>Save</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

Object.assign(window, { KpiView });


;/* ==== contacts.jsx ==== */
// Contacts view — full CRUD + 名刺スキャナ(カメラ → Claude Vision で直接抽出)
const CONTACT_SCHEMA = {
  eyebrow: "CONTACT",
  title: "Contact",
  titleNew: "新規連絡先",
  titleEdit: "連絡先編集",
  headerImageKey: "cardImage",
  headerImageLabel: "BUSINESS CARD",
  fields: [
    { key: "name", label: "氏名", type: "text", required: true },
    { key: "kana", label: "ふりがな", type: "text" },
    { key: "role", label: "役職", type: "text" },
    { key: "department", label: "部署", type: "text" },
    { key: "company", label: "会社・所属", type: "text", span: 2 },
    { key: "email", label: "メール", type: "text" },
    { key: "phone", label: "電話", type: "text" },
    { key: "mobile", label: "携帯", type: "text" },
    { key: "fax", label: "FAX", type: "text" },
    { key: "address", label: "住所", type: "text", span: 2 },
    { key: "website", label: "URL", type: "text" },
    { key: "last", label: "名刺登録日", type: "text", placeholder: "YYYY/MM/DD" },
    { key: "note", label: "メモ", type: "textarea", span: 2 },
  ],
};

// Resize/compress a dataURL image so the payload is reasonable for the model
const downscaleDataURL = (dataURL, maxEdge = 1280, quality = 0.85) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    c.getContext("2d").drawImage(img, 0, 0, w, h);
    resolve(c.toDataURL("image/jpeg", quality));
  };
  img.onerror = () => resolve(dataURL);
  img.src = dataURL;
});

// Crop a dataURL using a normalized bbox {x, y, width, height} (0–1) and return a new dataURL.
// If bbox is invalid or missing, returns null.
// `pad` adds breathing room per side for AI-estimated boxes; pass 0 for
// user-drawn rectangles (WYSIWYG).
const cropDataURL = (dataURL, bbox, outMaxEdge = 720, pad = 0.05) => new Promise((resolve) => {
  if (!bbox || typeof bbox.x !== "number") return resolve(null);
  const img = new Image();
  img.onload = () => {
    // Clamp bbox to [0, 1]
    let x = Math.max(0, Math.min(1, bbox.x));
    let y = Math.max(0, Math.min(1, bbox.y));
    let w = Math.max(0, Math.min(1 - x, bbox.width));
    let h = Math.max(0, Math.min(1 - y, bbox.height));
    // AI bboxes often sit slightly inside the card edge, so err generously
    // toward including background.
    const padX = Math.min(pad, x, 1 - (x + w));
    const padY = Math.min(pad, y, 1 - (y + h));
    x -= padX; y -= padY; w += padX * 2; h += padY * 2;
    if (w < 0.05 || h < 0.05) return resolve(null);
    const sx = Math.round(x * img.width);
    const sy = Math.round(y * img.height);
    const sw = Math.round(w * img.width);
    const sh = Math.round(h * img.height);
    const scale = Math.min(1, outMaxEdge / Math.max(sw, sh));
    const dw = Math.round(sw * scale);
    const dh = Math.round(sh * scale);
    const c = document.createElement("canvas");
    c.width = dw; c.height = dh;
    c.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, dw, dh);
    resolve(c.toDataURL("image/jpeg", 0.88));
  };
  img.onerror = () => resolve(null);
  img.src = dataURL;
});

// ────────────────────────────────────────────────────────────
// Perspective dewarp — 4 corner points → rectified rectangle
// ────────────────────────────────────────────────────────────

// Gaussian elimination on an n×n linear system (returns x or null if singular)
const _solveLinear = (A, b) => {
  const n = A.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let i = 0; i < n; i++) {
    let p = i;
    for (let j = i + 1; j < n; j++) if (Math.abs(M[j][i]) > Math.abs(M[p][i])) p = j;
    if (Math.abs(M[p][i]) < 1e-12) return null;
    [M[i], M[p]] = [M[p], M[i]];
    for (let j = 0; j < n; j++) {
      if (j === i) continue;
      const f = M[j][i] / M[i][i];
      for (let k = i; k <= n; k++) M[j][k] -= f * M[i][k];
    }
  }
  return M.map(row => row[n] / row[M.indexOf(row)] /* placeholder */);
};
// More robust: pull diagonal explicitly
const solveLinear = (A, b) => {
  const n = A.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let i = 0; i < n; i++) {
    let p = i;
    for (let j = i + 1; j < n; j++) if (Math.abs(M[j][i]) > Math.abs(M[p][i])) p = j;
    if (Math.abs(M[p][i]) < 1e-12) return null;
    [M[i], M[p]] = [M[p], M[i]];
    for (let j = 0; j < n; j++) {
      if (j === i) continue;
      const f = M[j][i] / M[i][i];
      for (let k = i; k <= n; k++) M[j][k] -= f * M[i][k];
    }
  }
  const x = new Array(n);
  for (let i = 0; i < n; i++) x[i] = M[i][n] / M[i][i];
  return x;
};

// Find 3×3 homography mapping src[i] (4 pts) → dst[i] (4 pts).
// Returns flat array of length 9, or null.
const solveHomography = (src, dst) => {
  const A = [], b = [];
  for (let i = 0; i < 4; i++) {
    const sx = src[i].x, sy = src[i].y;
    const dx = dst[i].x, dy = dst[i].y;
    A.push([sx, sy, 1, 0, 0, 0, -dx * sx, -dx * sy]);
    b.push(dx);
    A.push([0, 0, 0, sx, sy, 1, -dy * sx, -dy * sy]);
    b.push(dy);
  }
  const h = solveLinear(A, b);
  if (!h) return null;
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
};

const applyH = (H, x, y) => {
  const w = H[6] * x + H[7] * y + H[8];
  return { x: (H[0] * x + H[1] * y + H[2]) / w, y: (H[3] * x + H[4] * y + H[5]) / w };
};

// Affine [a,b,c,d,e,f] taking 3 src points to 3 dst points.
// setTransform(a,b,c,d,e,f) makes drawing at src appear at dst.
const affineFrom3 = (s0, s1, s2, d0, d1, d2) => {
  const det = (s1.x - s0.x) * (s2.y - s0.y) - (s2.x - s0.x) * (s1.y - s0.y);
  if (Math.abs(det) < 1e-9) return null;
  const inv = 1 / det;
  const a = ((d1.x - d0.x) * (s2.y - s0.y) - (d2.x - d0.x) * (s1.y - s0.y)) * inv;
  const c = ((d2.x - d0.x) * (s1.x - s0.x) - (d1.x - d0.x) * (s2.x - s0.x)) * inv;
  const b = ((d1.y - d0.y) * (s2.y - s0.y) - (d2.y - d0.y) * (s1.y - s0.y)) * inv;
  const d = ((d2.y - d0.y) * (s1.x - s0.x) - (d1.y - d0.y) * (s2.x - s0.x)) * inv;
  const e = d0.x - a * s0.x - c * s0.y;
  const f = d0.y - b * s0.x - d * s0.y;
  return [a, b, c, d, e, f];
};

// Draw a triangle of `img` (source coords) onto ctx (dest coords) with affine warp.
const drawWarpedTri = (ctx, img, s0, s1, s2, d0, d1, d2) => {
  const m = affineFrom3(s0, s1, s2, d0, d1, d2);
  if (!m) return;
  ctx.save();
  ctx.beginPath();
  // Expand triangle slightly to hide seams between cells
  const cx = (d0.x + d1.x + d2.x) / 3, cy = (d0.y + d1.y + d2.y) / 3;
  const exp = (p) => ({ x: p.x + (p.x - cx) * 0.003, y: p.y + (p.y - cy) * 0.003 });
  const e0 = exp(d0), e1 = exp(d1), e2 = exp(d2);
  ctx.moveTo(e0.x, e0.y); ctx.lineTo(e1.x, e1.y); ctx.lineTo(e2.x, e2.y); ctx.closePath();
  ctx.clip();
  ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
  ctx.drawImage(img, 0, 0);
  ctx.restore();
};

// Expand a 4-point quad outward from its centroid. AI corner estimates tend to
// land slightly INSIDE the physical card edge, which used to shave off the
// card's border/text — a few % of background bleed is always safer than a cut.
// Points are normalized 0–1 and get clamped back into the image.
const expandQuad = (corners, factor = 1.08) => {
  const cx = (corners[0].x + corners[1].x + corners[2].x + corners[3].x) / 4;
  const cy = (corners[0].y + corners[1].y + corners[2].y + corners[3].y) / 4;
  return corners.map((p) => ({
    x: Math.max(0, Math.min(1, cx + (p.x - cx) * factor)),
    y: Math.max(0, Math.min(1, cy + (p.y - cy) * factor)),
  }));
};

// A homography maps a rectangle onto a CONVEX quad only — a concave/twisted
// quad makes the warp sample garbage (black/duplicated regions), so both the
// AI path and the manual adjuster must reject those.
const isConvexQuad = (pts) => {
  let sign = 0;
  for (let i = 0; i < 4; i++) {
    const a = pts[i], b = pts[(i + 1) % 4], c = pts[(i + 2) % 4];
    const z = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(z) < 1e-9) continue;
    if (sign === 0) sign = Math.sign(z);
    else if (Math.sign(z) !== sign) return false;
  }
  return sign !== 0;
};

// Validate that 4 corner points form a sensible convex non-degenerate quad.
const validCorners = (corners) => {
  if (!Array.isArray(corners) || corners.length !== 4) return false;
  for (const p of corners) {
    if (!p || typeof p.x !== "number" || typeof p.y !== "number") return false;
    if (p.x < -0.05 || p.x > 1.05 || p.y < -0.05 || p.y > 1.05) return false;
  }
  // Check minimum area (use cross-product based shoelace)
  const [a, b, c, d] = corners;
  const area = Math.abs((a.x * b.y - b.x * a.y) + (b.x * c.y - c.x * b.y) + (c.x * d.y - d.x * c.y) + (d.x * a.y - a.x * d.y)) / 2;
  if (area <= 0.05) return false;
  return isConvexQuad(corners);
};

// Perspective-dewarp the card from 4 corners (normalized 0–1, order TL TR BR BL)
// into a rectangle. Returns dataURL or null.
// `expand` > 1 adds the expandQuad safety margin (AI-estimated corners);
// pass 1 for user-adjusted corners — those are WYSIWYG.
const dewarpCardFromCorners = (dataURL, corners, outMaxEdge = 1100, expand = 1) => new Promise((resolve) => {
  if (!validCorners(corners)) return resolve(null);
  const expanded = expand > 1 ? expandQuad(corners, expand) : corners;
  const img = new Image();
  img.onload = () => {
    // Convert normalized → pixel coords
    const W = img.width, H = img.height;
    const pts = expanded.map(p => ({ x: p.x * W, y: p.y * H }));
    const [TL, TR, BR, BL] = pts;

    // Estimate output aspect by averaging top/bottom widths and left/right heights
    const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    const topW = dist(TL, TR), botW = dist(BL, BR);
    const leftH = dist(TL, BL), rightH = dist(TR, BR);
    let outW = (topW + botW) / 2;
    let outH = (leftH + rightH) / 2;
    if (outW < 10 || outH < 10) return resolve(null);
    // Cap output resolution
    const scale = Math.min(1, outMaxEdge / Math.max(outW, outH));
    outW = Math.max(80, Math.round(outW * scale));
    outH = Math.max(50, Math.round(outH * scale));

    // Destination rectangle (axis-aligned) corners
    const dstRect = [
      { x: 0,    y: 0    }, // TL
      { x: outW, y: 0    }, // TR
      { x: outW, y: outH }, // BR
      { x: 0,    y: outH }, // BL
    ];

    // Homography mapping dest-rect coords → source quad coords (for mesh sampling)
    const H_d2s = solveHomography(dstRect, pts);
    if (!H_d2s) return resolve(null);

    const canvas = document.createElement("canvas");
    canvas.width = outW; canvas.height = outH;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    // Subtle off-white backing
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, outW, outH);

    // Mesh subdivision: more cells = closer to true perspective sampling
    const N = 28;
    for (let i = 0; i < N; i++) {
      for (let j = 0; j < N; j++) {
        const x0 = (i / N) * outW, x1 = ((i + 1) / N) * outW;
        const y0 = (j / N) * outH, y1 = ((j + 1) / N) * outH;
        const d00 = { x: x0, y: y0 }, d10 = { x: x1, y: y0 };
        const d11 = { x: x1, y: y1 }, d01 = { x: x0, y: y1 };
        const s00 = applyH(H_d2s, d00.x, d00.y);
        const s10 = applyH(H_d2s, d10.x, d10.y);
        const s11 = applyH(H_d2s, d11.x, d11.y);
        const s01 = applyH(H_d2s, d01.x, d01.y);
        drawWarpedTri(ctx, img, s00, s10, s11, d00, d10, d11);
        drawWarpedTri(ctx, img, s00, s11, s01, d00, d11, d01);
      }
    }
    resolve(canvas.toDataURL("image/jpeg", 0.9));
  };
  img.onerror = () => resolve(null);
  img.src = dataURL;
});

const EMPTY_PARSED = { name:"", kana:"", role:"", company:"", department:"", email:"", phone:"", mobile:"", fax:"", address:"", website:"", note:"" };

// Build a TL TR BR BL quad from an axis-aligned bbox (normalized), or null.
const quadFromBbox = (b) => {
  if (!b || typeof b.x !== "number" || typeof b.width !== "number") return null;
  const clamp = (v) => Math.max(0, Math.min(1, v));
  const x0 = clamp(b.x), y0 = clamp(b.y);
  const x1 = clamp(b.x + b.width), y1 = clamp(b.y + b.height);
  if (x1 - x0 < 0.05 || y1 - y0 < 0.05) return null;
  return [
    { x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 },
  ];
};

const DEFAULT_QUAD = [
  { x: 0.1, y: 0.1 }, { x: 0.9, y: 0.1 }, { x: 0.9, y: 0.9 }, { x: 0.1, y: 0.9 },
];

// Manual crop adjustment — a single RECTANGLE manipulated as a unit:
// drag inside the frame to move it, drag a corner handle to resize.
// (Cards are always rectangular; per-corner quad editing was too fiddly.)
// WYSIWYG: exactly the highlighted rectangle is cropped (no extra margin).
const RECT_MIN = 0.08; // minimum rect edge (normalized)

const RectAdjuster = ({ src, initial, onApply, onCancel }) => {
  // Seed from the bounding box of the quad behind the current crop
  const [rect, setRect] = React.useState(() => {
    const q = Array.isArray(initial) && initial.length === 4 ? initial : DEFAULT_QUAD;
    const xs = q.map((p) => p.x), ys = q.map((p) => p.y);
    return {
      x0: Math.max(0, Math.min(...xs)), y0: Math.max(0, Math.min(...ys)),
      x1: Math.min(1, Math.max(...xs)), y1: Math.min(1, Math.max(...ys)),
    };
  });
  const [busy, setBusy] = React.useState(false);
  const boxRef = React.useRef(null);
  const drag = React.useRef(null); // { mode: "move", start, rect0 } | { mode: "corner", idx }

  const toNorm = (e) => {
    const r = boxRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)),
      y: Math.max(0, Math.min(1, (e.clientY - r.top) / r.height)),
    };
  };
  const capture = (e) => { try { e.currentTarget.setPointerCapture(e.pointerId); } catch {} };
  const onMoveDown = (e) => {
    e.preventDefault();
    drag.current = { mode: "move", start: toNorm(e), rect0: { ...rect } };
    capture(e);
  };
  const onCornerDown = (i) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    drag.current = { mode: "corner", idx: i };
    capture(e);
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const p = toNorm(e);
    if (d.mode === "move") {
      // whole-frame translation, clamped inside the photo, size preserved
      const w = d.rect0.x1 - d.rect0.x0, h = d.rect0.y1 - d.rect0.y0;
      const x0 = Math.max(0, Math.min(1 - w, d.rect0.x0 + (p.x - d.start.x)));
      const y0 = Math.max(0, Math.min(1 - h, d.rect0.y0 + (p.y - d.start.y)));
      setRect({ x0, y0, x1: x0 + w, y1: y0 + h });
    } else {
      // corner resize: 0 TL, 1 TR, 2 BR, 3 BL — opposite corner stays anchored
      setRect((r) => {
        const n = { ...r };
        if (d.idx === 0 || d.idx === 3) n.x0 = Math.min(p.x, r.x1 - RECT_MIN);
        else n.x1 = Math.max(p.x, r.x0 + RECT_MIN);
        if (d.idx === 0 || d.idx === 1) n.y0 = Math.min(p.y, r.y1 - RECT_MIN);
        else n.y1 = Math.max(p.y, r.y0 + RECT_MIN);
        return n;
      });
    }
  };
  const onUp = () => { drag.current = null; };

  const apply = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onApply({ x: rect.x0, y: rect.y0, width: rect.x1 - rect.x0, height: rect.y1 - rect.y0 });
    } finally { setBusy(false); }
  };

  const corners = [
    { x: rect.x0, y: rect.y0 }, { x: rect.x1, y: rect.y0 },
    { x: rect.x1, y: rect.y1 }, { x: rect.x0, y: rect.y1 },
  ];
  const R = [rect.x0 * 1000, rect.y0 * 1000, rect.x1 * 1000, rect.y1 * 1000];
  const rectPath = `M${R[0]},${R[1]} L${R[2]},${R[1]} L${R[2]},${R[3]} L${R[0]},${R[3]} Z`;

  return (
    <>
      <div style={{fontSize:12, color:"var(--ash)", marginBottom:10, textAlign:"center"}}>
        枠の中をドラッグで移動、◯ で大きさ調整。明るい範囲がそのまま切り抜かれます。
      </div>
      <div style={{display:"flex", justifyContent:"center"}}>
        <div
          ref={boxRef}
          className="corner-adjust-stage"
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        >
          <img src={src} alt="" draggable={false} style={{display:"block", maxWidth:"100%", maxHeight:"56vh"}}/>
          <svg viewBox="0 0 1000 1000" preserveAspectRatio="none" style={{position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none"}}>
            <path d={`M0,0 H1000 V1000 H0 Z ${rectPath}`} fill="rgba(2,6,18,0.6)" fillRule="evenodd"/>
            <path d={rectPath} fill="none" stroke="var(--gold)" strokeWidth="2" vectorEffect="non-scaling-stroke"/>
          </svg>
          <div
            className="rect-move"
            onPointerDown={onMoveDown}
            style={{
              left: `${rect.x0 * 100}%`,
              top: `${rect.y0 * 100}%`,
              width: `${(rect.x1 - rect.x0) * 100}%`,
              height: `${(rect.y1 - rect.y0) * 100}%`,
            }}
          />
          {corners.map((p, i) => (
            <div
              key={i}
              className="corner-handle"
              onPointerDown={onCornerDown(i)}
              style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
            />
          ))}
        </div>
      </div>
      <div style={{display:"flex", justifyContent:"center", gap:10, marginTop:14}}>
        <button className="btn ghost" onClick={onCancel} disabled={busy}><span>キャンセル</span></button>
        <button className="btn gold" onClick={apply} disabled={busy}>
          <Icon name="check" size={12}/>
          <span>{busy ? "切り抜き中…" : "この範囲で切り抜く"}</span>
        </button>
      </div>
    </>
  );
};

const CardScanner = ({ open, onClose, onParsed }) => {
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const [phase, setPhase] = React.useState("camera"); // camera | preview | analyzing | done
  const [previewSrc, setPreviewSrc] = React.useState(null);
  const [permissionDenied, setPermissionDenied] = React.useState(false);
  const [parsed, setParsed] = React.useState(null);
  const [cardImage, setCardImage] = React.useState(null);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [zoomSrc, setZoomSrc] = React.useState(null);
  const [fullImage, setFullImage] = React.useState(null); // downscaled full photo (manual fallback)
  const [useOriginal, setUseOriginal] = React.useState(false); // user chose full photo over the crop
  const [cardCorners, setCardCorners] = React.useState(null); // quad behind the current crop (seed for manual adjust)

  // Start the camera (called on open + on "retake")
  const startCamera = React.useCallback(async () => {
    setPermissionDenied(false);
    // Stop any existing stream first
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      // The <video> element may not be mounted yet (we just switched phase to "camera").
      // Retry briefly until it appears.
      for (let i = 0; i < 30; i++) {
        if (videoRef.current) break;
        await new Promise(r => setTimeout(r, 50));
      }
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch {}
      }
    } catch (e) {
      console.log("camera err", e);
      setPermissionDenied(true);
    }
  }, []);

  // Reset to camera phase (used by both "撮り直す" buttons)
  const retake = () => {
    setPreviewSrc(null);
    setParsed(null);
    setCardImage(null);
    setFullImage(null);
    setUseOriginal(false);
    setCardCorners(null);
    setErrorMsg("");
    setPhase("camera");
    startCamera();
  };

  // Start camera when opened
  React.useEffect(() => {
    if (!open) return;
    setPhase("camera");
    setPreviewSrc(null); setParsed(null); setCardImage(null); setFullImage(null); setUseOriginal(false); setCardCorners(null); setPermissionDenied(false); setErrorMsg("");
    startCamera();
    return () => {
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    };
  }, [open, startCamera]);

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  };

  const capture = async () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const data = canvas.toDataURL("image/jpeg", 0.92);
    stopCamera();
    setPreviewSrc(data);
    setPhase("preview");
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      stopCamera();
      setPreviewSrc(reader.result);
      setPhase("preview");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const runVision = async () => {
    if (!previewSrc) return;
    setPhase("analyzing");
    setErrorMsg("");

    // Downscale to keep payload reasonable
    const compressed = await downscaleDataURL(previewSrc, 1280, 0.85);
    setFullImage(compressed);
    setUseOriginal(false);
    const base64 = compressed.replace(/^data:image\/[a-z]+;base64,/, "");

    const instruction = `添付の画像は名刺の写真です(背景・手・傾き・遠近の歪みがある場合があります)。以下の3点を行い、**JSONのみ**で返してください(コードフェンス・前置き不要)。

1. 画像内の**名刺カードの四隅**を、画像全体に対する正規化座標(0〜1, 左上原点)で特定してください。
   - 四隅は**紙の物理的な最外縁(カードのフチの角そのもの)**を指定すること。印刷文字やロゴの外接ではなく、紙の端。
   - 位置に迷ったら**必ずカードの外側(背景側)に寄せる**こと。カードの文字・ロゴ・フチが1mmでも欠けるのは厳禁。背景が少し写り込むのは問題ありません。
   - card_corners は必ず **4 点** の配列。順序は **[左上, 右上, 右下, 左下]** ですが、ここで言う「上」「下」は**カードに印刷されている文字の向きを基準**にしてください(写真上で名刺が90°/180°回転していても、印刷の上辺に来る2点が TL, TR になるように)。
   - 例: カードが写真の中で90°時計回りに倒れていれば、写真の右側にある辺が「印刷の上」、左側が「印刷の下」。
2. 名刺カード本体を**完全に含む**軸並行バウンディングボックスも併記してください(フォールバック用、こちらも欠けるより広めに): card_bbox = { x, y, width, height }
3. 名刺記載の連絡先情報を読み取る。読み取れない項目は空文字 "" に。電話番号はハイフン付き。日本語の文字を OCR する際、傾きは無視して文字内容に集中してください。

形式:
{
  "card_corners": [
    { "x": 0.00, "y": 0.00 },
    { "x": 0.00, "y": 0.00 },
    { "x": 0.00, "y": 0.00 },
    { "x": 0.00, "y": 0.00 }
  ],
  "card_bbox": { "x": 0.0, "y": 0.0, "width": 0.0, "height": 0.0 },
  "name": "氏名(姓 名)",
  "kana": "ふりがな(ローマ字表記があればそれを採用)",
  "role": "役職",
  "company": "会社名",
  "department": "部署",
  "email": "メールアドレス",
  "phone": "代表/直通電話",
  "mobile": "携帯",
  "fax": "FAX番号",
  "address": "住所(郵便番号含む)",
  "website": "URL",
  "note": "その他特記事項(空欄でも可)"
}`;

    const tryVision = async () => {
      // Try multimodal call first
      const resp = await window.claude.complete({
        messages: [{
          role: "user",
          content: [
            { type: "image", source: { type: "base64", media_type: "image/jpeg", data: base64 } },
            { type: "text", text: instruction },
          ],
        }],
      });
      return resp;
    };

    let respText = "";
    let visionFailed = false;
    try {
      respText = await tryVision();
    } catch (e) {
      console.log("vision call failed", e);
      visionFailed = true;
    }

    if (visionFailed || !respText) {
      // Couldn't run vision — fall through to manual entry
      setParsed({ ...EMPTY_PARSED });
      setErrorMsg("AIで自動読み取りができませんでした。画像を見て手動で入力してください。");
      setPhase("done");
      return;
    }

    try {
      let j = String(respText).trim();
      const m = j.match(/\{[\s\S]*\}/);
      if (m) j = m[0];
      const data = JSON.parse(j);
      const { card_bbox, card_corners, ...fields } = data;
      setParsed({ ...EMPTY_PARSED, ...fields });
      // 1) Try perspective dewarp from 4 corners (handles tilt + perspective),
      //    expanded outward 8% — AI corners tend to sit slightly inside the edge
      // 2) Fall back to axis-aligned bbox crop
      // 3) Fall back to the downscaled full image
      // Whatever quad ends up behind the crop seeds the manual 範囲を調整 UI.
      const aiQuad = validCorners(card_corners) ? expandQuad(card_corners, 1.08) : null;
      let cardOut = aiQuad ? await dewarpCardFromCorners(previewSrc, aiQuad, 1100) : null;
      if (!cardOut) cardOut = await cropDataURL(previewSrc, card_bbox, 1000);
      setCardCorners(aiQuad || quadFromBbox(card_bbox));
      setCardImage(cardOut || compressed);
      setPhase("done");
    } catch (e) {
      console.log("parse failed", e, respText);
      setParsed({ ...EMPTY_PARSED, note: String(respText).slice(0, 300) });
      setCardImage(compressed);
      setErrorMsg("AIの返答を解析できませんでした。下のフォームを編集して登録してください。");
      setPhase("done");
    }
  };

  const updateField = (key, value) => {
    setParsed(p => ({ ...p, [key]: value }));
  };

  // Re-crop with the user-drawn rectangle (WYSIWYG — no extra margin)
  const applyAdjusted = async (bbox) => {
    const out = await cropDataURL(previewSrc, bbox, 1100, 0);
    if (out) {
      setCardImage(out);
      setCardCorners(quadFromBbox(bbox)); // seeds the adjuster next time
      setUseOriginal(false);
      setErrorMsg("");
    } else {
      setErrorMsg("切り抜きに失敗しました。枠の位置を見直してください。");
    }
    setPhase("done");
  };

  const handleSave = () => {
    if (!parsed) return;
    // Every extracted item now has a dedicated contact field (2026-06-12) —
    // the note carries only genuine free-text remarks.
    onParsed({
      name: parsed.name || "",
      kana: parsed.kana || "",
      role: parsed.role || "",
      department: parsed.department || "",
      company: parsed.company || "",
      email: parsed.email || "",
      phone: parsed.phone || "",
      mobile: parsed.mobile || "",
      fax: parsed.fax || "",
      website: parsed.website || "",
      address: parsed.address || "",
      last: (() => { const d = new Date(); const p = n => String(n).padStart(2, "0"); return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`; })(),
      note: parsed.note || "",
      cardImage: (useOriginal ? fullImage : cardImage) || "",
    });
    onClose();
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <React.Fragment>
    <div className="modal-overlay" onClick={(e) => { if (e.target.classList.contains("modal-overlay")) onClose(); }}>
      <div className="modal-stage" style={{maxWidth:720}}>
        <div className="modal-head">
          <div style={{flex:1}}>
            <span className="eyebrow">CARD SCANNER</span>
            <h2 className="modal-title">名刺をAIで読み取り</h2>
          </div>
          <button className="icon-btn" onClick={onClose} title="閉じる" aria-label="閉じる"><Icon name="x" size={18}/></button>
        </div>
        <div className="modal-body">
          {phase === "camera" && (
            <>
              {permissionDenied ? (
                <div style={{padding:30, textAlign:"center"}}>
                  <Icon name="alert" size={36} color="var(--crimson-hi)"/>
                  <div style={{fontSize:13, color:"var(--ivory)", marginTop:14, lineHeight:1.7}}>
                    カメラへのアクセスが拒否されました。<br/>
                    ブラウザ設定で許可するか、画像ファイルをアップロードしてください。
                  </div>
                  <button className="btn gold mt-3" onClick={() => fileInputRef.current?.click()}>
                    <Icon name="download" size={12}/>
                    <span>画像をアップロード</span>
                  </button>
                </div>
              ) : (
                <>
                  <div style={{position:"relative", aspectRatio:"4/3", background:"#000", borderRadius:8, overflow:"hidden", marginBottom:14}}>
                    <video ref={videoRef} style={{width:"100%", height:"100%", objectFit:"cover"}} playsInline muted/>
                    <div style={{position:"absolute", inset:24, border:"2px dashed rgba(245,197,24,0.6)", borderRadius:6, pointerEvents:"none"}}/>
                    <div style={{position:"absolute", bottom:10, left:0, right:0, textAlign:"center", color:"var(--ivory)", fontSize:11, fontFamily:"var(--f-mono)", letterSpacing:"0.12em", textShadow:"0 1px 4px rgba(0,0,0,0.8)"}}>
                      名刺を枠内に収めてください
                    </div>
                  </div>
                  <div style={{display:"flex", justifyContent:"center", gap:10}}>
                    <button className="btn ghost" onClick={() => fileInputRef.current?.click()}>
                      <Icon name="download" size={12}/>
                      <span>画像から選択</span>
                    </button>
                    <button className="btn gold lg" onClick={capture}>
                      <Icon name="mic" size={16}/>
                      <span>撮影する</span>
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {phase === "preview" && (
            <>
              <div style={{aspectRatio:"4/3", background:"#000", borderRadius:8, overflow:"hidden", marginBottom:14}}>
                <img src={previewSrc} alt="" style={{width:"100%", height:"100%", objectFit:"contain"}}/>
              </div>
              <div style={{display:"flex", justifyContent:"center", gap:10}}>
                <button className="btn ghost" onClick={retake}><span>撮り直す</span></button>
                <button className="btn gold lg" onClick={runVision}>
                  <Icon name="sparkle" size={14}/>
                  <span>AIで読み取る</span>
                </button>
              </div>
            </>
          )}

          {phase === "analyzing" && (
            <div style={{padding:30, textAlign:"center"}}>
              <div style={{width:48, height:48, border:"3px solid var(--gold)", borderTopColor:"transparent", borderRadius:"50%", margin:"0 auto", animation:"spin 0.8s linear infinite"}}/>
              <div style={{marginTop:20, fontFamily:"var(--f-display)", fontSize:16, letterSpacing:"0.10em", color:"var(--ivory)"}}>名刺をAIが解析中…</div>
              <div style={{marginTop:8, fontSize:11, color:"var(--ash)"}}>画像から直接、氏名・会社・連絡先を抽出しています</div>
            </div>
          )}

          {phase === "adjust" && (
            <RectAdjuster
              src={previewSrc}
              initial={cardCorners}
              onApply={applyAdjusted}
              onCancel={() => setPhase("done")}
            />
          )}

          {phase === "done" && parsed && (
            <>
              <div style={{display:"grid", gridTemplateColumns:"180px 1fr", gap:16, marginBottom:14}}>
                <div>
                  <button
                    type="button"
                    onClick={() => setZoomSrc((useOriginal ? fullImage : cardImage) || previewSrc)}
                    title="クリックで拡大"
                    style={{display:"block", width:"100%", aspectRatio:"16/10", background:"#0a0e1a", borderRadius:6, overflow:"hidden", border:"1px solid var(--hairline)", padding:0, cursor:"zoom-in"}}
                  >
                    <img src={(useOriginal ? fullImage : cardImage) || previewSrc} alt="" style={{width:"100%", height:"100%", objectFit:"contain"}}/>
                  </button>
                  <div style={{fontFamily:"var(--f-mono)", fontSize:9, letterSpacing:"0.10em", color:"var(--ash)", textAlign:"center", marginTop:6}}>
                    {!useOriginal && cardImage ? "CARD · AI DEWARPED" : "ORIGINAL PHOTO"}
                  </div>
                  <button className="btn ghost sm" style={{marginTop:8, width:"100%"}} onClick={() => setPhase("adjust")} title="名刺が欠けている・余計な背景が入っている場合に、四隅をドラッグして切り抜き範囲を直せます">
                    <span>✂ 範囲を調整</span>
                  </button>
                  {cardImage && (
                    <button className="btn ghost sm" style={{marginTop:8, width:"100%"}} onClick={() => setUseOriginal(v => !v)} title="切り抜きで名刺が欠けている場合は元の写真をそのまま登録できます">
                      <span>{useOriginal ? "切り抜きに戻す" : "元の写真を使う"}</span>
                    </button>
                  )}
                  <button className="btn ghost sm" style={{marginTop:8, width:"100%"}} onClick={retake}>
                    <span>撮り直す</span>
                  </button>
                </div>
                <div className="summary-stage" style={{marginTop:0, padding:14}}>
                  <h4 style={{margin:"0 0 10px 0"}}>★ AI抽出結果 <span style={{fontFamily:"var(--f-mono)", fontSize:10, color:"var(--ash)", letterSpacing:"0.10em", marginLeft:6}}>EDITABLE</span></h4>
                  {errorMsg && (
                    <div style={{padding:"8px 10px", background:"rgba(220, 38, 38, 0.12)", border:"1px solid rgba(220, 38, 38, 0.4)", borderRadius:4, fontSize:11, color:"var(--crimson-hi, #ff8888)", marginBottom:10}}>
                      {errorMsg}
                    </div>
                  )}
                  <div style={{display:"grid", gridTemplateColumns:"72px 1fr", gap:"6px 10px", alignItems:"center"}}>
                    {[
                      ["氏名", "name"], ["ふりがな", "kana"], ["役職", "role"],
                      ["会社", "company"], ["部署", "department"], ["メール", "email"],
                      ["電話", "phone"], ["携帯", "mobile"], ["FAX", "fax"],
                      ["住所", "address"], ["URL", "website"],
                    ].map(([label, key]) => (
                      <React.Fragment key={key}>
                        <div style={{color:"var(--smoke)", fontFamily:"var(--f-mono)", fontSize:10, letterSpacing:"0.10em", textAlign:"right"}}>{label}</div>
                        <input
                          value={parsed[key] || ""}
                          onChange={e => updateField(key, e.target.value)}
                          placeholder="—"
                          style={{
                            background:"rgba(0,0,0,0.35)",
                            border:"1px solid var(--hairline)",
                            borderRadius:3,
                            padding:"5px 8px",
                            color:"var(--ivory)",
                            fontSize:12,
                            outline:"none",
                            fontFamily:"var(--f-body, inherit)",
                          }}
                        />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={handleFile}/>
        </div>
        <div className="modal-foot">
          {phase === "done" && (
            <>
              <button className="btn ghost" onClick={retake}>もう一度撮影</button>
              <div style={{flex:1}}/>
              <button className="btn ghost" onClick={onClose}>キャンセル</button>
              <button className="btn primary" onClick={handleSave} disabled={!(parsed && (parsed.name || parsed.company))}>
                <Icon name="check" size={12}/>
                <span>連絡先に登録</span>
              </button>
            </>
          )}
          {phase !== "done" && (
            <>
              <div style={{flex:1}}/>
              <button className="btn ghost" onClick={onClose}>キャンセル</button>
            </>
          )}
        </div>
      </div>
    </div>
    <ImageLightbox src={zoomSrc} caption="BUSINESS CARD" onClose={() => setZoomSrc(null)}/>
    </React.Fragment>,
    document.body
  );
};

const ContactsView = ({ state, setState }) => {
  const [q, setQ] = React.useState("");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [scannerOpen, setScannerOpen] = React.useState(false);
  const [zoomSrc, setZoomSrc] = React.useState(null);
  const crud = useCrud(state, setState, "contacts");

  const filtered = (state.contacts || []).filter(c => !q || (c.name + (c.kana||"") + (c.role||"") + (c.department||"") + (c.company||"") + (c.note||"") + (c.email||"") + (c.phone||"") + (c.mobile||"") + (c.fax||"") + (c.website||"")).toLowerCase().includes(q.toLowerCase()));

  const openEdit = (c) => { setEditing(c); setModalOpen(true); };
  const openNew = () => { setEditing(null); setModalOpen(true); };
  const handleSave = (vals) => {
    if (editing) crud.update(editing.id, vals);
    else crud.add(vals);
    setModalOpen(false);
  };
  const handleDelete = () => {
    if (editing) crud.remove(editing.id);
    setModalOpen(false);
  };
  const handleScannerParsed = (data) => {
    // Directly add to contacts — the scanner form is already editable
    crud.add(data);
  };

  return (
    <div className="view-fade">
      <div className="panel cinematic mb-3 contact-toolbar" style={{padding:"16px 22px", display:"flex", gap:14, alignItems:"center"}}>
        <Icon name="search" size={16} color="var(--gold)"/>
        <input
          placeholder="名前・会社・役職で検索…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{flex:1, background:"transparent", border:"none", outline:"none", color:"var(--ivory)", fontSize:14}}
        />
        <button className="btn ghost sm" onClick={() => setScannerOpen(true)}>
          <Icon name="mic" size={12}/>
          <span>名刺を撮影</span>
        </button>
        <button className="btn gold sm" onClick={openNew}>
          <Icon name="plus" size={12}/>
          <span>追加</span>
        </button>
      </div>

      <div className="panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">NETWORK · {filtered.length}人</span>
            <h3>連絡先</h3>
          </div>
        </div>
        <div className="panel-body">
          <div className="contact-list-head">
            <div></div>
            <div className="label-mono">名前 / 役職</div>
            <div className="label-mono">会社・所属</div>
            <div className="label-mono">登録日</div>
            <div className="label-mono">メモ</div>
            <div></div>
          </div>
          {filtered.map(c => (
            <div key={c.id} className="contact-row" onClick={() => openEdit(c)}>
              {c.cardImage ? (
                <button
                  type="button"
                  className="contact-avatar contact-avatar-img"
                  title="名刺画像を拡大"
                  onClick={(e) => { e.stopPropagation(); setZoomSrc(c.cardImage); }}
                >
                  <img src={c.cardImage} alt=""/>
                </button>
              ) : (
                <div className="contact-avatar">{c.name?.slice(0,1) || "?"}</div>
              )}
              <div className="contact-main">
                <div className="contact-name">{c.name}</div>
                <div className="contact-role">{c.role}</div>
              </div>
              <div className="contact-cell contact-company">{c.company}</div>
              <div className="contact-cell contact-last">{c.last || "—"}</div>
              <div className="contact-cell contact-note" style={{fontSize:11, color:"var(--ash)"}}>{c.note}</div>
              <div className="row-actions">
                <button className="row-action-btn" onClick={(e) => { e.stopPropagation(); openEdit(c); }}><Icon name="edit" size={13}/></button>
                <button className="row-action-btn danger" onClick={(e) => { e.stopPropagation(); if (confirm("削除しますか?")) crud.remove(c.id); }}><Icon name="trash" size={13}/></button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="empty-state"><h4>連絡先がありません</h4><div>{q ? "検索条件にマッチする連絡先がありません。" : "「名刺を撮影」または「追加」から始めてください。"}</div></div>
          )}
        </div>
      </div>

      <EntityModal
        open={modalOpen}
        schema={CONTACT_SCHEMA}
        initial={editing}
        onSave={handleSave}
        onDelete={editing && editing.id && !editing.__isNew ? handleDelete : null}
        onClose={() => setModalOpen(false)}
      />

      <CardScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onParsed={handleScannerParsed}
      />

      <ImageLightbox src={zoomSrc} caption="BUSINESS CARD" onClose={() => setZoomSrc(null)}/>
    </div>
  );
};

Object.assign(window, { ContactsView, expandQuad, validCorners, isConvexQuad, solveHomography, applyH, quadFromBbox });


;/* ==== applicants.jsx ==== */
// Applicants — recruitment tracker integrated into SUPERMAN
// Seed: 3 candidates imported from applicant management system.

// =============================================================
// DEFAULTS — used until user customizes them in Settings → 応募者
// =============================================================
const DEFAULT_APPLICANT_STATUSES = [
  { id: 'interested',     label: '興味あり',       group: 'lead',   color: '#94a3b8', nextLabel: 'WEB面談予定',     nextDateKey: 'webMeetingAt' },
  { id: 'self_failed',    label: '本人応募不合格',  group: 'lead',   color: '#ef4444', terminal: true },
  { id: 'web_meeting',    label: 'WEB面談',        group: 'lead',   color: '#64748b', nextLabel: 'WEB面談実施',     nextDateKey: 'webMeetingAt' },
  { id: 'web_passed',     label: 'WEB面談済',      group: 'lead',   color: '#475569', nextLabel: '書類選考',         nextDateKey: null },
  { id: 'web_failed',     label: 'WEB面談不合格',  group: 'lead',   color: '#ef4444', terminal: true },
  { id: 'docs_received',  label: '書類受領',       group: 'docs',   color: '#6366f1', nextLabel: '書類選考',         nextDateKey: null },
  { id: 'docs_passed',    label: '書類合格',       group: 'docs',   color: '#4f46e5', nextLabel: '1次面接予定',      nextDateKey: 'interview1At' },
  { id: 'i1_pending',     label: '1次面接待ち',    group: 'i1',     color: '#0ea5e9', nextLabel: '1次面接',          nextDateKey: 'interview1At' },
  { id: 'i1_failed',      label: '1次不合格',      group: 'i1',     color: '#ef4444', terminal: true },
  { id: 'i1_passed',      label: '1次合格',        group: 'i1',     color: '#0284c7', nextLabel: '2次面接予定',      nextDateKey: 'interview2At' },
  { id: 'i2_pending',     label: '2次面接待ち',    group: 'i2',     color: '#14b8a6', nextLabel: '2次面接',          nextDateKey: 'interview2At' },
  { id: 'i2_failed',      label: '2次不合格',      group: 'i2',     color: '#ef4444', terminal: true },
  { id: 'i2_passed',      label: '2次合格',        group: 'i2',     color: '#0d9488', nextLabel: '内定通知',         nextDateKey: 'offerAt' },
  { id: 'offer',          label: '内定',           group: 'offer',  color: '#f59e0b', nextLabel: '内定受理確認',     nextDateKey: 'offerAt' },
  { id: 'offer_pending',  label: '内定受理待ち',   group: 'offer',  color: '#d97706', nextLabel: '内定受理確認',     nextDateKey: null },
  { id: 'offer_accepted', label: '内定受理',       group: 'offer',  color: '#16a34a', nextLabel: '入社',             nextDateKey: 'hireDate' },
  { id: 'hired',          label: '入社済',         group: 'hired',  color: '#15803d', terminal: true },
];
const DEFAULT_APPLICANT_SOURCES = ['ビズリーチ','LinkedIn','Wantedly','リファラル','エージェント','直接応募','その他'];
const DEFAULT_APPLICANT_POSITIONS = [
  'マーケティング責任者候補',
  'マーケティングマネージャー(2026)',
  'ブランドマネージャー',
  '営業マネージャー候補',
  'デザイナー',
  'バックオフィス',
  '情シス',
  'その他',
];

// Date/datetime field keys used by applicants — central registry so settings
// can offer them in the "次のアクション 予定日" dropdown.
const APPLICANT_DATE_FIELDS = [
  { key: 'webMeetingAt',  label: 'WEB面談日時',  type: 'datetime' },
  { key: 'interview1At',  label: '1次面接日時',  type: 'datetime' },
  { key: 'interview2At',  label: '2次面接日時',  type: 'datetime' },
  { key: 'offerAt',       label: '内定日',       type: 'date' },
  { key: 'hireDate',      label: '入社日',       type: 'date' },
];

// Helpers — read from state with defaults
const getApplicantStatuses = (state) => (state?.applicantStatuses && state.applicantStatuses.length) ? state.applicantStatuses : DEFAULT_APPLICANT_STATUSES;
const getApplicantSources = (state) => (state?.applicantSources && state.applicantSources.length) ? state.applicantSources : DEFAULT_APPLICANT_SOURCES;
const getApplicantPositions = (state) => (state?.applicantPositions && state.applicantPositions.length) ? state.applicantPositions : DEFAULT_APPLICANT_POSITIONS;
// 職種ごとの「求めるレベル」定義（Settings で編集）。AI人物評価の役職レベル適正の基準。
const getPositionLevels = (state) => (state && state.positionLevels) || {};
const getStatusById = (statuses, id) => statuses.find(s => s.id === id) || statuses[0];

// 自社・業界の前提（AI評価に渡す。社内マスター/従業員データは含めない＝生成AIへの社員データ投入を回避）。
const APPLICANT_COMPANY_CONTEXT = "セイムHD（関西・大阪を地盤とする企業グループ。不動産販売・建築・マーケティング等の事業を展開）";
// 5段階適正スコア → 色（5緑〜1赤）。取り込みレビューと詳細のAI評価カードで共有。
const scoreColor = (n) => n >= 5 ? "#16a34a" : n >= 4 ? "#5ac994" : n >= 3 ? "#f5c518" : n >= 2 ? "#ed8c4a" : "#ef4444";

// Shared stage metadata — used by BOTH the board lanes and the funnel so the
// two views stay in sync. The `lead` group from the underlying status schema
// is split into TWO funnel stages because the operational reality is split:
//   lead_intake = ビズリーチ等から取り込んだだけのターゲット（まだ接点ゼロ）
//   lead_active = WEB面談を案内・実施した段階（実際に会話が始まった）
// Without this split, hundreds of "ただ登録しただけ" lurkers drown out the
// real funnel and conversion% from lead→docs becomes meaningless.
const APPLICANT_STAGE_META = {
  lead_intake: { label: 'WEB登録 (ターゲット)', color: '#cbd5e1' },
  lead_active: { label: 'WEB面談プロセス',     color: '#94a3b8' },
  docs:        { label: '書類選考',           color: '#6366f1' },
  i1:          { label: '1次面接',            color: '#0284c7' },
  i2:          { label: '2次面接',            color: '#0d9488' },
  offer:       { label: '内定',               color: '#f59e0b' },
  hired:       { label: '入社',               color: '#15803d' },
};

// Map a status object to the funnel stage it belongs to. This is the single
// source of truth — board lanes, funnel rows and conversion math all flow
// through here so a future re-split only edits this one function.
const stageForStatus = (status) => {
  if (!status) return 'other';
  const g = status.group || 'other';
  if (g === 'lead') {
    // 'interested' (興味あり) = WEB登録のみ・コンタクト前。残りの lead 系
    // (web_meeting / web_passed / self_failed / web_failed) は WEB面談プロセス。
    return status.id === 'interested' ? 'lead_intake' : 'lead_active';
  }
  return g;
};

// Ordered list of unique funnel stages as they appear in the status list.
// Includes terminal-at-stage statuses (i1_failed etc.) because their stage
// is still a progression step the applicant actually reached.
const stageOrderFrom = (statuses) => {
  const order = [];
  statuses.forEach(s => { const stage = stageForStatus(s); if (!order.includes(stage)) order.push(stage); });
  return order;
};

// Pure: compute every number the funnel panel shows from a list of applicants
// and the project's status definitions. Returning a single shaped object lets
// the JSX layer stay declarative and the math stay unit-testable.
function computeFunnelAnalytics(applicants, statuses) {
  const safe = Array.isArray(applicants) ? applicants : [];
  const st = Array.isArray(statuses) ? statuses : [];
  const order = stageOrderFrom(st);
  const stageIdxOf = Object.fromEntries(order.map((g, i) => [g, i]));
  const stageOf = (a) => {
    const s = st.find(x => x.id === a.status) || st[0];
    return stageForStatus(s);
  };
  const isTerminal = (a) => {
    const s = st.find(x => x.id === a.status);
    return !!(s && s.terminal);
  };
  // Reach is cumulative: an applicant who was rejected at i1 still REACHED
  // lead, docs and i1. So reachedIdx returns the applicant's current stage
  // index regardless of terminality.
  const reachedIdx = (a) => {
    const stage = stageOf(a);
    return stageIdxOf[stage] != null ? stageIdxOf[stage] : -1;
  };

  // 1) Funnel rows — reached & here per stage, with prev→here conversion and
  //    a bottleneck flag for low-conversion drops the user should notice.
  //    'here' excludes terminal applicants — they're no longer active at the
  //    stage, they're done (rejected/hired). 'reached' still counts them.
  const rows = order.map((g, i) => {
    const meta = APPLICANT_STAGE_META[g] || { label: g, color: '#94a3b8' };
    const reached = safe.filter(a => reachedIdx(a) >= i).length;
    const here = safe.filter(a => reachedIdx(a) === i && !isTerminal(a)).length;
    return { group: g, label: meta.label, color: meta.color, reached, here };
  });
  rows.forEach((row, i) => {
    const prev = i > 0 ? rows[i - 1].reached : null;
    row.conversion = (prev && prev > 0) ? Math.round((row.reached / prev) * 100) : null;
    // Mark bottlenecks only on stages with real intake AND a sharp drop —
    // a 100→0 fall on a 1-person prev slice is noise, not a bottleneck.
    row.bottleneck = (i > 0 && row.conversion !== null && row.conversion < 50 && (prev || 0) >= 3);
  });

  // 2) AI evaluation distribution — buckets 1..5 from evaluation.overall.
  //    Round to the nearest integer so 4.7 lands in the 5-bucket.
  const evaluated = safe.filter(a => a.evaluation && typeof a.evaluation.overall === 'number');
  const scoreDist = [5, 4, 3, 2, 1].map(s => ({
    score: s,
    count: evaluated.filter(a => Math.round(a.evaluation.overall) === s).length,
  }));
  const avgScore = evaluated.length > 0
    ? Math.round((evaluated.reduce((sum, a) => sum + a.evaluation.overall, 0) / evaluated.length) * 10) / 10
    : null;

  // 3) Per-source mini-funnel (応募 → 書類 → 面接到達 → 内定到達 → 入社).
  const idxDocs = stageIdxOf['docs'];
  const idxI1 = stageIdxOf['i1'];
  const idxOffer = stageIdxOf['offer'];
  const idxHired = stageIdxOf['hired'];
  const bySrc = new Map();
  safe.forEach(a => {
    const src = (a.source && String(a.source).trim()) || "(未設定)";
    if (!bySrc.has(src)) bySrc.set(src, { source: src, total: 0, docs: 0, interview: 0, offer: 0, hired: 0 });
    const row = bySrc.get(src);
    row.total++;
    const r = reachedIdx(a);
    if (idxDocs != null && r >= idxDocs) row.docs++;
    if (idxI1 != null && r >= idxI1) row.interview++;
    if (idxOffer != null && r >= idxOffer) row.offer++;
    if (idxHired != null && r >= idxHired) row.hired++;
  });
  // % from total — null when there are no applicants in that source
  const channelRows = Array.from(bySrc.values()).map(c => ({
    ...c,
    docsPct: c.total > 0 ? Math.round((c.docs / c.total) * 100) : null,
    interviewPct: c.total > 0 ? Math.round((c.interview / c.total) * 100) : null,
    offerPct: c.total > 0 ? Math.round((c.offer / c.total) * 100) : null,
  })).sort((a, b) => b.total - a.total);

  return {
    rows,
    total: safe.length,
    evaluated: evaluated.length,
    avgScore,
    scoreDist,
    channelRows,
  };
}

// Next action for an applicant — { label, dateValue, dateKey } or null when terminal
const nextActionFor = (applicant, statuses) => {
  const s = getStatusById(statuses, applicant.status);
  if (!s || s.terminal) return null;
  if (!s.nextLabel) return null;
  return {
    label: s.nextLabel,
    dateKey: s.nextDateKey || null,
    dateValue: s.nextDateKey ? applicant[s.nextDateKey] : null,
    color: s.color,
  };
};

// Format a datetime or date string for display
const fmtNext = (val) => {
  if (!val) return "";
  // datetime-local: "2026-06-01T10:00"
  if (val.length >= 16 && val[10] === "T") {
    const [d, t] = val.split("T");
    const [, m, day] = d.split("-");
    return `${parseInt(m)}/${parseInt(day)} ${t.slice(0,5)}`;
  }
  // date only: "2026-06-01"
  if (val.length === 10) {
    const [, m, day] = val.split("-");
    return `${parseInt(m)}/${parseInt(day)}`;
  }
  return val;
};
// Human label for a stored gender code. Legacy seed data uses male/female;
// tolerate already-localized values too. Returns "" for empty/unknown so the
// caller can omit it entirely rather than render a dash.
const genderLabel = (g) => ({ male: "男性", female: "女性", other: "その他", 男性: "男性", 女性: "女性", その他: "その他" }[g] || "");

const isPast = (val) => {
  if (!val) return false;
  // Compare to the real current time. (This used to be pinned to the demo date
  // 2026-05-27, which silently disabled every ⚠ overdue marker once the real
  // calendar passed that day.)
  const d = new Date(val.replace(" ", "T"));
  return !isNaN(d.getTime()) && d.getTime() < Date.now();
};

// Toolbar filter predicate (職種 / ソース / 検索). Pure + exported so the table,
// board and funnel all share ONE definition of "is this applicant in view" — the
// funnel used to ignore this entirely (computed from the raw list), which is why
// 絞り込み had no effect there.
function applicantMatchesFilters(a, { position = "all", source = "all", status = "all", search = "" } = {}) {
  if (position !== "all" && a.position !== position) return false;
  if (source !== "all" && a.source !== source) return false;
  if (status !== "all" && a.status !== status) return false;
  if (search) {
    const hay = ((a.name || "") + (a.kana || "") + (a.position || "") +
      (a.currentCompany || "") + (a.tags || []).join(" ")).toLowerCase();
    if (!hay.includes(search.toLowerCase())) return false;
  }
  return true;
}

// Expose for settings.jsx
Object.assign(window, {
  DEFAULT_APPLICANT_STATUSES,
  DEFAULT_APPLICANT_SOURCES,
  DEFAULT_APPLICANT_POSITIONS,
  APPLICANT_DATE_FIELDS,
  getApplicantStatuses,
  getApplicantSources,
  getApplicantPositions,
  getPositionLevels,
  applicantMatchesFilters,
  genderLabel,
  normalizeImportedApplicant,
  buildEvaluation,
  buildEvalProfiles,
  parseJsonLoose,
  mergeEvaluations,
  extractPdfText,
  splitResumeChunks,
});

const APPLICANT_SOURCES = DEFAULT_APPLICANT_SOURCES; // retained for seed

const SEED_APPLICANTS = [
  {
    id: 'a_ishida', name: '石田 剛司', kana: 'いしだ つよし', gender: 'male', age: 56,
    email: 'tishida88@gmail.com', phone: '090-6054-7793',
    address: '大阪府大阪市西区本田3-1-6 ルックハイツ本田703',
    photoColor: '#e0e7ff', photoInitial: '石',
    source: 'ビズリーチ', position: 'マーケティング責任者候補',
    appliedAt: '2026-05-13', status: 'i1_failed',
    desiredSalary: '応相談', tags: ['即戦力','マネジメント','建築/不動産'],
    currentCompany: '株式会社アイ工務店', currentRole: '営業推進部 課長',
    motivation: '建築・不動産35年の知見でondoの成長を牽引したい。',
    note: '5/22 落選確定。'
  },
  {
    id: 'a_tanizawa', name: '谷沢 知哉', kana: 'たにざわ ともや', gender: 'male', age: 38,
    email: 'tomoya.tanizawa@icloud.com', phone: '090-8283-7121',
    address: '大阪府大阪市西区江之子島2-1-37 阿波座ライズタワー46-803',
    photoColor: '#dcfce7', photoInitial: '谷',
    source: 'ビズリーチ', position: 'ブランドマネージャー',
    appliedAt: '2026-05-12', status: 'i1_passed',
    desiredSalary: '〜900万円', tags: ['ラグジュアリー','海外経験','EC'],
    currentCompany: '株式会社トムブラウンジャパン', currentRole: '担当店舗',
    motivation: '事業の最前線で経営層の方向性を理解し利益と成長に貢献したい。',
    note: 'グローバルマーケコンテスト1位(3000人中)。'
  },
  {
    id: 'a_hamabe', name: '浜辺 佳奈', kana: 'はまべ かな', gender: 'female', age: 44,
    email: 'hamabe.k7@gmail.com', phone: '090-5018-8325',
    address: '大阪府守口市八雲西町 4-5-10',
    photoColor: '#fce7f3', photoInitial: '浜',
    source: 'ビズリーチ', position: 'マーケティングマネージャー(2026)',
    appliedAt: '2026-05-11', status: 'interested',
    desiredSalary: '貴社規定に従う', tags: ['新規事業','チームビルディング'],
    currentCompany: '株式会社BuySell Technologies', currentRole: '',
    motivation: '課題の構造化と推進力で成果創出、チームビルディングが強み。',
    note: 'スカウト未送信。'
  },
];

// Build schema dynamically using current state's status & source lists
const buildApplicantSchema = (state) => {
  const statuses = getApplicantStatuses(state);
  const sources = getApplicantSources(state);
  const positions = getApplicantPositions(state);
  return {
    eyebrow: "APPLICANT",
    title: "Applicant",
    titleNew: "新規応募者",
    titleEdit: "応募者編集",
    fields: [
      { key: "name", label: "氏名", type: "text", required: true },
      { key: "kana", label: "ふりがな", type: "text" },
      { key: "age", label: "年齢", type: "number", min: 16, max: 99 },
      { key: "email", label: "メール", type: "text" },
      { key: "phone", label: "電話", type: "text" },
      { key: "address", label: "住所", type: "text", span: 2 },
      { key: "position", label: "応募職種", type: "select", options: positions, required: true },
      { key: "source", label: "応募ソース", type: "select", options: sources },
      { key: "appliedAt", label: "応募日", type: "date" },
      { key: "status", label: "ステータス", type: "select", options: statuses.map(s => ({ value: s.id, label: s.label })) },
      { key: "webMeetingAt", label: "WEB面談日時", type: "datetime" },
      { key: "interview1At", label: "1次面接日時", type: "datetime" },
      { key: "interview2At", label: "2次面接日時", type: "datetime" },
      { key: "offerAt",      label: "内定日",       type: "date" },
      { key: "hireDate",     label: "入社日",       type: "date" },
      { key: "employmentStatus", label: "就業状況（在職/離職）", type: "text" },
      { key: "currentCompany", label: "現職会社", type: "text" },
      { key: "currentRole", label: "現職役割", type: "text" },
      { key: "desiredSalary", label: "希望給与", type: "text" },
      // ── Resume detail (often auto-filled by AI import; freely editable here) ──
      { key: "summary", label: "職務要約", type: "textarea", span: 2 },
      { key: "experience", label: "経験（職種・業種・マネジメント）", type: "textarea", span: 2 },
      { key: "education", label: "学歴", type: "text", span: 2 },
      { key: "qualifications", label: "資格・免許", type: "textarea", span: 2 },
      { key: "languages", label: "語学", type: "text" },
      { key: "desiredConditions", label: "希望条件（職種/業種/年収/勤務地/働き方）", type: "textarea", span: 2 },
      { key: "motivation", label: "志望動機", type: "textarea", span: 2 },
      { key: "note", label: "メモ", type: "textarea", span: 2 },
    ],
  };
};

const ApplicantStatusChip = ({ status, statuses }) => {
  const list = statuses || DEFAULT_APPLICANT_STATUSES;
  const s = list.find(x => x.id === status) || list[0];
  return (
    <span className="chip" style={{
      borderColor: s.color + "66",
      background: s.color + "20",
      color: s.color,
      fontWeight: 600,
    }}>{s.label}</span>
  );
};

const ApplicantsView = ({ state, setState }) => {
  const [mode, setMode] = React.useState("table"); // table | board | analytics
  const [filterPosition, setFilterPosition] = React.useState("all");
  const [filterSource, setFilterSource] = React.useState("all");
  const [filterStatus, setFilterStatus] = React.useState("all");
  const [sortKey, setSortKey] = React.useState(null);
  const [sortDir, setSortDir] = React.useState("asc");
  const [search, setSearch] = React.useState("");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  const [collapsedStages, setCollapsedStages] = React.useState({});
  const [aiOpen, setAiOpen] = React.useState(false);
  const [reEvaluating, setReEvaluating] = React.useState(false);
  const [reEvalErr, setReEvalErr] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [bulkStatusVal, setBulkStatusVal] = React.useState("");
  const [bulkEvaluating, setBulkEvaluating] = React.useState(false);
  const [bulkEvalProgress, setBulkEvalProgress] = React.useState({ done: 0, total: 0 });
  const crud = useCrud(state, setState, "applicants");

  // Read current customizable lists
  const statuses = getApplicantStatuses(state);
  const sources = getApplicantSources(state);
  const positions = getApplicantPositions(state);
  const schema = React.useMemo(() => buildApplicantSchema(state), [state.applicantStatuses, state.applicantSources, state.applicantPositions]);
  const isTerminalStatus = (id) => {
    const s = statuses.find(x => x.id === id);
    return !!(s && s.terminal);
  };

  // Seed if empty
  React.useEffect(() => {
    if (!state.applicants || state.applicants.length === 0) {
      setState(s => ({ ...s, applicants: SEED_APPLICANTS }));
    }
    // eslint-disable-next-line
  }, []);

  const list = state.applicants || [];

  const filtered = React.useMemo(
    () => list.filter(a => applicantMatchesFilters(a, { position: filterPosition, source: filterSource, status: filterStatus, search })),
    [list, filterPosition, filterSource, filterStatus, search],
  );

  const toggleSort = (key) => {
    if (sortKey === key) {
      if (sortDir === "asc") setSortDir("desc");
      else { setSortKey(null); setSortDir("asc"); } // third click clears
    } else { setSortKey(key); setSortDir("asc"); }
  };
  const sortValue = (a, key) => {
    switch (key) {
      case "name": return (a.kana || a.name || "");
      case "status": return statuses.findIndex(s => s.id === a.status);
      case "nextAction": { const na = nextActionFor(a, statuses); return na?.dateValue || "\uffff"; }
      case "position": return a.position || "";
      case "company": return a.currentCompany || "";
      case "appliedAt": return a.appliedAt || "";
      case "source": return a.source || "";
      default: return "";
    }
  };
  const sorted = React.useMemo(() => {
    if (!sortKey) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = sortValue(a, sortKey), vb = sortValue(b, sortKey);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir, statuses]);

  const openNew = () => { setEditing({ __isNew: true, status: statuses[0]?.id, source: sources[0], appliedAt: new Date().toISOString().slice(0,10), tags: [] }); setModalOpen(true); };
  const openEdit = (a) => { setEditing(a); setModalOpen(true); };
  const handleSave = (vals) => {
    if (editing && !editing.__isNew) crud.update(editing.id, vals);
    else { const { __isNew, id, ...clean } = vals; crud.add(clean); }
    setModalOpen(false);
  };
  const handleDelete = () => {
    if (editing && editing.id && !editing.__isNew) crud.remove(editing.id);
    setModalOpen(false);
  };
  const changeStatus = (id, status) => crud.update(id, { status });

  // Shared: build evaluation prompt + run AI call → evaluation object (or null).
  const runOneEval = async (applicant) => {
    const posLevels = getPositionLevels(state);
    const levelDef = (posLevels && posLevels[applicant.position]) || "";
    const profiles = buildEvalProfiles([applicant]);
    const prompt = `あなたは採用担当者のアシスタントです。下記【評価の前提】に照らして各候補者の適性を評価し、**JSON配列のみ**で返してください。コードフェンス・説明文は禁止。先頭は [ 末尾は ] 。
各要素の形式: { "idx": 候補者の番号(入力のidxと一致させる), "positionFit": 1〜5の整数, "industryFit": 1〜5の整数, "levelFit": 1〜5の整数, "overall": 1〜5の整数, "persona": "人物像を2〜3文", "strengths": "この募集における強み", "concerns": "懸念・確認点(無ければ空文字)", "comment": "総合所見を2〜4文(なぜそのスコアか)" }

【評価の前提】
- 当社／業界: ${APPLICANT_COMPANY_CONTEXT}
- 募集職種: ${applicant.position}
- 求める役職・レベル: ${levelDef || "(明示の定義なし。職種名と業界から標準的な期待値を推測して評価)"}
【公正な評価の厳守】判断材料は下記の経歴・スキル・実績・資格・語学・希望条件のみ。氏名・性別・年齢・住所等は意図的に渡していないので推測・考慮しないこと（厚生労働省『公正な採用選考』に準拠）。根拠が薄い項目は控えめなスコアにし、その理由を comment に記すこと。

候補者(JSON配列): ${JSON.stringify(profiles)}`;
    const resp = await window.claude.complete({ prompt, tier: "advanced", max_tokens: 4096 });
    const parsed = parseJsonLoose(resp);
    const arr = Array.isArray(parsed) ? parsed : [parsed];
    return buildEvaluation(arr[0], { position: applicant.position, levelDef, today: todayYMD() });
  };

  // Single re-evaluate (from detail view footer button).
  const reEvaluate = async (applicant) => {
    setReEvaluating(true);
    setReEvalErr("");
    try {
      const ev = await runOneEval(applicant);
      if (ev) {
        crud.update(applicant.id, { evaluation: ev });
        setDetail(d => d && d.id === applicant.id ? { ...d, evaluation: ev } : d);
      } else {
        setReEvalErr("評価結果を取得できませんでした。もう一度お試しください。");
      }
    } catch {
      setReEvalErr("AI評価に失敗しました。通信状態を確認してください。");
    } finally {
      setReEvaluating(false);
    }
  };

  // Bulk evaluate: sequential (rate-limit safe), progress counter shown in bar.
  const bulkEvaluate = async () => {
    const toEval = sorted.filter(a => selectedIds.has(a.id));
    if (!toEval.length || bulkEvaluating) return;
    setBulkEvaluating(true);
    setBulkEvalProgress({ done: 0, total: toEval.length });
    for (let i = 0; i < toEval.length; i++) {
      try {
        const ev = await runOneEval(toEval[i]);
        if (ev) {
          crud.update(toEval[i].id, { evaluation: ev });
          setDetail(d => d && d.id === toEval[i].id ? { ...d, evaluation: ev } : d);
        }
      } catch { /* skip failed individual; continue to next */ }
      setBulkEvalProgress({ done: i + 1, total: toEval.length });
    }
    setBulkEvaluating(false);
    setSelectedIds(new Set());
  };

  const bulkDelete = () => {
    const toDelete = sorted.filter(a => selectedIds.has(a.id));
    if (!toDelete.length) return;
    if (!confirm(`選択した ${toDelete.length} 件の応募者を削除しますか？この操作は元に戻せません。`)) return;
    toDelete.forEach(a => crud.remove(a.id));
    setSelectedIds(new Set());
  };

  const bulkChangeStatus = () => {
    const ns = bulkStatusVal || statuses[0]?.id;
    if (!ns) return;
    sorted.filter(a => selectedIds.has(a.id)).forEach(a => changeStatus(a.id, ns));
    setSelectedIds(new Set());
  };

  return (
    <div className="view-fade">
      <div className="ws-toolbar">
        <div className="ws-tabs">
          {[
            ["table", "テーブル", "dashboard"],
            ["board", "ボード", "tasks"],
            ["analytics", "ファネル", "chart"],
          ].map(([k, l, ic]) => (
            <div key={k} className={"ws-tab" + (mode === k ? " active" : "")} onClick={() => {
              setMode(k);
            }}>
              <Icon name={ic} size={13}/>
              <span>{l}</span>
            </div>
          ))}
        </div>
        <div className="ws-spacer"/>
        <div className="ws-filters">
          <select value={filterPosition} onChange={e => setFilterPosition(e.target.value)} className="ws-filter">
            <option value="all">全職種</option>
            {positions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filterSource} onChange={e => setFilterSource(e.target.value)} className="ws-filter">
            <option value="all">全ソース</option>
            {sources.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="ws-filter">
            <option value="all">全ステータス</option>
            {statuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <div className="ws-search">
            <Icon name="search" size={12}/>
            <input placeholder="検索…" value={search} onChange={e => setSearch(e.target.value)}/>
          </div>
        </div>
        <button className="btn ghost sm" onClick={() => setAiOpen(true)} title="テキストやファイルを貼り付けてAIが自動登録">
          <Icon name="sparkle" size={12}/>
          <span>AIで取り込み</span>
        </button>
        <button className="btn gold sm" onClick={openNew}>
          <Icon name="plus" size={12}/>
          <span>新規応募者</span>
        </button>
      </div>

      {mode === "table" && selectedIds.size > 0 && (
        <div style={{ background: "var(--panel)", border: "1px solid var(--hairline)", borderLeft: "3px solid var(--gold)", borderRadius: 8, padding: "10px 14px", marginBottom: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontFamily: "var(--f-mono)", fontSize: 12, color: "var(--gold)", fontWeight: 600 }}>{selectedIds.size}件 選択中</span>
          <button className="btn ghost sm" onClick={() => setSelectedIds(new Set())}>選択解除</button>
          <div style={{ width: 1, height: 20, background: "var(--hairline)", margin: "0 4px" }}/>
          <select value={bulkStatusVal || (statuses[0]?.id || "")} onChange={e => setBulkStatusVal(e.target.value)} className="ws-filter">
            {statuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <button className="btn ghost sm" onClick={bulkChangeStatus}>ステータス変更</button>
          <div style={{ width: 1, height: 20, background: "var(--hairline)", margin: "0 4px" }}/>
          <button className="btn sm gold" disabled={bulkEvaluating} onClick={bulkEvaluate}>
            <span>{bulkEvaluating ? `評価中 ${bulkEvalProgress.done}/${bulkEvalProgress.total}…` : "⚡ AI評価"}</span>
          </button>
          <div style={{ flex: 1 }}/>
          <button className="btn sm danger" onClick={bulkDelete}>削除</button>
        </div>
      )}

      {mode === "table" && (
        <div className="ws-table-wrap">
          <table className="ws-table applicants-table">
            <thead>
              <tr>
                <th style={{width:36, padding:"0 8px"}} onClick={e => e.stopPropagation()}>
                  <input type="checkbox"
                    checked={sorted.length > 0 && sorted.every(a => selectedIds.has(a.id))}
                    ref={el => { if (el) el.indeterminate = sorted.some(a => selectedIds.has(a.id)) && !sorted.every(a => selectedIds.has(a.id)); }}
                    onChange={() => {
                      const allSel = sorted.length > 0 && sorted.every(a => selectedIds.has(a.id));
                      setSelectedIds(allSel ? new Set() : new Set(sorted.map(a => a.id)));
                    }}
                    aria-label="全選択"
                    style={{cursor:"pointer"}}
                  />
                </th>
                {[
                  ["name", "応募者", 170],
                  ["status", "ステータス", 130],
                  ["nextAction", "次のアクション", 150],
                  ["position", "応募職種", 120],
                  ["company", "現職", 140],
                  ["appliedAt", "応募日", 80],
                  ["source", "ソース", 80],
                ].map(([key, label, w]) => (
                  <th key={key} style={{width:w, cursor:"pointer", userSelect:"none"}} onClick={() => toggleSort(key)}>
                    <span style={{display:"inline-flex", alignItems:"center", gap:5}}>
                      {label}
                      <span style={{fontFamily:"var(--f-mono)", fontSize:10, opacity: sortKey === key ? 1 : 0.25, color: sortKey === key ? "var(--gold, #c9a24b)" : "var(--smoke)"}}>
                        {sortKey === key ? (sortDir === "asc" ? "▲" : "▼") : "⇵"}
                      </span>
                    </span>
                  </th>
                ))}
                <th style={{width:70, userSelect:"none"}}>
                  <span style={{fontFamily:"var(--f-mono)", fontSize:10, letterSpacing:"0.06em", color:"var(--ash)"}}>AI評価</span>
                </th>
                <th style={{width:40}}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(a => {
                const na = nextActionFor(a, statuses);
                const past = na?.dateValue ? isPast(na.dateValue) : false;
                return (
                <tr key={a.id} onClick={() => setDetail(a)}>
                  <td style={{padding:"0 8px"}} onClick={e => e.stopPropagation()}>
                    <input type="checkbox"
                      checked={selectedIds.has(a.id)}
                      onChange={e => { e.stopPropagation(); setSelectedIds(prev => { const n = new Set(prev); n.has(a.id) ? n.delete(a.id) : n.add(a.id); return n; }); }}
                      aria-label={`${a.name}を選択`}
                      style={{cursor:"pointer"}}
                    />
                  </td>
                  <td>
                    <div style={{display:"flex", alignItems:"center", gap:10}}>
                      <div style={{width:32, height:32, borderRadius:"50%", background: a.photoColor || "#5B7BC4", display:"grid", placeItems:"center", color:"#0B1330", fontFamily:"var(--f-display)", fontSize:13, fontWeight:600}}>{a.photoInitial || a.name?.[0]}</div>
                      <div>
                        <div className="ws-table-title">{a.name}</div>
                        <div className="ws-table-notes">{a.kana} · {a.age}歳</div>
                      </div>
                    </div>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <select value={a.status} onChange={e => changeStatus(a.id, e.target.value)} className="ws-filter" style={{width: "100%"}}>
                      {statuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  </td>
                  <td>
                    {na ? (
                      <div style={{display:"flex", flexDirection:"column", gap:3}}>
                        <span style={{fontSize:12, color: na.color, fontWeight:500}}>{na.label}</span>
                        {na.dateValue ? (
                          <span style={{
                            fontFamily:"var(--f-mono)", fontSize:11,
                            color: past ? "var(--crimson-hi)" : "var(--ivory)",
                            letterSpacing:"0.04em",
                          }}>
                            {past && "⚠ "}{fmtNext(na.dateValue)}
                          </span>
                        ) : na.dateKey ? (
                          <span style={{fontFamily:"var(--f-mono)", fontSize:10, color:"var(--ash)", letterSpacing:"0.08em"}}>
                            日時未設定
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <span style={{color:"var(--smoke)", fontSize:11}}>—</span>
                    )}
                  </td>
                  <td>{a.position}</td>
                  <td>
                    <div style={{fontSize:12, color:"var(--ivory)"}}>{a.currentCompany || "—"}</div>
                    {a.currentRole && <div style={{fontSize:10, color:"var(--smoke)", marginTop:2}}>{a.currentRole}</div>}
                  </td>
                  <td><DateBadge date={a.appliedAt} compact/></td>
                  <td><span className="chip">{a.source}</span></td>
                  <td>
                    {a.evaluation?.overall != null ? (
                      <div style={{display:"flex", alignItems:"center", gap:5}}>
                        <span style={{fontFamily:"var(--f-mono)", fontSize:14, fontWeight:700, color: scoreColor(a.evaluation.overall)}}>{a.evaluation.overall}</span>
                        <span style={{fontFamily:"var(--f-mono)", fontSize:10, color:"var(--ash)"}}>/5</span>
                        <div style={{flex:1, height:4, background:"var(--hairline)", borderRadius:2, overflow:"hidden", minWidth:28}}>
                          <div style={{height:"100%", width:(a.evaluation.overall/5*100)+"%", background: scoreColor(a.evaluation.overall), borderRadius:2}}/>
                        </div>
                      </div>
                    ) : (
                      <span style={{fontFamily:"var(--f-mono)", fontSize:10, color:"var(--smoke)"}}>未評価</span>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="row-actions" style={{opacity:1}}>
                      <button className="row-action-btn" onClick={() => openEdit(a)}><Icon name="edit" size={12}/></button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="empty-state"><h4>該当する応募者はいません</h4><div>フィルタを変更するか「新規応募者」から追加してください。</div></div>
          )}
        </div>
      )}

      {mode === "board" && (() => {
        // Vertically-stacked stage lanes. Each lane holds one column PER status
        // (fine-grained control), laid out side-by-side and flexing to fill the
        // width — so most stages fit without horizontal scroll. Lanes are
        // collapsible to focus on the stages you care about.
        const groupOrder = stageOrderFrom(statuses);
        return (
        <div className="applicants-board">
          {groupOrder.map(g => {
            const meta = APPLICANT_STAGE_META[g] || { label: g, color: '#94a3b8' };
            const stageStatuses = statuses.filter(s => stageForStatus(s) === g);
            const stageCount = filtered.filter(a => stageForStatus(getStatusById(statuses, a.status)) === g).length;
            const collapsed = !!collapsedStages[g];
            return (
              <div key={g} className={"applicants-stage-lane" + (collapsed ? " collapsed" : "")} style={{"--stage-color": meta.color}}>
                <button className="applicants-lane-head" onClick={() => setCollapsedStages(c => ({ ...c, [g]: !c[g] }))}>
                  <span className="applicants-lane-chevron"><Icon name={collapsed ? "chevronRight" : "chevronDown"} size={14}/></span>
                  <span className="applicants-stage-dot"></span>
                  <span className="applicants-stage-label">{meta.label}</span>
                  <span className="applicants-stage-count">{stageCount}</span>
                </button>
                {!collapsed && (
                  <div className="applicants-lane-cols">
                    {stageStatuses.map(s => {
                      const items = filtered.filter(a => a.status === s.id);
                      return (
                        <div key={s.id} className="board-col applicants-board-col"
                          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("dragover"); }}
                          onDragLeave={(e) => e.currentTarget.classList.remove("dragover")}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove("dragover");
                            const id = e.dataTransfer.getData("text/plain");
                            if (id) changeStatus(id, s.id);
                          }}>
                          <div className="board-col-head applicants-status-head">
                            <span className="applicants-status-name" style={{color: s.color}}>
                              <span className="applicants-status-mark" style={{background: s.color}}></span>
                              {s.label}
                            </span>
                            <span className="applicants-status-count">{items.length}</span>
                          </div>
                          <div className="board-col-body applicants-board-body">
                            {items.map(a => {
                              const na = nextActionFor(a, statuses);
                              const past = na?.dateValue ? isPast(na.dateValue) : false;
                              return (
                                <div key={a.id} className={"board-card applicants-board-card" + (s.terminal ? " done" : "")}
                                  draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", a.id)}
                                  onClick={() => setDetail(a)}>
                                  <div style={{display:"flex", alignItems:"center", gap:8}}>
                                    <div className="applicants-board-avatar" style={{background: a.photoColor || "#5B7BC4"}}>{a.photoInitial || a.name?.[0]}</div>
                                    <div style={{minWidth:0, flex:1}}>
                                      <div className="board-card-title" style={{fontSize:12}}>{a.name}</div>
                                      <div className="applicants-board-company">{a.currentCompany || a.position || "—"}</div>
                                    </div>
                                  </div>
                                  {na?.dateValue && (
                                    <div className="applicants-board-foot">
                                      <span className="applicants-board-nextlabel" style={{color: na.color}}>{na.label}</span>
                                      <span className="applicants-board-next" style={past ? {color:"var(--crimson-hi)"} : null}>{past && "⚠ "}{fmtNext(na.dateValue)}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {items.length === 0 && <div className="board-col-empty applicants-board-empty">—</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        );
      })()}

      {mode === "analytics" && (() => {
        // Funnel mirrors the board's stage structure (shared APPLICANT_STAGE_META).
        // Honour the toolbar filters (職種 / ソース / 検索): the funnel reflects the
        // SAME subset the board & table show.
        const analytics = computeFunnelAnalytics(filtered, statuses);
        const { rows, total, evaluated, avgScore, scoreDist, channelRows } = analytics;
        const isFiltered = filterPosition !== "all" || filterSource !== "all" || filterStatus !== "all" || !!search;
        const maxDist = Math.max(1, ...scoreDist.map(d => d.count));
        const bottlenecks = rows.filter(r => r.bottleneck);
        return (
        <React.Fragment>
        <div className="panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">RECRUITMENT FUNNEL · 応募者ベース{isFiltered ? " · 絞り込み中" : ""}</span>
              <h3>選考ファネル{isFiltered ? <span style={{fontSize:13, color:"var(--gold, #c9a24b)", fontFamily:"var(--f-mono)", marginLeft:10}}>{filtered.length}/{list.length}件</span> : null}</h3>
            </div>
          </div>
          <div className="panel-body">
            {bottlenecks.length > 0 && (
              <div className="funnel-alert" style={{marginBottom:18, padding:"10px 14px", borderRadius:6, background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.4)", display:"flex", alignItems:"flex-start", gap:10}}>
                <span style={{fontSize:16, lineHeight:1}}>⚠️</span>
                <div style={{flex:1}}>
                  <div style={{fontFamily:"var(--f-mono)", fontSize:10, color:"#fca5a5", letterSpacing:"0.12em", marginBottom:4}}>BOTTLENECK DETECTED</div>
                  <div style={{fontSize:12, color:"var(--ivory)", lineHeight:1.6}}>
                    {bottlenecks.map(b => <span key={b.group} style={{marginRight:14, whiteSpace:"nowrap"}}><b style={{color:b.color}}>{b.label}</b> 通過率 <b style={{color:"#fca5a5", fontFamily:"var(--f-mono)"}}>{b.conversion}%</b></span>)}
                  </div>
                </div>
              </div>
            )}
            {rows.map((row, i) => {
              const denom = total || 1;
              const pct = row.reached > 0 ? Math.max(6, (row.reached / denom) * 100) : 0;
              return (
                <div key={i} className={"funnel-row" + (row.bottleneck ? " bottleneck" : "")}>
                  <div className="funnel-row-head">
                    <span className="funnel-row-label">
                      <span className="funnel-row-dot" style={{background: row.color}}></span>
                      {row.label}
                      {row.bottleneck && <span style={{marginLeft:6, fontSize:11, color:"#fca5a5"}}>⚠ ボトルネック</span>}
                    </span>
                    <span className="funnel-row-nums">
                      {row.conversion !== null && <span className="funnel-row-conv" style={row.bottleneck ? {color:"#fca5a5"} : null}>{row.conversion}%</span>}
                      <span className="funnel-row-count" style={{color: row.color}}>到達 {row.reached}</span>
                      <span className="funnel-row-here">現在 {row.here}</span>
                    </span>
                  </div>
                  <div className="funnel-track">
                    <div className="funnel-fill" style={{width: pct + "%", background: row.color}}/>
                  </div>
                </div>
              );
            })}
            <hr className="divider"/>
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(120px, 1fr))", gap:14, marginTop:14}}>
              {[
                [isFiltered ? "対象応募者" : "総応募者", filtered.length],
                ["選考中", filtered.filter(a => !isTerminalStatus(a.status)).length],
                ["内定", filtered.filter(a => statuses.find(s => s.id === a.status && s.group === "offer")).length],
                ["不合格・見送り", filtered.filter(a => { const s = statuses.find(x => x.id === a.status); return s?.terminal && s.id !== "hired"; }).length],
              ].map((kv, i) => (
                <div key={i} style={{padding:14, background:"rgba(11,19,48,0.5)", border:"1px solid var(--hairline)", borderRadius:6, textAlign:"center"}}>
                  <div style={{fontFamily:"var(--f-mono)", fontSize:10, color:"var(--smoke)", letterSpacing:"0.12em"}}>{kv[0]}</div>
                  <div style={{fontFamily:"var(--f-display)", fontSize:28, color:"var(--ivory)", marginTop:6}}>{kv[1]}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="panel mt-3">
          <div className="panel-head">
            <div>
              <span className="eyebrow">AI EVALUATION DISTRIBUTION</span>
              <h3>AI評価スコア分布 <span style={{fontSize:13, color:"var(--smoke)", fontFamily:"var(--f-mono)", marginLeft:10}}>{evaluated}/{total}名 評価済</span></h3>
            </div>
            {avgScore !== null && (
              <div style={{textAlign:"right"}}>
                <div style={{fontFamily:"var(--f-mono)", fontSize:10, color:"var(--smoke)", letterSpacing:"0.12em"}}>AVG</div>
                <div style={{fontFamily:"var(--f-mono)", fontSize:26, fontWeight:700, color: scoreColor(avgScore)}}>{avgScore.toFixed(1)} <span style={{fontSize:13, color:"var(--ash)", fontWeight:400}}>/5</span></div>
              </div>
            )}
          </div>
          <div className="panel-body">
            {evaluated === 0 ? (
              <div style={{padding:"24px 0", textAlign:"center", color:"var(--ash)", fontSize:12}}>AI評価がまだ実行されていません。一覧画面で「一括AI評価」または個別評価を実行してください。</div>
            ) : (
              <div style={{display:"flex", flexDirection:"column", gap:8}}>
                {scoreDist.map(d => (
                  <div key={d.score} style={{display:"grid", gridTemplateColumns:"56px 1fr 56px", alignItems:"center", gap:10}}>
                    <span style={{fontFamily:"var(--f-mono)", fontSize:13, fontWeight:700, color:scoreColor(d.score)}}>{d.score} ★</span>
                    <div className="funnel-track" style={{height:18}}>
                      <div className="funnel-fill" style={{
                        width: (d.count > 0 ? Math.max(4, (d.count / maxDist) * 100) : 0) + "%",
                        background: scoreColor(d.score),
                      }}/>
                    </div>
                    <span style={{fontFamily:"var(--f-mono)", fontSize:12, color:"var(--ivory)", textAlign:"right"}}>{d.count}名</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {channelRows.length > 0 && (
        <div className="panel mt-3">
          <div className="panel-head">
            <div>
              <span className="eyebrow">SOURCE × CONVERSION</span>
              <h3>チャネル別の到達率</h3>
            </div>
          </div>
          <div className="panel-body" style={{overflowX:"auto"}}>
            <table className="channel-funnel-table" style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
              <thead>
                <tr style={{textAlign:"left", color:"var(--smoke)", fontFamily:"var(--f-mono)", fontSize:10, letterSpacing:"0.12em"}}>
                  <th style={{padding:"8px 10px"}}>SOURCE</th>
                  <th style={{padding:"8px 10px", textAlign:"right"}}>応募</th>
                  <th style={{padding:"8px 10px", textAlign:"right"}}>書類通過</th>
                  <th style={{padding:"8px 10px", textAlign:"right"}}>面接到達</th>
                  <th style={{padding:"8px 10px", textAlign:"right"}}>内定到達</th>
                  <th style={{padding:"8px 10px", textAlign:"right"}}>入社</th>
                </tr>
              </thead>
              <tbody>
                {channelRows.map(c => (
                  <tr key={c.source} style={{borderTop:"1px solid var(--hairline)"}}>
                    <td style={{padding:"10px", color:"var(--ivory)"}}>{c.source}</td>
                    <td style={{padding:"10px", textAlign:"right", fontFamily:"var(--f-mono)", color:"var(--ivory)"}}>{c.total}</td>
                    <td style={{padding:"10px", textAlign:"right", fontFamily:"var(--f-mono)"}}>
                      <span style={{color:"var(--ivory)"}}>{c.docs}</span>
                      {c.docsPct !== null && <span style={{color:"var(--ash)", marginLeft:6, fontSize:10}}>{c.docsPct}%</span>}
                    </td>
                    <td style={{padding:"10px", textAlign:"right", fontFamily:"var(--f-mono)"}}>
                      <span style={{color:"var(--ivory)"}}>{c.interview}</span>
                      {c.interviewPct !== null && <span style={{color:"var(--ash)", marginLeft:6, fontSize:10}}>{c.interviewPct}%</span>}
                    </td>
                    <td style={{padding:"10px", textAlign:"right", fontFamily:"var(--f-mono)"}}>
                      <span style={{color:"var(--gold, #c9a24b)"}}>{c.offer}</span>
                      {c.offerPct !== null && <span style={{color:"var(--ash)", marginLeft:6, fontSize:10}}>{c.offerPct}%</span>}
                    </td>
                    <td style={{padding:"10px", textAlign:"right", fontFamily:"var(--f-mono)", color:"#5ac994"}}>{c.hired}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}
        </React.Fragment>
        );
      })()}

      <EntityModal
        open={modalOpen}
        schema={schema}
        initial={editing}
        onSave={handleSave}
        onDelete={editing && editing.id && !editing.__isNew ? handleDelete : null}
        onClose={() => setModalOpen(false)}
      />

      <ApplicantAIImport
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        positions={positions}
        sources={sources}
        statuses={statuses}
        positionLevels={getPositionLevels(state)}
        onImport={(records) => {
          // Single batched insert with per-record unique ids. (crud.add uses
          // "n"+Date.now() which collides when called in a tight loop → dup keys.)
          if (records && records.length) {
            const base = Date.now();
            const toAdd = records.map((r, i) => ({ ...r, id: r.id || `ai${base}_${i}` }));
            setState(s => ({ ...s, applicants: [...toAdd, ...(s.applicants || [])] }));
          }
          setAiOpen(false);
        }}
      />

      {detail && ReactDOM.createPortal((
        <div className="rec-overlay" onClick={(e) => { if (e.target.classList.contains("rec-overlay")) setDetail(null); }}>
          <div className="rec-stage">
            <div className="rec-stage-head">
              <div style={{width:56, height:56, borderRadius:"50%", background: detail.photoColor || "#5B7BC4", display:"grid", placeItems:"center", color:"#0B1330", fontFamily:"var(--f-display)", fontSize:22, fontWeight:600}}>{detail.photoInitial || detail.name?.[0]}</div>
              <div style={{flex:1}}>
                <span className="eyebrow">{detail.position}</span>
                <h2>{detail.name}{detail.anonymous ? <span style={{fontSize:12, color:"var(--ash)", fontWeight:400, marginLeft:8}}>（氏名非公開）</span> : null} <span style={{fontSize:13, color:"var(--ash)", letterSpacing:"0.05em", marginLeft:8}}>{[detail.kana, detail.age ? `${detail.age}歳` : "", genderLabel(detail.gender)].filter(Boolean).join(" · ")}</span></h2>
              </div>
              <ApplicantStatusChip status={detail.status} statuses={statuses}/>
              <button className="icon-btn" onClick={() => setDetail(null)} title="閉じる" aria-label="閉じる"><Icon name="x" size={18}/></button>
            </div>
            <div className="rec-stage-body">
              <div className="form-grid">
                {/* AI人物評価（取り込み時に生成）。4軸スコア＋人物像＋総合所見。表示専用・編集時保持。 */}
                {detail.evaluation && (() => {
                  const ev = detail.evaluation;
                  const axes = [
                    ["職種適正", ev.positionFit],
                    ["業界適正", ev.industryFit],
                    ["役職レベル適正", ev.levelFit],
                    ["総合", ev.overall],
                  ].filter(a => a[1] != null);
                  if (!axes.length && !ev.persona && !ev.comment && !ev.strengths && !ev.concerns) return null;
                  return (
                    <div className="span-2 appl-eval">
                      <div className="appl-eval-head">
                        <span className="label-mono">AI人物評価</span>
                        {ev.evaluatedFor && ev.evaluatedFor.position && <span className="appl-eval-for">対象職種: {ev.evaluatedFor.position}</span>}
                      </div>
                      <div className="appl-eval-caution">⚠ AIによる参考評価です。<strong>単独の合否判断には用いず</strong>、最終判断は必ず人が行ってください（性別・年齢等の属性は評価に使用していません）。</div>
                      {axes.length > 0 && (
                        <div className="appl-eval-axes">
                          {axes.map(([label, v]) => (
                            <div key={label} className="appl-eval-axis">
                              <div className="appl-eval-axis-label">{label}</div>
                              <div className="appl-eval-axis-score" style={{ color: scoreColor(v) }}>{v}<span>/5</span></div>
                              <div className="appl-eval-bar"><div className="appl-eval-bar-fill" style={{ width: (v/5*100)+"%", background: scoreColor(v) }}/></div>
                            </div>
                          ))}
                        </div>
                      )}
                      {ev.persona && <div className="appl-eval-block"><span className="label-mono">人物像</span><div style={{whiteSpace:"pre-wrap"}}>{ev.persona}</div></div>}
                      {ev.strengths && <div className="appl-eval-block"><span className="label-mono">強み</span><div style={{whiteSpace:"pre-wrap"}}>{ev.strengths}</div></div>}
                      {ev.concerns && <div className="appl-eval-block"><span className="label-mono">懸念・確認点</span><div style={{whiteSpace:"pre-wrap"}}>{ev.concerns}</div></div>}
                      {ev.comment && <div className="appl-eval-block"><span className="label-mono">総合所見</span><div style={{whiteSpace:"pre-wrap"}}>{ev.comment}</div></div>}
                    </div>
                  );
                })()}
                <div><div className="label-mono">応募職種</div><div style={{color:"var(--ivory)", marginTop:4}}>{detail.position || "—"}</div></div>
                <div><div className="label-mono">応募日</div><div style={{color:"var(--ivory)", marginTop:4}}><DateBadge date={detail.appliedAt}/></div></div>
                <div><div className="label-mono">ソース</div><div style={{color:"var(--ivory)", marginTop:4}}>{detail.source || "—"}</div></div>
                <div><div className="label-mono">希望給与</div><div style={{color:"var(--ivory)", marginTop:4}}>{detail.desiredSalary || "—"}</div></div>
                <div><div className="label-mono">電話</div><div style={{color:"var(--ivory)", marginTop:4, fontFamily:"var(--f-mono)"}}>{detail.phone || "—"}</div></div>
                <div><div className="label-mono">メール</div><div style={{color:"var(--ivory)", marginTop:4, fontFamily:"var(--f-mono)"}}>{detail.email || "—"}</div></div>
                <div className="span-2"><div className="label-mono">住所</div><div style={{color:"var(--ivory)", marginTop:4}}>{detail.address || "—"}</div></div>

                {/* Computed next action — the single most actionable field for a recruiter. */}
                {(() => {
                  const na = nextActionFor(detail, statuses);
                  const past = na && na.dateValue ? isPast(na.dateValue) : false;
                  return (
                    <div className="span-2">
                      <div className="label-mono">次のアクション</div>
                      <div style={{marginTop:6, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap"}}>
                        {na ? (
                          <>
                            <span style={{color:"var(--ivory)", fontWeight:600}}>{na.label}</span>
                            {na.dateValue
                              ? <span style={{fontFamily:"var(--f-mono)", fontSize:12, color: past ? "var(--crimson-hi)" : "var(--gold)"}}>{fmtNext(na.dateValue)}{past ? " ⚠ 期限超過" : ""}</span>
                              : <span style={{color:"var(--ash)", fontSize:12}}>日程未定</span>}
                          </>
                        ) : <span style={{color:"var(--ash)"}}>選考終了 / 次アクションなし</span>}
                      </div>
                    </div>
                  );
                })()}

                {/* Selection timeline */}
                <div className="span-2">
                  <div className="label-mono" style={{marginBottom:8}}>選考スケジュール</div>
                  <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(92px, 1fr))", gap:10}}>
                    {APPLICANT_DATE_FIELDS.map(df => {
                      const v = detail[df.key];
                      const past = v && isPast(v);
                      return (
                        <div key={df.key} style={{
                          padding:"10px 12px",
                          background:"rgba(0,0,0,0.28)",
                          border:"1px solid var(--hairline)",
                          borderRadius:5,
                          textAlign:"center",
                        }}>
                          <div style={{fontFamily:"var(--f-mono)", fontSize:9, color:"var(--ash)", letterSpacing:"0.10em", marginBottom:6}}>{df.label}</div>
                          <div style={{fontFamily:"var(--f-mono)", fontSize:12, color: v ? (past ? "var(--smoke)" : "var(--gold)") : "var(--smoke)", letterSpacing:"0.02em"}}>
                            {v ? fmtNext(v) : "—"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="span-2"><div className="label-mono">現職</div><div style={{color:"var(--ivory)", marginTop:4}}>{[detail.currentCompany, detail.currentRole].filter(Boolean).join(" · ") || "—"}{detail.employmentStatus && <span style={{color:"var(--ash)", fontSize:12, marginLeft:8}}>（{detail.employmentStatus}）</span>}</div></div>

                {detail.summary && (
                  <div className="span-2"><div className="label-mono">職務要約</div><div style={{color:"var(--ivory)", marginTop:6, lineHeight:1.7, fontSize:13, whiteSpace:"pre-wrap"}}>{detail.summary}</div></div>
                )}
                {detail.workHistory && detail.workHistory.length > 0 && (
                  <div className="span-2">
                    <div className="label-mono" style={{marginBottom:8}}>職務経歴</div>
                    <div style={{display:"flex", flexDirection:"column", gap:10}}>
                      {detail.workHistory.map((w, wi) => (
                        <div key={wi} style={{padding:"12px 14px", background:"rgba(0,0,0,0.28)", border:"1px solid var(--hairline)", borderRadius:6}}>
                          <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:10, flexWrap:"wrap"}}>
                            <span style={{color:"var(--ivory)", fontWeight:600}}>{w.company}{w.title && <span style={{color:"var(--ash)", fontWeight:400}}> · {w.title}</span>}</span>
                            {w.period && <span style={{fontFamily:"var(--f-mono)", fontSize:11, color:"var(--ash)"}}>{w.period}</span>}
                          </div>
                          {w.detail && <div style={{color:"var(--ash)", marginTop:6, lineHeight:1.6, fontSize:12, whiteSpace:"pre-wrap"}}>{w.detail}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {detail.skills && detail.skills.length > 0 && (
                  <div className="span-2"><div className="label-mono">スキル・活かせる経験</div><div style={{display:"flex", gap:6, marginTop:8, flexWrap:"wrap"}}>{detail.skills.map((s, si) => <span key={si} className="chip">{s}</span>)}</div></div>
                )}
                {detail.experience && (
                  <div className="span-2"><div className="label-mono">経験（職種・業種・マネジメント）</div><div style={{color:"var(--ivory)", marginTop:6, lineHeight:1.6, fontSize:13, whiteSpace:"pre-wrap"}}>{detail.experience}</div></div>
                )}
                {detail.education && (
                  <div className="span-2"><div className="label-mono">学歴</div><div style={{color:"var(--ivory)", marginTop:4, whiteSpace:"pre-wrap"}}>{detail.education}</div></div>
                )}
                {detail.qualifications && (
                  <div className="span-2"><div className="label-mono">資格・免許</div><div style={{color:"var(--ivory)", marginTop:6, lineHeight:1.6, fontSize:13, whiteSpace:"pre-wrap"}}>{detail.qualifications}</div></div>
                )}
                {detail.languages && (
                  <div><div className="label-mono">語学</div><div style={{color:"var(--ivory)", marginTop:4}}>{detail.languages}</div></div>
                )}
                {detail.desiredConditions && (
                  <div className="span-2"><div className="label-mono">希望条件</div><div style={{color:"var(--ivory)", marginTop:6, lineHeight:1.6, fontSize:13, whiteSpace:"pre-wrap"}}>{detail.desiredConditions}</div></div>
                )}

                {detail.tags && detail.tags.length > 0 && (
                  <div className="span-2"><div className="label-mono">タグ</div><div style={{display:"flex", gap:6, marginTop:8, flexWrap:"wrap"}}>{detail.tags.map(t => <span key={t} className="chip gold">{t}</span>)}</div></div>
                )}
                {detail.motivation && (
                  <div className="span-2"><div className="label-mono">志望動機</div><div style={{color:"var(--ivory)", marginTop:6, lineHeight:1.7, fontSize:13}}>{detail.motivation}</div></div>
                )}
                {detail.note && (
                  <div className="span-2"><div className="label-mono">メモ</div><div style={{color:"var(--ash)", marginTop:6, lineHeight:1.6, fontSize:12, fontStyle:"italic"}}>{detail.note}</div></div>
                )}
              </div>
            </div>
            <div className="rec-stage-foot" style={{ flexWrap: "wrap" }}>
              <button className="btn ghost" onClick={() => { setDetail(null); openEdit(detail); }}><Icon name="edit" size={12}/><span>編集</span></button>
              <button className="btn sm gold" disabled={reEvaluating} onClick={() => reEvaluate(detail)} title="AIがこの応募者の適性を評価します">
                <span>{reEvaluating ? "評価中…" : detail.evaluation ? "⚡ 再評価" : "⚡ AI評価"}</span>
              </button>
              {reEvalErr && <span style={{ fontSize: 11, color: "var(--crimson-hi)", alignSelf: "center" }}>{reEvalErr}</span>}
              <div style={{flex:1}}/>
              <select className="ws-filter" value={detail.status} onChange={e => { changeStatus(detail.id, e.target.value); setDetail({ ...detail, status: e.target.value }); }}>
                {statuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <button className="btn primary" onClick={() => { setDetail(null); setReEvalErr(""); }}>閉じる</button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
};

// ── AI import: paste text or drop files (image/PDF/text) → Claude extracts
// applicant records → review → register. Mirrors the contacts business-card flow. ──
const AI_IMPORT_PASTELS = ['#e0e7ff','#dcfce7','#fce7f3','#fef9c3','#ffe4e6','#cffafe','#ede9fe','#fae8ff'];

// Clamp an AI score to a 1..5 integer (boolean/null/""/non-numeric → null).
const __clampScore = (n) => {
  if (typeof n === "boolean" || n == null || n === "") return null;
  const num = Number(n);
  if (!Number.isFinite(num)) return null;
  const v = Math.round(num);
  return v >= 1 && v <= 5 ? v : null;
};

// Build the stored evaluation object from a raw AI evaluation, or null when the
// model produced nothing usable. Pure + exported. evaluatedFor records the
// position/level the candidate was judged against.
function buildEvaluation(rawEv, opts) {
  const { position = "", levelDef = "", today = "" } = opts || {};
  const ev = (rawEv && typeof rawEv === "object") ? rawEv : {};
  const str = (v) => (v == null ? "" : String(v)).trim();
  const out = {
    positionFit: __clampScore(ev.positionFit),
    industryFit: __clampScore(ev.industryFit),
    levelFit: __clampScore(ev.levelFit),
    overall: __clampScore(ev.overall),
    persona: str(ev.persona),
    strengths: str(ev.strengths),
    concerns: str(ev.concerns),
    comment: str(ev.comment),
    evaluatedFor: { position: str(position), level: str(levelDef) },
    evaluatedAt: today,
  };
  const has = out.positionFit != null || out.industryFit != null || out.levelFit != null ||
    out.overall != null || out.persona || out.comment || out.strengths || out.concerns;
  return has ? out : null;
}

// Tolerant JSON extraction from an LLM response (strips fences, recovers the
// outermost array/object). Throws if no JSON is parseable.
function parseJsonLoose(resp) {
  const raw = String(resp || "").trim().replace(/^```(?:json)?/i, "").replace(/```$/, "").trim();
  try { return JSON.parse(raw); }
  catch {
    const aS = raw.indexOf("["), oS = raw.indexOf("{");
    const useArr = aS >= 0 && (oS < 0 || aS < oS);
    const s = useArr ? aS : oS;
    const e2 = raw.lastIndexOf(useArr ? "]" : "}");
    return JSON.parse(raw.slice(s, e2 + 1));
  }
}

// Compact, ATTRIBUTE-BLIND profiles for the separate evaluation call: only
// career/skill facts are sent — name, gender, age, address, email, phone are
// deliberately omitted so the model physically cannot factor protected
// attributes into the score (公正な採用選考). idx keys the result back to the row.
function buildEvalProfiles(applicants) {
  return (applicants || []).map((a, i) => ({
    idx: i,
    currentCompany: a.currentCompany || "",
    currentRole: a.currentRole || "",
    summary: a.summary || "",
    workHistory: (a.workHistory || []).map((w) => ({ company: w.company, period: w.period, title: w.title, detail: w.detail })),
    skills: a.skills || [],
    experience: a.experience || "",
    education: a.education || "",
    qualifications: a.qualifications || "",
    languages: a.languages || "",
    desiredConditions: a.desiredConditions || "",
    employmentStatus: a.employmentStatus || "",
    tags: a.tags || [],
  }));
}

// Merge the evaluation-pass result into the extracted applicants. Trusts EXPLICIT
// numeric idx; only falls back to positional order when NO element carries idx —
// this avoids cross-candidate score collisions on a partially-idx'd response (a
// recruiter must never see another person's evaluation). Duplicate idx → first
// wins. Returns how many applicants actually received a (non-null) evaluation.
function mergeEvaluations(applicants, evArr, opts) {
  const { levelDef = "", today = "" } = opts || {};
  const arr = Array.isArray(evArr) ? evArr : [];
  const anyIdx = arr.some((e) => e && typeof e.idx === "number");
  const byIdx = new Map();
  if (anyIdx) arr.forEach((e) => { if (e && typeof e.idx === "number" && !byIdx.has(e.idx)) byIdx.set(e.idx, e); });
  else arr.forEach((e, i) => byIdx.set(i, e));
  let matched = 0;
  (applicants || []).forEach((a, i) => {
    const e = byIdx.get(i);
    if (!e) return;
    const ev = buildEvaluation(e, { position: a.position, levelDef, today });
    if (ev) { a.evaluation = ev; matched++; }
  });
  return matched;
}

// Extract plain text from a PDF File in the browser via pdf.js (window.pdfjsLib,
// vendored UMD). Caps pages + total length so a huge resume can't make the prompt
// unbounded. Returns "" if pdf.js is unavailable or the PDF has no text layer
// (scanned) — the caller then falls back to sending the document for vision.
async function extractPdfText(file, maxPages = 60, maxChars = 120000) {
  const lib = (typeof window !== "undefined") && window.pdfjsLib;
  if (!lib || !lib.getDocument) throw new Error("pdfjs unavailable");
  if (lib.GlobalWorkerOptions && !lib.GlobalWorkerOptions.workerSrc) {
    lib.GlobalWorkerOptions.workerSrc = "/vendor/pdf.worker.min.js";
  }
  const buf = await file.arrayBuffer();
  const pdf = await lib.getDocument({ data: new Uint8Array(buf) }).promise;
  try {
    const pages = Math.min(pdf.numPages || 0, maxPages);
    const parts = [];
    let total = 0;
    for (let i = 1; i <= pages; i++) {
      const page = await pdf.getPage(i);
      const tc = await page.getTextContent();
      const s = (tc.items || []).map((it) => (it && it.str) || "").join(" ").replace(/[ \t]+/g, " ").trim();
      if (s) { parts.push(s); total += s.length; }
      if (total >= maxChars) break;
    }
    return parts.join("\n").slice(0, maxChars);
  } finally {
    try { pdf.destroy && pdf.destroy(); } catch (e) {}
  }
}

// Split combined résumé text into one chunk per candidate so each is extracted in
// its own small/fast AI call (extracting many rich résumés in a single call made the
// response long enough to hit Anthropic's 524 gateway timeout). A multi-person
// recruiting export delimits each record with a unique "BU<digits>" id that repeats
// per page; we cut at the FIRST appearance of each distinct id, in order. Fewer than
// two distinct ids → a single chunk (one résumé / generic paste). Pure + exported.
function splitResumeChunks(text) {
  const s = String(text || "");
  if (!s.trim()) return [];
  const firstAt = new Map();
  const re = /BU\d{5,}/g;
  let m;
  while ((m = re.exec(s)) !== null) {
    if (!firstAt.has(m[0])) firstAt.set(m[0], m.index);
  }
  const bounds = [...firstAt.values()].sort((a, b) => a - b);
  if (bounds.length < 2) return [s.trim()];
  const chunks = [];
  for (let i = 0; i < bounds.length; i++) {
    const chunk = s.slice(bounds[i], i + 1 < bounds.length ? bounds[i + 1] : s.length).trim();
    if (chunk) chunks.push(chunk);
  }
  return chunks;
}

// Normalize one raw AI-extracted record into a stored applicant. Pure + exported
// so the rich-resume parsing (work history, skills, desired conditions, etc.) is
// unit-testable without the network.
// opts: { positions, sources, statuses, today, index, forcedPosition, levelDef }.
//   forcedPosition — the single 募集職種 chosen for this import batch (analysis runs
//   AFTER it's picked, so the AI evaluates fit against it). When set, every record
//   takes this position regardless of what the model echoed back.
function normalizeImportedApplicant(x, opts) {
  const { positions = [], sources = [], statuses = [], today = "", index = 0, forcedPosition = "", levelDef = "",
    forcedCandidateId = "", operatorName = "" } = opts || {};
  x = x || {};
  const str = (v) => (v == null ? "" : String(v)).trim();
  // 人材紹介サービスのエクスポートは候補者が匿名（BU<id>のみ・氏名なし）で、文書内の
  // 唯一の人名がアカウント名（=採用担当者本人, 例:竹内雅明）であることが多い。
  // ① 抽出された氏名が利用者名と一致したら捨てる（取り違え防止）
  // ② 候補者IDがあれば識別子にし、匿名（氏名なし）でも捨てない
  const candidateId = str(forcedCandidateId) || str(x.candidateId);
  let realName = str(x.name);
  if (operatorName && realName && realName.replace(/\s/g, "") === String(operatorName).replace(/\s/g, "")) realName = "";
  const anonymous = !realName && !!candidateId;
  const name = realName || candidateId || str(x.email) || "応募者";
  const g = str(x.gender);
  const gender = (g === "male" || g === "男性" || g === "男") ? "male"
    : (g === "female" || g === "女性" || g === "女") ? "female"
    : (g ? "other" : "");
  const workHistory = Array.isArray(x.workHistory)
    ? x.workHistory.slice(0, 8).map((w) => ({
        company: str(w && w.company),
        period: str(w && w.period),
        title: str(w && (w.title || w.role)),
        detail: str(w && w.detail),
      })).filter((w) => w.company || w.title || w.detail)
    : [];
  const finalPosition = forcedPosition || (positions.includes(x.position) ? x.position : "その他");
  return {
    name, kana: x.kana || "",
    age: typeof x.age === "number" ? x.age : (parseInt(x.age) || null),
    gender,
    email: x.email || "", phone: x.phone || "", address: x.address || "",
    currentCompany: x.currentCompany || "", currentRole: x.currentRole || "",
    desiredSalary: x.desiredSalary || "",
    position: finalPosition,
    source: sources.includes(x.source) ? x.source : "その他",
    appliedAt: today, status: (statuses[0] && statuses[0].id) || "interested",
    motivation: x.motivation || "", note: x.note || "",
    tags: Array.isArray(x.tags) ? x.tags.slice(0, 5).map(str).filter(Boolean) : [],
    // ── rich resume fields (2026-06-15) — preserved through edits via EntityModal's
    //    {...initial, ...values} merge even though some aren't in the edit schema ──
    summary: x.summary || "",
    workHistory,
    education: x.education || "",
    qualifications: x.qualifications || "",
    languages: x.languages || "",
    skills: Array.isArray(x.skills) ? x.skills.slice(0, 18).map(str).filter(Boolean) : [],
    experience: x.experience || "",
    desiredConditions: x.desiredConditions || "",
    employmentStatus: x.employmentStatus || "",
    candidateId,
    anonymous,
    // Single-call compat: if the model embedded an evaluation, keep it. The 2-call
    // flow leaves this null and fills it from the separate (Opus) eval pass.
    evaluation: buildEvaluation(x.evaluation, { position: finalPosition, levelDef, today }),
    photoInitial: name[0] || "?",
    photoColor: AI_IMPORT_PASTELS[index % AI_IMPORT_PASTELS.length],
    __pick: true,
  };
}

const ApplicantAIImport = ({ open, onClose, positions, sources, statuses, positionLevels = {}, onImport }) => {
  const [text, setText] = React.useState("");
  const [files, setFiles] = React.useState([]);
  const [phase, setPhase] = React.useState("input"); // input | analyzing | review
  const [results, setResults] = React.useState([]);
  const [err, setErr] = React.useState("");
  const [evalNote, setEvalNote] = React.useState(""); // soft notice if the eval pass was skipped
  const [dragging, setDragging] = React.useState(false);
  // 募集職種は分析の前に選ぶ（AIがその職種・求めるレベルへの適正を評価できるように）。
  const [position, setSelectedPosition] = React.useState((positions && positions[0]) || "その他");
  const fileRef = React.useRef(null);
  const dragDepth = React.useRef(0); // ignore dragenter/leave bubbling from children

  React.useEffect(() => {
    if (!open) { setText(""); setFiles([]); setResults([]); setErr(""); setEvalNote(""); setPhase("input"); setDragging(false); dragDepth.current = 0; }
    else setSelectedPosition((positions && positions[0]) || "その他");
  }, [open]);
  if (!open) return null;

  const levelDef = (positionLevels && positionLevels[position]) || "";

  const fileToBase64 = (file) => new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^;]+;base64,/, ""));
    r.readAsDataURL(file);
  });
  const fileToText = (file) => new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.readAsText(file);
  });
  const readFile = async (file) => {
    const isImg = (file.type || "").startsWith("image/");
    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
    if (isImg) return { name: file.name, kind: "image", base64: await fileToBase64(file), mime: file.type || "image/png" };
    if (isPdf) {
      // Extract text in the BROWSER. We NEVER send the PDF binary to the AI: a real
      // resume PDF is many pages with embedded images, and Anthropic rasterizes every
      // page → upstream 524 timeout (the repeatedly-reported failure). Local text
      // extraction is instant. If there's no usable text layer (scanned PDF) we mark
      // it unreadable and ask the user to paste the text — never a doomed 524 send.
      try {
        const text = await extractPdfText(file);
        if (text && text.replace(/\s/g, "").length >= 30) {
          return { name: file.name, kind: "text", textContent: text, fromPdf: true };
        }
      } catch (e) { /* no text layer / pdf.js failure → unreadable */ }
      return { name: file.name, kind: "pdf-unreadable" };
    }
    return { name: file.name, kind: "text", textContent: await fileToText(file) };
  };

  // Shared: read a FileList and append to state (used by picker AND drag-drop).
  const addFiles = async (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    const parsed = await Promise.all(picked.map(readFile));
    setFiles((f) => [...f, ...parsed]);
  };

  const onFiles = async (e) => {
    await addFiles(e.target.files);
    if (fileRef.current) fileRef.current.value = "";
  };

  // ── Drag & drop ──────────────────────────────────────────────────────────
  const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const onDragEnter = (e) => {
    if (phase === "analyzing" || !isFileDrag(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragOver = (e) => {
    if (phase === "analyzing" || !isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = (e) => {
    if (!isFileDrag(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  const onDrop = async (e) => {
    if (phase === "analyzing") return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    const dt = e.dataTransfer;
    if (dt?.files && dt.files.length) { await addFiles(dt.files); return; }
    // Some browsers drop text (e.g. selected resume text) instead of a file.
    const dropped = dt?.getData?.("text/plain");
    if (dropped) setText((t) => (t ? t + "\n\n" : "") + dropped);
  };

  const analyze = async () => {
    setErr(""); setEvalNote("");
    const textFiles = files.filter((f) => f.kind === "text").map((f) => `【ファイル: ${f.name}】\n${f.textContent}`).join("\n\n");
    const combinedText = [text.trim(), textFiles].filter(Boolean).join("\n\n");
    const mediaFiles = files.filter((f) => f.kind === "image"); // PDFs are extracted to text in the browser; never sent as a document
    const unreadable = files.filter((f) => f.kind === "pdf-unreadable");
    if (!combinedText && mediaFiles.length === 0) {
      setErr(unreadable.length
        ? `PDF（${unreadable.map((f) => f.name).join("・")}）からテキストを取得できませんでした。スキャン画像のPDFの可能性があります。PDFを開いてテキストをコピーし、上の入力欄に貼り付けてからもう一度お試しください。`
        : "テキストを貼り付けるか、ファイルを追加してください。");
      return;
    }
    setPhase("analyzing");

    // Extraction prompt for ONE chunk of input. Per-candidate chunks are extracted in
    // separate small calls (see below) so each response is short — asking one call to
    // emit rich data for 5 résumés made the generation so long that Anthropic's gateway
    // returned 524.
    const instructionFor = (inputText, candidateId) => `あなたは採用担当者のアシスタントです。以下の入力(履歴書・職務経歴書・メール・スカウト返信・応募者リスト等)から**応募者本人**の情報を抽出し、**JSON配列のみ**で返してください。コードフェンス・前置き・後置きの説明は禁止。先頭は [ 末尾は ] 。

複数名いれば全員を配列要素に。1名なら要素1つ。**同一人物を重複して出力しないこと**(1つの履歴書/職務経歴書が複数ページに渡っていても、それは1名です)。読み取れない項目は空文字 ""(ageはnull、tagsは空配列)。

【重要・取り違え防止】
- これは人材紹介サービスの候補者データで、**応募者の氏名は伏せられている（匿名）ことが多い**。氏名が明記されていなければ name は必ず空文字 "" にすること。BU番号などのIDを name に入れないこと。
- 「アカウント名：」「出力日時：」に続く会社名・担当者名（例: 株式会社セイムホールディングス / 竹内雅明 など）は、この検索を行った**採用担当者（サービス利用者）側の情報**です。**応募者として絶対に抽出しないでください**（name・currentCompany 等に使わない）。応募者の現職・経歴は職務経歴欄から読み取ること。${candidateId ? `\n- この候補者の識別ID: ${candidateId}（氏名が無い場合の識別用。name には入れない）。この入力に含まれる候補者は1名だけです。` : ""}

各要素の形式(読み取れた情報はできる限り埋める。職務経歴書があれば職務要約・職務経歴・スキル・資格・希望条件も必ず抽出する):
{
  "name": "氏名(漢字。なければ読み取れた表記)",
  "kana": "ふりがな(ひらがな)",
  "age": 数値またはnull,
  "gender": "male / female のいずれか。不明なら空文字",
  "email": "メール",
  "phone": "電話(ハイフン付き)",
  "address": "住所(都道府県だけでも可)",
  "employmentStatus": "就業状況。在職 / 離職 のいずれか(分かれば)",
  "currentCompany": "現職(直近)の会社名",
  "currentRole": "現職(直近)の役職/担当",
  "desiredSalary": "希望給与(例 600万円以上)",
  "position": "応募職種。次のいずれかに正規化: ${positions.join(' / ')} / その他",
  "source": "応募経路。次のいずれか: ${sources.join(' / ')} / その他",
  "summary": "職務要約(あれば原文を尊重して2〜5文に。無ければ空文字)",
  "workHistory": [
    { "company": "会社名", "period": "在籍期間(例 2015年4月〜2026年3月)", "title": "役職/職種", "detail": "担当業務と主な実績を2〜4文で要約" }
  ],
  "education": "学歴(学校名・学部/専攻)",
  "qualifications": "資格・免許(複数は読点や改行で区切る)",
  "languages": "語学(例 英語/ビジネスレベル。無ければ空文字)",
  "skills": ["活かせる経験・スキルを5〜15個。短い語句で"],
  "experience": "経験職種・経験業種・マネジメント経験を簡潔に要約(例 営業/個人営業(10年)、広報・PR(7年)、マネジメント 最大20名)",
  "desiredConditions": "希望条件を1まとめに(希望職種/希望業種/希望年収/希望勤務地/興味のある働き方)",
  "motivation": "志望動機(あれば1〜2文に要約)",
  "note": "特記事項(あれば)",
  "tags": ["強みやキーワードを2〜4個"]
}

職務経歴書のように情報量が多い場合でも、workHistory・skills・summary を省略せずできる限り埋めること。読み取れない項目は空文字 ""(age は null、workHistory/skills/tags は空配列)。

入力:
${inputText || "(テキストなし。添付ファイルを読み取ってください)"}`;

    // Diagnostic context baked into any error — so a single failure screenshot tells
    // us the running build (stale cache vs new code) and exactly what was sent.
    const buildId = (typeof window !== "undefined" && window.__SUPERSYS_BUILD) || "dev";
    const inputKinds = [];
    if (text.trim()) inputKinds.push("貼付テキスト");
    if (files.some((f) => f.kind === "text" && f.fromPdf)) inputKinds.push(`PDF抽出テキスト${combinedText.length}字`);
    if (files.some((f) => f.kind === "image")) inputKinds.push("画像");
    if (files.some((f) => f.kind === "pdf-unreadable")) inputKinds.push("未抽出PDF");
    const inputDesc = inputKinds.join("+") || "なし";

    const today = new Date().toISOString().slice(0, 10);
    // The export header carries the SERVICE-USER's name ("アカウント名：<会社> / <氏名>",
    // e.g. 竹内雅明). Detect it so we never mistake the recruiter for a candidate.
    const opMatch = combinedText.match(/アカウント名[：:]\s*[^\n／/]*[／/]\s*([^\s／/\n]{2,20})/);
    const operatorName = opMatch ? opMatch[1].trim() : "";
    // Split the combined résumé text into PER-CANDIDATE chunks (a multi-person export
    // delimits each record with a unique "BU<id>"). One résumé → one chunk.
    const textChunks = combinedText ? splitResumeChunks(combinedText) : [];
    const candidateIdOf = (chunk) => { const m = String(chunk).match(/BU\d{5,}/); return m ? m[0] : ""; };
    let extractFails = 0;

    try {
      // ── 1) EXTRACTION — one fast Sonnet call PER candidate chunk (run in parallel),
      // plus one vision call for any attached images. Each call's output is bounded to
      // ~1 person, which is what eliminates the 524 timeout. A chunk that fails just
      // contributes nothing (partial success), never aborting the whole import. ──
      const extractOne = async (content, candidateId) => {
        try {
          const resp = await window.claude.complete({ messages: [{ role: "user", content }], max_tokens: 4096 });
          const parsed = parseJsonLoose(resp);
          const arr = Array.isArray(parsed) ? parsed : [parsed];
          return arr
            .map((x) => normalizeImportedApplicant(x, { positions, sources, statuses, today, index: 0, forcedPosition: position, levelDef, forcedCandidateId: candidateId, operatorName }))
            // Keep anonymous candidates: a per-chunk record always has a candidateId, so
            // it survives even without a name/email (the old name||email filter dropped
            // exactly these — the "5名→2名" bug).
            .filter((r) => r.name || r.email || r.candidateId);
        } catch (err) { extractFails++; return []; }
      };
      const tasks = textChunks.map((chunk) => { const cid = candidateIdOf(chunk); return extractOne([{ type: "text", text: instructionFor(chunk, cid) }], cid); });
      if (mediaFiles.length) {
        const imgContent = mediaFiles.map((f) => ({ type: "image", source: { type: "base64", media_type: f.mime, data: f.base64 } }));
        imgContent.push({ type: "text", text: instructionFor("(添付画像から読み取ってください)", "") });
        tasks.push(extractOne(imgContent, ""));
      }
      const norm = (await Promise.all(tasks)).flat();
      // De-duplicate: by candidateId when present (unique per candidate), else name+email+phone.
      const seen = new Set();
      const deduped = norm.filter((r) => {
        const k = r.candidateId
          ? "id:" + r.candidateId
          : [(r.name || "").replace(/\s+/g, ""), (r.email || "").trim().toLowerCase(), (r.phone || "").replace(/\D/g, "")].join("|");
        if (k === "||") return true; // nothing to key on — keep
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      // Stable, distinct avatar colors across the merged set.
      deduped.forEach((r, i) => { r.photoColor = AI_IMPORT_PASTELS[i % AI_IMPORT_PASTELS.length]; });
      if (deduped.length === 0) {
        setErr(`応募者情報を抽出できませんでした${extractFails ? `（${extractFails}件の抽出が失敗）` : ""}。テキストを増やすか別のファイルでお試しください。　［build ${buildId} / 入力:${inputDesc}］`);
        setPhase("input"); return;
      }
      if (extractFails > 0) setEvalNote(`一部（${extractFails}件）の抽出に失敗しました。読み取れた ${deduped.length}名 のみ表示しています。`);

      // ── 2) EVALUATION — advanced tier, on a COMPACT, ATTRIBUTE-BLIND profile
      // (career/skills only; no name/gender/age/address). Small in/out → fast, and
      // fair. A failure here must NOT block the import: register without scores. ──
      try {
        const profiles = buildEvalProfiles(deduped);
        const evalPrompt = `あなたは採用担当者のアシスタントです。下記【評価の前提】に照らして各候補者の適性を評価し、**JSON配列のみ**で返してください。コードフェンス・説明文は禁止。先頭は [ 末尾は ] 。
各要素の形式: { "idx": 候補者の番号(入力のidxと一致させる), "positionFit": 1〜5の整数, "industryFit": 1〜5の整数, "levelFit": 1〜5の整数, "overall": 1〜5の整数, "persona": "人物像を2〜3文", "strengths": "この募集における強み", "concerns": "懸念・確認点(無ければ空文字)", "comment": "総合所見を2〜4文(なぜそのスコアか)" }

【評価の前提】
- 当社／業界: ${APPLICANT_COMPANY_CONTEXT}
- 募集職種: ${position}
- 求める役職・レベル: ${levelDef || "(明示の定義なし。職種名と業界から標準的な期待値を推測して評価)"}
【公正な評価の厳守】判断材料は下記の経歴・スキル・実績・資格・語学・希望条件のみ。氏名・性別・年齢・住所等は意図的に渡していないので推測・考慮しないこと（厚生労働省『公正な採用選考』に準拠）。根拠が薄い項目は控えめなスコアにし、その理由を comment に記すこと。

候補者(JSON配列): ${JSON.stringify(profiles)}`;
        const evalResp = await window.claude.complete({ prompt: evalPrompt, tier: "advanced", max_tokens: 4096 });
        const evParsed = parseJsonLoose(evalResp);
        const matched = mergeEvaluations(deduped, Array.isArray(evParsed) ? evParsed : [evParsed], { levelDef, today });
        if (matched < deduped.length) {
          setEvalNote(matched === 0
            ? "AI評価は今回省略されました（混雑・タイムアウトの可能性）。経歴の取り込みは完了しています。登録後に再評価できます。"
            : `AI評価は${deduped.length}名中${matched}名分のみ生成されました。残りはスコアなしで登録され、後から再評価できます。`);
        }
      } catch (ee) {
        setEvalNote("AI評価は今回省略されました（混雑・タイムアウトの可能性）。経歴の取り込みは完了しています。登録後に再評価できます。");
      }

      setResults(deduped); setPhase("review");
    } catch (e) {
      // The outer catch = the EXTRACTION phase (the eval phase has its own inner
      // catch). Surface build id + what was sent so we can tell stale-cache from a
      // real bug at a glance.
      console.error("[AI import] extraction failed", { buildId, inputDesc, error: e });
      setErr(`AI解析(抽出)に失敗しました: ${(e && e.message) || ""}　［build ${buildId} / 入力:${inputDesc}］`);
      setPhase("input");
    }
  };

  const toggle = (i) => setResults((rs) => rs.map((r, idx) => idx === i ? { ...r, __pick: !r.__pick } : r));
  const pickedCount = results.filter((r) => r.__pick).length;
  const doRegister = () => {
    const picked = results.filter((r) => r.__pick).map(({ __pick, ...r }) => r);
    if (picked.length) onImport(picked);
  };

  return ReactDOM.createPortal((
    <div className="rec-overlay" onClick={(e) => { if (e.target.classList.contains("rec-overlay")) onClose(); }}>
      <div className="ai-import-card">
        <div className="ai-import-head">
          <div>
            <div className="eyebrow">AI IMPORT · build {(typeof window !== "undefined" && window.__SUPERSYS_BUILD) || "dev"}</div>
            <h3>応募者をAIで取り込み</h3>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="閉じる"><Icon name="x" size={18}/></button>
        </div>

        {phase !== "review" ? (
          <div
            className={"ai-import-body ai-import-dropzone" + (dragging ? " dragover" : "")}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <p className="ai-import-hint">履歴書・職務経歴書・スカウト返信メール・応募者リストなどを貼り付けるか、ファイル(画像 / PDF / テキスト)を<strong>ここにドラッグ＆ドロップ</strong>／追加して「AIで解析」を押してください。複数名でも一括で取り込めます。</p>
            {/* 募集職種は分析の前に選ぶ — AIがこの職種・求めるレベル・当社業界への適正を評価する。 */}
            <div className="ai-import-position">
              <label className="label-mono" htmlFor="ai-import-pos-select">この取り込みの募集職種（全員をこの職種で評価）</label>
              <select id="ai-import-pos-select" className="form-input" value={position} onChange={(e) => setSelectedPosition(e.target.value)} disabled={phase === "analyzing"}>
                {positions.map((p) => <option key={p} value={p}>{p}</option>)}
                {!positions.includes(position) && <option value={position}>{position}</option>}
              </select>
              <div className="ai-import-level">
                <span className="label-mono">求めるレベル（AI評価の基準）</span>
                <div className={"ai-import-level-body" + (levelDef ? "" : " muted")}>
                  {levelDef || "未設定：設定 → 応募者 → 職種ごとの『求めるレベル』で定義できます。未設定の場合は職種名と業界からAIが推測します。"}
                </div>
              </div>
            </div>
            <textarea className="ai-import-text" placeholder="ここにテキストを貼り付け…（ファイルはドラッグ＆ドロップも可）" value={text} onChange={(e) => setText(e.target.value)} disabled={phase === "analyzing"}/>
            <div className="ai-import-files">
              <button className="btn ghost sm" onClick={() => fileRef.current && fileRef.current.click()} disabled={phase === "analyzing"}>
                <Icon name="image" size={12}/><span>ファイルを追加</span>
              </button>
              <input ref={fileRef} type="file" multiple accept="image/*,application/pdf,.txt,.md,.csv" style={{ display: "none" }} onChange={onFiles}/>
              {files.map((f, i) => (
                <span key={i} className={"ai-import-file-chip" + (f.kind === "pdf-unreadable" ? " unreadable" : "")} title={f.kind === "pdf-unreadable" ? "テキストを取得できませんでした（スキャンPDF等）。テキストを貼り付けてください。" : (f.fromPdf ? "PDFからテキスト抽出済み" : "")}>
                  <Icon name={f.kind === "pdf-unreadable" ? "alert" : f.kind === "text" ? "note" : "image"} size={11}/>{f.name}{f.kind === "pdf-unreadable" ? "（テキスト取得不可）" : ""}
                  <button onClick={() => setFiles((fs) => fs.filter((_, idx) => idx !== i))} disabled={phase === "analyzing"} aria-label="削除">×</button>
                </span>
              ))}
            </div>
            {err && <div className="auth-err" style={{ textAlign: "left" }}>{err}</div>}
            {dragging && (
              <div className="ai-import-drop-overlay" aria-hidden="true">
                <Icon name="download" size={28}/>
                <span>ここにドロップしてファイルを追加</span>
              </div>
            )}
          </div>
        ) : (
          <div className="ai-import-body">
            <p className="ai-import-hint">{results.length}名を抽出しました（募集職種「<strong>{position}</strong>」）。{results.some(r => r.evaluation) ? "適性をAIが評価しています（総合スコアは目安）。" : ""}登録する応募者を選んでください。</p>
            {evalNote && <div className="auth-err" style={{ textAlign: "left", background: "rgba(245,197,24,0.08)", borderColor: "rgba(245,197,24,0.3)", color: "var(--ash)" }}>{evalNote}</div>}
            <div className="ai-import-results">
              {results.map((r, i) => {
                const ev = r.evaluation;
                return (
                <label key={i} className={"ai-import-result" + (r.__pick ? " picked" : "")}>
                  <input type="checkbox" checked={!!r.__pick} onChange={() => toggle(i)}/>
                  <span className="ai-import-avatar" style={{ background: r.photoColor }}>{r.photoInitial}</span>
                  <div className="ai-import-result-main">
                    <div className="ai-import-result-name">{r.name}{r.anonymous ? <span style={{ fontWeight: 400, color: "var(--ash)", fontSize: 11, marginLeft: 6 }}>（氏名非公開）</span> : ""}</div>
                    <div className="ai-import-result-sub">{[r.currentCompany, r.email, r.phone].filter(Boolean).join(" · ") || "—"}</div>
                    {r.tags && r.tags.length > 0 && <div className="ai-import-result-tags">{r.tags.map((t, ti) => <span key={ti}>{t}</span>)}</div>}
                  </div>
                  {ev && ev.overall != null && (
                    <div className="ai-import-result-score" title="AI総合適正（5段階）" style={{ color: scoreColor(ev.overall) }}>
                      <span className="ai-import-result-score-num">{ev.overall}</span>
                      <span className="ai-import-result-score-max">/5</span>
                    </div>
                  )}
                </label>
                );
              })}
            </div>
          </div>
        )}

        <div className="ai-import-foot">
          {phase === "review" ? (
            <>
              <button className="btn ghost" onClick={() => setPhase("input")}>戻る</button>
              <div style={{ flex: 1 }}/>
              <button className="btn gold" onClick={doRegister} disabled={pickedCount === 0}>{pickedCount}名を登録</button>
            </>
          ) : (
            <>
              <div style={{ flex: 1 }}/>
              <button className="btn primary" onClick={analyze} disabled={phase === "analyzing"}>
                {phase === "analyzing" ? "解析中…" : <><Icon name="sparkle" size={13}/><span>AIで解析</span></>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  ), document.body);
};

Object.assign(window, { ApplicantsView, computeFunnelAnalytics, stageForStatus, stageOrderFrom, APPLICANT_STAGE_META });


;/* ==== role-seed.js ==== */
// Role definition seed data — imported from 2026-05-13 人事総務部 役割定義 Excel
const ROLE_DEF_SEED = [
  {
    "period": "2025年 第1期",
    "name": "竹内雅明",
    "no": 1,
    "item": "生成AIの全社普及推進",
    "criteria": "導入の土台づくり：課題整理・簡易テスト",
    "weight": 0.35,
    "scoring": "下記１項目達成ごとに１点を加点する(全て達成で５点、全て未達成で１点)\r\n\r\n①2/7(金)部門長会議までに各部門から頂戴した業務課題を2/17(月)までに取りまとめ、表形式などで社長に報告する\r\n②2/28(金)までに伴走支援の法人サービスもしくは個人事業主を決定し社長に承認を得る\r\n③2/28(金)までにAI技術検証を進め導入できそうな仕組みやサービス/システムを選定し、①で作成した資料にマッピングし、仮説レベルでもいいのでまとめ社長に報告する\r\n④3/26(水)までに一定程度の成果物を納品する(タスクの重み大なら１つ、中なら２つ、小なら３つとする)\r\n\r\n※生成AI分野は進歩が著しく、この期で良いと思ったものでもより良いやり方が登場すれば潔く乗り換える",
    "self": 3,
    "final": 3,
    "selfComment": "部下の評価点：大志万さん７、深坂さん８、松川さん８　平均7.6\r\n役割項目①の生成AI成果物：経理部テスト中の税理士BOT1件、タスクとして中の判断に留まった\r\n役割項目②の業務効率化：砂坂さん回答にて施策平均58%の効率化となった\r\n\r\n今期は業務改善と経営計画で一時的に負荷が高く、自分のことで精一杯な時期もありました。またマネジメントについて、識学の理論を実践することと、見えているリスクをどうするのかについて悩むところもありました。\r\n\r\n悩み成長出来る環境に感謝しつつも身を引き締め、今後も努力してまいります。",
    "finalComment": "１期目お疲れさまです！\r\n中期経営計画も忙しい中、お疲れさまです！\r\n竹内さんも会社のバックオフィス側での\r\n大きな変更やAI対応もあり、尽力頂き感謝です。\r\n今後は人事採用にも労力かかりますが\r\n２期目も宜しくお願い致します。"
  },
  {
    "period": "2025年 第1期",
    "name": "竹内雅明",
    "no": 2,
    "item": "物件管理システム(kintone)運用サポート",
    "criteria": "運用定着させるための業務の効率化や情報の集約を推進するために、要望の対応や問題の改善を行う",
    "weight": 0.35,
    "scoring": "3/26(水)までに、砂坂さん(経理部門)と連携して業務課題を抽出したものに対して従来比で以下の作業時間を効率化し、定量的な成果を報告できた。\r\n評価点５：従来比で30%以上\r\n評価点４：従来比で25%以上\r\n評価点３：従来比で20%以上\r\n評価点２：従来比で15%以上\r\n評価点１：従来比で15%未満\r\n\r\n※直接的な作業の時間軽減に繋がらない改善であっても感覚的に作業時間に変換して換算するものとする",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第1期",
    "name": "竹内雅明",
    "no": 3,
    "item": "部門マネジメント",
    "criteria": "部門マネジメント",
    "weight": 0.3,
    "scoring": "５：生雲・大志万・深坂の平均が8点以上\r\n４：生雲・大志万・深坂の平均が7.5点以上\r\n３：生雲・大志万・深坂の平均が7点以上\r\n２：生雲・大志万・深坂の平均が6.5点以上\r\n１：生雲・大志万・深坂の平均が6.5点未満",
    "self": 4,
    "final": 4,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第1期",
    "name": "大志万拓",
    "no": 1,
    "item": "採用",
    "criteria": "各部門において必要人材の採用をする\r\n※人材は正社員、派遣、フリーランスを問わず、業務を任せられる人材であればカウントするものとする\r\n(期間内の目標　５ポイント、専門職Ⅱ系統のみ2ポイント計算、それ以外は1ポイント)",
    "weight": 0.35,
    "scoring": "評価5：5pt以上\r\n評価4：4pt\r\n評価3：3pt\r\n評価2：2pt\r\n評価1：1pt",
    "self": 5,
    "final": 5,
    "selfComment": "今期は採用をしながらも他タスクを進行させなければならない、時間との勝負でした。\r\nただ期の中盤まで打合せや採用面接をとにかく多く入れすぎた関係で、進捗に遅れが出るなど悪い影響が出てしまったことが反省点です。\r\n今後新卒採用も入ってくることを考えますと、1日の限られた時間をどのように使っていくか、期初の段階からしっかり管理していく必要があると感じています。",
    "finalComment": "派遣にやや偏りましたがなんとか会社を回せたのは大志万さんが人員を供給してくれたからですね、ありがとうございます！人事の内定も出せたので良かったです。\r\nマイナスでは役割項目②は3)を評価4まで持っていって欲しかったです！理由は理解できますが色々見越して項目は設定しているので、大志万さんに与えられた役割を果たすことを今後は重視してほしいです。\r\nこの会社は落ち着くヒマがないほど大変ですが退屈しませんし経験は積めます。あとは目立つ目立たない関わらず会社に貢献してくれている人がきちんと報われるようにしていきますので、来期もよろしくお願いします。"
  },
  {
    "period": "2025年 第1期",
    "name": "大志万拓",
    "no": 2,
    "item": "新卒採用の環境整備 人員確保、活動計画、外出する時間を作るための業務割り振り(事務員活用)等",
    "criteria": "新卒採用に向けた計画立案と実行を行う\r\nため、下記項目の達成を目指す\r\n1)人事増員の確保\r\n2)活動計画\r\n3)人事総務部事務メンバーへの業務割り振り\r\n※3つの項目の内、最も達成度の低い項目を参照し評価点を決める(例：1)評価5,2)評価4、3)評価３だった場合?最終評価は3)",
    "weight": 0.35,
    "scoring": "評価5：\r\n1)人事増員の入社日まで確定している\r\n2)年次・月次活動計画表に基づいて活動を行えており、外出時間が確保された状態にできている\r\n3)事務メンバーが応募者管理と、求人原稿の校正まででき、採用工数低減に寄与させることができている\r\n評価4：\r\n1)人事アシスタントの内定受諾まで完了している\r\n2)年次・月次活動計画表の承認まで終え、活動の開始ができている\r\n3)事務メンバーが日々の業務の中で応募者管理ができている\r\n評価3：\r\n1)人事アシスタントの内定候補者の選出まで終えている\r\n2)年次・月次活動計画表の提出と上長の承認を得ている\r\n3)事務メンバーへの業務割り振りと、実施までできている\r\n評価2：\r\n1)人事アシスタントの有力候補者の選定に留まった\r\n2)年次・月次活動計画表の企画までに留まった\r\n3)事務メンバーへの業務割り振り検討に留まった\r\n評価１： 1)、2)、3)いずれも進捗がなかった",
    "self": 3,
    "final": 3,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第1期",
    "name": "大志万拓",
    "no": 3,
    "item": "ネット採用強化 r-seimu、Instagram等が求職者の応募動機となった数を増やす",
    "criteria": "採用ホームページの刷新とコンテンツ制作\r\n・採用ホームページを刷新することで、Instagram、titok、youtubeなどの導線の受け皿を自社で制作する。\r\n(2025年内・・・ホームページ刷新、流入率の数値化、2026年・・・ホームページ内にメタバース空間を作るなど、新卒採用のためのコンテンツ制作の土台とする)",
    "weight": 0.3,
    "scoring": "評価5：採用サイトが完成しており、運用開始されている\r\n評価4：採用サイトに掲載するためのコンテンツ作りを開始している\r\n評価3：採用ホームページ刷新するための業者選定が完了し上長承認を得ており、掲載コンテンツを広報担当者と検討している状態\r\n評価2：採用サイトを刷新するために、業者選定まで完了している\r\n評価1：ネット採用に関するプロジェクトの進捗がなかった",
    "self": 3,
    "final": 3,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第1期",
    "name": "松川牧絵",
    "no": 1,
    "item": "就業規則の管理",
    "criteria": "就業規則改訂・就業規則・付属規程・その他規定の内容を現状に完全に合わせバージョン管理できている状態とし、従業員が閲覧可能な場所に保管する",
    "weight": 0.4,
    "scoring": "評価5：3/26迄に④を達成し就業規則改訂・就業規則・付属規程・その他規定について掲示板で補足掲載の必要があれば掲載出来ている\r\n評価4：2/17迄に就業規則改訂・就業規則・付属規程・その他規定を現状に合わせられており、掲示板に掲載出来ている\r\n評価3：2/17迄に就業規則改訂・就業規則・付属規程・その他規定の１つ以上が現状に即していない\r\n評価2：2/17迄に就業規則改訂・就業規則・付属規程・その他規定の２つ以上が現状に即していない\r\n評価1：1/31迄に就業規則改訂・就業規則・付属規程・その他規定の現在の数を上長へ報告する",
    "self": 5,
    "final": 5,
    "selfComment": "育児・介護休業規程の発表が3月中に間に合わず、その点の評価が「3」となってしまったのは悔しいですが、その他の目標については何とか達成できたことを良かったと感じています。\r\n今後は、少し難易度の高い課題にも挑戦しながら成長していくとともに、不測の事態が多い環境であることも考慮し、評価設定のバランスを意識したいと思います。特定の業務に偏ることなく、他の業務にも十分に取り組めるよう工夫していければと思っております。\r\n\r\n第2Qも引き続き精一杯取り組んでまいりますので、よろしくお願いいたします。",
    "finalComment": "今期も社長からの突発業務に対応しつつ役割定義の達成に強い意志を持って取り組んでいただきありがとうございました。できることをどんどん増やし、今は指示で動いていることも自発的に考えて行動していけるように成長していってください。\r\n来期は吉松さんが抜けてしまうことが痛手ですが、その穴を埋められる仲間を是非採用して、強固な基盤を築きましょう。"
  },
  {
    "period": "2025年 第1期",
    "name": "松川牧絵",
    "no": 2,
    "item": "育児介護休業改訂",
    "criteria": "2025年4月の育児介護休業に併せて就業規則を改訂する",
    "weight": 0.3,
    "scoring": "評価5：評価4を達成のうえ、3/26迄に追加で現実的で従業員・会社双方にメリットのある働きやすい環境について提案をし上長承認を得られた※期末までに導入されていなくても上長承認が得られればOKとする\r\n評価4：評価3を達成のうえ3/13の全体ミーティングで2025年4月の育児介護休業改定内容について発表し全社共有できた\r\n評価3：評価2を達成のうえ3/6迄に2025年4月の育児介護休業改定内容を反映した就業規則案を作成し上長承認を得られた\r\n評価2：2/28迄に2025年4月の育児介護休業改訂内容について調査し改訂箇所をリストアップし上長に報告できた\r\n評価1：2/28迄に2025年4月の育児介護休業改訂内容について調査できなかった",
    "self": 3,
    "final": 3,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第1期",
    "name": "松川牧絵",
    "no": 3,
    "item": "グループ会社の就業規則作成",
    "criteria": "YAKUNITATSUの就業規則を作成する",
    "weight": 0.3,
    "scoring": "評価5：3/26迄にYAKUNITATSUの就業規則を作成し社員が閲覧可能な場所に保管出来ており、YAKUNITATSUの3月全体MTGで発表出来た\r\n評価4：3/26迄にYAKUNITATSUの就業規則を作成が完了し、本上先生と上長の2人から承認が得られた\r\n評価3：3/26迄にYAKUNITATSUの就業規則を作成したが本上先生と上長のどちらか一方から承認が得られなかった\r\n評価2：3/26迄にYAKUNITATSUの就業規則を作成したが上長の承認が得られなかった\r\n評価1：3/26迄にYAKUNITATSUの就業規則を作成出来なかった",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第2期",
    "name": "竹内雅明",
    "no": 1,
    "item": "生成AI・DXの全社推進",
    "criteria": "各部門の業務課題や大小の効率化に対応する",
    "weight": 0.35,
    "scoring": "現在各部門より挙がっている課題の解決には生成AI不要な項目も多いため、手段を問わず業務課題の改善・要望や依頼の対応を行った件数で判断する\r\n第２期中の対応内容について表形式で報告し、そのタスク数で評価点を設定する\r\n\r\nタスク１つにつき３点\r\n\r\n評価点５：30点以上\r\n評価点４：24点以上\r\n評価点３：18点以上\r\n評価点２：12点以上\r\n評価点１：　6点以下\r\n\r\n※生成AI分野は進歩が著しく、この期で良いと思ったものでもより良いやり方が登場すれば潔く乗り換える",
    "self": 5,
    "final": null,
    "selfComment": "①21件対応済み、63点\r\n②前田さんの資金繰り表kintone生成による業務効率化➤今8時間が3時間程度に短縮　年間60時間削減の見込み 3 / 8 * 100 = 37.5%\r\n③大志万さん：8 松川さん：6 松井さん：9 合計23点、平均7.67\r\n\r\n「kintoneに情報を集約し中心的なシステムとする」目標を自分の中で掲げ一人で作り始めたのが約2年前でした。各部門が積極的な活用を考えるようになったのがとても嬉しいです。同時に、やはりどこの会社でも新しいものが普及するのに2年はかかるのだな、とも実感しました。\r\n来期も生成AI及びDXを主軸としつつ部門と会社の発展に寄与できるよう頑張ります。",
    "finalComment": ""
  },
  {
    "period": "2025年 第2期",
    "name": "竹内雅明",
    "no": 2,
    "item": "物件管理システム(kintone)運用サポートの特化項目",
    "criteria": "運用定着させるための業務の効率化や情報の集約を推進するために、要望の対応や問題の改善を行う",
    "weight": 0.35,
    "scoring": "6/25(水)までに、前田さん(財務担当)と連携して業務課題を抽出したものに対して従来比で以下の作業時間を効率化し、定量的な成果を報告できた。\r\n評価点５：従来比で30%以上\r\n評価点４：従来比で25%以上\r\n評価点３：従来比で20%以上\r\n評価点２：従来比で15%以上\r\n評価点１：従来比で15%未満\r\n\r\n※直接的な作業の時間軽減に繋がらない改善であっても感覚的に作業時間に変換して換算するものとする",
    "self": 5,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第2期",
    "name": "竹内雅明",
    "no": 3,
    "item": "部門マネジメント",
    "criteria": "部門マネジメント",
    "weight": 0.3,
    "scoring": "５：松川・大志万・松井の平均が8点以上\r\n４：松川・大志万・松井の平均が7.5点以上\r\n３：松川・大志万・松井の平均が7点以上\r\n２：松川・大志万・松井の平均が6.5点以上\r\n１：松川・大志万・松井の平均が6.5点未満",
    "self": 4,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第2期",
    "name": "大志万拓",
    "no": 1,
    "item": "採用",
    "criteria": "各部門において必要人材の採用をする\r\n※人材は正社員、派遣、フリーランスを問わず、業務を任せられる人材であればカウントするものとする\r\n（期間内の目標：5ポイント、専門職Ⅱ系統は2ポイント計算、それ以外は1ポイント。有期雇用者は0.5ポイント）",
    "weight": 0.35,
    "scoring": "評価5：5pt以上\r\n評価4：4pt\r\n評価3：3pt\r\n評価2：2pt\r\n評価1：1pt",
    "self": 5,
    "final": 5,
    "selfComment": "・ペルソナ設定は、採用サイト刷新を進める中で、各部門長からの「期待する人」という定義においては完成したが、実際に採用活動に適用させるほどの定義にまで至っておりません。\r\n・採用サイトの受け皿としての機能はある程度イメージ通りに完成したが、実運用には至っていないため評価3としました。",
    "finalComment": "中期経営計画によってガラっと雰囲気が変わってしまった中、ビズリーチを中心とした対応をよく漏れなくやっていただけていると思います。\r\n\r\n十分成熟されてきたと思いますが、あえて成長のために伝えるとすればまだメンタルで不安定な一面がみられます。僕自身やってみるのは、それぞれの位置で見えるものが違うので「自分からは見えないものがあってそういう考えになっているのかもしれない」と想像すると結構落ち着きます。\r\n的外れかもしれませんが、よければそういったことも考えてみてください。"
  },
  {
    "period": "2025年 第2期",
    "name": "大志万拓",
    "no": 2,
    "item": "採用活動の環境整備 活動計画、業務割り振り等",
    "criteria": "経営計画に基づき増員するため、下記項目を達成する\r\n\r\n1.2027卒採用の詳細な活動計画作成と承認\r\n2.人事総務部メンバーへの業務割り振り（応募者管理、窓口対応、面接日程調整など）\r\n3.従業員インタビュー等を行い、セイム人材ペルソナを完成させる\r\n\r\n※3項目のうち最も達成度が低いものに評価を合わせる",
    "weight": 0.35,
    "scoring": "評価5：\r\n1. 採用計画が詳細に作成され、全計画がスムーズに進行し採用活動も順調に開始\r\n2. 業務割り振りが明確で、人事が効率的に動き、全業務が予定通り完了\r\n3. インタビューとペルソナ作成が5/31迄に完了し、戦略に活用された\r\n評価4：\r\n1. 計画は作成・承認されたが、外部調整や準備に遅れがあった\r\n2. 一部に遅れやミスがあったが、人事の介入で解決\r\n3. インタビューは完了、ペルソナは80％進捗で止まった\r\n評価3：\r\n1. 計画は作成・承認済だが完全とは言えない\r\n2. 割り振りはあるが、人事の継続的介入が必要だった\r\n3. インタビュー完了、ペルソナは60％止まり\r\n評価2：\r\n1. 計画は提出までで承認されていない\r\n2. 割り振りに問題があり、人事が主導していた\r\n3. インタビュー遅延、ペルソナは完了せず\r\n評価1：1・2・3いずれも進捗なし",
    "self": 4,
    "final": 4,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第2期",
    "name": "大志万拓",
    "no": 3,
    "item": "ネット採用強化 r-seimuの刷新",
    "criteria": "採用ホームページの刷新と、ペルソナを活用したYouTube動画の制作・公開状況を評価する",
    "weight": 0.3,
    "scoring": "評価5：\r\n1. ホームページが計画通り刷新され、SNS導線の受け皿として完成し、流入率の数値化まで実行できた\r\n2. ペルソナをもとに制作したYouTube動画がすべて完成し、アップロードされた\r\n評価4：\r\n1. ホームページ刷新は完了したが、一部導線の調整が遅れ、流入率数値化は次期へ持ち越しとなった\r\n2. YouTube動画は予定数の半分以上が完成・公開された\r\n評価3：\r\n1. ホームページ刷新は完了したが、導線の整備が不十分で、SNS活用には至らなかった\r\n2. YouTube動画は半数未満が完成・公開された\r\n評価2：\r\n1. ホームページ刷新が未完で、導線の受け皿としても不十分だった\r\n2. YouTube動画は制作途中であり、1本も完成・公開に至っていない\r\n評価1：\r\n1、2ともに未着手であった",
    "self": 3,
    "final": 3,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第2期",
    "name": "松川牧絵",
    "no": 1,
    "item": "就業規則等整備",
    "criteria": "1.就業規則を労基に提出・確認し会社のリスクをなくす\r\n2.育児介護休業法改正と会社の対応について全体ミーティングで発表する\r\n3.従業員満足度向上 / 離職率低下につながる制度や取り組みを検討・承認・実施する（単純なモノの増加や高コストNG）",
    "weight": 0.35,
    "scoring": "評価5：上記3項目すべてを達成\r\n評価4：2項目達成、残り1項目は準備・承認段階\r\n評価3：1項目達成、他は準備・検討段階\r\n評価2：全項目に着手しているが実施・発表・提出に至っていない\r\n評価1：未着手項目が含まれており、進行が不十分",
    "self": 4,
    "final": 4,
    "selfComment": "就業規則の提出および法改正の社内周知は計画通り進めることができました。一方で、従業員満足度向上につながる制度の導入については、共有できる段階までに至っておらず評価は4となりましあｔ\r\nマニュアルについても一部は整備・掲載できたものの、進捗が遅れてます。\r\nまた、事務員採用については候補者の選定までは進んでいますが、内定に至っておらず評価は2となりました。来期も継続して採用活動を行い、早期の入社・引継ぎ完了を目指して業務の効率化につなげたいと考えています。",
    "finalComment": "採用は青木さんのイレギュラーがあり不本意な結果に終わってしまいましたが、それ以外はよくやっていただいたと思います。\r\n\r\nただ、最近細かなミスが見受けられます(この本人期末コメントにも)。大枠しっかりやってくれていても、接点の少ない人にミスを見られるとそれが大部分の評価(イメージ)となってしまって大変勿体ないです。\r\nミスは人間なくせないので、①必ず見直す ②それでもミスしたらすぐ謝って訂正する を心がけてください。これだけで全然変わってくると思います。"
  },
  {
    "period": "2025年 第2期",
    "name": "松川牧絵",
    "no": 2,
    "item": "人事総務部総務庶務マニュアル整備",
    "criteria": "新規・既存を問わず定型業務についてマニュアルを整備（LINEWORKS掲示板へ記載、必要に応じて動画アップも想定）",
    "weight": 0.35,
    "scoring": "評価5：総務庶務業務のマニュアルを8割以上作成・LINEWORKSに掲載\r\n評価4：6割以上作成・LINEWORKSに掲載\r\n評価3：3割以上作成・LINEWORKSに掲載\r\n評価2：タスク一覧は作成済みだがマニュアルは未作成\r\n評価1：タスク一覧自体も未作成",
    "self": 3,
    "final": 3,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第2期",
    "name": "松川牧絵",
    "no": 3,
    "item": "人事総務部事務員採用",
    "criteria": "期末までに事務員1名を採用し、吉松さんの業務を引き継げる状態にする（求人～面接～採用までを松井さんと協力して実施）",
    "weight": 0.3,
    "scoring": "評価5：事務員が期末までに入社し、業務をすべて引き継ぎ完了している\r\n評価4：事務員は入社済みだが、引き継ぎは未完了\r\n評価3：内定済みだが入社は期末以降\r\n評価2：最終候補は決定しているが内定は未達\r\n評価1：応募や面接は実施しているが選考は不十分\r\n\r\n※補足：引き継ぎ完了の定義は、本人が松井さん・吉松さんに確認せずに担当業務を遂行できる状態。\r\nまた、②のマニュアル整備タスクにスキルチェック欄を設けて確認を行う。",
    "self": 2,
    "final": 2,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第2期",
    "name": "松井美樹",
    "no": 1,
    "item": "竹内の業務改善業務補助",
    "criteria": "情報システム担当としてのスキルを磨くため竹内の業務改善業務を補助する\r\n竹内から振られた業務について遂行する\r\n（業務の難易度とこなした数によってポイント制で評価する）",
    "weight": 0.5,
    "scoring": "評価5：6月24日(火)までに竹内から依頼された情シス業務を担当し、完了の都度その難易度に応じて竹内が設定したポイントの合計数で評価する（低級1p 中級3p 高級5p）15p以上獲得できた\r\n評価4：13p以上獲得できた\r\n評価3：10p以上獲得できた\r\n評価2：5p以上獲得できた\r\n評価1：4p以下に留まった",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第2期",
    "name": "松井美樹",
    "no": 2,
    "item": "SEIMUサーバ管理",
    "criteria": "SEIMUサーバのファイル数・容量数を推奨される数値内に削減する\r\n（滅多に使用／参照しないデータはバックアップへ移動させる。バックアップへのアクセス権限者は管理表を作成して管理し、基本的に読み取り権限までとする）",
    "weight": 0.3,
    "scoring": "評価5：6月24日(火)までに30万ファイル以下に削減でき、バックアップのアクセス権限管理表を作成でき、バックアップアクセス可該当者への周知も適切に行った\r\n評価4：6月24日(火)までに30万ファイル以下に削減できた\r\n評価3：6月24日(火)までに32万ファイル以下に削減できた\r\n評価2：6月24日(火)までに34万ファイル以下に削減できた\r\n評価1：期内に34万ファイル以下に削減できなかった",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年 第2期",
    "name": "松井美樹",
    "no": 3,
    "item": "人事総務部業務",
    "criteria": "人事 総務 庶務業務について部の一員として協力して対応する\r\n（自ら率先して取り組めているか 協調性があるか ミスや漏れがないかの観点で評価する）",
    "weight": 0.2,
    "scoring": "評価5：協調性もあり必要に応じて周囲をサポートする姿勢もある。ミスや漏れも少なく、安定した業務遂行ができている\r\n評価4：指示に対して正確に対応しており基本的な協調性も備えている。大きなミスや漏れはなし\r\n評価3：指示に対して正確に対応しているが協調性が少し足りない\r\n評価2：協調性に欠ける面がみられる。ミスや漏れも度々ある\r\n評価1：業務への関与が消極的である。ミスや漏れが多く正確性で改善が必要である",
    "self": 5,
    "final": 4,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第3期",
    "name": "竹内雅明",
    "no": 1,
    "item": "生成AI活用推進",
    "criteria": "以下の項目を完了させる\r\n１．お客様向けの補助金調査、市場調査をするボット(もしくは手適した生成AIサービス＋プロンプトの組み合わせ)を制作・リリース\r\n２．全体ミーティングで生成AIの現状や最新の予測、社内外での活用方法等を共有\r\n３．セイムのルール・就業規則を網羅した統合BOTを制作・リリース\r\n４．社内システムやBOT、AI等への便利機能集として全社向けのポータルページをリリース",
    "weight": 0.35,
    "scoring": "下記１項目達成ごとに１点を加点する(全て達成で５点、全て未達成で１点)\r\n①7/31(木)までにお客様向けの補助金調査、市場調査をするボット(もしくは手適した生成AIサービス＋プロンプトの組み合わせ)を制作・リリースする\r\n②8/21(木)全体ミーティングで生成AIの現状や最新の予測、社内外での活用方法等を共有する\r\n③8/31(日)までにセイムのルール・就業規則を網羅した統合BOT「セイムくん」を制作・リリースする\r\n④9/24(水)までに社内システムやBOT、AI等への便利機能集として全社向けのポータルページをリリースする",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第3期",
    "name": "竹内雅明",
    "no": 2,
    "item": "物件管理システム(kintone)運用サポートの特化項目",
    "criteria": "運用定着させるための業務の効率化や情報の集約を推進するために、要望の対応や問題の改善を行う",
    "weight": 0.35,
    "scoring": "9/24(水)までに、kintoneの活用推進メンバー(仮)と連携して業務課題を抽出したものに対して従来比で以下の作業時間を効率化し、定量的な成果を報告できた。\r\n評価点５：従来比で35%以上\r\n評価点４：従来比で30%以上\r\n評価点３：従来比で25%以上\r\n評価点２：従来比で20%以上\r\n評価点１：従来比で20%未満\r\n\r\n※直接的な作業の時間軽減に繋がらない改善であっても感覚的に作業時間に変換して換算するものとする",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第3期",
    "name": "竹内雅明",
    "no": 3,
    "item": "部門マネジメント",
    "criteria": "部門マネジメント",
    "weight": 0.3,
    "scoring": "５：松川・大志万・松井の平均が8点以上\r\n４：松川・大志万・松井の平均が7.5点以上\r\n３：松川・大志万・松井の平均が7点以上\r\n２：松川・大志万・松井の平均が6.5点以上\r\n１：松川・大志万・松井の平均が6.5点未満",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第3期",
    "name": "大志万拓",
    "no": 1,
    "item": "中途採用",
    "criteria": "各部門において必要人材の採用をする\r\n※雇用形態は正社員・契約社員を対象とする\r\n※内定受諾日で計上する\r\n（期間内の目標：5ポイント、\r\n専門職Ⅱ系統・仕入れ営業➤2ポイント\r\n専門職Ⅰ系統➤1ポイント\r\n販売営業・事務職系統➤0.5ポイント）",
    "weight": 0.35,
    "scoring": "評価5：5pt以上\r\n評価4：4pt\r\n評価3：3pt\r\n評価2：2pt\r\n評価1：1pt以下\r\n※自己評価入力時に対象者の内定受諾日を示すこと",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第3期",
    "name": "大志万拓",
    "no": 2,
    "item": "新卒採用",
    "criteria": "2027卒に向けた採用活動を開始する\r\n(大学訪問の計画数に対する訪問数、企業説明会やインターンシップ・オープンオフィス等の開催に対する学生参加者数で評価点を設定)\r\n\r\n1)訪問した学校からの情報取得率\r\n2)職場体験会の参加率\r\n3)職場体験会後のアンケート結果評価\r\n\r\n※上記3項目から評価点を決める。評価点にばらつきが出た場合は最も低い評価項目＝評価点とする\r\n※アンケートは5点満点形式、評価点は全項目の平均値を参照とする",
    "weight": 0.35,
    "scoring": "評価5：\t\"1)80%以上\r\n2)10名以上\r\n3)4.5以上\"\t\t\t\r\n評価4：\t\"1)70%以上\r\n2)8名以上\r\n3)4.0以上\"\t\t\t\r\n評価3：\t\"1)60%以上\r\n2)5名以上\r\n３）3.5以上\"\t\t\t\r\n評価2：\t\"1)50%以上\r\n2)3名以上\r\n3)3.0以上\"\t\t\t\r\n評価１：\t\"1)50%未満\r\n2)3名未満\r\n3)3.0未満\"",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第3期",
    "name": "大志万拓",
    "no": 3,
    "item": "SNSの採用活用",
    "criteria": "インスタグラム・Youtube等の採用アカウントで採用活動に寄与するコンテンツを企画し実行する\r\n9月末までに下記コンテンツをリールorフィード化し投稿、採用サイトへの導線として設計しその流入数を評価点とする\r\n\r\n・面接後の20秒インタビュー\r\n・職場体験会優秀者発表",
    "weight": 0.3,
    "scoring": "評価5：\t20件以上\t\t\t\r\n評価4：\t15件以上\t\t\t\r\n評価3：\t10件以上\t\t\t\r\n評価2：\t5件以上\t\t\t\r\n評価１：\t5件未満",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第3期",
    "name": "松川牧絵",
    "no": 1,
    "item": "人事総務部総務庶務マニュアル整備",
    "criteria": "前期に引き続き定型業務についてマニュアル整備を進め、完了させる\r\n※既存分は成果に含まず新規分から評価に含める\r\n※作成されたマニュアルは他の部員にチェックを依頼し、問題なく理解できると判断をもらうこと",
    "weight": 0.5,
    "scoring": "評価点5：9/23(火)までにマニュアル29個を全て作成完了のうえ運用・更新体制の整備（更新日・更新履歴・次回見直し予定日・更新者など）の設定がされている\r\n評価点4：9/23(火)までに人事総務部のマニュアル一覧(総務部）のうち23～29個のマニュアルを新規で作成し、部員確認後、ラインワークス掲示板に掲載している\r\n評価点3：9/23(火)までに人事総務部のマニュアル一覧(総務部）のうち15～22個のマニュアルを新規で作成し、部員確認後、ラインワークス掲示板に掲載している\r\n評価点2：9/23(火)までに人事総務部のマニュアル一覧(総務部）のうち7～14個のマニュアルを新規で作成し、部員確認後、ラインワークス掲示板に掲載している\r\n評価点1：9/23(火)までに人事総務部のマニュアル一覧(総務部）のうち1～6個のマニュアルを新規で作成し、部員確認後、ラインワークス掲示板に掲載している",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第3期",
    "name": "松川牧絵",
    "no": 2,
    "item": "保険管理のkintoneアプリ化",
    "criteria": "車両以外の保険管理をExcel管理表からkintoneアプリに移行する\r\n(管理表の構成は一から見直し可読性や効率性を高めること）",
    "weight": 0.5,
    "scoring": "評価点5：アプリが9/20までに完成していて、Excelの保険管理データがすべて正確に移行されている。内容も最新の状態に整理されており、入力・検索・更新などの操作がスムーズにできる。実際に使う人の立場で作られている\r\n評価点4：アプリは9/20までに完成していて、構成も一新。Excel表の課題（可読性・項目配置・効率性）を改善し、業務に支障なく使用できる状態\r\n評価点3：アプリは9/20までに完成しているが、上長から見て構成に改善の余地あり／操作性に分かりづらさが残る\r\n評価点2：アプリは9/20までに完成しているが、旧Excelとほぼ同様の構成で改善が不十分\r\n評価点1：アプリが9/20までに未完成。または、Excel管理と並行せざるを得ない状況が続いている。構成の見直しが行われていない",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第3期",
    "name": "松井美樹",
    "no": 1,
    "item": "PC入れ替え",
    "criteria": "10/14(火)で公式サポート終了となるWindows１０がOSのノートPCを使用者がいる物は交換、使用していない物は廃棄する",
    "weight": 0.35,
    "scoring": "9/23(火)までにWindows１０がOSのノートPC６台をセットアップしPCの入れ替えを完了させる。\r\n\r\n⑤PC６台セットアップ完了、6台PC入れ替え完了。\r\n④PC5台セットアップ完了。\r\n③PC4台セットアップ完了。\r\n②PC2台または3台セットアップ完了。\r\n①PC1台セットアップ完了、または0台。",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第3期",
    "name": "松井美樹",
    "no": 2,
    "item": "車両管理のkintoneアプリ化",
    "criteria": "車両管理(車両本体・保険・駐車場)をExcel管理表からkintoneアプリに移行する\r\n(管理表の構成は一から見直し可読性や効率性を高めること）",
    "weight": 0.35,
    "scoring": "⑤9/23(火)までに車両管理(車両本体・保険・駐車場)をExcel管理表がkintoneでシステム化、一元管理ができる。またすべてのデーターが正確に移行されている。\r\n④9/23(火)までに車両管理(車両本体・保険・駐車場)をExcel管理表がkintoneでシステム化、一元管理ができる。\r\n③9/23(火)までに車両管理(車両本体・保険・駐車場)をExcel管理表がkintoneでシステム化、一元管理ができるが改善の必要がある。\r\n②9/23(火)までに車両管理(車両本体・保険・駐車場)をExcel管理表がkintoneでシステム化、一元管理ができない未完成の状況。\r\n①9/23(火)までに車両管理(車両本体・保険・駐車場)をExcel管理表がkintoneでシステム化、一元管理が進めれていない。",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第3期",
    "name": "松井美樹",
    "no": 3,
    "item": "人事総務部業務",
    "criteria": "情シス業務以外の部門業務を行った件数をLINEWORKSのタスク機能から取得し、業務の重さを加味した点数の合計値で評価点をつける\r\n※事務職加算式評価のテスト運用の位置づけ\r\n※重さでの点数：日常業務 １，中程度 ３, 大 ５ 追ってヒアリングし竹内が設定\r\n※1つのタスクを不必要なまでに細分化して登録していると見受けられるものはまとめて1つにして計算する\r\n※4/1～6/12の1日平均事務タスク数6.8から評価点を設定",
    "weight": 0.3,
    "scoring": "9/23(火)までの情シスを除いた完了タスク x 重さ が\r\n評価５：６５０点以上\r\n評価４：600点以上\r\n評価３：５００点以上\r\n評価２：４００点以上\r\n評価１：４００点未満",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第4期",
    "name": "竹内雅明",
    "no": 1,
    "item": "DX特化項目",
    "criteria": "AIもしくはRPA、ワークフロー自動化 \r\nツールを活用してワンクリックで処理が \r\n完了するような仕組みをDX化と別枠で \r\n構築する",
    "weight": 0.5,
    "scoring": "12/24(水)までに、自動化の仕組みを下記の個数構築できた。\r\n評価点５：5個以上 \r\n評価点４：4個 \r\n評価点３：3個 \r\n評価点２：2個 \r\n評価点１：1個以下",
    "self": 4,
    "final": 4,
    "selfComment": "松川9、大志万6 平均7.5\r\n\r\n中盤以降に父の死や家族のインフルエンザが立て続けにおき、遅れを取り戻せず年末を迎えました。\r\n\r\n個人タスクとしては悩みに悩み必死に微調整を繰り返した新等級制度を完成できたのが良かったです。\r\n部門としては情シス担当の山本さんに加入いただけたことでDXをスピードアップできています。特に自動化の技術検証が進み、かなり色々なことができそうな気がしています。\r\n人事は攻めを、総務はより守りを強くしていきます。\r\n\r\n来年は理想の会社をイメージし、具現化するために時には多少強引にでも部門を引っ張り実現していくために汗をかきたいと考えています。\r\n今後とも何卒よろしくお願いいたします。",
    "finalComment": "４期目もお疲れさまでした！そして１年間尽力頂き、ありがとうございました。人事総務部も全体数も増え大変な１年だったと思います。\r\n僕が、部門長会議を抜ける話をお伝え頂きありがとうございます。僕の役割を渡していき、各部門長の成長に繋がると思いますのでお伝え頂き感謝です。来年も引続き宜しくお願い致します！"
  },
  {
    "period": "2025年第4期",
    "name": "竹内雅明",
    "no": 2,
    "item": "中途採用",
    "criteria": "採用の重点職種\r\n経理部 経理2名\r\n不動産事業部 管理課事務1名　の採用",
    "weight": 0.2,
    "scoring": "評価点５：3名全員を内定受理とすることができた\r\n評価点４：３名のうち２名を内定受理とすることができた\r\n評価点３：３名のうち1名を内定受理とすることができた\r\n評価点２：１名以上に内定を出せたが受理されなかった\r\n評価点１：誰にも内定を出せなかった\r\n\r\n※内定受理＝採用条件通知書を出し、入社の承諾を得られた状態\r\n※入社日は来年でもよいものとする",
    "self": 1,
    "final": 1,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第4期",
    "name": "竹内雅明",
    "no": 3,
    "item": "部門マネジメント",
    "criteria": "部門マネジメント",
    "weight": 0.3,
    "scoring": "評価点５：松川・大志万の平均が8点以上\r\n評価点４：松川・大志万の平均が7.5点以上\r\n評価点３：松川・大志万の平均が7点以上\r\n評価点２：松川・大志万の平均が6.5点以上\r\n評価点１：松川・大志万の平均が6.5点未満",
    "self": 4,
    "final": 4,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第4期",
    "name": "大志万拓",
    "no": 1,
    "item": "経理職・事務職系統の採用",
    "criteria": "2025年第4期内に経理職・事務職に特化した中途採用計画を達成する。\r\n・販売営業部・・・事務系統社員1名\r\n・経理部・・・専門職Ⅰ系統社員3名\r\n・建築事業部・・・事務系統社員1名\r\n・不動産事業部営業課・・・事務系統社員1名\r\n・不動産事業部管理課・・・事務系統社員1名\r\n※評価は1名採用＝1ptとし、内定受諾ベースでカウント(入社時期が来年となるのは可とする)",
    "weight": 0.35,
    "scoring": "評価点5：5pt以上\r\n評価点4：4pt\r\n評価点3：3pt\r\n評価点2：2pt\r\n評価点1：1pt以下\r\n※自己評価入力時に対象者の内定受諾日を示すこと",
    "self": 2,
    "final": 2,
    "selfComment": "採用に関しては、得能さんの異動→新さん、清水さんの採用となりましたので建築事務1名、管理課事務1名のカウントとさせていただきました。\r\nただ経理社員の採用はもう少しできることがあったと思います。2026年からの行動変化に繋げられたらと思います。\r\n\r\n2026年の見通しとしてはまだまだ課題が山積みですが、楠田さんも頑張ってくれているので引き続き人事としての役割を全うできればと思っております。",
    "finalComment": "飛び級で主任にできたのは大志万さんがそれに見合う頑張りを周りに見せてくれたからです。来年は楠田さんの教育もですが新たな人員の予定もありますので、大変ですがまた一緒によろしくお願いします。\r\n他方、より良くなるための客観的なポイントとしては\r\n・随分改善されてきましたが、まだ感情的になっているのが見えるときがあります。意識していないところで周りを怖がらせているときがあるので、気を付けてください。\r\n・年に数回の盛大な凡ミス、今年も最後の最後にありましたね。来年は年に2、3回くらいまででお願いします。"
  },
  {
    "period": "2025年第4期",
    "name": "大志万拓",
    "no": 2,
    "item": "イオン鶴見出店PJにおける採用計画",
    "criteria": "イオン鶴見出店に向け、2025年第４期内に以下3項目を完了し、採用実行フェーズへ円滑に移行し内定者1名の獲得を目指す。\r\n①KPI設定の完了(採用人数・応募数など)\r\n②使用媒体の決定(媒体比較・選定理由・効果測定指標の設定)\r\n③想定予算の算出(媒体費・採用単価・全体予算)",
    "weight": 0.25,
    "scoring": "評価点５：募集を開始し内定者1名を出せた。\r\n\r\n評価点4：募集を開始したが内定候補者3名の輩出までに留まった。\r\n\r\n評価点３：募集を開始したが内定候補者2名の輩出までに留まった。\r\n\r\n評価点２：募集を開始したが内定候補者1名の輩出までに留まった。\r\n\r\n評価点１：募集の開始ができず内定候補者の輩出ができなかった。",
    "self": 3,
    "final": 3,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第4期",
    "name": "大志万拓",
    "no": 3,
    "item": "評価制度の昇格降格要件改版(SLP17ヶ条対応)",
    "criteria": "2025年11月末までに、SLP17ヶ条と整合する形で昇格・降格要件と人材要件を改版する。\r\n等級別の昇格基準を明確化し、組織運営上の判断軸として運用可能な状態を完成させる。",
    "weight": 0.25,
    "scoring": "評価点５：改版を11月15日までに完了し、上長が部門長会議で稟議・承認を得られる状態にまで整備できた\r\n【整備項目】レビュー・社員説明資料・運用マニュアルまで整備。SLP17条との整合性が高く、即運用可能。\r\n\r\n評価点４：改版を11月30日までに完了し、上長が部門長会議で稟議・承認を得られる状態にまで整備できた\r\n【整備項目】レビュー・社員説明資料・運用マニュアルまで整備。SLP17条との整合性が高く、即運用可能。\r\n\r\n評価点３：改版を11月30日までに完了したが、上長への提出までに留まり、手戻り状態で終えた。\r\n\r\n評価点２：原案はあるが期限超過または整合性に不備。\r\n\r\n評価点１：原案未提出あるいは未着手の状態。",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第4期",
    "name": "大志万拓",
    "no": 4,
    "item": "業務の引き継ぎ(楠田さんの後任体制構築)",
    "criteria": "2025年11月末までに、楠田さんの担当業務を整理・明文化し、\r\n将来的な引継先(後任候補含む)に対し、属人化を排除した業務体系を完成させる。\r\n\r\n第4期における引継ぎ目標項目【勤怠管理】+【給与計算】の全21項目",
    "weight": 0.15,
    "scoring": "評価点５：第4期内で引き継ぎ進捗100%\r\n\r\n評価点４：第4期内で引き継ぎ進捗90%\r\n\r\n評価点３：第4期内で引き継ぎ進捗80%\r\n\r\n評価点２：第4期内で引き継ぎ進捗70%\r\n\r\n評価点１：第4期内で引き継ぎ進捗70%未満",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第4期",
    "name": "松川牧絵",
    "no": 1,
    "item": "グループ会社総務業務の環境整備",
    "criteria": "今後の店舗拡大を見据え、YAKUNITATSUの総務業務を安定的かつ円滑に遂行できる仕組みを構築する\r\n店舗運営に関わる総務業務の流れ（車両・駐車場・自動車保険・健康診断）を整理・標準化し、業務負担の偏りを防ぐ体制を整え店舗増加にも対応できる運営基盤を構築する",
    "weight": 0.4,
    "scoring": "評価点5：YAKUNITATSUで構築した総務業務の仕組みを他店舗・他法人にも展開できる状態に整備。グループ全体で運用可能な基盤として承認・共有されている\r\n・マニュアル、体制、フローを全社共通仕様に統一\r\n\r\n評価点４：YAKUNITATSU単体での総務業務が仕組み化され、安定的かつ円滑に運用されている。承認済みの仕組みをもとに、属人化が解消された状態\r\n・マニュアルを整備しYAKUNITATSU総務事務へ共有を完了\r\n\r\n評価点３：現状分析と改善提案・承認まで完了しているが、運用開始が一部に留まる\r\n・提案内容は承認済みだが、試行段階または運用定着前\r\n\r\n評価点２：現状の課題把握や提案書作成には着手しているが、承認または運用に至っていない\r\n・現状整理やヒアリングは実施済み\r\n・仕組み案が未完成または承認待ち\r\n\r\n評価点１：着手が遅れ、提案・承認・運用のいずれも実現していない\r\n・現状把握や関係者調整が未実施\r\n・成果物や進捗が確認できない状態",
    "self": 5,
    "final": 5,
    "selfComment": "今年もたくさんのの業務に携わらせていただき、ありがとうございました。日々業務が立て込む中で色々とありますが、周囲の方にも助けてもらいながら仕事に取り組むことができました。来期も引き続き、業務の改善と組織への貢献を意識して取り組んでいきたいと思いますのでどうぞよろしくお願いします。",
    "finalComment": "すみません規程作成のみ上長チェックの再提出が多かったので意識を持っていただきたいため1点減点しています。\r\n妹尾さんと共に総務としてかなり多種に渡る業務をこなしてくれました、ありがとうございます。人事や情シスは攻めの要素もありますが総務は守りの部署です。会社を守れる知識と経験を来年もしっかり蓄えながら、自慢の会社に育てていけるようご協力ください。"
  },
  {
    "period": "2025年第4期",
    "name": "松川牧絵",
    "no": 2,
    "item": "規程作成",
    "criteria": "就業規則および附則の内容を定期的に確認し、法改正や運用実態に応じて必要な変更点を洗い出す\r\n変更が生じた場合は、速やかに労働基準監督署への届出を行い、適正な手続きを完了させる\r\nまた、海外出張や新制度導入など、運用上新たに必要となる規程の有無を確認し、報告・整備を通じてコンプライアンス体制を強化する",
    "weight": 0.3,
    "scoring": "評価点５：規程を整備し、届出・共有・完了\r\n・就業規則・附則に改定があれば届出を完了\r\n・海外出張規程、社宅規程を労基に届け出完了\r\n\r\n評価点４：自社の就業規則・附則の変更点を確認し、必要箇所を修正\r\n・就業規則・附則を最新版に更新\r\n・海外出張規程、社宅規程を作成し、関係部門へ改定内容を共有済み\r\n\r\n評価点３：改定内容の整理・案作成までは完了しているが、承認または届出が未実施\r\n・改定箇所を抽出し、修正案をまとめている\r\n\r\n評価点２：現行規程の確認を進めている段階。変更点の整理は未完\r\n・現行規程の確認・比較を実施中\r\n・法改正箇所の洗い出し途中\r\n\r\n評価点１：着手が遅れ、確認が実施されていない\r\n・規程確認・改定作業が未着手",
    "self": 5,
    "final": 4,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第4期",
    "name": "松川牧絵",
    "no": 3,
    "item": "業務の引き継ぎ",
    "criteria": "妹尾さんの将来的な担当領域を見据え、現行業務のうち引き継ぐ範囲を明確に定義し、業務一覧へ反映する\r\n第4期内にどこまで引き継ぎを進めるかの計画を立て、進捗に応じて業務移行を着実に進める",
    "weight": 0.3,
    "scoring": "評価点５：妹尾さんの業務を含む担当領域が明確化され、引き継ぎが完了。業務一覧・マニュアル・担当体制が整備され、誰でも遂行可能な状態\r\n・引継ぎ項目：SEIMUサーバー→207.人事総務専用→207.総務部業務担当一覧に記載している項目の52.5％(160項目中84項目)の引継ぎが完了\r\n\r\n評価点４：第4期に予定していた範囲の引き継ぎが計画どおり完了。担当業務の範囲と責任分担を明確にし、業務一覧へ反映済\r\n・妹尾さん担当業務を確定し、業務一覧へ反映\r\n・引継ぎ項目：SEIMUサーバー→207.人事総務専用→207.総務部業務担当一覧に記載している項目の40％の引継ぎが完了\r\n\r\n評価点３：引き継ぎ計画は策定済みで一部実施中。業務一覧への反映は一部のみ。\r\n・業務洗い出し・役割分担は完了。\r\n・引継ぎ項目：SEIMUサーバー→207.人事総務専用→207.総務部業務担当一覧に記載している項目の35％の引継ぎが完了\r\n\r\n評価点２：引き継ぎ方針や業務一覧の更新準備中。担当範囲やスケジュールが未確定。\r\n・引継ぎ項目：SEIMUサーバー→207.人事総務専用→207.総務部業務担当一覧に記載している項目の30％の引継ぎが完了\r\n\r\n評価点１：着手が遅れ、業務の整理・確定・引き継ぎのいずれも実施されていない\r\n・業務一覧未更新、引き継ぎ計画なし\r\n・属人化状態が継続している",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第4期",
    "name": "楠田彩",
    "no": 1,
    "item": "社内研修制度導入",
    "criteria": "新卒・中途採用の両面で、新入社員入社後研修の骨子を構築し、制度導入するため準備を進める。",
    "weight": 0.5,
    "scoring": "評価点５：研修準備・導入スケジュール策定済。協力会社の選定も完了。(120%)\r\n\r\n評価点４：完成した骨子をもとに上長の承認を得ている。\r\n導入に向けて具体的協力会社の比較・検討が始まっている(110%)\r\n\r\n評価点３：研修制度の骨子構築(全体設計)が完成している(100%)\r\n\r\n評価点２：骨子の一部が未整理、構想レベルにとどまる。(50%)\r\n目的・対象・課題整理はできているが、各部門へ必要な研修のヒアリングが未実施。\r\n\r\n評価点１：未着手または構想段階で止まっている。\r\n構想がメモレベルで方向性も未確定。(0%)",
    "self": 3,
    "final": 3,
    "selfComment": "今期を振り返り、業務全体に対する自分の理解がまだ十分ではなく、\r\n視野の広さや物事を深く考える力、またそれを言語化する力が不足していると感じました。\r\n研修制度の構築や産学連携など、長期的な視点が求められるプロジェクトを立ち上げていく中で、表面的な対応ではなく、背景や目的を踏まえて考える重要性を実感しています。\r\n次期も引き続きご指導のほどよろしくお願いいたします。",
    "finalComment": "第4期、本当にお疲れ様でした。\r\n楠田さんにご入社いただき、個人的にも大変心強く感じています。\r\n特に新卒採用においては、通常業務で多忙な中、多くの学校を訪問いただき、\r\n来年度以降の人事戦略の軸となる重要な知見を得ることができました。ありがとうございます。\r\n\r\n一方で、人事として今後も大切にしていただきたいのは「事実ベースで判断する姿勢」です。従業員それぞれに考え方があり、誰か一人が正しい・悪いという単純な話ではありません。常に俯瞰した視点で状況を捉え、何が必要で、なぜ起きているのか、その中で自分に何ができるのかを考え続けてください。私もまだ道半ばですので、一緒に取り組んでいければと思います。"
  },
  {
    "period": "2025年第4期",
    "name": "楠田彩",
    "no": 2,
    "item": "MiAIシステムの構築と精度向上",
    "criteria": "AI面接を職種ごとに対応できるよう、採用要件を確定する\r\n→重点職種：販売営業、営業アシスタント、新卒、イオンモールアルバイト\r\nまた要件確定時には、人事の採点が一致している\r\n\r\n・重点職種の採用要件を何職種落とし込めたかを評価\r\n・プロップスと連携し、評価表の出力は最低限でもできるようする\r\n・ AI面接の精度向上 AI面接による採点と人事による採点が一致している。\r\n　　1)職種問わず、10/15以降10名の受験を目指す。\r\n　　2)MiAIの評価点で7点以下になっている項目を1次面接にて人事が深堀し評価票を作成。\r\n　　　1次面接を通した人事の評価とMiAIの評価を比較し修正率を記載、最終的に修正率0%になっている状態を目指す。",
    "weight": 0.25,
    "scoring": "評価点５：重点4職種すべてで採用要件を確定。\r\nMiAI評価表の出力が自動化。\r\nAI採点と人事採点の修正率0％。\r\n\r\n評価点４：重点4職種のうち3職種の採用要件確定。\r\n評価表の出力可能。\r\nAI・人事採点の修正率20％未満。\r\n\r\n評価点３：重点4職種のうち2職種の採用要件確定。\r\n評価表は試験的に出力。\r\nAI・人事採点の修正率30％未満。\r\n\r\n評価点２：重点4職種のうち1職種の採用要件確定。\r\n評価表の出力が不安定または部分的。\r\nAI・人事採点の修正率40％未満。\r\n\r\n評価点１：重点4職種すべて採用要件定義が未完。\r\n評価表が進んでおらず、AI採点・人事採点の比較ができない状態。",
    "self": 2,
    "final": 2,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2025年第4期",
    "name": "楠田彩",
    "no": 3,
    "item": "28新卒採用",
    "criteria": "28新卒採用に向けたスケジュールの立案と学校との関係構築\r\n2025年10月～12月の期間においての訪問校のリストの確定と訪問校数、学校側とのパイプ形成実績件数を評価。\r\n\r\n★10〜12月期の大学訪問活動における、\r\n①学校訪問数\r\n②報告書提出の漏れがないか(訪問日から3営業日以内に提出)\r\n③学内説明会や会社見学等のパイプ形成実績\r\n各項目×5点満点の15点方式で合計点から評価を算出。\r\n\r\n評価5（13〜15点）\r\n評価4（11〜12点）\r\n評価3（9〜10点）\r\n評価2（7〜8点）\r\n評価1（6点以下)",
    "weight": 0.25,
    "scoring": "評価点５：①全20校訪問を完了\r\n②全件3営業日以内に報告書提出できている。\r\n③ゼミ内講義・学内説明会・会社見学いずれか1件以上の実施実績あり。\r\n\r\n評価点４：①15校訪問\r\n②提出の遅れが1回以上\r\n③ゼミ内講義・学内説明会・会社見学いずれか実施予定(スケジュール)まで組めた。\r\n\r\n評価点３：①10校訪問\r\n②提出の遅れが2回以上\r\n③次回訪問時に具体的なイベントの打ち合わせをする等、準備段階までパイプ形成できている。\r\n\r\n評価点２：①5校訪問。\r\n②提出の遅れが3回以上\r\n③接点は持てているが、成果や継続関係に繋がっていない。\r\n行動・報告いずれも改善の余地あり。\r\n\r\n評価点１：①5校未満の訪問\r\n②報告未提出・すべて期限超過\r\n③学内・会社見学などの実績なし。",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第1期",
    "name": "竹内雅明",
    "no": 1,
    "item": "生成AI利用推進と離職率改善のための取組み",
    "criteria": "①生成AIに関する情報発信を強化する。専用の情報発信用LINEWORKSグループを作成し、最新の生成AI情報や他社の活用事例等をネットや生成AI自身から入手して原則週に1回発信する\r\n\r\n②離職率改善のため、ハイクラス層転職者(課長職以上：黒田さん・黒瀬さん・福間さん・奥村さん)へ客観的にみたセイムの問題点を個別にヒアリングし、取りまとめたものを社長へ報告する。(対策を実施する/しないは社風に関わるものも想定されるため対象外)",
    "weight": 0.4,
    "scoring": "①3/26(木)までに発信が10回(週1回ペース)行え、かつその中から業務効率化に有効と判断し2部門でプロトタイプとして業務に取り入れたものがあった\r\n②2/27(金)までに4名全員のヒアリングが実施でき、3/13(金)までに内容をまとめかつ対策案を含む形で社長へ報告でき、その中から実際に採用された対策があった\r\n\r\n①3/26(木)までに発信が10回(週1回ペース)行え、かつその中から業務効率化に有効と判断し人事総務部でプロトタイプとして業務に取り入れたものがあった\r\n②4名全員のヒアリングが実施でき、3/13(金)までに内容をまとめかつ対策案を含む形で社長へ報告できた\r\n\r\n①3/26(木)までに発信が10回(週1回ペース)行えた\r\n②2/27(金)までに4名全員のヒアリングを実施し、3/13(金)までに内容をまとめ社長へ報告できた\r\n\r\n①3/26(木)までに発信が6回(月2回ペース)しか行えなかった\r\n②2/27(金)までに4名全員のヒアリングが完了しなかった\r\n\r\n①3/26(木)までに発信が3回(月1回ペース)しか行えなかった\r\n②2/27(金)までにヒアリングが1人も未実施に終わった",
    "self": 3,
    "final": 3,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第1期",
    "name": "竹内雅明",
    "no": 2,
    "item": "経理職(主任以上)の中途採用",
    "criteria": "経理職の主任以上1名について採用する(入社ではなく内定受諾の日を基準とする)",
    "weight": 0.3,
    "scoring": "3/31(火)までに内定受諾いただけた\r\n\r\n誰にも内定を出せなかった\r\n\r\n※内定受諾＝採用条件通知書を出し、入社の承諾を得られた状態",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第1期",
    "name": "竹内雅明",
    "no": 3,
    "item": "部門マネジメント",
    "criteria": "大志万さん・松川さん・山本さんの評価平均に連動",
    "weight": 0.3,
    "scoring": "松川・大志万・山本の平均が8点以上\r\n松川・大志万・山本の平均が7.5点以上\r\n松川・大志万・山本の平均が7点以上\r\n松川・大志万・山本の平均が6.5点以上\r\n松川・大志万・山本の平均が6.5点未満",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第1期",
    "name": "大志万拓",
    "no": 1,
    "item": "離職率管理の前準備",
    "criteria": "・部門別の離職率算出\r\n・高離職率であれば原因の確認\r\n・対応策の検討、報告\r\n(年間離職率を使用すること\r\n年間離職率(%)=その年に退職した人数÷平均従業員数x100",
    "weight": 0.3,
    "scoring": "3/26(木)までに管理スキームの提出と部門長承認が完了しており、部門長会議用資料が完成している\r\nまた2026年第1期分のデータ反映まで完了している\r\n\r\n3/26(木)までに管理スキーム確定および部門長承認が完了しており、部門長会議用資料案の作成・提出まで終えた\r\n\r\n3/26(木)までに管理スキーム案の提出を終え、部門長承認を得ており次期から運用できるよう準備が完了している\r\n\r\n3/26(木)までに管理スキーム案の提出まで終えたが、差し戻し状態になっている\r\n\r\n3/26(木)までに具体的な案の提出に至らなかった",
    "self": 3,
    "final": 3,
    "selfComment": "期の開始から相当な変化のあるフェーズだったと認識しています。\r\n厳しいご指示もありましたが、それは役職に応じた役割を与えている、達成可能と想定してのこととポジティブに考えています。\r\n他方、会社の方針と現場との乖離が大きい一面もあり、部下と会社側の意向をどう調整していくのかという点においては、来期以降も継続的に考えねばならないと思っています。",
    "finalComment": "社長からの要求がタイトになっているのはそれだけ会社として成長しているからだと思っていて、我々も負けずに成長するだけのことだと思っています。言われてやるのはつまらないので、言われる前に期待に答えたいので来期も一緒に頑張ってください。\r\n\r\n以下AI\r\n採用・研修制度の両軸で企画立案力と行動力が際立つ四半期だった。KPI管理表の設計から新卒コンペの企画まで、主任として牽引すべき役割を十分に果たしている。一方、給与・委託料関連での重大ミスが2件発生しており、「猛省」という言葉が出るほどの重さがあった。企画力の高さとミスリスクの高さが同居しており、主任として品質管理への意識強化が最優先課題。"
  },
  {
    "period": "2026年第1期",
    "name": "大志万拓",
    "no": 2,
    "item": "経理職・事務職系統の採用",
    "criteria": "2026年第1期内に経理職・事務職に特化した中途採用計画を達成する。\r\n・販売営業部・・・事務職社員1名\r\n・経理部・・・経理職社員3名(1名は主任以上)\r\n・不動産事業部営業課・・・事務職社員1名\r\n・建築事業部・・・事務職社員1名\r\n※内定受諾ベースでカウント、3名以上で評価5",
    "weight": 0.4,
    "scoring": "3/26(木)までに評価基準の対象者から3名以上の内定受諾を得た\r\n\r\n3/26(木)までに評価基準の対象者から2名以上の内定受諾を得た\r\n\r\n3/26(木)までに評価基準の対象者から1名以上の内定受諾を得た\r\n\r\n内定送付のみに留まり1名も内定受諾に至らなかった\r\n\r\n1名も内定に至らなかった",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第1期",
    "name": "大志万拓",
    "no": 3,
    "item": "新卒採用インターンシップ+産学連携プロジェクト",
    "criteria": "セイム独自の28卒向けインターンシップを企画し、関係各所と連携したものを創り上げる\r\n・販売営業部(営業)、建築事業部(設計)との協業想定\r\n　└1次ゴール：インターンシップ企画・運営・実施\r\n　└2次ゴール：産学連携プロジェクトまで成長・企画\r\n　└(数年後想定で)インターンシップ→産学連携プロジェクトへの導線設計も行い、\r\n　　将来的にはondoの新コンセプトとして「大学×ondo」ブランドとして世に発信できることを目指す\r\n※同評価レンジで複数の基準があるものは、いずれも達成されている評価レンジを評価点とする\r\n※注意点として、企画段階(承認前)から他部門への説明と協力の取り付けは進めておかないと間に合わないと思われる",
    "weight": 0.3,
    "scoring": "〇インターンシップ企画\r\n3/26(木)までにインターンシップサイトにて掲載を開始しており、既に応募者が1名以上いる状態\r\n〇産学連携プロジェクト\r\n3/26(木)までに、評価4までを達成かつ自身で販売営業部・建築事業部からの協力を取り付け協議を進めている状態\r\n\r\n〇インターンシップ企画\r\n3/26(木)までに企画書の部門長承認が得られた\r\n〇産学連携プロジェクト\r\n3/26(木)までに企画書の部門長承認が得られた\r\n\r\n〇インターンシップ企画\r\n3/26(木)までに企画書を提出できたが部門長承認が得られなかった\r\n〇産学連携プロジェクト\r\n3/26(木)までに企画書を提出できたが部門長承認が得られなかった\r\n\r\n〇インターンシップ企画\r\n3/26(木)までに企画書の提出ができなかった\r\n〇産学連携プロジェクト\r\n3/26(木)までに企画書の提出ができなかった\r\n\r\n〇インターンシップ企画\r\n3/26(木)までに具体的な進捗がまったく報告できなかった\r\n〇産学連携プロジェクト\r\n3/26(木)までに具体的な進捗がまったく報告できなかった",
    "self": 4,
    "final": 4,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第1期",
    "name": "松川牧絵",
    "no": 1,
    "item": "法務機能強化",
    "criteria": "・契約書管理(紙媒体の集約、紙媒体のデータ化(何ヶ月で終わらせるか)、クラウドサイン契約書類のダウンロード➤すべてのデータのサーバ格納＋他社との契約書類はkintoneでも管理\r\n・ファイル名は検索しやすい様に命名ルールで統一\r\n・その他法令違反状態のリストアップおよび解消のスケジューリング\r\n※複数の評価レンジがある場合はすべて達成できた評価レンジを評価点とする",
    "weight": 0.34,
    "scoring": "3/26(木)時点で下記の状態である\r\n・評価4に加え、契約書管理ルールが作成され掲示板に掲載されている\r\n・法令違反・是正対象の一覧リストが完成しており、今後出てきそうな法令違反についても報告が出来ている\r\n\r\n3/26(木)時点で下記の状態である\r\n・評価3に加え、部下がルールに沿って運用できていることが確認できる\r\n・サーバに格納している契約書データはすべてkintoneにも格納されている\r\n・法令違反・是正対象のリストが完成し是正スケジュールが提出できている\r\n\r\n3/26(木)時点で下記の状態である\r\n・クラウドサインで契約したデータがすべてPDF化されサーバに格納されている\r\n・ファイル命名、格納ルールが確定し、第三者が迷わず運用できる\r\n・法令違反リストが完成している\r\n・紙媒体のデータ化の完了期限を上長に報告し、承認を得られている\r\n\r\n3/26(木)時点で下記の状態である\r\n・契約書のルールや管理方針が一部未確定\r\n・法令違反リストが不完全で是正期限・対応方法が未整理\r\n・契約書については運用できない\r\n\r\n3/26(木)時点で下記の状態である\r\n・管理ルールが未確定\r\n・ファイル命名・格納ルールが曖昧\r\n・法令違反状態が把握できていない\r\n・仕組みとして残っていない（口頭・途中止まり）",
    "self": 5,
    "final": 5,
    "selfComment": "契約書の確認業務が想定以上に多く、また不慣れな分野でもあったため、確認に時間を要しました。\r\n一方で、契約書の確認を通じて条文の意図やリスクポイントについて理解を深めることができ、今後の業務に活かせる知識を蓄積できたと考えています。\r\n\r\nその影響により、新卒用規程およびパート・アルバイト規程については、完成まで持っていくことができなかった点が課題として残りました。\r\n次期については、優先順位の整理やスケジュール管理をより意識し、規程整備の完了まで確実に進めていきたいと考えています。",
    "finalComment": "総務としての形は出来てきていると思いますので、中身もそれに伴うように勉強・理解して発言に信憑性を持ってもらえるようになっていってください。理解が深まっていけば仕事もラクになっていきます！\r\n\r\n以下AI\r\n総務の中核を担う主任として、高い処理量と自発的な仕組み改善を両立した四半期だった。特に補助金申請・各種ルール整備・清掃体制見直しなど、前例のない業務にも積極的に取り組む姿勢が評価に値する。課題は①トーク送信の「取り消し」に象徴される情報発信の精度と、②一部タスクの大幅な期限超過の再発防止。多忙な中でのミスは理解できるが、主任として後輩（妹尾）への模範姿勢も意識してほしい。"
  },
  {
    "period": "2026年第1期",
    "name": "松川牧絵",
    "no": 2,
    "item": "総務業務対応",
    "criteria": "・アルバイト用の就業規則作成(夜市、イオンモール等で使用)\r\n・イオンモール鶴見緑地出店の補助金調査\r\n・新卒用の資格支援制度規定作成\r\n・車両管理規定に事故発生時の罰則規定を盛り込む",
    "weight": 0.33,
    "scoring": "3/26(木)時点で下記の状態である\r\n評価「4」の状態をすべて満たしたうえで、以下のいずれかを達成\r\n●アルバイト用就業規則\r\n・今回作成した就業規則が将来の出店形態にも流用可能な汎用設計になっている\r\n●イオンモール鶴見緑地出店の補助金調査\r\n・補助金について、実行スケジュールや申請体制まで整理されている\r\n●新卒用の資格支援制度規定作成\r\n・新卒向け資格支援制度の運用ルールが社内掲示板に掲載されている\r\n●車両管理規定に事故発生時の罰則規定\r\n・事故発生時のルールが掲示板に掲載されている\r\n\r\n3/26(木)時点で下記の状態である\r\n評価「3」の状態をすべて満たしたうえで、以下のいずれかを達成\r\n●アルバイト用就業規則\r\n・契約条件、法令要件を満たしたアルバイト用就業規則が完成している\r\n●イオンモール鶴見緑地出店の補助金調査\r\n・補助金の申請可否判断が可能な調査結果が完了している\r\n●新卒用の資格支援制度規定作成\r\n・新卒向け資格支援制度の運用ルールが社内掲示板に掲載されている\r\n●車両管理規定に事故発生時の罰則規定\r\n・車両管理規程に明文化されており上長承認を得ている\r\n\r\n3/26(木)時点で下記の状態である\r\n●アルバイト用就業規則\r\n・就業規則または補助金調査のいずれかが未完成\r\n・もしくは両方とも存在するが、使用判断に必要な情報が不足している\r\n●イオンモール鶴見緑地出店の補助金調査\r\n・補助金の申請可否判断が可能な調査結果が完了している\r\n●新卒用の資格支援制度規定作成\r\n・新卒向け資格支援制度の運用ルールが社内掲示板に掲載されている\r\n●車両管理規定に事故発生時の罰則規定\r\n・事故発生時の対応フローや考え方が整理されており罰則の方向性が決まっている\r\n\r\n3/26(木)時点で下記の状態である\r\n●アルバイト用就業規則\r\n・就業規則がドラフト段階にとどまっている\r\n●イオンモール鶴見緑地出店の補助金調査\r\n・補助金調査が断片的で、制度全体を整理できていない\r\n●新卒用の資格支援制度規定作成\r\n・資格支援制度の具体的な内容やルールが整理できていない\r\n●車両管理規定に事故発生時の罰則規定\r\n・事故発生時の罰則の基準が定まっていない\r\n\r\n3/26(木)時点で下記の状態である\r\n●アルバイト用就業規則\r\n・就業規則が未作成、または実務で使用できない\r\n●イオンモール鶴見緑地出店の補助金調査\r\n・補助金について調査結果が存在しない、または把握できていない\r\n●新卒用の資格支援制度規定作成\r\n・具体的な案がなく未着手の状態\r\n●車両管理規定に事故発生時の罰則規定\r\n・事故発生時の対応・罰則の内容が未定義",
    "self": 3,
    "final": 3,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第1期",
    "name": "松川牧絵",
    "no": 3,
    "item": "総務業務対応",
    "criteria": "・ベトナム旅行準備\r\n・業務委託契約書ひな形、依頼書作成",
    "weight": 0.33,
    "scoring": "3/26(木)時点で下記の状態である\r\nベトナム旅行準備\r\n・トラブル発生時の連絡体制・緊急対応方針が整理されている\r\n\r\n業務委託契約書ひながた、依頼書作成\r\n・業務委託契約書のひな形および依頼書を整備・完成させ、運用ルールとして社内掲示板に掲載し、全社員へ周知が完了している状態\r\n\r\n3/26(木)時点で下記の状態である\r\nベトナム旅行準備\r\n・人数・行程・部屋割りがすべて確定している\r\n・最終行程表・費用内訳・社内案内が完成している\r\n\r\n業務委託契約書ひながた、依頼書作成\r\n・業務委託契約書のひな形が完成している\r\n・依頼書のフォーマットが完成している\r\n\r\n3/26(木)時点で下記の状態である\r\nベトナム旅行\r\n旅行準備は進んでいるが、最終行程表・社内案内・緊急対応体制のいずれかが未完成\r\n\r\n業務委託契約書ひながた、依頼書作成\r\n・業務委託契約書のひな形が作成されている\r\n・内容に一部未整理・調整事項が残っているが、たたきとして使用可能な状態\r\n\r\n3/26(木)時点で下記の状態である\r\nベトナム旅行\r\n旅行準備が確定情報の整理段階にとどまっている\r\n\r\n業務委託契約書ひながた、依頼書作成\r\n・契約書の構成や条文案の整理に着手している\r\n・ひな形は未完成だが、方向性や方針は整理できている\r\n\r\n3/26(木)時点で下記の状態である\r\nベトナム旅行\r\n旅行準備について、実施判断に必要な確定情報が揃っていない\r\n\r\n業務委託契約書ひながた、依頼書作成\r\n・業務委託契約書・依頼書ともに未着手、または構想段階にとどまっている",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第1期",
    "name": "山本摩耶",
    "no": 1,
    "item": "YAKUNITATSUのkintone環境構築(フェーズ１)",
    "criteria": "①YAKUNITATSU組織、ユーザの追加設定\r\n②フォームラン反響フォームのアプリ化\r\n③フォームラン受任用フォームのアプリ化\r\n④過去データ（反響帳）のアプリ化\r\n⑤進捗状況確認表(売上）のアプリ化\r\n※②～⑤については別のアプリ作成に差し替える場合がある",
    "weight": 0.5,
    "scoring": "3/12までに①が完了し、4つ以上のkintoneアプリ構築およびデータ移行が完了し、YAKUNITATSUメンバーがkintoneの利用をスタートできた。\r\n\r\n3/26までに①が完了し、4つ以上のkintoneアプリ構築およびデータ移行が完了し、YAKUNITATSUメンバーがkintoneの利用をスタートできた。\r\n\r\n3/26までに①が完了し、4つ以上のkintoneアプリ構築が完了した\r\n\r\n3/26までに①が完了し、3つのkintoneアプリ構築が完了した\r\n\r\n3/26までに①が完了し、1～2つのkintoneアプリ構築およびデータ移行が完了した",
    "self": 3,
    "final": 3,
    "selfComment": "3月全体MTGでの発表後には、複数人から「面白かった」「分かりやすかった」とお声がけをいただき、手ごたえを感じました。\r\n自動化推進については、社員側から「こんなことできないかなと思っているので相談させてほしい」と声を上げてくださったおかげで、想定より多くのフローをリリースすることができました。",
    "finalComment": "YAKUNITATSUの作業中断はありましたがそれ以外は自動化推進もミーティングでの発表も非常に助かりました。来期からはAI活用推進PJがスタートしますので、山本さん自身ももっとAIに作業させることを取り込んでいってもらたらなと思います。\r\n\r\n以下AI\r\n今四半期の部内MVP候補に値するパフォーマンスを発揮した。単なる「IT作業者」にとどまらず、業務自動化設計・生成AIルール策定・全体MTGプレゼン資料作成など、組織のデジタル化をリードする戦略的役割を担えている。唯一の課題は「チーム貢献・協調性」のさらなる強化で、自身の高いスキルを他メンバーへの教育・底上げにも活かすフェーズへの移行が期待される。"
  },
  {
    "period": "2026年第1期",
    "name": "山本摩耶",
    "no": 2,
    "item": "業務自動化推進",
    "criteria": "生成AIを中心としてkintone、JENKA、Robotango等を活用し業務の自動化を推進する\r\n※複数の評価レンジがある場合はすべて達成できた評価レンジを評価点とする",
    "weight": 0.5,
    "scoring": "・3/26(木)までに自動化できたフローが5個以上、うち3個以上で生成AIが活用されている\r\n・3/26(木)までに社内の生成AI利用率向上に繋がる活動（セミナー開催や教材資料配布）が1回以上できた\r\n\r\n3/26(木)までに自動化できたフローが4個、うち2個以上で生成AIが活用されている\r\n\r\n3/26(木)までに自動化できたフローが3個、うち1個以上で生成AIが活用されいている\r\n\r\n3/26(木)までに自動化できたフローが2個\r\n\r\n3/26(木)までに自動化できたフローが1個以下",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第1期",
    "name": "妹尾祐紀",
    "no": 1,
    "item": "総務部管轄の契約書をすべてkintoneで一元管理できる状態を構築し、誰でも検索・閲覧しやすい契約書管理アプリを完成させる",
    "criteria": "・既存の総務部管轄契約書が漏れなくkintoneに登録されている\r\n・原本の有無に関わらず、PDFデータとして格納されている\r\n・契約書名、取引先、契約期間等で検索できる設計になっている\r\n・利用者視点での改善提案が行われ、アプリに反映されている\r\n・経理部の社員でも迷わず使える状態になっている",
    "weight": 0.4,
    "scoring": "・3/26(木)までに④の状態を満たしたうえで、書類原本(ファイル)をあいうえお順に整理する\r\n\r\n3/26(木)までに\r\n・契約書の登録「100％」完了\r\n・PDFデータ格納「100％」完了\r\n・検索設定（契約書名・取引先・契約期間）「100％」完了\r\n\r\n3/26(木)までに\r\n・契約書の登録・PDF格納・検索設定(契約書名・取引先・契約期間)のいずれかに不備が「0〜4件」存在\r\n※契約内容確認において、業務上の支障は発生していない状態\r\n\r\n3/26(木)までに\r\n・契約書の登録・PDF格納・検索設定(契約書名・取引先・契約期間)のいずれかに不備が「5〜10件」存在\r\n※契約書の所在は把握でき、業務上の確認自体は可能な状態\r\n\r\n3/26(木)までに\r\n・契約書の登録・PDF格納・検索設定(契約書名・取引先・契約期間)のいずれかに不備が「11件以上」存在\r\n※業務上の支障が発生している状態",
    "self": 4,
    "final": 4,
    "selfComment": "役割項目①は評価レンジ5の達成に至りませんでしたが、役割項目③は早期に達成することができました。\r\nそのため、残りの期間を有効活用し、管理ができていなかった車庫証明の発行履歴の整理など、追加の取組みも実施しました。\r\nまた、役割項目②においても車検・保険等の期限アラートを活用し、先回りで対応することを意識するとともに、不備のない管理を行っております（進行中の案件については、他者でも把握できるよう備考欄へ進捗入力）。\r\nさらに、車両関連及びボート・水上バイク関連のお取引先様へ担当者としてのご挨拶を行うとともに、曖昧になっていた運用ルールの確認・見直しを実施、現時点では円滑なやり取りができております。",
    "finalComment": "コメントにあったとおり、丁寧に業務へ取り組んでいただいており、細かな部分まで把握・整理されている点は良いと感じています。\r\n特に、追加の取組みや運用ルールの見直しなど、自発的に改善を進めている点も評価できるポイントです。\r\n今後は、業務の優先順位や時間の使い方も意識しながら取り組むことで、さらに効率的な業務遂行につながることを期待しています。"
  },
  {
    "period": "2026年第1期",
    "name": "妹尾祐紀",
    "no": 2,
    "item": "社用車に関する情報をkintoneで一元管理し、誰でも車両状況・保険・点検期限等を把握できる管理体制を構築する",
    "criteria": "・社用車・持込マイカーがすべてkintoneに登録されている\r\n・車検・任意保険・自賠責保険の更新期限が一覧で確認できる\r\n・登録・更新(任意保険・自賠責保険・車検)のタイミングを理解し、抜け漏れなく対応できている\r\n・不明点や判断が必要な点を自己判断せず、適切に相談できている\r\n・新入社員の車両の手配が入社までに出来た（間に合わない場合は代車でも可）\r\n※登録情報：マイカーの方の書類紛失による再発行期間・証券番号など原本郵送待ちの情報は除く(該当する場合は進捗記入で対応)",
    "weight": 0.3,
    "scoring": "・3/26(木)までに④の状態を満たしたうえで、新入社員の車両手配（代車対応含む）が、入社日までに「100％」完了している\r\n\r\n3/26(木)までに\r\n・全車両(社用車・マイカー)の登録「100％」完了\r\n・車検・任意保険・自賠責保険の更新期限が一覧で把握でき、更新が必要な車両は期限内に「100％」対応完了している\r\n\r\n3/26(木)までに\r\n・車両登録(社用車・マイカー)・更新対応(車検・任意保険・自賠責保険)のいずれかに不備が「0〜4件」存在\r\n※車両の所在・状況は把握でき、業務上の確認自体は可能、不備件数が微少の為、業務上の支障は発生していない状態\r\n\r\n3/26(木)までに\r\n・車両登録(社用車・マイカー)・更新対応(車検・任意保険・自賠責保険)のいずれかに不備が「5〜10件」存在\r\n※車両の所在・状況は把握でき、業務上の確認自体は可能な状態\r\n\r\n3/26(木)までに\r\n・車両登録(社用車・マイカー)・更新対応(車検・任意保険・自賠責保険)のいずれかに不備が「11件以上」存在\r\n※業務上の支障が発生している状態",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第1期",
    "name": "妹尾祐紀",
    "no": 3,
    "item": "お客様第一主義を理解し、駐車場停車位置変更を1月中に完了する",
    "criteria": "・固定する車両を決める\r\n・使用頻度の少ない車両を決めて空き駐車場へ移動する\r\n・①誰が②どこから③どこへを決定\r\n・「日時」で区切るなどして移動を周知する",
    "weight": 0.3,
    "scoring": "・3/26(木)までに車両移動における掲示板用ルールを作成する\r\n\r\n・対象社員の車両移動が完了後、Excel管理データと実際の再配置場所（駐車位置）にずれがない\r\n\r\n・再配置が「1月中」に完了している\r\n\r\n・運用ルールは決定しているが、こちらの周知不足により移動対象社員「2/3以上」から確認が発生している状態\r\n\r\n・運用ルールが未決定、または未周知で現場対応が属人化している状態",
    "self": 5,
    "final": 5,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第1期",
    "name": "楠田彩",
    "no": 1,
    "item": "社内研修制度の構築",
    "criteria": "2026年に一部で開始される研修制度の骨子の確定および研修委託先の選定開始\r\n└骨子の完成度は80%\r\n└骨子には「研修内容」「導入～実施までのタイムライン」も含む\r\n└開始予定となるのは部門長以上の役職者\r\n└研修内容については大志万→竹内ラインでの要承認",
    "weight": 0.4,
    "scoring": "3/6(金)の部門長会議にて決裁を得られるレベルの研修企画書を完成させ、\r\n研修内容骨子および研修委託先の方向性について実施に向けた判断が取れる状態まで到達している。\r\n※来期に全体MTGで社員へ説明できる状態\r\n\r\n2/20(金)までに提出した研修内容骨子において、段階的リリースのスケジュール含め内容がほぼ確定しており微調整のみ必要な状態。\r\n\r\n1/31(土)までに研修内容骨子を提出、大志万さん→竹内さんに承認を得られる状態になっている。\r\nまた研修委託先の選定に向けた検討を進めている。\r\n\r\n研修制度の検討や情報収集は行っているが、\r\n研修内容骨子として整理されていない。\r\n各部署に必要なアンケートを行えていない。\r\n\r\nまったく着手できていない。",
    "self": 3,
    "final": 3,
    "selfComment": "研修制度については、会社方針の変更が発生する中で、その都度見直しや調整を行いながら進めました。結果として、研修内容の骨子や段階的リリースを含めた全体設計は概ね固めることができましたが、実装に向けた調整事項が多く残っておりまだまだ不安な状況です。来期からオファーボックスが開始することに加えて、27卒アルバイトの受け入れがあります。アルバイトや内定者フォローの導線を整理し、28卒にも問題なく運用できるよう仕組み化や制度の整備を進めます。以降も複数の部署との連携が必要になるプロジェクトが多いため、皆さんと協力しながら進めていけるよう取り組んでいきたいと思います。",
    "finalComment": ""
  },
  {
    "period": "2026年第1期",
    "name": "楠田彩",
    "no": 2,
    "item": "28卒インターンシップ企画書の立案",
    "criteria": "セイム版28卒向けインターンシップとして、学生からの自由応募によるコンペ開催ができるよう企画立案と実行準備",
    "weight": 0.4,
    "scoring": "設計部門と連携した28卒向けコンペ型インターン企画書を完成させ、\r\n3/1までに竹内さんの承認を得たうえで、修正なく来期に実行フェーズに進める完成度となっている。\r\n\r\n設計部門と連携した28卒向けコンペ型インターン企画書を作成し、\r\n2/15までに大志万さんへの提出・説明まで完了しているが、\r\n修正や追加検討を前提とした状態。\r\n\r\nコンペ型インターンの企画書を作成し、\r\n設計部門との連携・具体的な検討を進めている。\r\n\r\n企画の検討・情報収集は行っているが、\r\n企画書として整理されておらず、設計部門との連携にも至っていない。\r\n\r\nコンペ企画の検討・準備に着手できていない。",
    "self": 5,
    "final": 5,
    "selfComment": "方針変更が色々とある中、臨機応変に立ち回り、よく付いてきていただいたと思います。来期からはオファーボックスも始まります、非常に忙しくなるかと思いますが、セイム版新卒採用計画を更に磨いていく必要がありますので、引き続きよろしくお願いいたします。\r\nまた私自身にも言えることですが、常に事実をベースにした判断をしていくこと、想うところはあれども粛々と業務遂行を務めること、この2点は忘れずに進めていきましょう。",
    "finalComment": ""
  },
  {
    "period": "2026年第1期",
    "name": "楠田彩",
    "no": 3,
    "item": "MiAI面接精度向上",
    "criteria": "AI面接の精度向上 AI面接による採点と人事による採点が一致している。\r\n　　1)職種問わず、1/15以降10名の受験を目指す。\r\n　　2)Ai判定の評価と人事からみた評価を比較し修正率を記載、最終的に修正率0%になっている状態を目指す。",
    "weight": 0.2,
    "scoring": "AI採点と人事採点の修正率0％。\r\n\r\nAI・人事採点の修正率20％未満。\r\n\r\nAI・人事採点の修正率40％未満。\r\n\r\nAI・人事採点の修正率50％未満。\r\n\r\n採用要件の整理・AI面接精度検証にまったく着手できていない。",
    "self": 3,
    "final": 3,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "竹内雅明",
    "no": 1,
    "item": "部門マネジメント",
    "criteria": "大志万さん・松川さん・山本さんの評価平均に連動",
    "weight": 1,
    "scoring": "松川・大志万・山本の平均が8点以上\r\n松川・大志万・山本の平均が7.5点以上\r\n松川・大志万・山本の平均が7点以上\r\n松川・大志万・山本の平均が6.5点以上\r\n松川・大志万・山本の平均が6.5点未満",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "大志万拓",
    "no": 1,
    "item": "社内研修制度の基盤完成",
    "criteria": "6/25(木)までにHS評価に紐づいた、SLPに準拠した研修制度を構築するために、下記内容を第2期中に確定させる。\r\n・研修手法の確立\r\n・研修テーマの策定\r\n・対象レイヤーの確定および実施スケジュール\r\n\r\n【目的】\r\n社員が現段階の視座よりもう一つ上の階層にステップアップすることを目的とし、SLPをより具体的に体現できる社内風土づくりを目指す。",
    "weight": 0.3,
    "scoring": "6月25日(木)時点で下記の状態すべてが完遂しており部門長承認を得ている\r\n・企画書を提出でき部門長の承認も得られ部門長会議でも最終承認を得られた\r\n・運用フローの策定が完了しており、各種システム(Googleフォーム、カオナビへの転載等)の動作を部門長が確認できている\r\n・人事評価制度との整合が取れている(TS軸との整合性)\r\n・研修スケジュールの確定(具体的な対象者、実施時期の予測)\r\n\r\n6月25日(木)時点で下記の状態\r\n・企画書を提出でき部門長の承認も得られたが部門長会議で未承認\r\n・運用フローの構想まで終えており、実際に設計として各種システムに落とし込まれているが部門長が動作確認できるレベルには達していない\r\n・人事評価制度と照らし合わせ不整合な点があり、差し戻し状態\r\n・研修スケジュールの提出は終えたものの、実施時期の調整が必要な状態\r\n\r\n6月25日(木)時点で下記の状態\r\n・企画書を提出できたが部門長で未承認\r\n・運用フローが構想段階(案)で終わった(提示できる資料はある)\r\n・人事評価制度に組み込んだ設計初案の提出に留まった\r\n・研修スケジュールは初案を提出済み\r\n\r\n・企画書が部門長に提出できていない\r\n・運用フローが定まっていない(提示できる資料もない)\r\n・人事評価制度への組み込み設計まで検討できていない\r\n・研修スケジュール案は未提出\r\n\r\n・研修プログラムに関して、企画書等の資料がいずれも未提出\r\n・部門長に相談レベルで実行にまで至っていない",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "大志万拓",
    "no": 2,
    "item": "評価制度の昇格降格要件改版、役職のスキル要件設定",
    "criteria": "6/25(木)までに\r\n・職種別役職毎のスキル要件(テクニカルとヒューマン)が定まっている\r\n・新等級制度に対応した昇格降格要件が定まっている\r\n(普通に貢献していれば毎年1等級は昇格、良い成績であれば2等級昇格、ただし役職の境目では部所属部門長の申請なければそれ以上上がらない、組織構成維持のための飛び級は社長承認によって有り、という運用のイメージ)",
    "weight": 0.4,
    "scoring": "・職種別・役職別のスキル要件が定義され、評価指標が紐付いている\r\n　➤翌期から即運用できるレベルに設計完了している\r\n・昇格降格要件が明確化され、ケース別運用（例：例外昇格、降格ライン）が整理されている\r\n・評価シミュレーション（複数社員パターン）を実施し、制度の妥当性検証が完了している\r\n・部門長会議等にて、上長が各部門長の合意形成が得られるよう資料作成まで終えている\r\n\r\n・職種別・役職別スキル要件が整理されている\r\n・昇格降格要件が新等級制度に対応して定義されている\r\n・基本的な運用ルール（例：部門長承認・社長承認条件）が明文化されている\r\n\r\n・スキル要件が職種別・役職別に整理されている\r\n・昇格降格要件のルールが定義されている（文章ベース）\r\n\r\n・スキル要件が一部職種・役職で未整理\r\n・昇格降格要件が曖昧または未完成\r\n\r\n・スキル要件未整理\r\n・昇格降格要件未定義",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "大志万拓",
    "no": 3,
    "item": "採用",
    "criteria": "3pt　施工管理(メンテ含む)➤KPI管理上3名\r\n2pt　販売営業・仕入営業➤KPI管理上4名\r\n1pt　その他すべて\r\n(派遣・契約の場合は半分、パートはゼロ)\r\n※内定受諾ベースでカウント",
    "weight": 0.3,
    "scoring": "5pt以上\r\n\r\n4pt以上\r\n\r\n3pt以上\r\n\r\n2pt以上\r\n\r\n1pt以下",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "松川牧絵",
    "no": 1,
    "item": "契約書作成",
    "criteria": "6/25(木)の時点で\r\n・6/11(木)以降に依頼されたもの以外の契約書作成待ち分がゼロ\r\n・原本、PDFデータともにきちんと保管されkintoneDBも登録されている",
    "weight": 0.4,
    "scoring": "6/25時点で、6/11以降依頼分を除く契約書作成待ちがゼロ。かつ原本・PDFデータの保管とkintone DBへの登録がすべて完了している。\r\n\r\n作成待ちはゼロだが、原本・PDF保管及びkintone登録に1件以上の漏れ・不備がある。（契約相手方の押印待ちは除く）\r\n\r\n6/11以降依頼分を除く未作成が1〜2件残っている。\r\n\r\n未作成が3〜5件残っている。\r\n\r\n未作成が6件以上残っている。",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "松川牧絵",
    "no": 2,
    "item": "9F移動",
    "criteria": "6/25(木)までに\r\n・レイアウトが確定している\r\n・必要な什器や小物類の手配が完了している\r\n・９Fへ人事総務部移動に伴う他部門も含めた移動について計画され社内での周知が完了している",
    "weight": 0.3,
    "scoring": "移動計画書が作成され関係他部門・関係者への共有が完了している。移動日が決まればスムーズに移動できる状態で、什器も計画書に沿ってすべて発注されている。財務経理の移動まで含め問題ない状態となっている。\r\n\r\n移動計画書は作成されているが、関係他部門・関係者に共有がされておらず、什器に未発注がある。\r\n\r\n移動計画書を作成し部門長承認がおりている。\r\n\r\nレイアウトが完成しているが移動計画書が未完成。\r\n\r\nレイアウトが未確定で建築事業部へも催促できていない。",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "松川牧絵",
    "no": 3,
    "item": "その他総務業務",
    "criteria": "・来年3月予定の式典イベントの詳細決定",
    "weight": 0.3,
    "scoring": "企画書の社長承認に加え、日時・移動手段の確定および場所の候補選定が完了している。\r\n\r\n来年3月の式典イベントの企画書の社長承認がとれている。\r\n\r\n来年3月の式典イベントの企画書の部門長承認がとれている。\r\n\r\n3月の式典イベントについて企画書を作成した。（部門長承認は未）\r\n\r\n3月の式典イベントについて上長への報告・相談ができていない。",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "山本摩耶",
    "no": 1,
    "item": "生成AI活用推進PJ運営",
    "criteria": "6/25(木)までに\r\n・月１回計３回の会議が行えた\r\n・プロジェクト管理(自分およびメンバーのタスクと期限の管理)ができている(LINEWORKSグループタスク機能を利用)\r\n・業務効率化手法やAI最新情報の発信(共有)ができた\r\n\r\n※評価項目のすべてを満たした評価レンジを評価点として採用する",
    "weight": 0.5,
    "scoring": "・評価3の項目を全て満たせた\r\n・会議3回とも90分以内で実施できた\r\n・自分およびメンバーのタスクの期限超過が1件以内だった\r\n\r\n・評価3の項目を全て満たせた\r\n・会議2回が90分以内で実施できた\r\n・自分およびメンバーのタスクの期限超過が3件以内だった\r\n\r\n・月1回計3回の会議の前日までに会議資料を上司に提出でき承認を得られた\r\n・会議開催の翌営業日までに、次回までのタスク(自分およびメンバーのタスク)を登録できた\r\n・期限超過しそうなタスクについては前日までに担当者にリマインドできた\r\n・業務効率化手法やAI最新情報の発信(共有)が2回できた(キックオフ回は除外)\r\n\r\n・月1回計3回の会議が行えた\r\n・業務効率化手法やAI最新情報の発信(共有)が1回できた(キックオフ回は除外)\r\n\r\n・月1回計3回の会議が行えなかった",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "山本摩耶",
    "no": 2,
    "item": "業務自動化推進",
    "criteria": "アプリ(exe)開発、ClaudeCowork、CodeCode、kintone、JENKA、AI-OCR等を活用し業務の自動化を推進する",
    "weight": 0.5,
    "scoring": "自動化/効率化できたタスクが10件以上\r\n\r\n自動化/効率化できたタスクが8件以上\r\n\r\n自動化/効率化できたタスクが6件以上\r\n\r\n自動化/効率化できたタスクが4件以上\r\n\r\n自動化/効率化できたタスクが4件未満",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "妹尾祐紀",
    "no": 1,
    "item": "アルコールチェック運用開始",
    "criteria": "期限：5月〜運用開始\r\n- 運用開始に向けた準備・周知を完了させる\r\n- 5月より正式運用スタート（5点基準）",
    "weight": 0.3,
    "scoring": "・5月31日までに運用開始している\r\n・マニュアル作成、投稿依頼\r\n・安定した継続運用ができるよう、状況に応じた見直し・業務改善を実施する（例：実施率の低い利用者に対する個別指導・全体アナウンス等）\r\n・社用車利用者の実施率が100％以上である（運用開始から1ヶ月間）\r\n\r\n・6月25日までに運用開始している\r\n・社用車利用者の実施率が100％以上である\r\n\r\n・6月25日までに運用開始している\r\n・社用車利用者の実施率が50〜99％である\r\n\r\n・6月25日までに運用開始していない\r\n　または\r\n・社用車利用者の実施率が50％未満である\r\n\r\n・ルール未整備である\r\n・社用車利用者への周知を実施していない",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "妹尾祐紀",
    "no": 2,
    "item": "契約書原本の書類整理",
    "criteria": "期限：6月25日まで\r\n- 契約書原本をあいうえお順に整理・並び替え\r\n- 誰でも取り出しやすい状態に整備する",
    "weight": 0.2,
    "scoring": "6月25日までに\r\n・契約書原本のあいうえお順整理完了率が100％である\r\n・第三者が迷うことなく目的の契約書を取り出せる状態である\r\n（あいうえお順かつ、インデックス・付箋等により識別可能な状態）\r\n\r\n6月25日までに\r\n・契約書原本のあいうえお順整理完了率が100％である\r\n\r\n6月25日までに\r\n・契約書原本のあいうえお順整理完了率が50％以上99％以下である\r\n\r\n6月25日までに\r\n・契約書原本のあいうえお順整理完了率が1％以上49％以下である\r\n\r\n・未着手である",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "妹尾祐紀",
    "no": 3,
    "item": "第２Q打ち上げの実施",
    "criteria": "期限：6月25日まで\r\n- 参加人数を事前に把握する\r\n- お店の候補を選定し、予約を完了させる\r\n- 参加時の注意事項など周知（掲示板に記載あり）\r\n\r\n【評価の状態定義】\r\n※直前の参加増減を除く",
    "weight": 0.3,
    "scoring": "6月25日までに以下すべて満たしている\r\n・参加人数が確定している（アンケート回答率100％）\r\n・会場予約が完了している（1人8,000円以内）\r\n・キャンセルポリシーを確認している\r\n・支払い方法（クレジットカード可否）を確認している\r\n・参加者へ開催概要・注意事項が周知されている\r\n・会場が以下の条件を満たしている\r\n　└ アクセスが良好である（最寄駅から徒歩圏内で移動に支障がない）\r\n　└ 料理内容・飲み放題等が適切である（予算内で参加者満足が見込める内容）\r\n・YAKUNITATSU本上先生の出席確認が完了している\r\n\r\n6月25日までに以下すべて満たしている\r\n・参加人数が確定している（アンケート回答率100％）\r\n・会場予約が完了している（1人8,000円以内）\r\n・キャンセルポリシーを確認している\r\n・支払い方法（クレジットカード可否）を確認している\r\n・参加者へ開催概要・注意事項が周知されている\r\n\r\n6月25日までに以下すべて満たしている\r\n・アンケートを実施している\r\n・アンケート回答率が70％以上99％以下である\r\n・参加人数が概ね確定している（未回答者を除き確定）\r\n・会場候補が選定されている（予算・立地の確認済み）\r\n\r\n6月25日までに以下のいずれかに該当する\r\n・日程が確定している\r\n・会場候補の選定に着手している\r\n・アンケートを実施しているが、回答率が70％未満である\r\n・会場候補が未選定である\r\n\r\n・未着手である",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "妹尾祐紀",
    "no": 4,
    "item": "複合タスク（３点をまとめて管理）",
    "criteria": "④-1　事故時の保険会社の周知・対応手順の整備\r\n期限：6月25日まで\r\n- 使用している2社の保険会社について、社内へ周知する\r\n- 事故発生時の対応手順をまとめ、共有する\r\n\r\n ④-2　GW準備\r\n期限：4月30日まで\r\nGW期間中に必要な対応・準備事項を整理・完了させる\r\n\r\n ④-3　9階への移動準備\r\n期限：6月25日（木）までに完了\r\n- 移動に必要な什器・備品・小物類の手配を完了させる\r\n※④は3つで一つのタスクグループとして管理すること\r\n\r\n----------\r\n【評価の状態定義】　\r\n※ ④-3は直前の変更を除く\r\n\r\n■ ④-1 保険対応整備（5/5）\r\n① 保険会社2社の情報が文書化されている\r\n② 事故発生時の対応手順が文書化されている\r\n③ 掲示板にマニュアルが掲載されている\r\n④ 社内へ周知が完了している\r\n⑤ 全社有車に保険証券コピーが配備されている　※証券受領待ちは除く\r\n\r\n■ ④-2 GW準備（3/3）\r\n① GW期間中の対応事項が文書化されている\r\n② 必要な手配・連絡が完了している\r\n③ 社内へ周知が完了している\r\n\r\n■ ④-3 9階移動準備（２/２）\r\n① 必要な什器・備品・小物類が一覧化されている\r\n② 手配が完了している　※移動日未定の為、完了＝6月25日時点で必要とされるものに限る\r\n----------",
    "weight": 0.2,
    "scoring": "6月25日までに\r\n・④-1（5/5）、④-2（3/3）、④-3（2/2）がすべて完了している\r\n・かつ、各項目の内容に不備・不足がなく、実運用可能な状態である\r\n\r\n6月25日までに\r\n・④-1（5/5）、④-2（3/3）、④-3（2/2）がすべて完了している\r\n\r\n6月25日までに\r\n・④-1（5/5）、④-2（3/3）、④-3（2/2）のうち、2項目がすべて完了している\r\n\r\n6月25日までに\r\n・④-1（5/5）、④-2（3/3）、④-3（2/2）のうち、1項目が完了している\r\n\r\n・④-1、④-2、④-3のいずれも完了していない",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "楠田彩",
    "no": 1,
    "item": "新卒採用における学生訴求企画の立案・実行",
    "criteria": "▼背景・目的\r\nOfferBox・設計コンペ・学校訪問などで初回接点を持った学生が、内定承諾に至るまでに離脱してしまうことを防ぐため、接点後のフォロースキームを体系的に設計・実行することを求めます。\r\n▼期待する達成内容\r\n・初回接点の種類（OfferBox / 設計コンペ / 学校訪問）ごとに、学生の心理や状況の違いを踏まえ、有効なフォロー内容を整理すること\r\n・「なぜその施策が入社意欲の向上に繋がるのか」の根拠を明示した企画書を6月25(木)までに完成させ、上長レビューを受けること\r\n・企画内容のうち少なくとも1つの施策を期中に実行し、学生の反応（返信・参加・温度感など）を記録すること",
    "weight": 0.3,
    "scoring": "評価4の状態に加え、\r\n記録した数値をもとに課題および改善点を整理し、次回施策に向けた改善内容まで落とし込めている\r\n\r\n評価3の状態に加え、\r\n実行した施策について対象数および反応（返信・参加・温度感等）を記録できている\r\n\r\n接点別に学生の心理・状況を踏まえたフォロー設計を行い、\r\n6月25日までに施策内容とその理由を明記した企画書を提出し、\r\nその中から1施策以上を実行できている\r\n\r\n接点別整理および企画書の作成はできているが、\r\n学生の心理や状況を踏まえた設計になっておらず、施策の意図および根拠が不明確\r\n\r\n接点別の整理ができておらず、企画書の作成にも取り掛かれていない",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "楠田彩",
    "no": 2,
    "item": "新卒アルバイト研修企画の立案・初期実行",
    "criteria": "▼背景・目的\r\n内定後の早期から学生をアルバイトとして受け入れ、入社前の育成と離脱防止を図ります。現在28卒学生が従事していますが、「どの部署をどの順番で経験させるか」「各フェーズでどのような研修を行うか」という年間設計が体系化されていない状況です。この初案を今期中に策定し、実行へ移すことを求めます。\r\n▼期待する達成内容\r\n・対象部署・ローテーションの順序・各期間・習得させたいスキルや経験を明記した年間研修計画の初案を6月25(木)までに提出すること\r\n・「なぜこの順番でこの部署を経験させるのか」という意図が読み取れる設計であること。場当たりの配置ではなく、離脱防止・モチベーション維持の観点が反映されていること\r\n・策定した計画を28卒学生のうち少なくとも1名に対して適用を開始すること",
    "weight": 0.3,
    "scoring": "評価4の状態に加え、年間研修計画において、\r\nフェーズごとの順序・期間・内容に一貫性があり、成長の流れ（基礎理解 → 実務経験 → 自走）および離脱防止・モチベーション維持の観点が反映されており、大きな修正なく29卒にも適用可能な状態になっている\r\n\r\n評価3の状態に加え、各フェーズごとに「目的・実施内容・到達基準」が具体的に明記されている。\r\nまた人事及び教育担当者(OJT担当者)が評価・フィードバックを行うための記入様式（FBシート）が整備されている\r\n\r\n対象部署・ローテーション順序・期間・習得スキルを明記し、「なぜこの順番でこの部署を経験させるのか」という意図を踏まえた年間研修計画の初案を6月25日までに提出し、その計画をもとに1名以上に対して適用を開始できている\r\n\r\n年間研修計画は作成できているが、\r\n部署・順序・期間・習得内容または配置意図が不明確であり、体系的な設計になっていない\r\n\r\n全く初案の作成に取り掛かれておらず、育成の全体像が整理できていない",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  },
  {
    "period": "2026年第2期",
    "name": "楠田彩",
    "no": 3,
    "item": "学校訪問アプローチ戦略の策定・実行",
    "criteria": "▼背景・目的\r\n学校訪問は件数をこなすだけでは採用成果に繋がりません。現時点でキャリアセンター（CC）が機能している学校はごく一部であり、CC機能の有無によってアプローチの方法を変える必要があります。この戦略を言語化・体系化し、実際の訪問行動に活かすことを求めます。\r\n\r\n▼期待する達成内容\r\n・「CCが機能している学校」と「機能していない学校」それぞれに対して、具体的なアプローチ戦略（誰に・何を・どのように働きかけるか）を策定し、戦略シートとして整理すること\r\n・両パターンの戦略シートを6月25日(木)までに完成させ、最低限大志万からのフィードバックを受けること\r\n・期中に訪問する学校のうち少なくとも1校で策定した戦略を実際に適用し、訪問後に結果と気づきを報告すること",
    "weight": 0.4,
    "scoring": "評価4の状態に加え、比較結果をもとに課題および改善点を整理し、戦略内容をブラッシュアップできている\r\n\r\n評価3の状態に加え、2校以上で実行し、それぞれの結果および気づきを比較・整理できている\r\n\r\n「CCが機能している学校」と「機能していない学校」それぞれに対して、「誰に・何を・どのように」のアプローチ戦略を整理した戦略シートを6月25日までに提出し、その内容に基づき1校以上で実行し、結果および気づきを報告できている\r\n\r\nCC機能の有無に応じた戦略は作成できているが、\r\n「誰に・何を・どのように」のいずれかが不明確であり、実行に活かせる内容になっていない状態\r\n\r\n未着手",
    "self": null,
    "final": null,
    "selfComment": "",
    "finalComment": ""
  }
];
window.ROLE_DEF_SEED = ROLE_DEF_SEED;


;/* ==== roles.jsx ==== */
// Roles — 役割定義管理 (Role Definitions)
// Imports seed from role-seed.js (window.ROLE_DEF_SEED) and provides full CRUD.

const DEFAULT_ROLE_PERIODS = [
  "2026年 第1期",
  "2026年 第2期",
  "2027年 第1期",
  "2027年 第2期",
];
const getRolePeriods = (state) => (state?.rolePeriods && state.rolePeriods.length) ? state.rolePeriods : DEFAULT_ROLE_PERIODS;
Object.assign(window, { DEFAULT_ROLE_PERIODS, getRolePeriods });

const buildRoleSchema = (state) => {
  const periods = getRolePeriods(state);
  return {
    eyebrow: "ROLE DEFINITION",
    title: "役割定義",
    titleNew: "新規役割項目",
    titleEdit: "役割項目編集",
    fields: [
      { key: "period", label: "期", type: "select", required: true, options: periods },
      { key: "name", label: "対象者", type: "select", options: ["竹内雅明", "大志万拓", "松川牧絵", "松井", "山本", "妹尾", "楠田", "その他"] },
      { key: "no", label: "NO", type: "number", min: 1, max: 10 },
      { key: "item", label: "評価項目", type: "text", required: true, span: 2 },
      { key: "criteria", label: "評価基準", type: "textarea", span: 2, minHeight: 220 },
      { key: "weight", label: "ウェイト (0〜1)", type: "number", min: 0, max: 1, step: 0.05 },
      { key: "scoring", label: "評価点ルール", type: "textarea", span: 2, minHeight: 220 },
      { key: "self", label: "自己評価 (1-5)", type: "number", min: 0, max: 5 },
      { key: "final", label: "最終評価 (1-5)", type: "number", min: 0, max: 5 },
      { key: "selfComment", label: "自分コメント", type: "textarea", span: 2 },
      { key: "finalComment", label: "最終コメント (上長)", type: "textarea", span: 2 },
    ],
  };
};

const RolesView = ({ state, setState }) => {
  const schema = React.useMemo(() => buildRoleSchema(state), [state.rolePeriods]);
  // Seed once
  React.useEffect(() => {
    if (!state.roles || state.roles.length === 0) {
      const seed = (window.ROLE_DEF_SEED || []).map((r, i) => ({ id: `role_${i+1}`, ...r }));
      if (seed.length > 0) setState(s => ({ ...s, roles: seed }));
    }
    // eslint-disable-next-line
  }, []);

  const list = state.roles || [];

  // Persisted filter (remembered across reloads via app state → localStorage)
  const savedFilter = state.rolesFilter || {};
  const filterPeriod = savedFilter.period || "all";
  const filterPerson = savedFilter.person || "all";
  const setFilterPeriod = (v) => setState(s => ({ ...s, rolesFilter: { ...(s.rolesFilter||{}), period: v } }));
  const setFilterPerson = (v) => setState(s => ({ ...s, rolesFilter: { ...(s.rolesFilter||{}), person: v } }));
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [expanded, setExpanded] = React.useState({});
  const crud = useCrud(state, setState, "roles");

  const periods = React.useMemo(() => {
    const ps = new Set(list.map(r => r.period).filter(Boolean));
    return ["all", ...Array.from(ps)];
  }, [list]);
  const people = React.useMemo(() => {
    const ns = new Set(list.map(r => r.name).filter(Boolean));
    return ["all", ...Array.from(ns)];
  }, [list]);

  const filtered = list.filter(r => {
    if (filterPeriod !== "all" && r.period !== filterPeriod) return false;
    if (filterPerson !== "all" && r.name !== filterPerson) return false;
    return true;
  });

  // Group by period > person
  const groups = React.useMemo(() => {
    const g = {};
    filtered.forEach(r => {
      const k1 = r.period || "未分類";
      const k2 = r.name || "未分類";
      g[k1] = g[k1] || {};
      g[k1][k2] = g[k1][k2] || [];
      g[k1][k2].push(r);
    });
    // sort items by NO
    Object.values(g).forEach(byP => Object.values(byP).forEach(arr => arr.sort((a,b) => (a.no||99) - (b.no||99))));
    return g;
  }, [filtered]);

  const openNew = () => { setEditing({ __isNew: true, weight: 0.3 }); setModalOpen(true); };
  const openEdit = (r) => { setEditing(r); setModalOpen(true); };
  const handleSave = (vals) => {
    if (editing && !editing.__isNew) crud.update(editing.id, vals);
    else { const { __isNew, id, ...clean } = vals; crud.add(clean); }
    setModalOpen(false);
  };
  const handleDelete = () => { if (editing && editing.id) crud.remove(editing.id); setModalOpen(false); };

  // Weighted score helper
  const personScore = (rs) => {
    let weighted = 0, totalW = 0;
    rs.forEach(r => {
      if (r.final != null && r.weight) {
        weighted += r.final * r.weight;
        totalW += r.weight;
      }
    });
    return totalW > 0 ? (weighted / totalW).toFixed(2) : "—";
  };

  return (
    <div className="view-fade">
      <div className="ws-toolbar">
        <div className="ws-tabs">
          <div className="ws-tab active"><Icon name="tasks" size={13}/><span>役割定義</span></div>
        </div>
        <div className="ws-spacer"/>
        <div className="ws-filters">
          <select className="ws-filter" value={filterPeriod} onChange={e => setFilterPeriod(e.target.value)}>
            {periods.map(p => <option key={p} value={p}>{p === "all" ? "全期" : p}</option>)}
          </select>
          <select className="ws-filter" value={filterPerson} onChange={e => setFilterPerson(e.target.value)}>
            {people.map(p => <option key={p} value={p}>{p === "all" ? "全員" : p}</option>)}
          </select>
        </div>
        <button className="btn gold sm" onClick={openNew}><Icon name="plus" size={12}/><span>新規項目</span></button>
      </div>

      {Object.keys(groups).length === 0 && (
        <div className="empty-state"><h4>役割定義はまだありません</h4><div>「新規項目」から追加してください。</div></div>
      )}

      {Object.entries(groups).map(([period, byPerson]) => (
        <div key={period} className="panel mb-3">
          <div className="panel-head">
            <div>
              <span className="eyebrow">PERIOD</span>
              <h3>{period}</h3>
            </div>
            <div className="panel-head-meta">
              <span className="chip">{Object.keys(byPerson).length}人 / {Object.values(byPerson).reduce((s, a) => s + a.length, 0)}項目</span>
            </div>
          </div>
          <div className="panel-body">
            {Object.entries(byPerson).map(([person, items]) => (
              <div key={person} style={{marginBottom: 22}}>
                <div style={{display:"flex", alignItems:"center", gap:12, padding:"10px 14px", background:"rgba(91,123,196,0.08)", borderRadius:6, marginBottom:8}}>
                  <div style={{width:32, height:32, borderRadius:"50%", background:"linear-gradient(135deg, var(--crimson), var(--crimson-lo))", display:"grid", placeItems:"center", color:"var(--ivory)", fontFamily:"var(--f-display)", fontSize:13}}>{person.slice(0,1)}</div>
                  <div style={{flex:1}}>
                    <div style={{fontFamily:"var(--f-display)", fontSize:14, letterSpacing:"0.06em", color:"var(--ivory)"}}>{person}</div>
                    <div style={{fontSize:10, color:"var(--ash)", marginTop:2, fontFamily:"var(--f-mono)", letterSpacing:"0.06em"}}>{items.length}項目 · 加重平均: {personScore(items)}</div>
                  </div>
                </div>
                {items.map(r => {
                  const isOpen = expanded[r.id];
                  const evalColor = r.final >= 5 ? "var(--gold)" : r.final >= 4 ? "var(--gold)" : r.final >= 3 ? "var(--crystal)" : r.final >= 1 ? "var(--crimson-hi)" : "var(--smoke)";
                  return (
                    <div key={r.id} style={{marginBottom:6, border:"1px solid var(--hairline)", borderRadius:6, background:"rgba(11,19,48,0.4)", overflow:"hidden"}}>
                      <div onClick={() => setExpanded(e => ({ ...e, [r.id]: !e[r.id] }))} style={{display:"grid", gridTemplateColumns:"36px 1fr 80px 70px 70px 60px", gap:10, padding:"10px 14px", alignItems:"center", cursor:"pointer"}}>
                        <span className="chip" style={{fontSize:10, justifyContent:"center", display:"flex"}}>#{r.no}</span>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:13, color:"var(--ivory)", fontWeight:500, lineHeight:1.4}}>{r.item}</div>
                          {r.criteria && <div style={{fontSize:11, color:"var(--ash)", marginTop:3, lineHeight:1.5, overflow:"hidden", textOverflow:"ellipsis", whiteSpace: isOpen ? "normal" : "nowrap"}}>{r.criteria}</div>}
                        </div>
                        <div style={{textAlign:"center"}}>
                          <div style={{fontSize:9, color:"var(--smoke)", letterSpacing:"0.08em", fontFamily:"var(--f-mono)"}}>WEIGHT</div>
                          <div style={{fontSize:13, color:"var(--gold)", fontFamily:"var(--f-mono)", fontWeight:600}}>{(r.weight * 100).toFixed(0)}%</div>
                        </div>
                        <div style={{textAlign:"center"}}>
                          <div style={{fontSize:9, color:"var(--smoke)", letterSpacing:"0.08em", fontFamily:"var(--f-mono)"}}>SELF</div>
                          <div style={{fontSize:15, color:"var(--ivory)", fontFamily:"var(--f-display)"}}>{r.self ?? "—"}</div>
                        </div>
                        <div style={{textAlign:"center"}}>
                          <div style={{fontSize:9, color:"var(--smoke)", letterSpacing:"0.08em", fontFamily:"var(--f-mono)"}}>FINAL</div>
                          <div style={{fontSize:18, color: evalColor, fontFamily:"var(--f-display)", fontWeight:600}}>{r.final ?? "—"}</div>
                        </div>
                        <div className="row-actions" style={{opacity:1, justifyContent:"flex-end"}}>
                          <button className="row-action-btn" onClick={(e) => { e.stopPropagation(); openEdit(r); }}><Icon name="edit" size={12}/></button>
                        </div>
                      </div>
                      {isOpen && (
                        <div style={{padding:"14px 18px", borderTop:"1px solid var(--hairline)", background:"rgba(0,0,0,0.18)"}}>
                          {r.scoring && (
                            <div style={{marginBottom:14}}>
                              <div className="label-mono">評価点ルール</div>
                              <div style={{whiteSpace:"pre-wrap", fontSize:12, color:"var(--ivory)", marginTop:6, lineHeight:1.7}}>{r.scoring}</div>
                            </div>
                          )}
                          {r.selfComment && (
                            <div style={{marginBottom:14, padding:12, background:"rgba(91,123,196,0.06)", borderLeft:"3px solid var(--crystal)", borderRadius:"0 4px 4px 0"}}>
                              <div className="label-mono" style={{color:"var(--crystal)"}}>自己コメント</div>
                              <div style={{whiteSpace:"pre-wrap", fontSize:12, color:"var(--ivory)", marginTop:6, lineHeight:1.7}}>{r.selfComment}</div>
                            </div>
                          )}
                          {r.finalComment && (
                            <div style={{padding:12, background:"rgba(245,197,24,0.06)", borderLeft:"3px solid var(--gold)", borderRadius:"0 4px 4px 0"}}>
                              <div className="label-mono" style={{color:"var(--gold)"}}>上長コメント</div>
                              <div style={{whiteSpace:"pre-wrap", fontSize:12, color:"var(--ivory)", marginTop:6, lineHeight:1.7}}>{r.finalComment}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ))}

      <EntityModal
        open={modalOpen}
        schema={schema}
        initial={editing}
        onSave={handleSave}
        onDelete={editing && editing.id && !editing.__isNew ? handleDelete : null}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

Object.assign(window, { RolesView });


;/* ==== templates.jsx ==== */
// Templates — テンプレート管理 (スカウト文・面接質問・1on1・メール下書きなど)
// CRUD + 「Claudeでパーソナライズ」機能

const TEMPLATE_CATEGORIES = ["スカウト文", "面接質問", "1on1議題", "メール下書き", "通知文", "その他"];

const TEMPLATE_SCHEMA = {
  eyebrow: "TEMPLATE",
  title: "テンプレート",
  titleNew: "新規テンプレート",
  titleEdit: "テンプレート編集",
  fields: [
    { key: "name", label: "名前", type: "text", required: true, span: 2 },
    { key: "category", label: "カテゴリ", type: "select", options: TEMPLATE_CATEGORIES },
    { key: "tags", label: "タグ (カンマ区切り)", type: "text" },
    { key: "body", label: "本文", type: "textarea", span: 2, required: true, placeholder: "[名前]、[会社]、[役職] などのプレースホルダで変数化できます" },
  ],
};

const SEED_TEMPLATES = [
  {
    id: "tpl_scout_mkt",
    name: "マーケ責任者候補 スカウト文 (汎用)",
    category: "スカウト文",
    tags: "ビズリーチ,マーケ,管理職",
    body: `[名前]さま\n\n突然のご連絡失礼いたします。セイムグループ 人事総務部の竹内と申します。\n[現職]でのご経験を拝見し、当社が注力する注文住宅ブランド「ondo」のマーケティング強化において、ぜひお力をお借りしたく、ご連絡いたしました。\n\nまずは30分程度のカジュアル面談で、事業のご紹介や想定ポジションの意見交換ができればと考えております。\nご都合いかがでしょうか。\n\nセイムグループ 人事総務部\n竹内雅明`,
  },
  {
    id: "tpl_iv_managerial",
    name: "面接質問テンプレート (マネジメント職)",
    category: "面接質問",
    tags: "面接,マネジメント",
    body: `■ アイスブレイク (5分)\n- これまでのキャリアで一番印象深いプロジェクトは?\n\n■ 実績・スキル (15分)\n- 直近1年で最も成果を出した取り組みは?\n- そのKPI設計と振り返りプロセスは?\n- 失敗から学んだ大きな教訓は?\n\n■ マネジメント (15分)\n- 何名/どんなメンバーを率いていますか?\n- 評価制度の運用で工夫していることは?\n- メンバーが辞めそうな時、どう察知していますか?\n\n■ カルチャーフィット (10分)\n- 当社の理念で響いた箇所は?\n- 不満を感じる組織文化は?\n\n■ 質問タイム (5分)\n- 弊社へのご質問をどうぞ`,
  },
  {
    id: "tpl_1on1_general",
    name: "1on1 議題テンプレート (汎用)",
    category: "1on1議題",
    tags: "1on1,定例",
    body: `■ チェックイン (5分)\n- 最近の調子は? (健康・気分・集中度)\n\n■ 業務進捗 (10分)\n- 今週の進捗・成果\n- 詰まっていること・障害\n\n■ 関係性・チーム (5分)\n- チームメンバーとの関係で気になること\n- 上司として改善してほしいこと\n\n■ 成長・キャリア (5分)\n- 半年後/1年後のなりたい姿\n- 今学んでいること\n\n■ アクションアイテム (5分)\n- 次回までに私(上司)がやること\n- 次回までに本人がやること`,
  },
  {
    id: "tpl_email_decline",
    name: "選考お見送り メール下書き",
    category: "メール下書き",
    tags: "採用,お見送り",
    body: `[名前]さま\n\n先日はお忙しい中、面接にお越しいただきありがとうございました。\n\n社内で慎重に検討させていただいた結果、誠に残念ながら、今回はご縁を見送らせていただくこととなりました。\n\n[名前]さまの今後のご活躍を、心よりお祈り申し上げます。\n\nセイムグループ 人事総務部\n竹内雅明`,
  },
];

const TemplatesView = ({ state, setState }) => {
  React.useEffect(() => {
    if (!state.templates || state.templates.length === 0) {
      setState(s => ({ ...s, templates: SEED_TEMPLATES }));
    }
    // eslint-disable-next-line
  }, []);

  const list = state.templates || [];
  const [filterCat, setFilterCat] = React.useState("all");
  const [search, setSearch] = React.useState("");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [aiOpen, setAiOpen] = React.useState(null); // { template, vars, result }
  const [aiLoading, setAiLoading] = React.useState(false);
  const crud = useCrud(state, setState, "templates");

  const filtered = list.filter(t => {
    if (filterCat !== "all" && t.category !== filterCat) return false;
    if (search && !((t.name || "") + (t.body || "") + (t.tags || "")).toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const openNew = () => { setEditing({ __isNew: true, category: "メール下書き" }); setModalOpen(true); };
  const openEdit = (t) => { setEditing(t); setModalOpen(true); };
  const handleSave = (vals) => {
    if (editing && !editing.__isNew) crud.update(editing.id, vals);
    else { const { __isNew, id, ...clean } = vals; crud.add(clean); }
    setModalOpen(false);
  };
  const handleDelete = () => { if (editing && editing.id) crud.remove(editing.id); setModalOpen(false); };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); alert("コピーしました"); } catch (e) { alert("コピー失敗"); }
  };

  // Detect placeholders [xxx]
  const extractPlaceholders = (body) => {
    const matches = (body || "").match(/\[([^\[\]\n]+)\]/g);
    if (!matches) return [];
    return [...new Set(matches.map(m => m.slice(1, -1)))];
  };

  const aiPersonalize = async () => {
    const t = aiOpen.template;
    setAiLoading(true);
    const filled = Object.entries(aiOpen.vars).reduce((b, [k, v]) => b.replaceAll(`[${k}]`, v || `[${k}]`), t.body);
    const prompt = `あなたは経営層の文章作成アシスタントです。以下のテンプレート文を、ユーザーの依頼内容を踏まえて自然な日本語にパーソナライズしてください。元の構成と敬語は維持し、相手の文脈に合うよう本文を磨いてください。

テンプレ:
"""
${filled}
"""

ユーザーの追加指示:
"""
${aiOpen.instruction || "より自然で温かみのある文章に調整してください"}
"""

パーソナライズ後の本文のみを返してください(前置きなし):`;
    try {
      const resp = await window.claude.complete(prompt);
      setAiOpen(s => ({ ...s, result: resp.trim() }));
    } catch (e) {
      setAiOpen(s => ({ ...s, result: "(エラー: 再試行してください)" }));
    }
    setAiLoading(false);
  };

  return (
    <div className="view-fade">
      <div className="ws-toolbar">
        <div className="ws-tabs">
          <div className="ws-tab active"><Icon name="copy" size={13}/><span>テンプレート</span></div>
        </div>
        <div className="ws-spacer"/>
        <div className="ws-filters">
          <select className="ws-filter" value={filterCat} onChange={e => setFilterCat(e.target.value)}>
            <option value="all">全カテゴリ</option>
            {TEMPLATE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="ws-search">
            <Icon name="search" size={12}/>
            <input placeholder="検索…" value={search} onChange={e => setSearch(e.target.value)}/>
          </div>
        </div>
        <button className="btn gold sm" onClick={openNew}><Icon name="plus" size={12}/><span>新規テンプレ</span></button>
      </div>

      <div className="memo-grid">
        {filtered.map(t => (
          <div key={t.id} className="memo-card" style={{height:240}} onClick={() => openEdit(t)}>
            <div className="row-actions">
              <button className="row-action-btn" onClick={(e) => { e.stopPropagation(); copy(t.body); }} title="コピー"><Icon name="copy" size={12}/></button>
              <button className="row-action-btn" onClick={(e) => { e.stopPropagation(); setAiOpen({ template: t, vars: Object.fromEntries(extractPlaceholders(t.body).map(p => [p, ""])), instruction: "" }); }} title="AIで仕上げる"><Icon name="sparkle" size={12}/></button>
              <button className="row-action-btn danger" onClick={(e) => { e.stopPropagation(); if (confirm("削除しますか?")) crud.remove(t.id); }}><Icon name="trash" size={12}/></button>
            </div>
            <div className="memo-card-head">
              <span className="chip gold">{t.category}</span>
            </div>
            <div className="memo-title">{t.name}</div>
            <div className="memo-body" style={{whiteSpace:"pre-wrap"}}>{t.body}</div>
            <div className="memo-foot">
              {t.tags && t.tags.split(",").slice(0, 3).map(tag => <span key={tag} className="chip" style={{fontSize:9}}>{tag.trim()}</span>)}
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="empty-state"><h4>テンプレートはありません</h4><div>「新規テンプレ」から追加するか、検索条件を変えてください。</div></div>
      )}

      <EntityModal
        open={modalOpen}
        schema={TEMPLATE_SCHEMA}
        initial={editing}
        onSave={handleSave}
        onDelete={editing && editing.id && !editing.__isNew ? handleDelete : null}
        onClose={() => setModalOpen(false)}
      />

      {aiOpen && (
        <div className="modal-overlay" onClick={(e) => { if (e.target.classList.contains("modal-overlay")) setAiOpen(null); }}>
          <div className="modal-stage">
            <div className="modal-head">
              <div style={{flex:1}}>
                <span className="eyebrow">AI PERSONALIZE</span>
                <h2 className="modal-title">{aiOpen.template.name}</h2>
              </div>
              <button className="icon-btn" onClick={() => setAiOpen(null)} title="閉じる" aria-label="閉じる"><Icon name="x" size={18}/></button>
            </div>
            <div className="modal-body">
              {Object.keys(aiOpen.vars).length > 0 && (
                <>
                  <div className="label-mono" style={{marginBottom:8}}>プレースホルダ</div>
                  <div className="form-grid" style={{marginBottom:18}}>
                    {Object.keys(aiOpen.vars).map(p => (
                      <div key={p} className="form-field">
                        <label className="form-label">[{p}]</label>
                        <input className="form-input" value={aiOpen.vars[p]} onChange={e => setAiOpen(s => ({ ...s, vars: { ...s.vars, [p]: e.target.value } }))}/>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <div className="form-field">
                <label className="form-label">AIへの追加指示 (任意)</label>
                <textarea className="form-input form-textarea" rows={2} placeholder="例: もう少しカジュアルに / 文末をやわらかく / 1段落短く" value={aiOpen.instruction} onChange={e => setAiOpen(s => ({ ...s, instruction: e.target.value }))}/>
              </div>
              {aiOpen.result && (
                <div style={{marginTop:14, padding:16, background:"rgba(245,197,24,0.06)", border:"1px solid rgba(245,197,24,0.32)", borderRadius:6}}>
                  <div className="label-mono" style={{color:"var(--gold)", marginBottom:8}}>★ AI仕上げ結果</div>
                  <div style={{fontSize:13, color:"var(--ivory)", whiteSpace:"pre-wrap", lineHeight:1.7}}>{aiOpen.result}</div>
                </div>
              )}
            </div>
            <div className="modal-foot">
              {aiOpen.result && <button className="btn ghost" onClick={() => copy(aiOpen.result)}><Icon name="copy" size={12}/><span>結果をコピー</span></button>}
              <div style={{flex:1}}/>
              <button className="btn ghost" onClick={() => setAiOpen(null)}>閉じる</button>
              <button className="btn gold" onClick={aiPersonalize} disabled={aiLoading}>
                <Icon name="sparkle" size={12}/>
                <span>{aiLoading ? "生成中…" : "AIで仕上げる"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

Object.assign(window, { TemplatesView });


;/* ==== youtube.jsx ==== */
// YouTube Learning — 動作する設計に作り直し
//
// 旧設計の問題: CORSプロキシ経由でYouTube内部HTMLを取得 → ほぼ常に失敗 → AIがタイトルから推測 → 役に立たない
//
// 新設計: ユーザー主導
//   1. URL貼付 → oEmbedでメタデータのみ取得 (常に成功)
//   2. プレイヤーを埋め込み、横に「ノート」パネルを表示
//   3. 入力モード3種:
//      📜 文字起こし — YouTubeの「文字起こしを表示」からコピペ
//      📝 ノート — 動画を見ながら手書きメモ
//      🎙️ 音声入力 — Web Speech APIで日本語ライブ書き起こし
//   4. AIが入力ソースを把握した上で要約 (推測ではなく実データから)

// Robust clipboard helper — falls back to a hidden textarea + execCommand
// because some sandbox / non-secure iframe contexts block navigator.clipboard.
async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

const extractVideoId = (u) => {
  if (!u) return null;
  const patterns = [
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) { const m = u.match(p); if (m) return m[1]; }
  return null;
};

// Strip leading timestamps like "0:23\n本文" or "1:23:45 本文" — YouTube's transcript
// copy includes these on separate lines.
const cleanTranscriptPaste = (raw) => {
  if (!raw) return "";
  const lines = raw.split(/\r?\n/);
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    // pure timestamp line "0:12" or "1:23:45"
    if (/^\d{1,2}(:\d{2}){1,2}$/.test(line)) continue;
    // leading inline timestamp "0:23 本文"
    out.push(line.replace(/^\d{1,2}(:\d{2}){1,2}\s+/, ""));
  }
  return out.join(" ").replace(/\s+/g, " ").trim();
};

// Fetch only what is reliably available: oEmbed (title + author + thumbnail).
// Tries multiple paths because sandboxed iframes often block direct cross-origin fetches:
//   1. YouTube oEmbed direct
//   2. noembed.com (CORS-friendly oEmbed proxy)
//   3. corsproxy.io → YouTube oEmbed
//   4. allorigins → YouTube oEmbed
async function fetchOEmbed(videoId) {
  const watchUrl = `https://www.youtube.com/watch?v=${videoId}`;
  const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(watchUrl)}&format=json`;
  const sources = [
    oembedUrl,
    `https://noembed.com/embed?url=${encodeURIComponent(watchUrl)}`,
    `https://corsproxy.io/?${encodeURIComponent(oembedUrl)}`,
    `https://api.allorigins.win/raw?url=${encodeURIComponent(oembedUrl)}`,
  ];
  const tryFetch = async (url) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    try {
      const r = await fetch(url, { signal: controller.signal });
      clearTimeout(timer);
      if (!r.ok) return null;
      const text = await r.text();
      if (!text) return null;
      try { return JSON.parse(text); } catch (e) { return null; }
    } catch (e) { clearTimeout(timer); return null; }
  };
  for (const src of sources) {
    const meta = await tryFetch(src);
    if (meta && (meta.title || meta.author_name)) {
      return { title: meta.title || "", author: meta.author_name || "", ok: true };
    }
  }
  return { title: "", author: "", ok: false };
}

// Robust JSON extraction from a Claude response that may include code fences,
// preambles, or trailing commas. Also tries to repair truncated output by closing
// dangling structures (common when hitting the 1024-token output cap).
function extractJSON(resp) {
  if (!resp) return null;
  let s = String(resp).trim();
  // Strip code fences
  s = s.replace(/^```(?:json|javascript|js)?\s*\n?/i, "");
  s = s.replace(/\n?```\s*$/i, "");
  s = s.trim();

  const start = s.indexOf("{");
  if (start === -1) return null;
  s = s.substring(start);

  // Try strict parse on the full string first
  const tryParse = (raw) => {
    try { return JSON.parse(raw); } catch (e) {}
    try { return JSON.parse(raw.replace(/,(\s*[}\]])/g, "$1")); } catch (e) {}
    try { return JSON.parse(raw.replace(/[\u201c\u201d]/g, '"').replace(/[\u2018\u2019]/g, "'")); } catch (e) {}
    return null;
  };

  // If there is a closing }, try that first.
  const end = s.lastIndexOf("}");
  if (end !== -1) {
    const candidate = s.substring(0, end + 1);
    const parsed = tryParse(candidate);
    if (parsed) return parsed;
  }

  // Truncation repair: walk the string, track structural state, and append
  // synthetic closers for any unclosed strings / arrays / objects so the
  // partial JSON becomes parseable.
  let inString = false;
  let escape = false;
  const stack = []; // '{', '['
  let lastSafeIdx = -1; // index AFTER the last fully complete key-value pair at depth 1
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (escape) { escape = false; continue; }
    if (inString) {
      if (ch === "\\") escape = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') { inString = true; continue; }
    if (ch === "{" || ch === "[") stack.push(ch);
    else if (ch === "}" || ch === "]") stack.pop();
    else if (ch === "," && stack.length === 1) lastSafeIdx = i; // safe cut point at top level
  }

  // Strategy A: cut at lastSafeIdx (drop the partial last field) and close
  if (lastSafeIdx > 0) {
    const repaired = s.substring(0, lastSafeIdx);
    // Rebuild closer stack from repaired
    let st = [];
    let inS = false; let esc = false;
    for (let i = 0; i < repaired.length; i++) {
      const ch = repaired[i];
      if (esc) { esc = false; continue; }
      if (inS) { if (ch === "\\") esc = true; else if (ch === '"') inS = false; continue; }
      if (ch === '"') { inS = true; continue; }
      if (ch === "{" || ch === "[") st.push(ch);
      else if (ch === "}") st.pop();
      else if (ch === "]") st.pop();
    }
    let closed = repaired;
    while (st.length) { closed += (st.pop() === "{" ? "}" : "]"); }
    const parsed = tryParse(closed);
    if (parsed) return parsed;
  }

  // Strategy B: aggressively close from current state at end-of-string
  let closed = s;
  if (inString) closed += '"';
  // Drop trailing comma / colon / partial token
  closed = closed.replace(/[,:]\s*$/, "");
  // Close remaining containers
  while (stack.length) { closed += (stack.pop() === "{" ? "}" : "]"); }
  const parsed = tryParse(closed);
  if (parsed) return parsed;

  return null;
}

// NOTE: A fully-automatic caption fetch (server-side InnerTube + retries) was
// built, live-tested, and REMOVED on 2026-06-10 at the user's request: YouTube
// applies a dynamic per-IP bot wall to Cloudflare egress (and to every CORS
// proxy we probed — corsproxy 413 >1MB, allorigins serves the bot page), so it
// could not be made reliable. Don't reintroduce without residential egress.

// Shared learning-summary prompt + runner.
function buildLearningPrompt(draft, activeLabel, content) {
  return `あなたは経営層の学習サポートAI。ユーザー: セイムグループ人事総務部部長まさあきさん(採用・人事制度・離職対策・生成AI推進・総務 管掌)。

動画: ${draft.title}
チャンネル: ${draft.author}
入力ソース: ${activeLabel}
内容:
"""
${content.slice(0, 5000)}
"""

以下のJSONだけを返してください(コードフェンス・前置き・後置き厳禁、先頭{ 末尾})。**文字数厳守**。

{
"topic":"中心テーマ(35字以内,体言止め)",
"points":["要点1(50字)","要点2(50字)","要点3(50字)"],
"insights":["業務への気付き1(50字)","気付き2(50字)"],
"actions":["明日からのアクション1(40字)","アクション2(40字)"],
"tags":["タグ1","タグ2","タグ3"],
"category":"学習/採用/マネジメント/人事制度/生成AI/経営"
}`;
}

async function runLearningSummary(draft, activeLabel, content) {
  const prompt = buildLearningPrompt(draft, activeLabel, content);
  const resp = await window.claude.complete(prompt);
  let parsed = extractJSON(resp);
  if (!parsed) {
    const retry = await window.claude.complete(prompt + "\n\n重要: 必ず生のJSONオブジェクトだけを返してください。コードフェンス(```)・前置き・後置きの説明文は厳禁です。先頭は { 末尾は } で終わらせてください。");
    parsed = extractJSON(retry);
    if (!parsed) throw new Error("JSON解析エラー。応答冒頭: " + String(retry).slice(0, 80));
  }
  return parsed;
}

// Build the saved learning record (single place → archive cards/detail stay consistent).
function buildLearningRecord(draft, mode, sourceLabel, sourceText, parsed) {
  return {
    id: "yt_" + Date.now(),
    url: `https://www.youtube.com/watch?v=${draft.videoId}`,
    videoId: draft.videoId,
    title: draft.title,
    author: draft.author,
    thumbnail: `https://i.ytimg.com/vi/${draft.videoId}/hqdefault.jpg`,
    sourceMode: mode,
    sourceLabel,
    sourceText,
    tag: parsed.category || "学習",
    summary: parsed,
    savedAt: new Date().toISOString(),
  };
}

// Web Speech API — ja-JP, continuous, interim results.
const useSpeechRecognition = (onFinal, onInterim) => {
  const recRef = React.useRef(null);
  const [listening, setListening] = React.useState(false);
  const [supported, setSupported] = React.useState(true);

  React.useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const rec = new SR();
    rec.lang = "ja-JP";
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let finalChunk = "";
      let interimChunk = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalChunk += t;
        else interimChunk += t;
      }
      if (finalChunk) onFinal && onFinal(finalChunk);
      if (interimChunk) onInterim && onInterim(interimChunk);
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => { console.log("speech err", e); setListening(false); };
    recRef.current = rec;
    return () => { try { rec.abort(); } catch {} };
  }, []);

  const start = () => {
    if (!recRef.current) return;
    try { recRef.current.start(); setListening(true); } catch (e) { console.log("start err", e); }
  };
  const stop = () => {
    if (!recRef.current) return;
    try { recRef.current.stop(); } catch {}
    setListening(false);
  };
  return { listening, supported, start, stop };
};

// "プレイヤー" としての見た目を持つサムネイルカード。
// 動画オーナーが埋め込みを禁止していると iframe では Error 150/153 が出るため、
// 最初から iframe を使わず YouTube に飛ばす UI に統一(100% 動作 + 制限の影響を受けない)。
const YouTubePlayer = ({ videoId, title }) => {
  const url = `https://www.youtube.com/watch?v=${videoId}`;
  // Try max-res thumbnail first, fall back to hq
  const [thumb, setThumb] = React.useState(`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`);
  const [copied, setCopied] = React.useState(false);
  return (
    <div style={{position:"relative"}}>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title="YouTubeで開く"
        style={{
          display:"block",
          aspectRatio:"16/9",
          background:"#000",
          borderRadius:6,
          overflow:"hidden",
          border:"1px solid var(--hairline)",
          textDecoration:"none",
          position:"relative",
          cursor:"pointer",
        }}
      >
        <img
          src={thumb}
          alt=""
          onError={() => setThumb(`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`)}
          style={{width:"100%", height:"100%", objectFit:"cover", display:"block"}}
        />
        {/* Dim overlay */}
        <div style={{position:"absolute", inset:0, background:"linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.45) 100%)"}}/>
        {/* Center play button */}
        <div style={{position:"absolute", inset:0, display:"grid", placeItems:"center"}}>
          <div style={{
            width:74, height:74, borderRadius:"50%",
            background:"rgba(196,30,58,0.95)",
            display:"grid", placeItems:"center",
            color:"#fff",
            boxShadow:"0 0 60px rgba(196,30,58,0.5), 0 12px 32px rgba(0,0,0,0.5)",
            transition:"transform 0.18s",
          }}>
            <Icon name="play" size={34}/>
          </div>
        </div>
        {/* Top-right "open in YouTube" badge */}
        <div style={{
          position:"absolute", top:10, right:10,
          padding:"5px 10px",
          background:"rgba(0,0,0,0.78)",
          border:"1px solid rgba(255,255,255,0.18)",
          borderRadius:4,
          color:"var(--ivory)",
          fontFamily:"var(--f-mono)", fontSize:10, letterSpacing:"0.12em",
          display:"flex", alignItems:"center", gap:6,
          backdropFilter:"blur(4px)",
        }}>
          <Icon name="arrow" size={11}/>
          <span>YOUTUBEで開く</span>
        </div>
        {/* Bottom title overlay */}
        <div style={{
          position:"absolute", bottom:0, left:0, right:0,
          padding:"14px 16px 12px",
          background:"linear-gradient(180deg, transparent, rgba(0,0,0,0.85))",
          color:"var(--ivory)",
          fontSize:12, fontFamily:"var(--f-display)", letterSpacing:"0.02em",
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
        }}>
          {title}
        </div>
      </a>
      <div style={{marginTop:8, display:"flex", alignItems:"center", gap:8}}>
        <div style={{
          flex:1, minWidth:0,
          display:"flex", alignItems:"center", gap:8,
          padding:"7px 10px",
          background:"rgba(0,0,0,0.32)",
          border:"1px solid var(--hairline)",
          borderRadius:4,
        }}>
          <Icon name="play" size={11} color="var(--ash)"/>
          <code style={{
            flex:1, minWidth:0,
            fontFamily:"var(--f-mono)", fontSize:11,
            color:"var(--ivory)",
            whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
            letterSpacing:"0.02em",
          }}>{url}</code>
        </div>
        <button
          className="btn ghost sm"
          onClick={async () => {
            const ok = await copyText(url);
            if (ok) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1400);
            } else {
              alert("コピーに失敗しました。URLを長押しして手動でコピーしてください。");
            }
          }}
          title="URLをコピー"
          style={{minWidth: 88, justifyContent:"center"}}
        >
          {copied ? (
            <React.Fragment><Icon name="check" size={11}/><span>コピー済</span></React.Fragment>
          ) : (
            <React.Fragment><Icon name="copy" size={11}/><span>URLコピー</span></React.Fragment>
          )}
        </button>
      </div>
      <div style={{marginTop:6, fontSize:10, color:"var(--ash)", fontFamily:"var(--f-mono)", letterSpacing:"0.08em"}}>
        ↑ サムネイルクリックで YouTube を新しいタブで開きます
      </div>
    </div>
  );
};

// ============================================================
// Learning Session — overlay shown after user pastes URL
// ============================================================
const LearningSession = ({ draft: initialDraft, onCancel, onSave }) => {
  const [draft, setDraft] = React.useState(initialDraft);
  const [mode, setMode] = React.useState("transcript"); // transcript | notes | voice
  const [transcriptText, setTranscriptText] = React.useState("");
  const [notesText, setNotesText] = React.useState("");
  const [voiceText, setVoiceText] = React.useState("");
  const [voiceInterim, setVoiceInterim] = React.useState("");
  const [phase, setPhase] = React.useState("idle"); // idle | summarizing
  const [error, setError] = React.useState(null);
  const [editingTitle, setEditingTitle] = React.useState(false);

  const speech = useSpeechRecognition(
    (final) => setVoiceText(t => (t + " " + final).trim()),
    (interim) => setVoiceInterim(interim),
  );

  // Pick currently active content
  const activeContent = mode === "transcript" ? transcriptText
    : mode === "notes" ? notesText
    : voiceText;
  const activeLabel = mode === "transcript" ? "YouTubeの文字起こし"
    : mode === "notes" ? "あなたのメモ"
    : "音声入力";
  const charCount = activeContent.length;

  const summarize = async () => {
    if (charCount < 30) { setError("30文字以上の内容が必要です"); return; }
    setError(null);
    setPhase("summarizing");
    try {
      const parsed = await runLearningSummary(draft, activeLabel, activeContent);
      onSave(buildLearningRecord(draft, mode, activeLabel, activeContent, parsed));
    } catch (e) {
      setError("AI要約に失敗: " + (e.message || e));
      setPhase("idle");
    }
  };


  return (
    <div className="rec-overlay" onClick={(e) => { if (e.target.classList.contains("rec-overlay")) onCancel(); }}>
      <div className="rec-stage" style={{maxWidth: 1320, maxHeight: "94vh"}}>
        <div className="rec-stage-head">
          <div style={{flex:1, minWidth:0}}>
            <span className="eyebrow">
              YOUTUBE LEARNING · {draft.author || (draft.titleFetchFailed ? "メタデータ取得失敗" : "—")}
            </span>
            {editingTitle ? (
              <input
                autoFocus
                value={draft.title}
                onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
                onBlur={() => setEditingTitle(false)}
                onKeyDown={e => { if ((e.key === "Enter" && !e.nativeEvent.isComposing) || e.key === "Escape") setEditingTitle(false); }}
                style={{
                  width:"100%",
                  background:"rgba(0,0,0,0.32)",
                  border:"1px solid var(--gold)",
                  borderRadius:4,
                  padding:"6px 10px",
                  color:"var(--ivory)",
                  fontFamily:"var(--f-display)",
                  fontSize:20,
                  letterSpacing:"0.02em",
                  outline:"none",
                }}
                placeholder="動画タイトルを入力"
              />
            ) : (
              <h2
                onClick={() => setEditingTitle(true)}
                title="クリックでタイトルを編集"
                style={{
                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                  cursor:"text",
                  padding:"2px 4px",
                  borderRadius:3,
                  margin:"0 -4px",
                  color: (draft.title && !draft.titleFetchFailed) ? "var(--ivory)" : "var(--ash)",
                  fontStyle: draft.titleFetchFailed && !draft.title ? "italic" : "normal",
                }}
              >
                {draft.title || "(タイトル未取得 — クリックして入力)"}
              </h2>
            )}
          </div>
          <button className="icon-btn" onClick={onCancel} title="閉じる" aria-label="閉じる"><Icon name="x" size={18}/></button>
        </div>

        <div className="rec-stage-body" style={{padding: 0, display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)", gap: 0, overflow: "hidden"}}>
          {/* LEFT — Player */}
          <div style={{padding:"24px 22px 24px 32px", borderRight:"1px solid var(--hairline)", display:"flex", flexDirection:"column", gap:14, minHeight:0, overflow:"auto"}}>
            <YouTubePlayer videoId={draft.videoId} title={draft.title}/>

            {/* How to get transcript */}
            <div style={{padding:"12px 14px", background:"rgba(245,197,24,0.06)", border:"1px solid rgba(245,197,24,0.22)", borderRadius:5}}>
              <div className="label-mono" style={{marginBottom:8}}>💡 YouTubeの文字起こしを取り込む手順</div>
              <ol style={{margin:0, paddingLeft:20, fontSize:12, color:"var(--ivory)", lineHeight:1.85}}>
                <li>動画の説明欄の下の「<b>…(その他)</b>」をクリック</li>
                <li>「<b>文字起こしを表示</b>」を選択</li>
                <li>右側に表示されたパネルでテキストをドラッグ選択 → コピー(⌘C / Ctrl+C)</li>
                <li>右の📜タブの欄に貼り付け(タイムスタンプは自動で除去します)</li>
              </ol>
              <div style={{marginTop:10, display:"flex", gap:8}}>
                <a
                  href={`https://www.youtube.com/watch?v=${draft.videoId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn ghost sm"
                  style={{textDecoration:"none"}}
                >
                  <Icon name="arrow" size={11}/>
                  <span>YouTubeで動画を開く</span>
                </a>
              </div>
            </div>
          </div>

          {/* RIGHT — Content input */}
          <div style={{padding:"24px 32px 24px 22px", display:"flex", flexDirection:"column", minHeight:0, overflow:"hidden"}}>
            {/* Mode tabs */}
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:4, padding:3, background:"rgba(0,0,0,0.32)", border:"1px solid var(--hairline)", borderRadius:6, marginBottom:14}}>
              {[
                { id: "transcript", icon: "note", label: "文字起こし", sub: "推奨" },
                { id: "notes", icon: "edit", label: "ノート", sub: "手入力" },
                { id: "voice", icon: "mic", label: "音声入力", sub: "ライブ" },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setMode(t.id)}
                  className={"yt-mode-tab" + (mode === t.id ? " active" : "")}
                  style={{
                    display:"flex", flexDirection:"column", alignItems:"center", gap:2,
                    padding:"8px 6px", borderRadius:4, border:"none", cursor:"pointer",
                    background: mode === t.id ? "linear-gradient(180deg, rgba(245,197,24,0.18), rgba(245,197,24,0.08))" : "transparent",
                    color: mode === t.id ? "var(--gold)" : "var(--ash)",
                    transition: "all 0.15s",
                  }}
                >
                  <div style={{display:"flex", alignItems:"center", gap:6}}>
                    <Icon name={t.icon} size={13}/>
                    <span style={{fontFamily:"var(--f-display)", fontSize:12, letterSpacing:"0.06em"}}>{t.label}</span>
                  </div>
                  <span style={{fontFamily:"var(--f-mono)", fontSize:9, letterSpacing:"0.10em", opacity:0.75}}>{t.sub}</span>
                </button>
              ))}
            </div>

            {/* Mode-specific UI */}
            <div style={{flex:1, display:"flex", flexDirection:"column", gap:10, minHeight:0}}>
              {mode === "transcript" && (
                <React.Fragment>
                  <div className="label-mono">YouTubeの文字起こしテキストをここに貼り付け</div>
                  <textarea
                    className="form-input form-textarea"
                    style={{flex:1, minHeight:0, fontSize:12, lineHeight:1.7, resize:"none"}}
                    placeholder="例:&#10;0:00&#10;こんにちは、本日のテーマは…&#10;0:12&#10;まず最初にお伝えしたいのは…&#10;&#10;↑のような形式そのままでOK(タイムスタンプは自動除去)"
                    value={transcriptText}
                    onChange={e => setTranscriptText(cleanTranscriptPaste(e.target.value))}
                    onPaste={(e) => {
                      const pasted = e.clipboardData.getData("text");
                      if (pasted && /\d{1,2}:\d{2}/.test(pasted)) {
                        e.preventDefault();
                        const cleaned = cleanTranscriptPaste(pasted);
                        setTranscriptText(prev => (prev ? prev + " " : "") + cleaned);
                      }
                    }}
                  />
                </React.Fragment>
              )}

              {mode === "notes" && (
                <React.Fragment>
                  <div className="label-mono">動画を見ながら気づいたことを自由に</div>
                  <textarea
                    className="form-input form-textarea"
                    style={{flex:1, minHeight:0, fontSize:13, lineHeight:1.7, resize:"none"}}
                    placeholder="この動画で語られている要点・印象的だった発言・自社にどう活かせるか・追加で調べたいこと、など。&#10;&#10;箇条書きでも自然文でもOK。30文字以上書けばAIが要約します。"
                    value={notesText}
                    onChange={e => setNotesText(e.target.value)}
                  />
                </React.Fragment>
              )}

              {mode === "voice" && (
                <React.Fragment>
                  {!speech.supported && (
                    <div style={{padding:"10px 12px", background:"rgba(196,30,58,0.10)", border:"1px solid rgba(196,30,58,0.32)", borderRadius:4, fontSize:11, color:"var(--crimson-hi)"}}>
                      お使いのブラウザは音声認識に対応していません。Chrome/Edgeでお試しください。
                    </div>
                  )}
                  <div style={{display:"flex", alignItems:"center", gap:10}}>
                    {!speech.listening ? (
                      <button className="btn gold sm" onClick={speech.start} disabled={!speech.supported}>
                        <Icon name="mic" size={12}/>
                        <span>録音開始</span>
                      </button>
                    ) : (
                      <button className="btn ghost sm" onClick={speech.stop} style={{borderColor:"var(--crimson)", color:"var(--crimson-hi)"}}>
                        <Icon name="stop" size={12}/>
                        <span>停止</span>
                      </button>
                    )}
                    {speech.listening && (
                      <div style={{display:"flex", alignItems:"center", gap:8, fontSize:11, color:"var(--crimson-hi)"}}>
                        <span style={{width:8, height:8, borderRadius:"50%", background:"var(--crimson-hi)", animation:"dot-blink 1s ease-in-out infinite"}}/>
                        <span style={{fontFamily:"var(--f-mono)", letterSpacing:"0.10em"}}>RECORDING…</span>
                      </div>
                    )}
                    {voiceText && (
                      <button
                        className="btn ghost sm"
                        onClick={() => { setVoiceText(""); setVoiceInterim(""); }}
                        style={{marginLeft:"auto"}}
                      >クリア</button>
                    )}
                  </div>
                  <div
                    style={{
                      flex:1, minHeight:0, overflow:"auto",
                      padding:"12px 14px",
                      background:"rgba(0,0,0,0.32)",
                      border:"1px solid var(--hairline)",
                      borderRadius:5,
                      fontSize:13, lineHeight:1.8, color:"var(--ivory)",
                      whiteSpace:"pre-wrap",
                    }}
                  >
                    {voiceText || <span style={{color:"var(--ash)"}}>マイクを許可して「録音開始」を押し、動画を見ながら気づいたことを話してください。</span>}
                    {voiceInterim && <span style={{color:"var(--ash)", fontStyle:"italic"}}> {voiceInterim}</span>}
                  </div>
                </React.Fragment>
              )}
            </div>

            {/* Footer counter + summarize */}
            <div style={{marginTop:12, paddingTop:12, borderTop:"1px solid var(--hairline)"}}>
              {error && (
                <div style={{marginBottom:10, padding:"8px 10px", background:"rgba(196,30,58,0.10)", border:"1px solid rgba(196,30,58,0.32)", borderRadius:4, fontSize:11, color:"var(--crimson-hi)"}}>
                  {error}
                </div>
              )}
              <div style={{display:"flex", alignItems:"center", gap:10}}>
                <div style={{flex:1, fontFamily:"var(--f-mono)", fontSize:10, letterSpacing:"0.10em", color: charCount >= 30 ? "var(--gold)" : "var(--ash)"}}>
                  {charCount.toLocaleString()} chars {charCount < 30 ? "(30以上必要)" : "· READY"}
                </div>
                <button
                  className="btn primary"
                  onClick={summarize}
                  disabled={phase === "summarizing" || charCount < 30}
                >
                  {phase === "summarizing" ? (
                    <React.Fragment>
                      <div style={{width:12, height:12, border:"2px solid currentColor", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite"}}/>
                      <span>要約中…</span>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <Icon name="sparkle" size={12}/>
                      <span>AIで要約して保存</span>
                    </React.Fragment>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ============================================================
// Detail view — saved learning session
// ============================================================
const LearningDetail = ({ detail, onClose, onDelete }) => {
  if (!detail) return null;
  return (
    <div className="rec-overlay" onClick={(e) => { if (e.target.classList.contains("rec-overlay")) onClose(); }}>
      <div className="rec-stage">
        <div className="rec-stage-head">
          <div style={{flex:1, minWidth:0}}>
            <span className="eyebrow">YOUTUBE · {detail.author}</span>
            <h2 style={{overflow:"hidden", textOverflow:"ellipsis"}}>{detail.title}</h2>
          </div>
          <button className="icon-btn" onClick={onClose} title="閉じる" aria-label="閉じる"><Icon name="x" size={18}/></button>
        </div>
        <div className="rec-stage-body">
          <div style={{marginBottom:18}}>
            <YouTubePlayer videoId={detail.videoId} title={detail.title}/>
          </div>

          {detail.summary && (
            <div className="summary-stage">
              <h4>★ AI 学習サマリ
                {detail.sourceLabel && <span style={{marginLeft:10, fontSize:10, color:"var(--ash)", fontFamily:"var(--f-mono)", letterSpacing:"0.10em", fontWeight:400}}>SOURCE: {detail.sourceLabel}</span>}
              </h4>
              <div style={{fontSize:13, color:"var(--ivory)", lineHeight:1.7, marginBottom:14, fontStyle:"italic", padding:"10px 12px", background:"rgba(245,197,24,0.06)", borderLeft:"3px solid var(--gold)", borderRadius:"0 4px 4px 0"}}>{detail.summary.topic}</div>

              {detail.summary.points?.length > 0 && (
                <div className="summary-section">
                  <h5>要点</h5>
                  <ul>{detail.summary.points.map((p, i) => <li key={i}>{p}</li>)}</ul>
                </div>
              )}
              {detail.summary.insights?.length > 0 && (
                <div className="summary-section">
                  <h5>業務への気付き</h5>
                  <ul>{detail.summary.insights.map((p, i) => <li key={i}>{p}</li>)}</ul>
                </div>
              )}
              {detail.summary.actions?.length > 0 && (
                <div className="summary-section">
                  <h5>明日からのアクション</h5>
                  <ul>{detail.summary.actions.map((p, i) => <li key={i}>{p}</li>)}</ul>
                </div>
              )}
              {detail.summary.quotes?.length > 0 && (
                <div className="summary-section">
                  <h5>印象的な発言・数値</h5>
                  <ul>{detail.summary.quotes.map((p, i) => <li key={i}>{p}</li>)}</ul>
                </div>
              )}
              {detail.summary.tags?.length > 0 && (
                <div style={{marginTop:14, display:"flex", gap:6, flexWrap:"wrap"}}>
                  {detail.summary.tags.map(t => <span key={t} className="chip gold">#{t}</span>)}
                </div>
              )}
            </div>
          )}

          {detail.sourceText && (
            <details style={{marginTop:14}}>
              <summary style={{cursor:"pointer", fontSize:11, color:"var(--ash)", marginBottom:6, fontFamily:"var(--f-mono)", letterSpacing:"0.12em"}}>入力ソースの原文を表示</summary>
              <div style={{padding:14, background:"rgba(0,0,0,0.4)", border:"1px solid var(--hairline)", borderRadius:4, fontSize:11, color:"var(--ash)", lineHeight:1.7, maxHeight:240, overflow:"auto", whiteSpace:"pre-wrap"}}>{detail.sourceText}</div>
            </details>
          )}
        </div>
        <div className="rec-stage-foot">
          <button className="btn ghost" onClick={() => { if (confirm("この学習記録を削除しますか?")) { onDelete(detail.id); onClose(); } }}>
            <Icon name="trash" size={12}/><span>削除</span>
          </button>
          <button className="btn ghost" onClick={() => {
            if (!detail.summary) return;
            const sum = detail.summary;
            const text = [
              `# ${detail.title}`,
              `${detail.author} · ${detail.url}`,
              "",
              `## テーマ`, sum.topic || "", "",
              `## 要点`, ...(sum.points || []).map(p => "- " + p), "",
              `## 業務への気付き`, ...(sum.insights || []).map(p => "- " + p), "",
              `## 明日からのアクション`, ...(sum.actions || []).map(p => "- " + p), "",
              `## 印象的な発言・数値`, ...(sum.quotes || []).map(p => "- " + p), "",
            ].join("\n");
            (async () => { const ok = await copyText(text); alert(ok ? "コピーしました" : "コピーに失敗しました"); })();
          }}>
            <Icon name="copy" size={12}/><span>Markdownコピー</span>
          </button>
          {detail.summary?.actions?.length > 0 && (
            <button className="btn gold" onClick={() => {
              if (!window.__superman_addTasks) return;
              const today = new Date().toISOString().slice(0, 10);
              const newItems = detail.summary.actions.map(a => ({
                id: "tsk_yt_" + Date.now() + "_" + Math.random().toString(36).slice(2,6),
                title: a,
                notes: `YouTube学習「${detail.title}」より抽出`,
                status: "todo", area: "HR", owner: "竹内",
                date: today, time: null, dur: null, tag: null,
              }));
              window.__superman_addTasks(newItems);
              alert(`${newItems.length}件のタスクを追加しました`);
            }}>
              <Icon name="plus" size={12}/><span>アクションをタスクに</span>
            </button>
          )}
          <div style={{flex:1}}/>
          <a href={detail.url} target="_blank" rel="noopener noreferrer" className="btn ghost" style={{textDecoration:"none"}}>
            <Icon name="arrow" size={12}/><span>YouTubeで開く</span>
          </a>
          <button className="btn primary" onClick={onClose}>閉じる</button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Main view
// ============================================================
const YouTubeView = ({ state, setState }) => {
  const list = state.youtube || [];
  const [url, setUrl] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [detail, setDetail] = React.useState(null);
  const [draft, setDraft] = React.useState(null); // active learning session
  const [starting, setStarting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const crud = useCrud(state, setState, "youtube");

  const filtered = list.filter(v =>
    !search || ((v.title || "") + " " + (v.author || "") + " " + (v.summary?.topic || "") + " " + (v.summary?.tags?.join(" ") || "") + " " + (v.tag || ""))
      .toLowerCase().includes(search.toLowerCase())
  );

  const startSession = async () => {
    const id = extractVideoId(url);
    if (!id) { setError("有効なYouTube URLを入力してください"); return; }
    setError(null);
    setStarting(true);
    const meta = await fetchOEmbed(id);
    setStarting(false);
    setDraft({
      videoId: id,
      title: meta.title || "",
      author: meta.author || "",
      titleFetchFailed: !meta.ok,
    });
  };


  const handleSessionSave = (saved) => {
    crud.add(saved);
    setDraft(null);
    setUrl("");
    setDetail(saved);
  };

  return (
    <div className="view-fade">
      <div className="ws-toolbar">
        <div className="ws-tabs">
          <div className="ws-tab active"><Icon name="play" size={13}/><span>YouTube Learning</span></div>
        </div>
        <div className="ws-spacer"/>
        <div className="ws-filters">
          <div className="ws-search" style={{width:240}}>
            <Icon name="search" size={12}/>
            <input placeholder="蓄積した動画を検索…" value={search} onChange={e => setSearch(e.target.value)}/>
          </div>
        </div>
      </div>

      {/* URL input */}
      <div className="panel mb-3">
        <div className="panel-head">
          <div>
            <span className="eyebrow">NEW LEARNING SESSION</span>
            <h3>YouTube URLを貼り付けて学習を開始</h3>
          </div>
        </div>
        <div className="panel-body">
          <div style={{display:"flex", gap:10}}>
            <input
              className="form-input"
              style={{flex:1}}
              placeholder="https://www.youtube.com/watch?v=…"
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.nativeEvent.isComposing && !starting && url && startSession()}
              disabled={starting}
            />
            <button className="btn gold" onClick={startSession} disabled={starting || !url}>
              {starting ? (
                <React.Fragment>
                  <div style={{width:12, height:12, border:"2px solid currentColor", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite"}}/>
                  <span>準備中…</span>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Icon name="play" size={12}/>
                  <span>学習を開始</span>
                </React.Fragment>
              )}
            </button>
          </div>
          {error && (
            <div style={{marginTop:12, padding:"10px 14px", background:"rgba(196,30,58,0.10)", border:"1px solid rgba(196,30,58,0.32)", borderRadius:5, fontSize:12, color:"var(--crimson-hi)"}}>
              {error}
            </div>
          )}
          <div style={{marginTop:14, display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:10, fontSize:11, color:"var(--ash)", lineHeight:1.6}}>
            <div style={{padding:"10px 12px", background:"rgba(255,255,255,0.02)", border:"1px solid var(--hairline)", borderRadius:5}}>
              <div style={{display:"flex", alignItems:"center", gap:6, color:"var(--gold)", fontFamily:"var(--f-mono)", letterSpacing:"0.08em", fontSize:10, marginBottom:6}}>
                <Icon name="note" size={11}/><span>文字起こし(推奨)</span>
              </div>
              YouTube画面下の「…」→「文字起こしを表示」からコピー&ペースト。精度最高。
            </div>
            <div style={{padding:"10px 12px", background:"rgba(255,255,255,0.02)", border:"1px solid var(--hairline)", borderRadius:5}}>
              <div style={{display:"flex", alignItems:"center", gap:6, color:"var(--gold)", fontFamily:"var(--f-mono)", letterSpacing:"0.08em", fontSize:10, marginBottom:6}}>
                <Icon name="edit" size={11}/><span>自分のメモ</span>
              </div>
              動画を見ながらキーボードで気づきを記入。あなたの視点が反映されます。
            </div>
            <div style={{padding:"10px 12px", background:"rgba(255,255,255,0.02)", border:"1px solid var(--hairline)", borderRadius:5}}>
              <div style={{display:"flex", alignItems:"center", gap:6, color:"var(--gold)", fontFamily:"var(--f-mono)", letterSpacing:"0.08em", fontSize:10, marginBottom:6}}>
                <Icon name="mic" size={11}/><span>音声入力</span>
              </div>
              マイクに向かって日本語で話すだけ。手が空いて動画に集中できます。
            </div>
          </div>
        </div>
      </div>

      {/* Archive */}
      <div className="panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">ARCHIVE · {list.length}本</span>
            <h3>蓄積されたYouTube学習</h3>
          </div>
        </div>
        <div className="panel-body">
          {filtered.length === 0 && (
            <div className="empty-state">
              <h4>まだ学習記録がありません</h4>
              <div>{list.length === 0 ? "上のフォームからYouTube URLを貼り付けてください。" : "検索条件にマッチする動画がありません。"}</div>
            </div>
          )}
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:14}}>
            {filtered.map(v => (
              <div key={v.id} className="memo-card" style={{height:"auto", padding:0, overflow:"hidden", cursor:"pointer"}} onClick={() => setDetail(v)}>
                <div style={{position:"relative", aspectRatio:"16/9", background:"#000", overflow:"hidden"}}>
                  <img src={v.thumbnail} alt="" style={{width:"100%", height:"100%", objectFit:"cover"}}/>
                  <div style={{position:"absolute", inset:0, display:"grid", placeItems:"center", background:"rgba(0,0,0,0.18)"}}>
                    <div style={{width:48, height:48, borderRadius:"50%", background:"rgba(196,30,58,0.92)", display:"grid", placeItems:"center", color:"#fff"}}>
                      <Icon name="play" size={20}/>
                    </div>
                  </div>
                  <span className="chip gold" style={{position:"absolute", bottom:8, right:8, fontSize:9}}>{v.tag}</span>
                  {v.sourceMode && (
                    <span style={{position:"absolute", top:8, left:8, padding:"3px 7px", background:"rgba(0,0,0,0.7)", borderRadius:3, fontFamily:"var(--f-mono)", fontSize:9, letterSpacing:"0.10em", color:"var(--ivory)"}}>
                      {{ transcript: "📜 TRANSCRIPT", notes: "📝 NOTES", voice: "🎙️ VOICE", auto: "⚡ AUTO" }[v.sourceMode] || v.sourceMode}
                    </span>
                  )}
                </div>
                <div style={{padding:"12px 14px"}}>
                  <div style={{fontFamily:"var(--f-display)", fontSize:13, letterSpacing:"0.02em", color:"var(--ivory)", marginBottom:6, lineHeight:1.4, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden"}}>{v.title}</div>
                  <div style={{fontSize:11, color:"var(--ash)", marginBottom:8}}>{v.author}</div>
                  {v.summary?.topic && (
                    <div style={{fontSize:11, color:"var(--ivory)", lineHeight:1.5, fontStyle:"italic", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden"}}>{v.summary.topic}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Active learning session */}
      {draft && (
        <LearningSession
          draft={draft}
          onCancel={() => setDraft(null)}
          onSave={handleSessionSave}
        />
      )}

      {/* Detail */}
      <LearningDetail
        detail={detail}
        onClose={() => setDetail(null)}
        onDelete={(id) => crud.remove(id)}
      />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

Object.assign(window, { YouTubeView });


;/* ==== learning.jsx ==== */
// Learning / library — full CRUD
const BOOK_SCHEMA = {
  eyebrow: "BOOK",
  title: "Book",
  titleNew: "New Book",
  titleEdit: "Edit Book",
  fields: [
    { key: "title", label: "Title", type: "text", required: true, span: 2 },
    { key: "author", label: "Author", type: "text", required: true },
    { key: "taken", label: "Started", type: "text", placeholder: "MM/DD" },
    { key: "progress", label: "Progress (%)", type: "number", min: 0, max: 100 },
    { key: "summary", label: "Notes / Key takeaways", type: "textarea", span: 2 },
  ],
};

const LearningView = ({ state, setState }) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const crud = useCrud(state, setState, "books");

  const openEdit = (b) => { setEditing(b); setModalOpen(true); };
  const openNew = () => { setEditing(null); setModalOpen(true); };
  const handleSave = (vals) => {
    if (editing) crud.update(editing.id, vals);
    else crud.add({ progress: 0, ...vals });
    setModalOpen(false);
  };
  const handleDelete = () => {
    if (editing) crud.remove(editing.id);
    setModalOpen(false);
  };

  const finished = state.books.filter(b => b.progress === 100).length;

  return (
    <div className="view-fade">
      <div className="panel cinematic mb-3" style={{padding:"24px 28px", display:"flex", alignItems:"center", gap:20}}>
        <div style={{width:56, height:56, borderRadius:8, background:"linear-gradient(135deg, var(--gold), var(--gold-lo))", display:"grid", placeItems:"center", color:"var(--void)"}}>
          <Icon name="book" size={26}/>
        </div>
        <div style={{flex:1}}>
          <span className="eyebrow">LIBRARY</span>
          <h3 style={{fontFamily:"var(--f-display)", fontSize:20, letterSpacing:"0.10em", color:"var(--ivory)", marginTop:4}}>Reading & Learning Log</h3>
          <div style={{fontSize:12, color:"var(--ash)", marginTop:4}}>{state.books.length} books · {finished} finished</div>
        </div>
        <button className="btn gold sm" onClick={openNew}>
          <Icon name="plus" size={12}/>
          <span>Add Book</span>
        </button>
      </div>

      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:14}}>
        {state.books.map(b => (
          <div key={b.id} className="book-card" onClick={() => openEdit(b)} style={{cursor:"pointer", position:"relative"}}>
            <div className="row-actions" style={{position:"absolute", top:10, right:10}}>
              <button className="row-action-btn" onClick={(e) => { e.stopPropagation(); openEdit(b); }}><Icon name="edit" size={13}/></button>
              <button className="row-action-btn danger" onClick={(e) => { e.stopPropagation(); if (confirm("Delete this book?")) crud.remove(b.id); }}><Icon name="trash" size={13}/></button>
            </div>
            <div className="book-cover">{b.title}</div>
            <div>
              <div className="book-title">{b.title}</div>
              <div className="book-author">{b.author}</div>
              <div className="book-summary">{b.summary}</div>
              <div className="book-progress">
                <span>{b.progress}%</span>
                <div className="book-progress-bar"><div className="book-progress-fill" style={{width: Math.max(0, Math.min(100, Number(b.progress) || 0)) + "%"}}/></div>
                <span>{b.taken}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {state.books.length === 0 && (
        <div className="empty-state"><h4>No books yet</h4><div>Click 'Add Book' to start your reading log.</div></div>
      )}

      <EntityModal
        open={modalOpen}
        schema={BOOK_SCHEMA}
        initial={editing}
        onSave={handleSave}
        onDelete={editing ? handleDelete : null}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

Object.assign(window, { LearningView });


;/* ==== review.jsx ==== */
// Weekly review — auto-generated via Claude, persisted in state.reviews
const ReviewView = ({ state, setState }) => {
  const [generating, setGenerating] = React.useState(false);
  const [review, setReview] = React.useState(state.reviews?.[0]?.review || null);
  const [error, setError] = React.useState(null);
  const lastGenerated = state.reviews?.[0]?.generatedAt;

  const generate = async () => {
    setGenerating(true);
    setError(null);
    const context = {
      tasks_done: state.items.filter(t => t.status === "done").map(t => t.title),
      tasks_pending: state.items.filter(t => t.status !== "done" && !t.time).slice(0, 12).map(t => `${t.title} (${t.area} / ${t.priority || "—"})`),
      meetings: state.items.filter(t => t.time).map(s => `${s.date || "?"} ${s.time} ${s.title}`),
      recordings: state.recordings.slice(0, 3).map(r => `${r.title}: ${r.summary?.points?.[0] || ""}`),
      projects: state.projects.map(p => `${p.name} ${p.pct}% (next: ${p.next})`),
      // Optional-chain everything: the funnel is user-editable (stages can be
      // deleted), and a hard `funnel[5].count` here threw before the try block,
      // leaving the generate button stuck on "generating".
      kpi: `Attrition ${state.kpi?.leaveRate?.now ?? "—"}% (2028 target ${state.kpi?.leaveRate?.target2028 ?? "—"}%) / Headcount ${state.kpi?.headcount?.current ?? "—"} / Funnel accepted ${state.kpi?.funnel?.[state.kpi.funnel.length - 1]?.count ?? "—"}`,
    };

    const prompt = `あなたは経営層の週次レビュー作成アシスタントです。ユーザーは大阪の不動産会社「セイムグループ」の人事総務部部長兼情報システム統括(まさあきさん)。社員数約94名、採用・80等級制移行・離職率改善・社屋移転・生成AI推進などを管掌しています。

今週のデータ:
${JSON.stringify(context, null, 2)}

**日本語で**以下のJSON形式のみで応答してください(コードフェンスや前置きは絶対に入れない):
{
  "title": "今週の短いタイトル",
  "summary": "今週の総括(2〜3文)",
  "wins": ["今週の勝ち筋・進捗1", "進捗2", "進捗3"],
  "concerns": ["懸念点・遅れ1", "懸念2"],
  "next_week_focus": ["来週フォーカス1", "フォーカス2", "フォーカス3"],
  "insight": "経営層視点の洞察・パターン(1〜2文)"
}`;

    try {
      const resp = await window.claude.complete({ prompt, tier: "advanced" }); // 週次レビュー＝分析・洞察 → advanced tier
      let json = resp.trim();
      const m = json.match(/\{[\s\S]*\}/);
      if (m) json = m[0];
      const parsed = JSON.parse(json);
      setReview(parsed);
      // Persist to state so it survives reload
      setState(s => ({ ...s, reviews: [{ generatedAt: Date.now(), review: parsed }, ...(s.reviews || []).slice(0, 9)] }));
    } catch (e) {
      console.log(e);
      setError("Generation failed. Please try again.");
    }
    setGenerating(false);
  };

  // Current week (Mon–Sun) computed live — this header used to be hardcoded
  // to "Week 22 / May 19–25" and went stale the following Monday.
  const { weekNo, weekRange, weekYear } = React.useMemo(() => {
    const now = new Date();
    const mon = new Date(now);
    mon.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    const t = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const day = t.getUTCDay() || 7;
    t.setUTCDate(t.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
    const wk = Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
    const f = (d) => `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} (${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d.getDay()]})`;
    return { weekNo: wk, weekYear: t.getUTCFullYear(), weekRange: `${f(mon)} — ${f(sun)}` };
  }, []);

  return (
    <div className="view-fade">
      <div className="review-hero">
        <span className="eyebrow">★ WEEKLY REVIEW · Week {weekNo} / {weekYear}</span>
        <h1 style={{fontFamily:"var(--f-display)", fontSize:52, letterSpacing:"0.05em", color:"var(--ivory)", marginTop:18, lineHeight:0.95}}>
          THIS <span style={{background:"linear-gradient(180deg, var(--gold-hi), var(--gold), var(--gold-lo))", WebkitBackgroundClip:"text", backgroundClip:"text", color:"transparent"}}>WEEK</span> IN REVIEW
        </h1>
        <div style={{fontFamily:"var(--f-title)", fontSize:14, letterSpacing:"0.20em", color:"var(--crystal)", marginTop:14}}>
          {weekRange}
        </div>
        <div style={{marginTop:28}}>
          <button className="btn gold lg" onClick={generate} disabled={generating}>
            {generating ? (
              <>
                <div style={{width:14, height:14, border:"2px solid var(--void)", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite"}}/>
                <span>Generating…</span>
              </>
            ) : (
              <>
                <Icon name="sparkle" size={14} color="var(--void)"/>
                <span>{review ? "Regenerate" : "Generate weekly review with AI"}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {review && (
        <div style={{display:"flex", justifyContent:"center", gap:10, marginBottom:18}}>
          <button className="btn ghost" onClick={() => {
            if (!review) return;
            const text = [`今週のレビュー — ${review.title}`, "", review.summary, "", "今週の進捗:", ...(review.wins || []).map(w => "• " + w), "", "懸念点:", ...(review.concerns || []).map(c => "• " + c), "", "来週のフォーカス:", ...(review.next_week_focus || []).map(f => "• " + f), "", "洞察: " + (review.insight || "")].join("\n");
            try {
              if (!window.speechSynthesis) { alert("お使いのブラウザは音声合成に対応していません"); return; }
              window.speechSynthesis.cancel();
              const u = new SpeechSynthesisUtterance(text);
              u.lang = "ja-JP";
              u.rate = 1.05;
              u.pitch = 1;
              const voices = window.speechSynthesis.getVoices();
              const jp = voices.find(v => v.lang.startsWith("ja"));
              if (jp) u.voice = jp;
              window.speechSynthesis.speak(u);
            } catch (e) { alert("音声再生に失敗しました"); }
          }}>
            <Icon name="play" size={12}/>
            <span>音声で聞く</span>
          </button>
          <button className="btn ghost" onClick={() => window.speechSynthesis?.cancel()}>
            <Icon name="stop" size={12}/>
            <span>停止</span>
          </button>
          <button className="btn ghost" onClick={() => {
            const text = [`今週のレビュー — ${review.title}`, "", review.summary, "", "## 今週の進捗", ...(review.wins || []).map(w => "- " + w), "", "## 懸念点", ...(review.concerns || []).map(c => "- " + c), "", "## 来週のフォーカス", ...(review.next_week_focus || []).map(f => "- " + f), "", "## 洞察", review.insight || ""].join("\n");
            const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
            const url = URL.createObjectURL(blob); const a = document.createElement("a");
            a.href = url; a.download = `weekly-review-${new Date().toISOString().slice(0,10)}.md`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          }}>
            <Icon name="download" size={12}/>
            <span>Markdownエクスポート</span>
          </button>
        </div>
      )}

      {error && (
        <div style={{padding:"10px 14px", background:"rgba(196,30,58,0.10)", border:"1px solid rgba(196,30,58,0.32)", borderRadius:6, fontSize:12, color:"var(--crimson-hi)", marginBottom:18}}>{error}</div>
      )}

      {review && (
        <div className="dash-grid">
          <div className="panel">
            <div className="panel-head">
              <div>
                <span className="eyebrow">SUMMARY</span>
                <h3>Week Summary</h3>
              </div>
            </div>
            <div className="panel-body">
              <div style={{fontFamily:"var(--f-display)", fontSize:22, letterSpacing:"0.04em", color:"var(--ivory)", marginBottom:14, lineHeight:1.3}}>
                {review.title}
              </div>
              <div style={{fontSize:14, color:"var(--ivory)", lineHeight:1.7, marginBottom:24}}>
                {review.summary}
              </div>
              <hr className="divider"/>
              <div style={{padding:16, background:"linear-gradient(90deg, rgba(245,197,24,0.06), transparent)", borderLeft:"3px solid var(--gold)", borderRadius:"0 6px 6px 0"}}>
                <div className="eyebrow">★ INSIGHT</div>
                <div style={{marginTop:8, fontSize:13, color:"var(--ivory)", lineHeight:1.7, fontStyle:"italic"}}>
                  {review.insight}
                </div>
              </div>
            </div>
          </div>

          <div className="col">
            <div className="panel">
              <div className="panel-head">
                <div>
                  <span className="eyebrow">WINS</span>
                  <h3>This Week's Wins</h3>
                </div>
              </div>
              <div className="panel-body">
                {review.wins?.map((w, i) => (
                  <div key={i} style={{display:"flex", gap:12, padding:"10px 0", borderBottom: i < review.wins.length - 1 ? "1px solid var(--hairline)" : "none"}}>
                    <div style={{width:24, height:24, borderRadius:"50%", background:"rgba(245,197,24,0.16)", display:"grid", placeItems:"center", color:"var(--gold)", flexShrink:0}}>
                      <Icon name="check" size={14}/>
                    </div>
                    <div style={{fontSize:13, color:"var(--ivory)", lineHeight:1.5}}>{w}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="panel">
              <div className="panel-head">
                <div>
                  <span className="eyebrow">CONCERNS</span>
                  <h3>Concerns & Slippage</h3>
                </div>
              </div>
              <div className="panel-body">
                {review.concerns?.map((c, i) => (
                  <div key={i} style={{display:"flex", gap:12, padding:"10px 0", borderBottom: i < review.concerns.length - 1 ? "1px solid var(--hairline)" : "none"}}>
                    <div style={{width:24, height:24, borderRadius:"50%", background:"rgba(196,30,58,0.12)", display:"grid", placeItems:"center", color:"var(--crimson-hi)", flexShrink:0}}>
                      <Icon name="alert" size={14}/>
                    </div>
                    <div style={{fontSize:13, color:"var(--ivory)", lineHeight:1.5}}>{c}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {review && (
        <div className="panel mt-3">
          <div className="panel-head">
            <div>
              <span className="eyebrow">NEXT WEEK</span>
              <h3>Next Week's Focus</h3>
            </div>
          </div>
          <div className="panel-body">
            <div style={{display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:14}}>
              {review.next_week_focus?.map((f, i) => (
                <div key={i} style={{padding:18, background:"rgba(11,19,48,0.5)", border:"1px solid var(--hairline)", borderRadius:6}}>
                  <div style={{fontFamily:"var(--f-display)", fontSize:40, color:"var(--crimson)", lineHeight:1, marginBottom:8}}>0{i+1}</div>
                  <div style={{fontSize:13, color:"var(--ivory)", lineHeight:1.5}}>{f}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!review && !generating && (
        <div className="panel">
          <div className="panel-body" style={{padding:"50px 30px"}}>
            <div style={{display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:18, marginBottom:30}}>
              {[
                ["Week activity", state.items.filter(t=>t.status==="done").length + state.items.filter(t=>t.time).length + state.recordings.length, ""],
                ["Tasks done", state.items.filter(t=>t.status==="done").length, ""],
                ["Recordings", state.recordings.length, ""],
                ["Project progress", "+12", "%"],
              ].map((k, i) => (
                <div key={i} style={{textAlign:"center"}}>
                  <div className="kpi-tile-label">{k[0]}</div>
                  <div className="kpi-tile-value" style={{fontSize:36, marginTop:8}}>{k[1]}<span style={{fontSize:14, color:"var(--ash)", marginLeft:4}}>{k[2]}</span></div>
                </div>
              ))}
            </div>
            <hr className="divider"/>
            <div style={{textAlign:"center", padding:"20px 0", color:"var(--ash)", lineHeight:1.7, fontSize:13}}>
              Press "Generate weekly review" — Claude analyzes this week's tasks, meetings, recordings and KPI,<br/>
              then reports your <span className="gold-text">wins, concerns and next-week focus</span>.
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

Object.assign(window, { ReviewView });


;/* ==== search.jsx ==== */
// Global search overlay (Cmd+K) — keyboard-first command palette.
// ↑↓ moves the selection, Enter opens it (IME-safe), Esc closes.
const SEARCH_QUICK_ACTIONS = [
  { type: "Quick", icon: "mic", title: "Start recording", sub: "Meetings · 1:1 · voice memos", action: "rec" },
  { type: "View", icon: "chart", title: "Hiring funnel", sub: "This month · monthly", view: "kpi" },
  { type: "View", icon: "star", title: "Generate weekly review", sub: "Wins, concerns & next-week focus", view: "review" },
  { type: "View", icon: "sparkle", title: "AI Sparring", sub: "Brainstorm partner for strategy", view: "sparring" },
];

const SearchOverlay = ({ open, onClose, state, setView, openRecord }) => {
  const [q, setQ] = React.useState("");
  const [sel, setSel] = React.useState(0);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);

  React.useEffect(() => {
    if (open) {
      setQ("");
      setSel(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Reset the highlight whenever the result set changes.
  React.useEffect(() => { setSel(0); }, [q]);

  const results = React.useMemo(() => {
    if (!q) return [];
    const Q = q.toLowerCase();
    const has = (...parts) => parts.some(p => (p || "").toLowerCase().includes(Q));
    const out = [];
    (state.items || []).forEach(t => {
      if (has(t.title)) {
        const sub = (t.area || "") + (t.date ? ` · ${t.date}` : "") + (t.time ? ` ${t.time}` : "");
        out.push({ type: t.time ? "Event" : "Task", icon: t.time ? "calendar" : "tasks", title: t.title, sub, view: t.time ? "calendar" : "tasks" });
      }
    });
    (state.recordings || []).forEach(r => {
      if (has(r.title, r.transcript, (r.tags || []).join(" "))) {
        out.push({ type: "Minutes", icon: "waveform", title: r.title, sub: (r.date || "") + " · " + (r.summary?.points?.[0]?.slice(0, 50) || ""), view: "memos" });
      }
    });
    (state.ideas || []).forEach(i => { if (has(i.title, i.body)) out.push({ type: "Memo", icon: "note", title: i.title, sub: (i.body || "").slice(0, 60), view: "memos" }); });
    (state.projects || []).forEach(p => { if (has(p.name, p.area)) out.push({ type: "Project", icon: "folder", title: p.name, sub: (p.area || "") + " · " + (p.pct ?? 0) + "%", view: "projects" }); });
    (state.contacts || []).forEach(c => { if (has(c.name, c.company, c.note, c.role)) out.push({ type: "Contact", icon: "users", title: c.name, sub: (c.role || "") + " / " + (c.company || ""), view: "contacts" }); });
    (state.books || []).forEach(b => { if (has(b.title, b.author, b.summary)) out.push({ type: "Book", icon: "book", title: b.title, sub: (b.author || "") + " · " + (b.progress ?? 0) + "%", view: "learning" }); });
    return out.slice(0, 30);
  }, [q, state]);

  if (!open) return null;

  const rows = q ? results : SEARCH_QUICK_ACTIONS;

  const choose = (r) => {
    if (!r) return;
    if (r.action === "rec") { if (openRecord) openRecord(); }
    else if (r.view) setView(r.view);
    onClose();
  };

  const move = (dir) => {
    if (!rows.length) return;
    const next = (sel + dir + rows.length) % rows.length;
    setSel(next);
    // Keep the highlighted row in view while cycling with the keyboard.
    requestAnimationFrame(() => {
      const el = listRef.current?.querySelectorAll(".search-result")[next];
      el?.scrollIntoView({ block: "nearest" });
    });
  };

  const onKey = (e) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); move(1); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); move(-1); return; }
    if (e.key === "Enter" && !e.nativeEvent.isComposing) { e.preventDefault(); choose(rows[sel]); }
  };

  const Row = (r, i) => (
    <div
      key={i}
      className={"search-result" + (i === sel ? " active" : "")}
      onClick={() => choose(r)}
      onMouseEnter={() => setSel(i)}
    >
      <div className="search-result-icon"><Icon name={r.icon} size={16}/></div>
      <div style={{minWidth:0}}>
        <div className="search-result-title">{r.title}</div>
        <div className="search-result-sub">{r.sub}</div>
      </div>
      <span className="search-result-type">{r.type}</span>
    </div>
  );

  return (
    <div className="search-overlay" onClick={(e) => { if (e.target.classList.contains("search-overlay")) onClose(); }}>
      <div className="search-modal">
        <div className="search-input-wrap">
          <Icon name="search" size={22} color="var(--gold)"/>
          <input
            ref={inputRef}
            placeholder="Search — tasks, minutes, memos, people, KPI…(↑↓で選択・Enterで開く)"
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={onKey}
          />
          <span className="kbd">ESC</span>
        </div>
        <div className="search-results" ref={listRef}>
          {q && results.length === 0 && (
            <div style={{padding:"40px 20px", textAlign:"center", color:"var(--ash)"}}>No results</div>
          )}
          {!q && (
            <div style={{padding: 16}}>
              <div className="label-mono mb-2">Quick actions</div>
              {SEARCH_QUICK_ACTIONS.map(Row)}
            </div>
          )}
          {q && results.map(Row)}
        </div>
      </div>
    </div>
  );
};

Object.assign(window, { SearchOverlay });


;/* ==== sparring.jsx ==== */
// AI sparring partner — chat (persisted via state.sparring)
const SparringView = ({ state, setState }) => {
  const persisted = state.sparring;
  const [conversations, setConversations] = React.useState(persisted && persisted.length ? persisted : [
    { id: "conv1", title: "Attrition path to 10%", date: "5/24", messages: [
      { role: "you", text: "The biggest attrition is 2nd-year new grads. Where should I start?" },
      { role: "ai", text: "Year-2 attrition typically fails on three axes: role-fit, manager relationship, and growth-perception. Start with diagnosis (a paired exit-interview & current-employee survey) to identify which axis is the largest driver. Then design interventions per axis — broad programs without diagnosis tend to dilute." },
    ]},
    { id: "conv2", title: "80-grade transition", date: "5/22", messages: []},
  ]);
  const [activeId, setActiveId] = React.useState(conversations[0]?.id);

  // Persist to state on changes (debounced via React effects)
  React.useEffect(() => {
    setState(s => ({ ...s, sparring: conversations }));
    // eslint-disable-next-line
  }, [conversations]);
  const [input, setInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [showSettings, setShowSettings] = React.useState(false);
  const scrollRef = React.useRef(null);
  const settingsRef = React.useRef(null);

  // Close settings popover on outside click
  React.useEffect(() => {
    if (!showSettings) return;
    const onDoc = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setShowSettings(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showSettings]);

  const updateOverrides = (patch) => {
    setConversations(c => c.map(co => {
      if (co.id !== activeId) return co;
      const next = { ...(co.overrides || {}), ...patch };
      // Strip keys with null (use-default)
      Object.keys(next).forEach(k => { if (next[k] == null) delete next[k]; });
      return { ...co, overrides: Object.keys(next).length ? next : undefined };
    }));
  };
  const resetOverrides = () => {
    setConversations(c => c.map(co => co.id === activeId ? { ...co, overrides: undefined } : co));
  };

  const active = conversations.find(c => c.id === activeId);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active?.messages?.length, loading]);

  const newConv = () => {
    const id = "conv" + Date.now();
    const conv = { id, title: "New session", date: new Date().toLocaleDateString("en-US",{month:"numeric",day:"numeric"}), messages: [] };
    setConversations(c => [conv, ...c]);
    setActiveId(id);
  };

  const send = async (preset) => {
    const text = preset || input;
    if (!text.trim() || loading) return;
    setInput("");
    setLoading(true);

    setConversations(c => c.map(co => co.id === activeId ? { ...co, messages: [...co.messages, { role: "you", text }] } : co));

    try {
      const ss = window.__supersys_settings || {};
      const ov = (conversations.find(c => c.id === activeId) || {}).overrides || {};
      const userName = ss.userName || "まさあきさん";
      const userRole = ss.userRole || "セイムグループ人事総務部部長兼情報システム統括";
      const aiCtx = ss.aiContext || "セイムグループ(大阪・不動産)。注文住宅「ondo」を主力。約94名→2031年124名計画。離職率20.4%を2028年に10%以下が目標。";
      const styleMap = {
        exec: "経営層向けに簡潔・構造的に、まず結論→理由→次の打ち手の順で",
        detailed: "詳細・分析的に、根拠と複数の選択肢を提示しながら",
        casual: "カジュアル・親しみやすく、対話風に",
        coaching: "コーチング型で、問いかけと内省を促しながら",
      };
      const lengthMap = {
        short: "2〜3文の短い回答",
        medium: "3〜5文程度の中程度の回答",
        long: "段落構成の長めの回答(必要なら箇条書きも可)",
      };
      const effStyle = ov.aiStyle || ss.aiStyle;
      const effLength = ov.aiLength || ss.aiLength;
      const effLang = ov.aiLang || ss.aiLang;
      const styleInstr = styleMap[effStyle] || styleMap.exec;
      const lengthInstr = lengthMap[effLength] || lengthMap.medium;
      const lang = effLang === "en" ? "English" : "日本語";

      const userContext = `あなたは${userRole}「${userName}」の壁打ち相手AIです。${styleInstr}、${lengthInstr}で${lang}で返してください。

[出力フォーマットの注意]
- アスタリスク(*)による強調・箇条書きは使わないでください。強調したい箇所はそのまま地の文で書くか、括弧などで補ってください。
- 絵文字は適度に(1回答につき0〜2個程度)、論点を和らげたり区切るためにのみ使用してください。多用は避け、ビジネス文脈に合うもの(💡📊✅⚠️🎯など)を選んでください。
- マークダウン記号(**, __, ###)は使わず、プレーンテキストで構造を示してください(改行・番号付き・「・」など)。

[コンテキスト]
${aiCtx}

[ユーザーの質問]
"${text}"

[回答]`;
      const resp = await window.claude.complete({ prompt: userContext, tier: "advanced" }); // 戦略壁打ち＝高度な推論 → advanced tier
      const cleaned = (resp || "")
        // Convert **bold** / __bold__ to plain text
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/__(.+?)__/g, "$1")
        // Convert *italic* / _italic_ to plain text (avoid touching numerics)
        .replace(/(^|[\s\(])\*(?!\s)([^\*\n]+?)\*(?=[\s\.,\)\!\?。、]|$)/g, "$1$2")
        // Bullet "* " or "- " at line start → "・"
        .replace(/^[\*\-]\s+/gm, "・")
        // Remove leading "### " markdown headers
        .replace(/^#{1,6}\s+/gm, "");
      setConversations(c => c.map(co => co.id === activeId ? {
        ...co,
        messages: [...co.messages, { role: "ai", text: cleaned }],
        title: co.messages.length === 0 ? text.slice(0, 24) : co.title,
      } : co));
    } catch (e) {
      setConversations(c => c.map(co => co.id === activeId ? { ...co, messages: [...co.messages, { role: "ai", text: "(error: please try again)" }] } : co));
    }
    setLoading(false);
  };

  const PROMPTS = [
    { label: "STRATEGY", text: "Give me 3 ambitious hypotheses for 10x'ing referral hires" },
    { label: "1:1", text: "How should I structure today's 1:1 with S-san (low motivation)?" },
    { label: "POLICY DESIGN", text: "What are the operational pitfalls of demotions in an 80-grade system?" },
    { label: "ROADMAP", text: "Sketch a roadmap for cutting attrition from 20.4% to <10%" },
  ];

  return (
    <div className="view-fade">
      <div className="sparring-shell">
        <div className="spar-side">
          <button className="btn gold sm mb-1" onClick={newConv} style={{justifyContent:"center"}}>
            <Icon name="plus" size={12}/>
            <span>New session</span>
          </button>
          <div className="label-mono" style={{padding:"10px 8px 4px"}}>Past sessions</div>
          {conversations.map(co => (
            <div key={co.id} className={"spar-conv" + (co.id === activeId ? " active" : "")} onClick={() => setActiveId(co.id)}>
              <div className="spar-conv-body">
                <div className="spar-conv-title">{co.title}</div>
                <div className="spar-conv-date">{co.date} · {co.messages.length} msgs</div>
              </div>
              <button
                className="spar-conv-del"
                title="Delete session"
                onClick={(e) => {
                  e.stopPropagation();
                  const ok = window.confirm(`Delete "${co.title}"?`);
                  if (!ok) return;
                  setConversations(prev => {
                    const next = prev.filter(x => x.id !== co.id);
                    if (co.id === activeId) {
                      setActiveId(next[0]?.id);
                    }
                    return next;
                  });
                }}
              >
                <Icon name="trash" size={12}/>
              </button>
            </div>
          ))}
        </div>

        <div className="spar-main">
          {active && (() => {
            const ss = window.__supersys_settings || {};
            const ov = active.overrides || {};
            const hasOv = Object.keys(ov).length > 0;
            const STYLE_OPTS = [
              { v: "exec", label: "経営層向け(簡潔)" },
              { v: "detailed", label: "詳細(分析重視)" },
              { v: "casual", label: "カジュアル" },
              { v: "coaching", label: "コーチング型" },
            ];
            const LENGTH_OPTS = [
              { v: "short", label: "短め(2-3文)" },
              { v: "medium", label: "標準(3-5文)" },
              { v: "long", label: "長め(段落構成)" },
            ];
            const LANG_OPTS = [
              { v: "ja", label: "日本語" },
              { v: "en", label: "English" },
            ];
            const labelOf = (opts, v) => (opts.find(o => o.v === v) || {}).label || v;
            return (
              <div className="spar-header">
                <div className="spar-header-title">
                  <div className="spar-header-name">{active.title}</div>
                  <div className="spar-header-meta">
                    {hasOv ? <span className="spar-ov-badge">カスタム設定</span> : <span className="spar-ov-badge muted">デフォルト設定</span>}
                    <span className="spar-header-dot">·</span>
                    <span>{labelOf(STYLE_OPTS, ov.aiStyle || ss.aiStyle || "exec")}</span>
                    <span className="spar-header-dot">·</span>
                    <span>{labelOf(LENGTH_OPTS, ov.aiLength || ss.aiLength || "medium")}</span>
                    <span className="spar-header-dot">·</span>
                    <span>{labelOf(LANG_OPTS, ov.aiLang || ss.aiLang || "ja")}</span>
                  </div>
                </div>
                <div className="spar-settings-wrap" ref={settingsRef}>
                  <button
                    className={"spar-settings-btn" + (hasOv ? " has-ov" : "")}
                    onClick={() => setShowSettings(s => !s)}
                    title="このセッションのAI設定"
                  >
                    <Icon name="settings" size={13}/>
                    <span>AI設定</span>
                    {hasOv && <span className="spar-settings-dot"></span>}
                  </button>
                  {showSettings && (
                    <div className="spar-settings-pop">
                      <div className="spar-settings-head">
                        <div>
                          <div className="spar-settings-title">このセッションのAI設定</div>
                          <div className="spar-settings-hint">未指定の項目は管理画面のデフォルトを使用します。</div>
                        </div>
                        {hasOv && (
                          <button className="spar-settings-reset" onClick={resetOverrides}>
                            デフォルトに戻す
                          </button>
                        )}
                      </div>
                      {[
                        { key: "aiStyle", label: "スタイル", opts: STYLE_OPTS, global: ss.aiStyle || "exec" },
                        { key: "aiLength", label: "文字数", opts: LENGTH_OPTS, global: ss.aiLength || "medium" },
                        { key: "aiLang", label: "言語", opts: LANG_OPTS, global: ss.aiLang || "ja" },
                      ].map(row => (
                        <div key={row.key} className="spar-settings-row">
                          <label className="spar-settings-label">{row.label}</label>
                          <select
                            className="spar-settings-select"
                            value={ov[row.key] || ""}
                            onChange={e => updateOverrides({ [row.key]: e.target.value || null })}
                          >
                            <option value="">デフォルト({labelOf(row.opts, row.global)})</option>
                            {row.opts.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          {!active ? (
            <div className="spar-empty">
              <div style={{width:72, height:72, borderRadius:"50%", background:"radial-gradient(circle, var(--gold), var(--gold-lo))", display:"grid", placeItems:"center", color:"var(--void)", boxShadow:"0 0 40px rgba(245,197,24,0.32)"}}>
                <Icon name="sparkle" size={32}/>
              </div>
              <h3>No active session</h3>
              <p>Start a new sparring session to think out loud with the council.</p>
              <button className="btn gold" onClick={newConv} style={{marginTop:8}}>
                <Icon name="plus" size={12}/>
                <span>New session</span>
              </button>
            </div>
          ) : active && active.messages.length === 0 ? (
            <div className="spar-empty">
              <div style={{width:72, height:72, borderRadius:"50%", background:"radial-gradient(circle, var(--gold), var(--gold-lo))", display:"grid", placeItems:"center", color:"var(--void)", boxShadow:"0 0 40px rgba(245,197,24,0.32)"}}>
                <Icon name="sparkle" size={32}/>
              </div>
              <h3>KRYPTON COUNCIL</h3>
              <p>Strategy, policy design, 1:1 framing, ideation. Knows your context — past meetings, current projects, KPIs.</p>
              <div className="spar-prompts">
                {PROMPTS.map((p, i) => (
                  <div key={i} className="spar-prompt" onClick={() => send(p.text)}>
                    <span className="spar-prompt-label">{p.label}</span>
                    {p.text}
                  </div>
                ))}
              </div>
            </div>
          ) : active && (
            <div className="spar-messages" ref={scrollRef}>
              {active.messages.map((m, i) => (
                <div key={i} className={"spar-msg " + m.role}>
                  <div className={"spar-msg-avatar " + m.role}>
                    {m.role === "you" ? "M" : <Icon name="sparkle" size={14} color="var(--void)"/>}
                  </div>
                  <div>
                    <div className="spar-msg-name">{m.role === "you" ? "Masaaki" : "AI Sparring"}</div>
                    <div className="spar-msg-body">{m.text}</div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="spar-msg ai">
                  <div className="spar-msg-avatar ai"><Icon name="sparkle" size={14} color="var(--void)"/></div>
                  <div>
                    <div className="spar-msg-name">AI Sparring</div>
                    <div style={{display:"flex", gap:6, alignItems:"center"}}>
                      <div style={{width:8, height:8, borderRadius:"50%", background:"var(--gold)", animation:"blink 1.4s infinite both"}}></div>
                      <div style={{width:8, height:8, borderRadius:"50%", background:"var(--gold)", animation:"blink 1.4s 0.2s infinite both"}}></div>
                      <div style={{width:8, height:8, borderRadius:"50%", background:"var(--gold)", animation:"blink 1.4s 0.4s infinite both"}}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="spar-input">
            <textarea
              placeholder="Think out loud — strategy, idea validation, framing…(Shift+Enterで改行)"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
              disabled={!active}
            />
            <button className="btn primary" onClick={() => send()} disabled={loading || !input.trim() || !active}>
              <Icon name="send" size={14}/>
              <span>Send</span>
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes blink { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }`}</style>
    </div>
  );
};

Object.assign(window, { SparringView });


;/* ==== settings.jsx ==== */
// Settings panel — full preferences UI, opened from sidebar avatar
const SettingsModal = ({ open, onClose, state, setState, tweaks, setTweak, folder, doConnectFolder, doDisconnectFolder, doExport, doImport, doReset }) => {
  const [section, setSection] = React.useState("profile");

  // Escape closes the modal — same affordance as search/lightbox.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const settings = state.settings || {};
  const setSetting = (key, val) => {
    setState(s => ({ ...s, settings: { ...(s.settings || {}), [key]: val } }));
  };

  // Mutable lists
  const owners = state.owners || ["竹内", "大志万", "山本", "松川", "楠田", "妹尾", "その他"];
  const areas = state.areas || ["HR", "Hiring", "GA", "IT", "Exec", "Personal"];
  const tags = state.tags || ["Exec", "1:1", "Project", "Internal", "GA", "Personal"];
  const ownerColors = state.ownerColors || (window.DEFAULT_OWNER_COLORS || {});

  const updateList = (key, list) => setState(s => ({ ...s, [key]: list }));

  const SECTIONS = [
    { id: "profile", label: "プロフィール", icon: "users" },
    { id: "display", label: "表示", icon: "eye" },
    { id: "navigation", label: "メニュー並び", icon: "folder" },
    { id: "locale", label: "言語・地域", icon: "calendar" },
    { id: "lists", label: "リスト管理", icon: "tasks" },
    { id: "applicants", label: "応募者", icon: "users" },
    { id: "recording", label: "録音", icon: "mic" },
    { id: "ai", label: "AI", icon: "sparkle" },
    { id: "notifications", label: "通知", icon: "bell" },
    { id: "shortcuts", label: "ショートカット", icon: "settings" },
    { id: "data", label: "データ管理", icon: "folder" },
    { id: "about", label: "情報", icon: "alert" },
  ];

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target.classList.contains("modal-overlay")) onClose(); }}>
      <div className="modal-stage settings-stage">
        <div className="modal-head">
          <div style={{flex:1}}>
            <span className="eyebrow">SETTINGS</span>
            <h2 className="modal-title">設定</h2>
          </div>
          <button className="icon-btn" onClick={onClose} title="閉じる (Esc)" aria-label="閉じる"><Icon name="x" size={18}/></button>
        </div>

        <div className="settings-body">
          <nav className="settings-nav">
            {SECTIONS.map(s => (
              <div
                key={s.id}
                className={"settings-nav-item" + (section === s.id ? " active" : "")}
                onClick={() => setSection(s.id)}
                role="tab"
                aria-selected={section === s.id}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSection(s.id); } }}
              >
                <Icon name={s.icon} size={14}/>
                <span>{s.label}</span>
              </div>
            ))}
          </nav>

          <div className="settings-content">
            {section === "profile" && (
              <Section title="プロフィール" subtitle="あなたの情報。AIや通知に使用されます。">
                <Field label="名前">
                  <input className="form-input" value={settings.userName ?? "まさあき"} onChange={e => setSetting("userName", e.target.value)}/>
                </Field>
                <Field label="役職">
                  <input className="form-input" value={settings.userRole ?? "人事総務部 部長 / 情シス統括"} onChange={e => setSetting("userRole", e.target.value)}/>
                </Field>
                <Field label="所属組織">
                  <input className="form-input" value={settings.company ?? "セイムグループ"} onChange={e => setSetting("company", e.target.value)}/>
                </Field>
                <Field label="メールアドレス">
                  <input className="form-input" type="email" value={settings.email ?? ""} onChange={e => setSetting("email", e.target.value)} placeholder="任意"/>
                </Field>
                <Field label="アバター文字" hint="サイドバー左下に表示されます(1文字推奨)">
                  <input className="form-input" maxLength={2} value={settings.avatar ?? "M"} onChange={e => setSetting("avatar", e.target.value)} style={{width:80}}/>
                </Field>
              </Section>
            )}

            {section === "display" && (
              <Section title="表示" subtitle="ビジュアル・アニメーション・UIのカスタマイズ。">
                <Field label="テーマ">
                  <select className="form-input" value={tweaks.theme} onChange={e => setTweak("theme", e.target.value)}>
                    <option value="fortress">Fortress(定番)</option>
                    <option value="krypton">クリプトン(青クリスタル)</option>
                    <option value="phantom">ファントム・ゾーン(灰)</option>
                    <option value="daily-planet">Daily Planet(ニュース室)</option>
                  </select>
                </Field>
                <Field label="星空背景"><Toggle value={tweaks.starfield} onChange={v => setTweak("starfield", v)}/></Field>
                <Field label="星の瞬き" hint="大気のゆらぎの強さ。星ごとにランダムに瞬きます">
                  <select className="form-input" value={settings.starTwinkle ?? "subtle"} onChange={e => setSetting("starTwinkle", e.target.value)}>
                    <option value="off">なし(静止)</option>
                    <option value="subtle">控えめ</option>
                    <option value="normal">標準</option>
                    <option value="medium">やや強め</option>
                    <option value="strong">強め</option>
                    <option value="vivid">はっきり</option>
                  </select>
                </Field>
                <Field label="星の数" hint="星空の密度(画面サイズに応じて自動調整)">
                  <select className="form-input" value={settings.starDensity ?? "1"} onChange={e => setSetting("starDensity", e.target.value)}>
                    <option value="0.6">少なめ</option>
                    <option value="1">標準</option>
                    <option value="1.5">多め</option>
                    <option value="2.2">満天</option>
                  </select>
                </Field>
                <Field label="流れ星" hint="出現はランダム間隔。まれに明るい火球も流れます">
                  <select className="form-input" value={settings.meteorFreq ?? "normal"} onChange={e => setSetting("meteorFreq", e.target.value)}>
                    <option value="off">なし</option>
                    <option value="rare">まれ(1分に1回ほど)</option>
                    <option value="normal">標準(20秒に1回ほど)</option>
                    <option value="frequent">多め(8秒に1回ほど)</option>
                    <option value="shower">流星群(数秒おき)</option>
                  </select>
                </Field>
                <Field label="ワープエフェクト" hint="映画オープニング風の流れる星"><Toggle value={tweaks.warp} onChange={v => setTweak("warp", v)}/></Field>
                <Field label="フィルムグレイン"><Toggle value={tweaks.filmGrain} onChange={v => setTweak("filmGrain", v)}/></Field>
                <Field label="情報密度" hint="ビューの詰め方">
                  <select className="form-input" value={settings.density ?? "comfortable"} onChange={e => setSetting("density", e.target.value)}>
                    <option value="compact">コンパクト</option>
                    <option value="comfortable">標準</option>
                    <option value="spacious">ゆったり</option>
                  </select>
                </Field>
                <Field label="フォントサイズ" hint="85〜150%・5%刻み。即時反映されます">
                  <div style={{display:"flex", alignItems:"center", gap:10}}>
                    <input
                      type="range" min={85} max={150} step={5}
                      value={parseInt(settings.fontScale ?? "100", 10) || 100}
                      onChange={e => setSetting("fontScale", e.target.value)}
                      style={{flex:1}}
                      aria-label="フォントサイズ"
                    />
                    <span className="chip" style={{minWidth:52, justifyContent:"center", fontFamily:"var(--f-mono)"}}>{parseInt(settings.fontScale ?? "100", 10) || 100}%</span>
                  </div>
                </Field>
                <Field label="サイドバー幅" hint="170〜300px・10px刻み">
                  <div style={{display:"flex", alignItems:"center", gap:10}}>
                    <input
                      type="range" min={170} max={300} step={10}
                      value={parseInt(settings.sidebarWidth ?? "200", 10) || 200}
                      onChange={e => setSetting("sidebarWidth", e.target.value)}
                      style={{flex:1}}
                      aria-label="サイドバー幅"
                    />
                    <span className="chip" style={{minWidth:60, justifyContent:"center", fontFamily:"var(--f-mono)"}}>{parseInt(settings.sidebarWidth ?? "200", 10) || 200}px</span>
                  </div>
                </Field>
                <Field label="アニメーション" hint="画面遷移・トースト・パネル展開">
                  <Toggle value={settings.animations ?? true} onChange={v => setSetting("animations", v)}/>
                </Field>
              </Section>
            )}

            {section === "navigation" && (
              <Section title="左メニューの並び" subtitle="サイドバーのメニュー項目を並び替え・表示/非表示できます。▲▼で順番を変更、目のアイコンで表示を切り替えます。">
                <NavOrderEditor
                  layout={window.getNav ? window.getNav(state, { includeHidden: true }) : []}
                  onChange={v => setState(s => ({ ...s, navOrder: v }))}
                  onReset={() => setState(s => { const { navOrder, ...rest } = s; return rest; })}
                />
              </Section>
            )}

            {section === "locale" && (
              <Section title="言語・地域" subtitle="日付・時刻・週の表示形式。">
                <Field label="表示言語">
                  <select className="form-input" value={settings.lang ?? "ja"} onChange={e => setSetting("lang", e.target.value)}>
                    <option value="ja">日本語</option>
                    <option value="en">English</option>
                  </select>
                </Field>
                <Field label="週の開始曜日">
                  <select className="form-input" value={settings.weekStart ?? "sun"} onChange={e => setSetting("weekStart", e.target.value)}>
                    <option value="sun">日曜始まり</option>
                    <option value="mon">月曜始まり</option>
                  </select>
                </Field>
                <Field label="日付フォーマット">
                  <select className="form-input" value={settings.dateFormat ?? "md-wd"} onChange={e => setSetting("dateFormat", e.target.value)}>
                    <option value="md-wd">5/26 (火)</option>
                    <option value="md">5/26</option>
                    <option value="full">2026-05-26</option>
                    <option value="full-wd">2026-05-26 (火)</option>
                  </select>
                </Field>
                <Field label="時刻フォーマット">
                  <select className="form-input" value={settings.timeFormat ?? "24h"} onChange={e => setSetting("timeFormat", e.target.value)}>
                    <option value="24h">24時間表示(14:30)</option>
                    <option value="12h">12時間表示(2:30 PM)</option>
                  </select>
                </Field>
                <Field label="タイムゾーン">
                  <select className="form-input" value={settings.tz ?? "Asia/Tokyo"} onChange={e => setSetting("tz", e.target.value)}>
                    <option value="Asia/Tokyo">日本標準時(JST / UTC+9)</option>
                    <option value="Asia/Seoul">韓国標準時(KST / UTC+9)</option>
                    <option value="Asia/Shanghai">中国標準時(CST / UTC+8)</option>
                    <option value="America/Los_Angeles">太平洋時間(PT)</option>
                    <option value="America/New_York">東部時間(ET)</option>
                    <option value="Europe/London">英国時間(BST/GMT)</option>
                  </select>
                </Field>
                <Field label="祝日表示" hint="日本の祝日をカレンダーに表示">
                  <Toggle value={settings.showHolidays ?? true} onChange={v => setSetting("showHolidays", v)}/>
                </Field>
              </Section>
            )}

            {section === "lists" && (
              <Section title="リスト管理" subtitle="タスクで使う担当者・領域・タイプを編集できます。担当者ごとに色を割り当てると、テーブル・ボード・カレンダーで色分け表示されます。">
                <OwnerColorEditor
                  owners={owners}
                  ownerColors={ownerColors}
                  onChangeOwners={v => updateList("owners", v)}
                  onChangeColors={v => updateList("ownerColors", v)}
                />
                <ListEditor label="領域(Area)" items={areas} onChange={v => updateList("areas", v)} placeholder="新しい領域…"/>
                <ListEditor label="タイプ(Tag)" items={tags} onChange={v => updateList("tags", v)} placeholder="新しいタイプ…"/>
                <ListEditor
                  label="役割定義の期(年・期)"
                  items={state.rolePeriods || window.DEFAULT_ROLE_PERIODS || []}
                  onChange={v => updateList("rolePeriods", v)}
                  placeholder="例: 2028年 第1期"
                />
              </Section>
            )}

            {section === "applicants" && (
              <Section title="応募者管理" subtitle="ステータス・色・次のアクション・応募ソース・応募職種をすべて編集できます。">
                <ApplicantStatusEditor
                  statuses={(window.getApplicantStatuses ? window.getApplicantStatuses(state) : (state.applicantStatuses || window.DEFAULT_APPLICANT_STATUSES || []))}
                  dateFields={window.APPLICANT_DATE_FIELDS || []}
                  onChange={v => updateList("applicantStatuses", v)}
                  onReset={() => updateList("applicantStatuses", window.DEFAULT_APPLICANT_STATUSES)}
                />
                <ListEditor
                  label="応募ソース"
                  items={(window.getApplicantSources ? window.getApplicantSources(state) : (state.applicantSources || window.DEFAULT_APPLICANT_SOURCES || []))}
                  onChange={v => updateList("applicantSources", v)}
                  placeholder="新しいソース…"
                />
                <ListEditor
                  label="応募職種"
                  items={(window.getApplicantPositions ? window.getApplicantPositions(state) : (state.applicantPositions || window.DEFAULT_APPLICANT_POSITIONS || []))}
                  onChange={v => updateList("applicantPositions", v)}
                  placeholder="新しい職種…"
                />
                {/* 職種ごとの「求めるレベル」— AI取り込みの人物評価（役職レベル適正）の基準。
                    社内の等級表・従業員マスターは書かず、求める役職階層・経験・人物像を記述する。 */}
                <div className="settings-position-levels">
                  <label className="form-label">職種ごとの「求めるレベル」（AI評価の基準）</label>
                  <div className="settings-hint" style={{marginBottom:10}}>各職種に求める役職階層・経験年数・マネジメント規模・人物像などを記述します。空欄なら職種名と業界からAIが推測します。※社内の等級表や従業員情報は書かないでください（AIに送信されます）。</div>
                  {(window.getApplicantPositions ? window.getApplicantPositions(state) : (state.applicantPositions || window.DEFAULT_APPLICANT_POSITIONS || [])).map(pos => (
                    <div key={pos} className="settings-position-level-row">
                      <div className="settings-position-level-name">{pos}</div>
                      <textarea
                        className="form-input"
                        rows={2}
                        placeholder="例：課長〜マネージャー相当。チーム10名規模のマネジメント経験、営業戦略の立案・実行、目標管理。"
                        value={(state.positionLevels || {})[pos] || ""}
                        onChange={e => { const v = e.target.value; setState(s => ({ ...s, positionLevels: { ...(s.positionLevels || {}), [pos]: v } })); }}
                      />
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {section === "recording" && (
              <Section title="録音" subtitle="クリスタル・レコーダーの動作設定。">
                <Field label="デフォルト言語">
                  <select className="form-input" value={settings.recLang ?? "ja-JP"} onChange={e => setSetting("recLang", e.target.value)}>
                    <option value="ja-JP">日本語</option>
                    <option value="en-US">English (US)</option>
                    <option value="en-GB">English (UK)</option>
                  </select>
                </Field>
                <Field label="要約のデフォルトスタイル" hint="録音停止後の要約画面で最初に選ばれるスタイル。停止後は自動実行せず、スタイルを選んで「スタート」を押すと開始します。">
                  <select className="form-input" value={settings.recDefaultStyle ?? "exec"} onChange={e => setSetting("recDefaultStyle", e.target.value)}>
                    {(window.SUMMARY_STYLES || []).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="自動でタスク抽出" hint="要約のNext Actionをタスクとして自動登録">
                  <Toggle value={settings.autoExtractTasks ?? false} onChange={v => setSetting("autoExtractTasks", v)}/>
                </Field>
                <Field label="デフォルトカテゴリ">
                  <select className="form-input" value={settings.recCategory ?? "Meeting"} onChange={e => setSetting("recCategory", e.target.value)}>
                    <option value="Meeting">会議</option>
                    <option value="1:1 / Interview">1on1・面談</option>
                    <option value="Dept-Head Mtg">部門長会議</option>
                    <option value="CEO Sync">社長定例</option>
                    <option value="Voice Memo">ボイスメモ</option>
                  </select>
                </Field>
                <Field label="長時間録音アラート" hint="この時間を超えると警告">
                  <select className="form-input" value={settings.recMaxMin ?? "0"} onChange={e => setSetting("recMaxMin", e.target.value)}>
                    <option value="0">無効</option>
                    <option value="30">30分</option>
                    <option value="60">60分</option>
                    <option value="120">120分</option>
                  </select>
                </Field>
              </Section>
            )}

            {section === "ai" && (
              <Section title="AI" subtitle="ClaudeのAI機能で使うコンテキストとプロンプト。">
                <Field label="AI返答のスタイル">
                  <select className="form-input" value={settings.aiStyle ?? "exec"} onChange={e => setSetting("aiStyle", e.target.value)}>
                    <option value="exec">経営層向け(簡潔・構造的)</option>
                    <option value="detailed">詳細(分析重視)</option>
                    <option value="casual">カジュアル</option>
                    <option value="coaching">コーチング型</option>
                  </select>
                </Field>
                <Field label="AI返答の文字数目安">
                  <select className="form-input" value={settings.aiLength ?? "medium"} onChange={e => setSetting("aiLength", e.target.value)}>
                    <option value="short">短め(2-3文)</option>
                    <option value="medium">標準(3-5文)</option>
                    <option value="long">長め(段落)</option>
                  </select>
                </Field>
                <Field label="AIに渡す追加コンテキスト" hint="あなたの業務状況をAIが理解するための背景情報">
                  <textarea className="form-input form-textarea" rows={4} value={settings.aiContext ?? "セイムグループ(大阪・不動産)。注文住宅「ondo」を主力。約94名→2031年124名計画。離職率20.4%を2028年に10%以下が目標。"} onChange={e => setSetting("aiContext", e.target.value)}/>
                </Field>
                <Field label="壁打ちのデフォルト言語">
                  <select className="form-input" value={settings.aiLang ?? "ja"} onChange={e => setSetting("aiLang", e.target.value)}>
                    <option value="ja">日本語</option>
                    <option value="en">English</option>
                  </select>
                </Field>
                <Field label="議事録要約に決定事項を含める"><Toggle value={settings.summaryDecisions ?? true} onChange={v => setSetting("summaryDecisions", v)}/></Field>
                <Field label="議事録要約にToDoを含める"><Toggle value={settings.summaryTodos ?? true} onChange={v => setSetting("summaryTodos", v)}/></Field>
                <Field label="議事録要約に未解決論点を含める"><Toggle value={settings.summaryQuestions ?? true} onChange={v => setSetting("summaryQuestions", v)}/></Field>
              </Section>
            )}

            {section === "notifications" && (
              <Section title="通知" subtitle="アラート条件と表示設定。">
                <Field label="期日が近いタスクを通知" hint="この日数以内のタスクをアラート表示">
                  <select className="form-input" value={settings.notifDueDays ?? "1"} onChange={e => setSetting("notifDueDays", e.target.value)}>
                    <option value="0">当日のみ</option>
                    <option value="1">1日前から</option>
                    <option value="3">3日前から</option>
                    <option value="7">1週間前から</option>
                  </select>
                </Field>
                <Field label="高優先度タスクを目立たせる"><Toggle value={settings.notifHighPriority ?? true} onChange={v => setSetting("notifHighPriority", v)}/></Field>
                <Field label="未対応タスクの期日超過アラート"><Toggle value={settings.notifOverdue ?? true} onChange={v => setSetting("notifOverdue", v)}/></Field>
                <Field label="ブラウザ通知を表示" hint="ブラウザの権限が必要">
                  <Toggle value={settings.notifBrowser ?? false} onChange={async v => {
                    if (v && "Notification" in window) {
                      const p = await Notification.requestPermission();
                      setSetting("notifBrowser", p === "granted");
                    } else { setSetting("notifBrowser", v); }
                  }}/>
                </Field>
                <Field label="朝のサマリ通知時刻">
                  {(() => {
                    const [h, m] = String(settings.notifMorningTime ?? "08:30").split(":");
                    const hh = String(parseInt(h, 10) || 0).padStart(2, "0");
                    // 分は5分刻みに丸める（00/05/10/…/55）
                    const mm = String(Math.round((parseInt(m, 10) || 0) / 5) * 5 % 60).padStart(2, "0");
                    return (
                      <div style={{display:"flex", alignItems:"center", gap:8}}>
                        <select
                          className="form-input"
                          style={{flex:1, maxWidth:90}}
                          size={1}
                          value={hh}
                          onChange={e => setSetting("notifMorningTime", `${e.target.value}:${mm}`)}
                        >
                          {Array.from({length:24}, (_, i) => String(i).padStart(2,"0")).map(v =>
                            <option key={v} value={v}>{v}時</option>
                          )}
                        </select>
                        <span style={{opacity:0.6}}>:</span>
                        <select
                          className="form-input"
                          style={{flex:1, maxWidth:90}}
                          size={1}
                          value={mm}
                          onChange={e => setSetting("notifMorningTime", `${hh}:${e.target.value}`)}
                        >
                          {Array.from({length:12}, (_, i) => String(i*5).padStart(2,"0")).map(v =>
                            <option key={v} value={v}>{v}分</option>
                          )}
                        </select>
                      </div>
                    );
                  })()}
                </Field>
                <Field label="通知音"><Toggle value={settings.notifSound ?? false} onChange={v => setSetting("notifSound", v)}/></Field>
              </Section>
            )}

            {section === "shortcuts" && (
              <Section title="キーボードショートカット" subtitle="覚えておくと作業が速くなります。">
                <ShortcutRow keys={["⌘", "K"]} label="横断検索を開く"/>
                <ShortcutRow keys={["Esc"]} label="モーダル・検索を閉じる"/>
                <ShortcutRow keys={["Enter"]} label="クイック追加で保存"/>
                <ShortcutRow keys={["Shift", "Enter"]} label="AI壁打ちで改行(送信せず)"/>
                <ShortcutRow keys={["クリック"]} label="タスクのステータスバッジで状態切替"/>
                <ShortcutRow keys={["ダブルクリック"]} label="カレンダーの日付で新規イベント作成"/>
                <ShortcutRow keys={["ドラッグ"]} label="ボードビューでカード移動"/>
              </Section>
            )}

            {section === "data" && (
              <Section title="データ管理" subtitle="バックアップ・同期・リセット。">
                {folder.supported ? (
                  <Field label="フォルダ同期" hint="同一PC内の複数ブラウザで同じデータを共有">
                    {folder.connected ? (
                      <div style={{display:"flex", alignItems:"center", gap:10}}>
                        <span className="chip gold">{folder.name}</span>
                        <button className="btn ghost sm" onClick={doDisconnectFolder}>切断</button>
                      </div>
                    ) : (
                      <button className="btn gold sm" onClick={doConnectFolder}>
                        <Icon name="folder" size={12}/>
                        <span>フォルダに接続</span>
                      </button>
                    )}
                  </Field>
                ) : (
                  <Field label="保存先" hint="操作後すぐにサーバへ自動保存されます">
                    <span style={{fontSize:12, color:"var(--ash)", lineHeight:1.6}}>
                      サーバ共有モード — データは自動的にサーバへ保存され、どの端末・ブラウザからも同じデータにアクセスできます。
                    </span>
                  </Field>
                )}
                <Field label="バックアップをエクスポート" hint="全データをJSONファイルにダウンロード">
                  <button className="btn ghost sm" onClick={doExport}><Icon name="download" size={12}/><span>エクスポート</span></button>
                </Field>
                <Field label="バックアップから復元">
                  <button className="btn ghost sm" onClick={doImport}><Icon name="copy" size={12}/><span>インポート</span></button>
                </Field>
                <Field label="データ統計">
                  <div style={{fontSize:12, color:"var(--ash)", lineHeight:1.7, fontFamily:"var(--f-mono)"}}>
                    タスク: {state.items?.length || 0}件 / 録音: {state.recordings?.length || 0}件 / 議事録要約: {(state.recordings || []).filter(r => r.summary).length}件 / アイデア: {state.ideas?.length || 0}件 / 人脈: {state.contacts?.length || 0}件 / 読書: {state.books?.length || 0}件
                  </div>
                </Field>
                {folder.supported && (
                  <Field label="フォルダ同期間隔" hint="ローカル保存は400ms固定。フォルダへの書き込みのみ間引きます。">
                    <select className="form-input" value={settings.autoSaveMs ?? "600000"} onChange={e => setSetting("autoSaveMs", e.target.value)}>
                      <option value="30000">30秒</option>
                      <option value="60000">1分</option>
                      <option value="300000">5分</option>
                      <option value="600000">10分(推奨)</option>
                      <option value="1800000">30分</option>
                    </select>
                  </Field>
                )}
                <Field label="デモデータにリセット" hint="⚠️ 現在のデータは消えます">
                  <button className="btn" style={{color:"var(--crimson-hi)", borderColor:"rgba(196,30,58,0.32)"}} onClick={doReset}>
                    <Icon name="refresh" size={12}/>
                    <span>リセット</span>
                  </button>
                </Field>
              </Section>
            )}

            {section === "about" && (
              <Section title="情報" subtitle="このシステムについて。">
                <div style={{padding:18, background:"linear-gradient(180deg, rgba(196,30,58,0.10), rgba(245,197,24,0.06))", border:"1px solid var(--border-hi)", borderRadius:8, marginBottom:18, textAlign:"center"}}>
                  <Shield size={56}/>
                  <div style={{fontFamily:"var(--f-display)", fontSize:24, letterSpacing:"0.14em", marginTop:14, background:"linear-gradient(180deg, var(--gold-hi), var(--gold), var(--gold-lo))", WebkitBackgroundClip:"text", backgroundClip:"text", color:"transparent"}}>SUPERSYS</div>
                  <div style={{fontFamily:"var(--f-title)", fontSize:10, letterSpacing:"0.32em", color:"var(--crystal)", marginTop:6}}>SEIMU · COMMAND SYSTEM</div>
                  <div style={{fontSize:11, color:"var(--ash)", marginTop:18, lineHeight:1.7}}>
                    人事・総務・情シスを統合する司令塔システム<br/>
                    シネマティック・スーパーマン(1978)インスパイア
                  </div>
                </div>
                <Field label="バージョン">
                  <span className="chip">v2.0.0</span>
                </Field>
                <Field label="データバージョン">
                  <span className="chip">v{state.__dataVersion ?? 2}</span>
                </Field>
                <Field label="ブラウザ">
                  <span style={{fontSize:11, fontFamily:"var(--f-mono)", color:"var(--ash)"}}>{navigator.userAgent.split(") ")[0].split("(").pop() || "Unknown"}</span>
                </Field>
                <Field label="File System Access API">
                  <span className="chip">{folder.supported ? "対応" : "非対応"}</span>
                </Field>
                <Field label="音声認識API">
                  <span className="chip">{("webkitSpeechRecognition" in window || "SpeechRecognition" in window) ? "対応" : "非対応"}</span>
                </Field>
              </Section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────
const Section = ({ title, subtitle, children }) => (
  <div className="settings-section">
    <div className="settings-section-head">
      <h3 className="settings-section-title">{title}</h3>
      {subtitle && <p className="settings-section-sub">{subtitle}</p>}
    </div>
    <div className="settings-section-body">{children}</div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div className="settings-field">
    <div className="settings-field-label">
      <div>{label}</div>
      {hint && <div className="settings-field-hint">{hint}</div>}
    </div>
    <div className="settings-field-control">{children}</div>
  </div>
);

const Toggle = ({ value, onChange }) => (
  <button
    className={"settings-toggle" + (value ? " on" : "")}
    onClick={() => onChange(!value)}
    type="button"
  >
    <span className="settings-toggle-knob"/>
  </button>
);

const ShortcutRow = ({ keys, label }) => (
  <div className="settings-shortcut">
    <div className="settings-shortcut-keys">
      {keys.map((k, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span style={{color:"var(--smoke)", margin:"0 4px"}}>+</span>}
          <span className="kbd-key">{k}</span>
        </React.Fragment>
      ))}
    </div>
    <div className="settings-shortcut-label">{label}</div>
  </div>
);

// Left-sidebar menu reorder / show-hide editor.
// `layout` = window.getNav(state, {includeHidden:true}) → [{group, items:[{id,label,icon,hidden}]}].
// We rebuild a complete navOrder ({groups, items, hidden}) from the layout after every
// edit, so the saved value is always self-contained and stable.
const NavOrderEditor = ({ layout, onChange, onReset }) => {
  const PROTECTED = ["dashboard"]; // 既定ホーム。非表示にすると戻れなくなるため固定表示。

  const toOrder = (lay) => ({
    groups: lay.map(g => g.group),
    items: Object.fromEntries(lay.map(g => [g.group, g.items.map(it => it.id)])),
    hidden: lay.flatMap(g => g.items.filter(it => it.hidden).map(it => it.id)),
  });
  const clone = (lay) => lay.map(g => ({ ...g, items: g.items.map(it => ({ ...it })) }));

  const moveItem = (gi, ii, dir) => {
    const lay = clone(layout);
    const items = lay[gi].items;
    const j = ii + dir;
    if (j < 0 || j >= items.length) return;
    [items[ii], items[j]] = [items[j], items[ii]];
    onChange(toOrder(lay));
  };
  const moveGroup = (gi, dir) => {
    const lay = clone(layout);
    const j = gi + dir;
    if (j < 0 || j >= lay.length) return;
    [lay[gi], lay[j]] = [lay[j], lay[gi]];
    onChange(toOrder(lay));
  };
  const toggleHidden = (gi, ii) => {
    const lay = clone(layout);
    lay[gi].items[ii].hidden = !lay[gi].items[ii].hidden;
    onChange(toOrder(lay));
  };

  return (
    <div className="settings-list-editor" style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div className="settings-field-label">サイドバーのメニュー</div>
        <button className="btn ghost sm" onClick={onReset} type="button" style={{ fontSize: 10 }}>
          <Icon name="refresh" size={10} />
          <span>デフォルトに戻す</span>
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {layout.map((group, gi) => (
          <div key={group.group}>
            {/* Group header + group reorder */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontFamily: "var(--f-mono)", fontSize: 10, letterSpacing: "0.14em", color: "var(--gold)", textTransform: "uppercase" }}>{group.group}</span>
              <div style={{ flex: 1, height: 1, background: "var(--hairline)" }} />
              <div style={{ display: "flex", gap: 2 }}>
                <button onClick={() => moveGroup(gi, -1)} disabled={gi === 0} style={{ background: "none", border: "none", color: "var(--ash)", cursor: gi === 0 ? "default" : "pointer", padding: 0, fontSize: 11, lineHeight: 1, opacity: gi === 0 ? 0.3 : 1 }} type="button" title="グループを上へ">▲</button>
                <button onClick={() => moveGroup(gi, 1)} disabled={gi === layout.length - 1} style={{ background: "none", border: "none", color: "var(--ash)", cursor: gi === layout.length - 1 ? "default" : "pointer", padding: 0, fontSize: 11, lineHeight: 1, opacity: gi === layout.length - 1 ? 0.3 : 1 }} type="button" title="グループを下へ">▼</button>
              </div>
            </div>

            {/* Items */}
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {group.items.map((it, ii) => {
                const locked = PROTECTED.includes(it.id);
                return (
                  <div
                    key={it.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "20px 1fr 28px 24px",
                      gap: 8, alignItems: "center",
                      padding: "7px 10px",
                      background: "rgba(0,0,0,0.28)",
                      border: "1px solid var(--hairline)",
                      borderRadius: 5,
                      opacity: it.hidden ? 0.5 : 1,
                    }}
                  >
                    <Icon name={it.icon} size={14} />
                    <span style={{ fontSize: 12, color: "var(--ivory)" }}>{it.label}</span>
                    {/* Reorder within group */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <button onClick={() => moveItem(gi, ii, -1)} disabled={ii === 0} style={{ background: "none", border: "none", color: "var(--ash)", cursor: ii === 0 ? "default" : "pointer", padding: 0, fontSize: 11, lineHeight: 1, opacity: ii === 0 ? 0.3 : 1 }} type="button" title="上へ">▲</button>
                      <button onClick={() => moveItem(gi, ii, 1)} disabled={ii === group.items.length - 1} style={{ background: "none", border: "none", color: "var(--ash)", cursor: ii === group.items.length - 1 ? "default" : "pointer", padding: 0, fontSize: 11, lineHeight: 1, opacity: ii === group.items.length - 1 ? 0.3 : 1 }} type="button" title="下へ">▼</button>
                    </div>
                    {/* Show / hide toggle */}
                    <button
                      onClick={() => !locked && toggleHidden(gi, ii)}
                      disabled={locked}
                      style={{ background: "none", border: "none", color: it.hidden ? "var(--smoke)" : "var(--gold)", cursor: locked ? "default" : "pointer", padding: 0, lineHeight: 0, opacity: locked ? 0.3 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}
                      type="button"
                      title={locked ? "ホームのため常に表示" : (it.hidden ? "表示する" : "非表示にする")}
                    >
                      <Icon name={it.hidden ? "eyeOff" : "eye"} size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ApplicantStatusEditor = ({ statuses, dateFields, onChange, onReset }) => {
  const [newLabel, setNewLabel] = React.useState("");
  const GROUPS = [
    { id: "lead",   label: "リード" },
    { id: "docs",   label: "書類" },
    { id: "i1",     label: "1次" },
    { id: "i2",     label: "2次" },
    { id: "offer",  label: "内定" },
    { id: "hired",  label: "入社" },
  ];
  const PALETTE = ["#94a3b8","#64748b","#6366f1","#4f46e5","#0ea5e9","#ef4444","#0284c7","#14b8a6","#0d9488","#f59e0b","#d97706","#16a34a","#15803d"];

  const updateAt = (idx, patch) => {
    const next = statuses.map((s, i) => i === idx ? { ...s, ...patch } : s);
    onChange(next);
  };
  const removeAt = (idx) => {
    if (!confirm(`ステータス「${statuses[idx].label}」を削除しますか?`)) return;
    onChange(statuses.filter((_, i) => i !== idx));
  };
  const moveAt = (idx, dir) => {
    const next = [...statuses];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const addNew = () => {
    if (!newLabel.trim()) return;
    const id = "custom_" + Date.now().toString(36);
    onChange([...statuses, {
      id, label: newLabel.trim(),
      group: "lead",
      color: PALETTE[statuses.length % PALETTE.length],
      nextLabel: "",
      nextDateKey: null,
    }]);
    setNewLabel("");
  };

  return (
    <div className="settings-list-editor" style={{marginBottom:24}}>
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10}}>
        <div className="settings-field-label">選考ステータス</div>
        <button className="btn ghost sm" onClick={onReset} type="button" style={{fontSize:10}}>
          <Icon name="refresh" size={10}/>
          <span>デフォルトに戻す</span>
        </button>
      </div>
      <div style={{display:"flex", flexDirection:"column", gap:6}}>
        {statuses.map((s, i) => (
          <div
            key={s.id}
            style={{
              display:"grid",
              gridTemplateColumns:"22px 1fr 110px 130px 130px 22px 28px 24px",
              gap:8, alignItems:"center",
              padding:"7px 10px",
              background:"rgba(0,0,0,0.28)",
              border:"1px solid var(--hairline)",
              borderRadius:5,
              borderLeft:`3px solid ${s.color}`,
            }}
          >
            {/* Color swatch */}
            <label
              title="色を変更"
              style={{
                position:"relative",
                width:18, height:18, borderRadius:"50%",
                background:s.color, border:"2px solid rgba(255,255,255,0.12)",
                cursor:"pointer",
              }}
            >
              <input
                type="color"
                value={s.color}
                onChange={e => updateAt(i, { color: e.target.value })}
                style={{position:"absolute", inset:0, opacity:0, cursor:"pointer", width:"100%", height:"100%"}}
              />
            </label>
            {/* Label */}
            <input
              className="form-input"
              style={{fontSize:12, padding:"4px 8px"}}
              value={s.label}
              onChange={e => updateAt(i, { label: e.target.value })}
              placeholder="ラベル"
            />
            {/* Group (for analytics funnel) */}
            <select
              className="form-input"
              style={{fontSize:11, padding:"4px 6px"}}
              value={s.group || "lead"}
              onChange={e => updateAt(i, { group: e.target.value })}
              title="ファネル分析のグループ"
            >
              {GROUPS.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
            </select>
            {/* Next action label */}
            <input
              className="form-input"
              style={{fontSize:11, padding:"4px 8px"}}
              value={s.nextLabel || ""}
              onChange={e => updateAt(i, { nextLabel: e.target.value })}
              placeholder="次のアクション"
              disabled={s.terminal}
            />
            {/* Date field key for the next action */}
            <select
              className="form-input"
              style={{fontSize:11, padding:"4px 6px"}}
              value={s.nextDateKey || ""}
              onChange={e => updateAt(i, { nextDateKey: e.target.value || null })}
              disabled={s.terminal}
              title="次のアクションの予定日フィールド"
            >
              <option value="">(日付なし)</option>
              {dateFields.map(df => <option key={df.key} value={df.key}>{df.label}</option>)}
            </select>
            {/* Terminal toggle */}
            <label title="終了状態(これ以上進まない)" style={{display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer"}}>
              <input
                type="checkbox"
                checked={!!s.terminal}
                onChange={e => updateAt(i, { terminal: e.target.checked })}
                style={{cursor:"pointer"}}
              />
            </label>
            {/* Reorder */}
            <div style={{display:"flex", flexDirection:"column", gap:1}}>
              <button onClick={() => moveAt(i, -1)} disabled={i === 0} style={{background:"none", border:"none", color:"var(--ash)", cursor: i === 0 ? "default" : "pointer", padding:0, fontSize:11, lineHeight:1, opacity: i === 0 ? 0.3 : 1}} type="button" title="上へ">▲</button>
              <button onClick={() => moveAt(i, 1)} disabled={i === statuses.length - 1} style={{background:"none", border:"none", color:"var(--ash)", cursor: i === statuses.length - 1 ? "default" : "pointer", padding:0, fontSize:11, lineHeight:1, opacity: i === statuses.length - 1 ? 0.3 : 1}} type="button" title="下へ">▼</button>
            </div>
            {/* Delete */}
            <button
              onClick={() => removeAt(i)}
              style={{background:"none", border:"none", color:"var(--crimson-hi)", cursor:"pointer", padding:0, fontSize:14, lineHeight:1}}
              type="button"
              title="削除"
            >×</button>
          </div>
        ))}
      </div>
      <div style={{display:"grid", gridTemplateColumns:"22px 1fr 110px 130px 130px 22px 28px 24px", gap:8, marginTop:6, fontSize:9, color:"var(--smoke)", letterSpacing:"0.10em", fontFamily:"var(--f-mono)", padding:"0 10px"}}>
        <span></span><span>ラベル</span><span>グループ</span><span>次のアクション</span><span>予定日</span><span title="終了状態">終了</span><span></span><span></span>
      </div>
      <div style={{display:"flex", gap:6, marginTop:10}}>
        <input
          className="form-input"
          placeholder="新しいステータス…"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.nativeEvent.isComposing && addNew()}
          style={{flex:1}}
        />
        <button className="btn gold sm" onClick={addNew} type="button">追加</button>
      </div>
    </div>
  );
};

const OwnerColorEditor = ({ owners, ownerColors, onChangeOwners, onChangeColors }) => {
  const [val, setVal] = React.useState("");
  const DEFAULTS = window.DEFAULT_OWNER_COLORS || {};
  const PALETTE = ["#f5c518", "#5b9bd5", "#5ac994", "#b58aff", "#ed7aa6", "#ed8c4a", "#8a93a6", "#e5536b", "#52b3a4"];
  const nextDefaultColor = (name) => {
    if (DEFAULTS[name]) return DEFAULTS[name];
    // pick first palette color not yet used
    const used = new Set(Object.values(ownerColors || {}));
    const free = PALETTE.find(c => !used.has(c));
    return free || PALETTE[(owners.length) % PALETTE.length];
  };
  const add = () => {
    const v = val.trim();
    if (!v || owners.includes(v)) return;
    onChangeOwners([...owners, v]);
    onChangeColors({ ...(ownerColors || {}), [v]: nextDefaultColor(v) });
    setVal("");
  };
  const remove = (name) => {
    onChangeOwners(owners.filter(o => o !== name));
    const { [name]: _, ...rest } = ownerColors || {};
    onChangeColors(rest);
  };
  const setColor = (name, hex) => {
    onChangeColors({ ...(ownerColors || {}), [name]: hex });
  };
  const resetDefaults = () => {
    if (!confirm("担当者の色をデフォルトにリセットしますか?")) return;
    onChangeColors({ ...DEFAULTS });
  };

  return (
    <div className="settings-list-editor">
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8}}>
        <div className="settings-field-label">担当者 / オーナー色</div>
        <button className="btn ghost sm" onClick={resetDefaults} type="button" style={{fontSize:10}}>
          <Icon name="refresh" size={10}/>
          <span>色をデフォルトに</span>
        </button>
      </div>
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8}}>
        {owners.map((name) => {
          const color = (ownerColors && ownerColors[name]) || DEFAULTS[name] || "#8a93a6";
          return (
            <div
              key={name}
              style={{
                display:"flex", alignItems:"center", gap:10,
                padding:"7px 10px",
                background:"rgba(0,0,0,0.28)",
                border:"1px solid var(--hairline)",
                borderRadius:5,
                borderLeft: `3px solid ${color}`,
              }}
            >
              {/* Color swatch — native color picker */}
              <label
                title="色を変更"
                style={{
                  position:"relative",
                  width:22, height:22, borderRadius:"50%",
                  background: color,
                  border:"2px solid rgba(255,255,255,0.12)",
                  cursor:"pointer",
                  flexShrink:0,
                  boxShadow:`0 0 0 2px ${color}33`,
                }}
              >
                <input
                  type="color"
                  value={color}
                  onChange={e => setColor(name, e.target.value)}
                  style={{position:"absolute", inset:0, opacity:0, cursor:"pointer", width:"100%", height:"100%"}}
                />
              </label>
              <span style={{flex:1, fontSize:12, color:"var(--ivory)"}}>{name}</span>
              <code style={{fontFamily:"var(--f-mono)", fontSize:10, color:"var(--ash)"}}>{color.toUpperCase()}</code>
              <button
                onClick={() => remove(name)}
                style={{background:"none", border:"none", color:"var(--crimson-hi)", cursor:"pointer", padding:"2px 4px", fontSize:14, lineHeight:1}}
                title="削除"
                type="button"
              >×</button>
            </div>
          );
        })}
      </div>
      <div style={{display:"flex", gap:6}}>
        <input
          className="form-input"
          placeholder="新しい担当者…"
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.nativeEvent.isComposing && add()}
          style={{flex:1}}
        />
        <button className="btn gold sm" onClick={add} type="button">追加</button>
      </div>
    </div>
  );
};

const ListEditor = ({ label, items, onChange, placeholder }) => {
  const [val, setVal] = React.useState("");
  const add = () => {
    if (!val.trim() || items.includes(val.trim())) return;
    onChange([...items, val.trim()]);
    setVal("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="settings-list-editor">
      <div className="settings-field-label" style={{marginBottom:8}}>{label}</div>
      <div className="settings-chip-list">
        {items.map((it, i) => (
          <span key={i} className="chip" style={{padding:"5px 10px", fontSize:11}}>
            {it}
            <button onClick={() => remove(i)} style={{marginLeft:6, background:"none", border:"none", color:"var(--crimson-hi)", cursor:"pointer", padding:0, fontSize:13}}>×</button>
          </span>
        ))}
      </div>
      <div style={{display:"flex", gap:6, marginTop:8}}>
        <input className="form-input" placeholder={placeholder} value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.nativeEvent.isComposing && add()} style={{flex:1}}/>
        <button className="btn gold sm" onClick={add}>追加</button>
      </div>
    </div>
  );
};

Object.assign(window, { SettingsModal });


;/* ==== tweaks-panel.jsx ==== */

// tweaks-panel.jsx
// Reusable Tweaks shell + form-control helpers.
//
// Owns the host protocol (listens for __activate_edit_mode / __deactivate_edit_mode,
// posts __edit_mode_available / __edit_mode_set_keys / __edit_mode_dismissed) so
// individual prototypes don't re-roll it. Ships a consistent set of controls so you
// don't hand-draw <input type="range">, segmented radios, steppers, etc.
//
// Usage (in an HTML file that loads React + Babel):
//
//   const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
//     "primaryColor": "#D97757",
//     "palette": ["#D97757", "#29261b", "#f6f4ef"],
//     "fontSize": 16,
//     "density": "regular",
//     "dark": false
//   }/*EDITMODE-END*/;
//
//   function App() {
//     const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
//     return (
//       <div style={{ fontSize: t.fontSize, color: t.primaryColor }}>
//         Hello
//         <TweaksPanel>
//           <TweakSection label="Typography" />
//           <TweakSlider label="Font size" value={t.fontSize} min={10} max={32} unit="px"
//                        onChange={(v) => setTweak('fontSize', v)} />
//           <TweakRadio  label="Density" value={t.density}
//                        options={['compact', 'regular', 'comfy']}
//                        onChange={(v) => setTweak('density', v)} />
//           <TweakSection label="Theme" />
//           <TweakColor  label="Primary" value={t.primaryColor}
//                        options={['#D97757', '#2A6FDB', '#1F8A5B', '#7A5AE0']}
//                        onChange={(v) => setTweak('primaryColor', v)} />
//           <TweakColor  label="Palette" value={t.palette}
//                        options={[['#D97757', '#29261b', '#f6f4ef'],
//                                  ['#475569', '#0f172a', '#f1f5f9']]}
//                        onChange={(v) => setTweak('palette', v)} />
//           <TweakToggle label="Dark mode" value={t.dark}
//                        onChange={(v) => setTweak('dark', v)} />
//         </TweaksPanel>
//       </div>
//     );
//   }
//
// ─────────────────────────────────────────────────────────────────────────────

const __TWEAKS_STYLE = `
  .twk-panel{position:fixed;right:16px;bottom:16px;z-index:2147483646;width:280px;
    max-height:calc(100vh - 32px);display:flex;flex-direction:column;
    transform:scale(var(--dc-inv-zoom,1));transform-origin:bottom right;
    background:rgba(250,249,247,.78);color:#29261b;
    -webkit-backdrop-filter:blur(24px) saturate(160%);backdrop-filter:blur(24px) saturate(160%);
    border:.5px solid rgba(255,255,255,.6);border-radius:14px;
    box-shadow:0 1px 0 rgba(255,255,255,.5) inset,0 12px 40px rgba(0,0,0,.18);
    font:11.5px/1.4 ui-sans-serif,system-ui,-apple-system,sans-serif;overflow:hidden}
  .twk-hd{display:flex;align-items:center;justify-content:space-between;
    padding:10px 8px 10px 14px;cursor:move;user-select:none}
  .twk-hd b{font-size:12px;font-weight:600;letter-spacing:.01em}
  .twk-x{appearance:none;border:0;background:transparent;color:rgba(41,38,27,.55);
    width:22px;height:22px;border-radius:6px;cursor:default;font-size:13px;line-height:1}
  .twk-x:hover{background:rgba(0,0,0,.06);color:#29261b}
  .twk-body{padding:2px 14px 14px;display:flex;flex-direction:column;gap:10px;
    overflow-y:auto;overflow-x:hidden;min-height:0;
    scrollbar-width:thin;scrollbar-color:rgba(0,0,0,.15) transparent}
  .twk-body::-webkit-scrollbar{width:8px}
  .twk-body::-webkit-scrollbar-track{background:transparent;margin:2px}
  .twk-body::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:4px;
    border:2px solid transparent;background-clip:content-box}
  .twk-body::-webkit-scrollbar-thumb:hover{background:rgba(0,0,0,.25);
    border:2px solid transparent;background-clip:content-box}
  .twk-row{display:flex;flex-direction:column;gap:5px}
  .twk-row-h{flex-direction:row;align-items:center;justify-content:space-between;gap:10px}
  .twk-lbl{display:flex;justify-content:space-between;align-items:baseline;
    color:rgba(41,38,27,.72)}
  .twk-lbl>span:first-child{font-weight:500}
  .twk-val{color:rgba(41,38,27,.5);font-variant-numeric:tabular-nums}

  .twk-sect{font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;
    color:rgba(41,38,27,.45);padding:10px 0 0}
  .twk-sect:first-child{padding-top:0}

  .twk-field{appearance:none;box-sizing:border-box;width:100%;min-width:0;height:26px;padding:0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;
    background:rgba(255,255,255,.6);color:inherit;font:inherit;outline:none}
  .twk-field:focus{border-color:rgba(0,0,0,.25);background:rgba(255,255,255,.85)}
  select.twk-field{padding-right:22px;
    background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='rgba(0,0,0,.5)' d='M0 0h10L5 6z'/></svg>");
    background-repeat:no-repeat;background-position:right 8px center}

  .twk-slider{appearance:none;-webkit-appearance:none;width:100%;height:4px;margin:6px 0;
    border-radius:999px;background:rgba(0,0,0,.12);outline:none}
  .twk-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;
    width:14px;height:14px;border-radius:50%;background:#fff;
    border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}
  .twk-slider::-moz-range-thumb{width:14px;height:14px;border-radius:50%;
    background:#fff;border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}

  .twk-seg{position:relative;display:flex;padding:2px;border-radius:8px;
    background:rgba(0,0,0,.06);user-select:none}
  .twk-seg-thumb{position:absolute;top:2px;bottom:2px;border-radius:6px;
    background:rgba(255,255,255,.9);box-shadow:0 1px 2px rgba(0,0,0,.12);
    transition:left .15s cubic-bezier(.3,.7,.4,1),width .15s}
  .twk-seg.dragging .twk-seg-thumb{transition:none}
  .twk-seg button{appearance:none;position:relative;z-index:1;flex:1;border:0;
    background:transparent;color:inherit;font:inherit;font-weight:500;min-height:22px;
    border-radius:6px;cursor:default;padding:4px 6px;line-height:1.2;
    overflow-wrap:anywhere}

  .twk-toggle{position:relative;width:32px;height:18px;border:0;border-radius:999px;
    background:rgba(0,0,0,.15);transition:background .15s;cursor:default;padding:0}
  .twk-toggle[data-on="1"]{background:#34c759}
  .twk-toggle i{position:absolute;top:2px;left:2px;width:14px;height:14px;border-radius:50%;
    background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.25);transition:transform .15s}
  .twk-toggle[data-on="1"] i{transform:translateX(14px)}

  .twk-num{display:flex;align-items:center;box-sizing:border-box;min-width:0;height:26px;padding:0 0 0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;background:rgba(255,255,255,.6)}
  .twk-num-lbl{font-weight:500;color:rgba(41,38,27,.6);cursor:ew-resize;
    user-select:none;padding-right:8px}
  .twk-num input{flex:1;min-width:0;height:100%;border:0;background:transparent;
    font:inherit;font-variant-numeric:tabular-nums;text-align:right;padding:0 8px 0 0;
    outline:none;color:inherit;-moz-appearance:textfield}
  .twk-num input::-webkit-inner-spin-button,.twk-num input::-webkit-outer-spin-button{
    -webkit-appearance:none;margin:0}
  .twk-num-unit{padding-right:8px;color:rgba(41,38,27,.45)}

  .twk-btn{appearance:none;height:26px;padding:0 12px;border:0;border-radius:7px;
    background:rgba(0,0,0,.78);color:#fff;font:inherit;font-weight:500;cursor:default}
  .twk-btn:hover{background:rgba(0,0,0,.88)}
  .twk-btn.secondary{background:rgba(0,0,0,.06);color:inherit}
  .twk-btn.secondary:hover{background:rgba(0,0,0,.1)}

  .twk-swatch{appearance:none;-webkit-appearance:none;width:56px;height:22px;
    border:.5px solid rgba(0,0,0,.1);border-radius:6px;padding:0;cursor:default;
    background:transparent;flex-shrink:0}
  .twk-swatch::-webkit-color-swatch-wrapper{padding:0}
  .twk-swatch::-webkit-color-swatch{border:0;border-radius:5.5px}
  .twk-swatch::-moz-color-swatch{border:0;border-radius:5.5px}

  .twk-chips{display:flex;gap:6px}
  .twk-chip{position:relative;appearance:none;flex:1;min-width:0;height:46px;
    padding:0;border:0;border-radius:6px;overflow:hidden;cursor:default;
    box-shadow:0 0 0 .5px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.06);
    transition:transform .12s cubic-bezier(.3,.7,.4,1),box-shadow .12s}
  .twk-chip:hover{transform:translateY(-1px);
    box-shadow:0 0 0 .5px rgba(0,0,0,.18),0 4px 10px rgba(0,0,0,.12)}
  .twk-chip[data-on="1"]{box-shadow:0 0 0 1.5px rgba(0,0,0,.85),
    0 2px 6px rgba(0,0,0,.15)}
  .twk-chip>span{position:absolute;top:0;bottom:0;right:0;width:34%;
    display:flex;flex-direction:column;box-shadow:-1px 0 0 rgba(0,0,0,.1)}
  .twk-chip>span>i{flex:1;box-shadow:0 -1px 0 rgba(0,0,0,.1)}
  .twk-chip>span>i:first-child{box-shadow:none}
  .twk-chip svg{position:absolute;top:6px;left:6px;width:13px;height:13px;
    filter:drop-shadow(0 1px 1px rgba(0,0,0,.3))}
`;

// ── useTweaks ───────────────────────────────────────────────────────────────
// Single source of truth for tweak values. setTweak persists via the host
// (__edit_mode_set_keys → host rewrites the EDITMODE block on disk).
function useTweaks(defaults) {
  const [values, setValues] = React.useState(defaults);
  // Accepts either setTweak('key', value) or setTweak({ key: value, ... }) so a
  // useState-style call doesn't write a "[object Object]" key into the persisted
  // JSON block.
  const setTweak = React.useCallback((keyOrEdits, val) => {
    const edits = typeof keyOrEdits === 'object' && keyOrEdits !== null
      ? keyOrEdits : { [keyOrEdits]: val };
    setValues((prev) => ({ ...prev, ...edits }));
    window.parent.postMessage({ type: '__edit_mode_set_keys', edits }, '*');
    // Same-window signal so in-page listeners (deck-stage rail thumbnails)
    // can react — the parent message only reaches the host, not peers.
    window.dispatchEvent(new CustomEvent('tweakchange', { detail: edits }));
  }, []);
  return [values, setTweak];
}

// ── TweaksPanel ─────────────────────────────────────────────────────────────
// Floating shell. Registers the protocol listener BEFORE announcing
// availability — if the announce ran first, the host's activate could land
// before our handler exists and the toolbar toggle would silently no-op.
// The close button posts __edit_mode_dismissed so the host's toolbar toggle
// flips off in lockstep; the host echoes __deactivate_edit_mode back which
// is what actually hides the panel.
function TweaksPanel({ title = 'Tweaks', children }) {
  const [open, setOpen] = React.useState(false);
  const dragRef = React.useRef(null);
  const offsetRef = React.useRef({ x: 16, y: 16 });
  const PAD = 16;

  const clampToViewport = React.useCallback(() => {
    const panel = dragRef.current;
    if (!panel) return;
    const w = panel.offsetWidth, h = panel.offsetHeight;
    const maxRight = Math.max(PAD, window.innerWidth - w - PAD);
    const maxBottom = Math.max(PAD, window.innerHeight - h - PAD);
    offsetRef.current = {
      x: Math.min(maxRight, Math.max(PAD, offsetRef.current.x)),
      y: Math.min(maxBottom, Math.max(PAD, offsetRef.current.y)),
    };
    panel.style.right = offsetRef.current.x + 'px';
    panel.style.bottom = offsetRef.current.y + 'px';
  }, []);

  React.useEffect(() => {
    if (!open) return;
    clampToViewport();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', clampToViewport);
      return () => window.removeEventListener('resize', clampToViewport);
    }
    const ro = new ResizeObserver(clampToViewport);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, [open, clampToViewport]);

  React.useEffect(() => {
    const onMsg = (e) => {
      const t = e?.data?.type;
      if (t === '__activate_edit_mode') setOpen(true);
      else if (t === '__deactivate_edit_mode') setOpen(false);
    };
    window.addEventListener('message', onMsg);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const dismiss = () => {
    setOpen(false);
    window.parent.postMessage({ type: '__edit_mode_dismissed' }, '*');
  };

  const onDragStart = (e) => {
    const panel = dragRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY;
    const startRight = window.innerWidth - r.right;
    const startBottom = window.innerHeight - r.bottom;
    const move = (ev) => {
      offsetRef.current = {
        x: startRight - (ev.clientX - sx),
        y: startBottom - (ev.clientY - sy),
      };
      clampToViewport();
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  if (!open) return null;
  return (
    <>
      <style>{__TWEAKS_STYLE}</style>
      <div ref={dragRef} className="twk-panel" data-omelette-chrome=""
           style={{ right: offsetRef.current.x, bottom: offsetRef.current.y }}>
        <div className="twk-hd" onMouseDown={onDragStart}>
          <b>{title}</b>
          <button className="twk-x" aria-label="Close tweaks"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={dismiss}>✕</button>
        </div>
        <div className="twk-body">
          {children}
        </div>
      </div>
    </>
  );
}

// ── Layout helpers ──────────────────────────────────────────────────────────

function TweakSection({ label, children }) {
  return (
    <>
      <div className="twk-sect">{label}</div>
      {children}
    </>
  );
}

function TweakRow({ label, value, children, inline = false }) {
  return (
    <div className={inline ? 'twk-row twk-row-h' : 'twk-row'}>
      <div className="twk-lbl">
        <span>{label}</span>
        {value != null && <span className="twk-val">{value}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Controls ────────────────────────────────────────────────────────────────

function TweakSlider({ label, value, min = 0, max = 100, step = 1, unit = '', onChange }) {
  return (
    <TweakRow label={label} value={`${value}${unit}`}>
      <input type="range" className="twk-slider" min={min} max={max} step={step}
             value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </TweakRow>
  );
}

function TweakToggle({ label, value, onChange }) {
  return (
    <div className="twk-row twk-row-h">
      <div className="twk-lbl"><span>{label}</span></div>
      <button type="button" className="twk-toggle" data-on={value ? '1' : '0'}
              role="switch" aria-checked={!!value}
              onClick={() => onChange(!value)}><i /></button>
    </div>
  );
}

function TweakRadio({ label, value, options, onChange }) {
  const trackRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  // The active value is read by pointer-move handlers attached for the lifetime
  // of a drag — ref it so a stale closure doesn't fire onChange for every move.
  const valueRef = React.useRef(value);
  valueRef.current = value;

  // Segments wrap mid-word once per-segment width runs out. The track is
  // ~248px (280 panel − 28 body pad − 4 seg pad), each button loses 12px
  // to its own padding, and 11.5px system-ui averages ~6.3px/char — so 2
  // options fit ~16 chars each, 3 fit ~10. Past that (or >3 options), fall
  // back to a dropdown rather than wrap.
  const labelLen = (o) => String(typeof o === 'object' ? o.label : o).length;
  const maxLen = options.reduce((m, o) => Math.max(m, labelLen(o)), 0);
  const fitsAsSegments = maxLen <= ({ 2: 16, 3: 10 }[options.length] ?? 0);
  if (!fitsAsSegments) {
    // <select> emits strings — map back to the original option value so the
    // fallback stays type-preserving (numbers, booleans) like the segment path.
    const resolve = (s) => {
      const m = options.find((o) => String(typeof o === 'object' ? o.value : o) === s);
      return m === undefined ? s : typeof m === 'object' ? m.value : m;
    };
    return <TweakSelect label={label} value={value} options={options}
                        onChange={(s) => onChange(resolve(s))} />;
  }
  const opts = options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }));
  const idx = Math.max(0, opts.findIndex((o) => o.value === value));
  const n = opts.length;

  const segAt = (clientX) => {
    const r = trackRef.current.getBoundingClientRect();
    const inner = r.width - 4;
    const i = Math.floor(((clientX - r.left - 2) / inner) * n);
    return opts[Math.max(0, Math.min(n - 1, i))].value;
  };

  const onPointerDown = (e) => {
    setDragging(true);
    const v0 = segAt(e.clientX);
    if (v0 !== valueRef.current) onChange(v0);
    const move = (ev) => {
      if (!trackRef.current) return;
      const v = segAt(ev.clientX);
      if (v !== valueRef.current) onChange(v);
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <TweakRow label={label}>
      <div ref={trackRef} role="radiogroup" onPointerDown={onPointerDown}
           className={dragging ? 'twk-seg dragging' : 'twk-seg'}>
        <div className="twk-seg-thumb"
             style={{ left: `calc(2px + ${idx} * (100% - 4px) / ${n})`,
                      width: `calc((100% - 4px) / ${n})` }} />
        {opts.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={o.value === value}>
            {o.label}
          </button>
        ))}
      </div>
    </TweakRow>
  );
}

function TweakSelect({ label, value, options, onChange }) {
  return (
    <TweakRow label={label}>
      <select className="twk-field" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => {
          const v = typeof o === 'object' ? o.value : o;
          const l = typeof o === 'object' ? o.label : o;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </TweakRow>
  );
}

function TweakText({ label, value, placeholder, onChange }) {
  return (
    <TweakRow label={label}>
      <input className="twk-field" type="text" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} />
    </TweakRow>
  );
}

function TweakNumber({ label, value, min, max, step = 1, unit = '', onChange }) {
  const clamp = (n) => {
    if (min != null && n < min) return min;
    if (max != null && n > max) return max;
    return n;
  };
  const startRef = React.useRef({ x: 0, val: 0 });
  const onScrubStart = (e) => {
    e.preventDefault();
    startRef.current = { x: e.clientX, val: value };
    const decimals = (String(step).split('.')[1] || '').length;
    const move = (ev) => {
      const dx = ev.clientX - startRef.current.x;
      const raw = startRef.current.val + dx * step;
      const snapped = Math.round(raw / step) * step;
      onChange(clamp(Number(snapped.toFixed(decimals))));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return (
    <div className="twk-num">
      <span className="twk-num-lbl" onPointerDown={onScrubStart}>{label}</span>
      <input type="number" value={value} min={min} max={max} step={step}
             onChange={(e) => onChange(clamp(Number(e.target.value)))} />
      {unit && <span className="twk-num-unit">{unit}</span>}
    </div>
  );
}

// Relative-luminance contrast pick — checkmarks drawn over a swatch need to
// read on both #111 and #fafafa without per-option configuration. Hex input
// only (#rgb / #rrggbb); named or rgb()/hsl() colors fall through to "light".
function __twkIsLight(hex) {
  const h = String(hex).replace('#', '');
  const x = h.length === 3 ? h.replace(/./g, (c) => c + c) : h.padEnd(6, '0');
  const n = parseInt(x.slice(0, 6), 16);
  if (Number.isNaN(n)) return true;
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return r * 299 + g * 587 + b * 114 > 148000;
}

const __TwkCheck = ({ light }) => (
  <svg viewBox="0 0 14 14" aria-hidden="true">
    <path d="M3 7.2 5.8 10 11 4.2" fill="none" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round"
          stroke={light ? 'rgba(0,0,0,.78)' : '#fff'} />
  </svg>
);

// TweakColor — curated color/palette picker. Each option is either a single
// hex string or an array of 1-5 hex strings; the card adapts — a lone color
// renders solid, a palette renders colors[0] as the hero (left ~2/3) with the
// rest stacked in a sharp column on the right. onChange emits the
// option in the shape it was passed (string stays string, array stays array).
// Without options it falls back to the native color input for back-compat.
function TweakColor({ label, value, options, onChange }) {
  if (!options || !options.length) {
    return (
      <div className="twk-row twk-row-h">
        <div className="twk-lbl"><span>{label}</span></div>
        <input type="color" className="twk-swatch" value={value}
               onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  // Native <input type=color> emits lowercase hex per the HTML spec, so
  // compare case-insensitively. String() guards JSON.stringify(undefined),
  // which returns the primitive undefined (no .toLowerCase).
  const key = (o) => String(JSON.stringify(o)).toLowerCase();
  const cur = key(value);
  return (
    <TweakRow label={label}>
      <div className="twk-chips" role="radiogroup">
        {options.map((o, i) => {
          const colors = Array.isArray(o) ? o : [o];
          const [hero, ...rest] = colors;
          const sup = rest.slice(0, 4);
          const on = key(o) === cur;
          return (
            <button key={i} type="button" className="twk-chip" role="radio"
                    aria-checked={on} data-on={on ? '1' : '0'}
                    aria-label={colors.join(', ')} title={colors.join(' · ')}
                    style={{ background: hero }}
                    onClick={() => onChange(o)}>
              {sup.length > 0 && (
                <span>
                  {sup.map((c, j) => <i key={j} style={{ background: c }} />)}
                </span>
              )}
              {on && <__TwkCheck light={__twkIsLight(hero)} />}
            </button>
          );
        })}
      </div>
    </TweakRow>
  );
}

function TweakButton({ label, onClick, secondary = false }) {
  return (
    <button type="button" className={secondary ? 'twk-btn secondary' : 'twk-btn'}
            onClick={onClick}>{label}</button>
  );
}

Object.assign(window, {
  useTweaks, TweaksPanel, TweakSection, TweakRow,
  TweakSlider, TweakToggle, TweakRadio, TweakSelect,
  TweakText, TweakNumber, TweakColor, TweakButton,
});


;/* ==== app.jsx ==== */
// Main app — wires all views together with localStorage + IndexedDB persistence
const App = () => {
  // F5/reload keeps the page you were on (per-device, like tweaks). Unknown ids
  // (e.g. a view removed in a later build) fall back to the dashboard.
  const [view, setView] = React.useState(() => {
    try {
      const v = localStorage.getItem("supersys_view");
      return v && VIEW_TITLES[v] ? v : "dashboard";
    } catch { return "dashboard"; }
  });
  React.useEffect(() => {
    try { localStorage.setItem("supersys_view", view); } catch {}
  }, [view]);
  const [state, setState] = React.useState(() => {
    // Initialize with full shape so first render never crashes
    const init = window.initialState || {};
    return {
      items: [], projects: [], recordings: [], ideas: [], contacts: [], quickMemos: [],
      books: [], applicants: [], roles: [], templates: [], youtube: [],
      alerts: [], settings: {}, sparring: [], reviews: [],
      kpi: { funnel: [], leaveRate: { now: 0, target2028: 0, industryAvg: 0 }, headcount: { current: 0, plan2025: 0, plan2031: 0 }, sales: { plan: 0, ondo: 0, ondoEx: 0, broker: 0, unit: "" } },
      ...init,
    };
  });
  const [loaded, setLoaded] = React.useState(false);
  const [recOpen, setRecOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [openedRecording, setOpenedRecording] = React.useState(null);
  const [savedAt, setSavedAt] = React.useState(null);
  const [importFlash, setImportFlash] = React.useState(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [folder, setFolder] = React.useState({ supported: window.FS_SUPPORTED, connected: false, name: null });
  // Mobile navigation drawer (only meaningful on phone-width screens)
  const [mobileNav, setMobileNav] = React.useState(false);
  // Pick a view and close the mobile drawer in one go
  const gotoView = React.useCallback((v) => { setView(v); setMobileNav(false); }, []);
  // Close the drawer on Escape, and whenever the window grows back to desktop width
  React.useEffect(() => {
    if (!mobileNav) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileNav(false); };
    const onResize = () => { if (window.innerWidth >= 768) setMobileNav(false); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("resize", onResize); };
  }, [mobileNav]);
  // Prevent body scroll behind the open drawer
  React.useEffect(() => {
    document.body.classList.toggle("mobile-nav-open", mobileNav);
    return () => document.body.classList.remove("mobile-nav-open");
  }, [mobileNav]);

  const recEngine = useRecordingEngine();
  const recActive = recEngine.phase !== "idle";
  const recLive = recEngine.phase === "live";

  const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
    "theme": "fortress",
    "starfield": true,
    "filmGrain": true,
    "warp": false
  }/*EDITMODE-END*/;
  // Tweaks are per-device view preferences. The EDITMODE postMessage host only
  // exists in the design tool — in production nothing persisted these, so the
  // theme/starfield choices silently reset on every reload. Overlay localStorage
  // on the baked defaults and write every change back.
  const [t, setTweakRaw] = useTweaks((() => {
    try { return { ...TWEAK_DEFAULTS, ...JSON.parse(localStorage.getItem("supersys_tweaks") || "{}") }; }
    catch { return TWEAK_DEFAULTS; }
  })());
  const setTweak = React.useCallback((keyOrEdits, val) => {
    setTweakRaw(keyOrEdits, val);
    const edits = typeof keyOrEdits === "object" && keyOrEdits !== null ? keyOrEdits : { [keyOrEdits]: val };
    try {
      const cur = JSON.parse(localStorage.getItem("supersys_tweaks") || "{}");
      localStorage.setItem("supersys_tweaks", JSON.stringify({ ...cur, ...edits }));
    } catch {}
  }, [setTweakRaw]);

  // ── Load persisted state on mount (folder first, then localStorage) ──
  React.useEffect(() => {
    (async () => {
      const status = await folderStatus();
      setFolder(status);
      let saved = null;
      let flash = null;
      if (status.connected) {
        saved = await readStateFromFolder();
        if (saved) flash = { kind: "info", text: `Loaded from folder "${status.name}".` };
      }
      if (!saved) {
        saved = await loadState();
        if (saved) flash = { kind: "info", text: "Loaded saved data from this browser." };
      }
      if (saved) setState(saved);
      if (flash) {
        setImportFlash(flash);
        setTimeout(() => setImportFlash(null), 2800);
      }
      setLoaded(true);
    })();
    // eslint-disable-next-line
  }, []);

  // ── Re-sync when the tab returns to the foreground ───────────────────
  // A phone tab can stay suspended for hours; without this it keeps showing
  // (and on the next flush, would try to save back) a stale document, hiding
  // contacts/tasks that were registered on the PC in the meantime.
  React.useEffect(() => {
    if (!loaded) return;
    let busy = false;
    const sync = async () => {
      if (busy || document.visibilityState === "hidden") return;
      busy = true;
      try {
        const fresh = await fetchStateIfNewer();
        if (fresh) setState(fresh);
      } finally { busy = false; }
    };
    const onVis = () => { if (document.visibilityState === "visible") sync(); };
    // 409-rejected stale save → storage.jsx already re-fetched the server copy.
    const onAdopted = (e) => { if (e.detail) setState(e.detail); };
    window.addEventListener("focus", sync);
    window.addEventListener("pageshow", sync);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("supersys:state-adopted", onAdopted);
    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("pageshow", sync);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("supersys:state-adopted", onAdopted);
    };
  }, [loaded]);

  // ── Save state on change ─────────────────────────────────────────────
  // Two paths:
  //   • localStorage  — fast, debounced 400ms (always)
  //   • Folder sync   — slow, debounced by settings.autoSaveMs (default 10min)
  // Falls back to a final save on unload so nothing's lost.
  const lastFolderSaveRef = React.useRef(0);
  React.useEffect(() => {
    if (!loaded) return;
    const folderDelay = parseInt(state.settings?.autoSaveMs ?? "600000", 10) || 600000;

    // Always save locally (fast)
    const localTimer = setTimeout(() => {
      const ok = saveState(state);
      if (ok) setSavedAt(Date.now());
    }, 400);

    // Throttle folder save by the user-configured interval
    let folderTimer = null;
    if (folder.connected) {
      const sinceLast = Date.now() - lastFolderSaveRef.current;
      const wait = Math.max(0, folderDelay - sinceLast);
      folderTimer = setTimeout(async () => {
        await writeStateToFolder(state);
        lastFolderSaveRef.current = Date.now();
      }, wait);
    }

    return () => { clearTimeout(localTimer); if (folderTimer) clearTimeout(folderTimer); };
  }, [state, loaded, folder.connected]);

  // Final flush on tab close / hide so no unsaved changes get lost.
  React.useEffect(() => {
    if (!loaded) return;
    const flush = () => {
      try { saveState(state); } catch {}
      if (folder.connected) { try { writeStateToFolder(state); } catch {} }
    };
    window.addEventListener("beforeunload", flush);
    window.addEventListener("pagehide", flush);
    const onVis = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("beforeunload", flush);
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [state, loaded, folder.connected]);

  // ── Track removed recordings to clean up IDB audio blobs ─────────────
  const prevRecIdsRef = React.useRef(new Set());
  React.useEffect(() => {
    const recs = state.recordings || [];
    if (!loaded) {
      prevRecIdsRef.current = new Set(recs.map(r => r.id));
      return;
    }
    const currentIds = new Set(recs.map(r => r.id));
    for (const id of prevRecIdsRef.current) {
      if (!currentIds.has(id)) {
        idbDelete(id);
        if (folder.connected) deleteAudioFromFolder(id);
      }
    }
    prevRecIdsRef.current = currentIds;
  }, [state.recordings, loaded, folder.connected]);

  // Apply theme + density + font scale + sidebar width to body
  React.useEffect(() => {
    const themes = { fortress: "", krypton: "krypton", phantom: "phantom", "daily-planet": "daily-planet" };
    document.body.setAttribute("data-theme", themes[t.theme] || "");
  }, [t.theme]);

  // Settings → CSS variables
  React.useEffect(() => {
    const s = state.settings || {};
    document.documentElement.style.setProperty("--rail", (s.sidebarWidth || 200) + "px");
    document.documentElement.style.setProperty("--font-scale", ((s.fontScale || 100) / 100).toString());
    document.body.setAttribute("data-density", s.density || "comfortable");
    document.body.classList.toggle("no-animations", s.animations === false);
    // Star twinkle level: off | subtle | normal | vivid (default subtle)
    const tw = s.starTwinkle || "subtle";
    ["twinkle-off","twinkle-subtle","twinkle-normal","twinkle-medium","twinkle-strong","twinkle-vivid"].forEach(c => document.body.classList.remove(c));
    document.body.classList.add("twinkle-" + tw);
    // Expose to global formatters and AI helpers
    window.__supersys_settings = s;
  }, [state.settings]);

  // Starfield / grain toggles
  React.useEffect(() => {
    document.body.classList.toggle("no-starfield", !t.starfield);
    document.body.classList.toggle("no-grain", !t.filmGrain);
  }, [t.starfield, t.filmGrain]);

  // Cmd+K
  React.useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearchOpen(true); }
      if (e.key === "Escape") { setSearchOpen(false); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // Expose addTasks helper so RecordingDetail can push extracted todos
  React.useEffect(() => {
    window.__superman_addTasks = (newItems) => {
      setState(s => ({ ...s, items: [...newItems, ...(s.items || [])] }));
    };
    // Generic patch helper: merge field patches into applicants by id.
    // patches = { [applicantId]: { field: value, ... } }
    window.__superman_patchApplicants = (patches) => {
      setState(s => ({
        ...s,
        applicants: (s.applicants || []).map(a => patches[a.id] ? { ...a, ...patches[a.id] } : a),
      }));
    };
    return () => { delete window.__superman_addTasks; delete window.__superman_patchApplicants; };
  }, []);

  // Save audio blob to IDB + folder when a new recording is created
  const saveRecording = async (rec) => {
    if (rec.audioUrl && rec.audioUrl.startsWith && rec.audioUrl.startsWith("blob:")) {
      await persistAudio(rec.id, rec.audioUrl);
      if (folder.connected) await writeAudioToFolder(rec.id, rec.audioUrl);
    }
    setState(s => ({ ...s, recordings: [rec, ...s.recordings] }));
  };

  const openRec = () => setRecOpen(true);
  const closeRec = () => setRecOpen(false);

  // ── Data actions ─────────────────────────────────────────────────────
  const doExport = () => exportData(state);
  const fileInputRef = React.useRef(null);
  const triggerImport = () => fileInputRef.current?.click();
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await importData(file);
      // Current backups store tasks under `items`; very old ones used `tasks`.
      // The old check (`data.tasks`) rejected every freshly exported backup.
      if (!data || (!Array.isArray(data.items) && !Array.isArray(data.tasks))) throw new Error("Invalid file");
      if (!Array.isArray(data.items) && Array.isArray(data.tasks)) data.items = data.tasks;
      const migrated = window.migrateCollections ? window.migrateCollections(data) : data;
      setState(migrated);
      setImportFlash({ kind: "ok", text: `Imported ${migrated.items.length} tasks, ${migrated.recordings?.length || 0} recordings, ${migrated.projects?.length || 0} projects.` });
      setTimeout(() => setImportFlash(null), 3500);
    } catch (err) {
      setImportFlash({ kind: "err", text: "Import failed — file is not a valid SUPERMAN backup." });
      setTimeout(() => setImportFlash(null), 3500);
    }
    e.target.value = "";
  };
  const doReset = async () => {
    if (!confirm("Reset everything to demo data? Your current data will be erased.")) return;
    await clearAllData();
    location.reload();
  };

  // ── Folder sync actions ──────────────────────────────────────────────
  const doConnectFolder = async () => {
    try {
      const handle = await connectFolder();
      setFolder({ supported: true, connected: true, name: handle.name });
      // Try loading existing data from folder
      const fromFolder = await readStateFromFolder();
      if (fromFolder && fromFolder.tasks) {
        if (confirm(`Folder "${handle.name}" already contains data. Load it and replace current data?`)) {
          setState(fromFolder);
          setImportFlash({ kind: "ok", text: `Loaded from folder "${handle.name}".` });
        } else {
          // Push current data to folder
          await writeStateToFolder(state);
          setImportFlash({ kind: "ok", text: `Connected. Current data saved to folder "${handle.name}".` });
        }
      } else {
        // No existing data in folder — push current state
        await writeStateToFolder(state);
        setImportFlash({ kind: "ok", text: `Connected. Data now syncs to folder "${handle.name}".` });
      }
      setTimeout(() => setImportFlash(null), 3500);
    } catch (e) {
      if (e?.name === "AbortError") return; // user cancelled
      setImportFlash({ kind: "err", text: e?.message || "Failed to connect folder." });
      setTimeout(() => setImportFlash(null), 3500);
    }
  };

  const doDisconnectFolder = async () => {
    if (!confirm("Disconnect from this folder? Your data will still be saved locally in this browser, but stop syncing to the folder.")) return;
    await disconnectFolder();
    setFolder({ supported: true, connected: false, name: null });
    setImportFlash({ kind: "info", text: "Folder disconnected. Data still saved locally." });
    setTimeout(() => setImportFlash(null), 2800);
  };

  // Dynamic alerts derived from items + settings (期日が近い/超過/高優先度)
  const effectiveAlerts = React.useMemo(() => {
    const s = state.settings || {};
    const todayY = (typeof window.todayYMD === "function") ? window.todayYMD() : new Date().toISOString().slice(0,10);
    const today = window.parseYMD ? window.parseYMD(todayY) : new Date();
    const dueDays = parseInt(s.notifDueDays ?? "1", 10);
    const wantOverdue = s.notifOverdue !== false;
    const highOnly = s.notifHighPriority !== false;
    const dyn = [];
    (state.items || []).forEach(it => {
      if (it.status === "done") return;
      if (!it.date) return;
      const d = window.parseYMD ? window.parseYMD(it.date) : null;
      if (!d) return;
      const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
      if (diff < 0) {
        if (wantOverdue) {
          dyn.push({ id: "ovr_" + it.id, title: it.title, meta: `期日超過 ${Math.abs(diff)}日`, severity: "high" });
        }
      } else if (diff <= dueDays) {
        if (!highOnly || it.priority === "high") {
          dyn.push({ id: "due_" + it.id, title: it.title, meta: diff === 0 ? "本日が期日" : `あと${diff}日`, severity: it.priority === "high" ? "high" : "med" });
        }
      }
    });
    dyn.sort((a, b) => (a.severity === "high" ? -1 : 1) - (b.severity === "high" ? -1 : 1));
    return [...dyn, ...(state.alerts || [])];
  }, [state.items, state.alerts, state.settings]);

  // Morning summary notification (browser + sound)
  React.useEffect(() => {
    const s = state.settings || {};
    if (!s.notifBrowser || !s.notifMorningTime) return;
    const tick = () => {
      const now = new Date();
      const [h, m] = String(s.notifMorningTime).split(":").map(Number);
      if (isNaN(h) || isNaN(m)) return;
      if (now.getHours() !== h || now.getMinutes() !== m) return;
      const key = `__sys_morning_${now.toISOString().slice(0,10)}_${s.notifMorningTime}`;
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
      const todayY = window.todayYMD ? window.todayYMD() : new Date().toISOString().slice(0,10);
      const todayTasks = (state.items || []).filter(it => it.status !== "done" && it.date === todayY).length;
      try {
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          const n = new Notification("SUPERSYS — Good Morning", {
            body: `本日のタスク: ${todayTasks}件 / 通知: ${effectiveAlerts.length}件`,
          });
          setTimeout(() => { try { n.close(); } catch {} }, 8000);
        }
        if (s.notifSound) {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator(); const gain = ctx.createGain();
          osc.frequency.value = 880; osc.connect(gain); gain.connect(ctx.destination);
          gain.gain.setValueAtTime(0.001, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
          osc.start(); osc.stop(ctx.currentTime + 0.42);
        }
      } catch {}
    };
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [state.settings?.notifBrowser, state.settings?.notifMorningTime, state.settings?.notifSound, state.items, effectiveAlerts.length]);

  return (
    <>
      {t.warp && (
        <>
          <WarpStars active={true} intensity={1.0} />
          <div className="warp-readability"/>
        </>
      )}
      {t.starfield && !t.warp && (
        <Starfield
          twinkle={state.settings?.starTwinkle || "subtle"}
          density={parseFloat(state.settings?.starDensity ?? "1") || 1}
          meteors={state.settings?.meteorFreq || "normal"}
        />
      )}
      <div className={"app" + (mobileNav ? " mobile-nav-open" : "")}>
        <Sidebar
          view={view}
          setView={gotoView}
          open={mobileNav}
          onClose={() => setMobileNav(false)}
          nav={React.useMemo(() => (window.getNav ? window.getNav(state) : null), [state.navOrder])}
          recordingCount={state.recordings.length}
          savedAt={savedAt}
          openSettings={() => { setSettingsOpen(true); setMobileNav(false); }}
          userName={state.settings?.userName}
          userRole={state.settings?.userRole}
          avatar={state.settings?.avatar}
        />
        {mobileNav && <div className="mobile-nav-overlay" onClick={() => setMobileNav(false)} aria-hidden="true" />}
        <Topbar view={view} openRecord={openRec} openSearch={() => setSearchOpen(true)} recording={recLive} recordingSeconds={recEngine.seconds} fmtTime={recEngine.fmtTime} alerts={effectiveAlerts} setView={setView} onMenu={() => setMobileNav(true)} />
        <main className="main">
          {view === "dashboard" && <Dashboard state={state} setState={setState} openRecord={openRec} setView={setView} openRecording={setOpenedRecording} effectiveAlerts={effectiveAlerts} />}
          {view === "tasks" && <WorkspaceShell state={state} setState={setState} defaultMode="board" />}
          {view === "calendar" && <WorkspaceShell state={state} setState={setState} defaultMode="calendar" />}
          {view === "projects" && <ProjectsView state={state} setState={setState} />}
          {view === "quickmemo" && <QuickMemoView state={state} setState={setState} />}
          {view === "memos" && <MemosView state={state} setState={setState} openRecording={setOpenedRecording} openRecord={openRec} />}
          {view === "screenshots" && <ScreenshotsView state={state} setState={setState} folderConnected={folder.connected} />}
          {view === "applicants" && <ApplicantsView state={state} setState={setState} />}
          {view === "roles" && <RolesView state={state} setState={setState} />}
          {view === "templates" && <TemplatesView state={state} setState={setState} />}
          {view === "youtube" && <YouTubeView state={state} setState={setState} />}
          {view === "kpi" && <KpiView state={state} setState={setState} />}
          {view === "contacts" && <ContactsView state={state} setState={setState} />}
          {view === "learning" && <LearningView state={state} setState={setState} />}
          {view === "review" && <ReviewView state={state} setState={setState} />}
          {view === "sparring" && <SparringView state={state} setState={setState} />}
        </main>
      </div>

      <RecordingStudio
        open={recOpen}
        onClose={closeRec}
        onSave={saveRecording}
        engine={recEngine}
      />

      {!recOpen && recActive && (
        <FloatingRecBadge engine={recEngine} onExpand={openRec} />
      )}

      <RecordingDetail
        rec={openedRecording}
        onClose={() => setOpenedRecording(null)}
        onUpdate={(id, patch) => {
          setState(s => ({ ...s, recordings: (s.recordings || []).map(r => r.id === id ? { ...r, ...patch } : r) }));
          setOpenedRecording(prev => prev && prev.id === id ? { ...prev, ...patch } : prev);
        }}
      />

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} state={state} setView={setView} openRecord={openRec} />

      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        state={state}
        setState={setState}
        tweaks={t}
        setTweak={setTweak}
        folder={folder}
        doConnectFolder={doConnectFolder}
        doDisconnectFolder={doDisconnectFolder}
        doExport={doExport}
        doImport={triggerImport}
        doReset={doReset}
      />

      {importFlash && (
        <div className={"toast " + importFlash.kind}>
          <Icon name={importFlash.kind === "err" ? "alert" : importFlash.kind === "ok" ? "check" : "download"} size={14}/>
          <span>{importFlash.text}</span>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="application/json,.json" style={{display:"none"}} onChange={onImportFile}/>

      <TweaksPanel title="Tweaks">
        <TweakSection label="Theme">
          <TweakSelect
            label="Visual"
            value={t.theme}
            options={[
              { value: "fortress", label: "Fortress (default)" },
              { value: "krypton", label: "Krypton (blue crystal)" },
              { value: "phantom", label: "Phantom Zone (grey)" },
              { value: "daily-planet", label: "Daily Planet (newsroom)" },
            ]}
            onChange={v => setTweak("theme", v)}
          />
          <TweakToggle label="Starfield background" value={t.starfield} onChange={v => setTweak("starfield", v)} />
          <TweakToggle label="Warp speed (映画オープニング風)" value={t.warp} onChange={v => setTweak("warp", v)} />
          <TweakToggle label="Film grain" value={t.filmGrain} onChange={v => setTweak("filmGrain", v)} />
        </TweakSection>
        <TweakSection label="Sync Folder">
          {folder.supported ? (
            <>
              {!folder.connected ? (
                <>
                  <div className="twk-row">
                    <button className="btn gold sm" onClick={doConnectFolder} style={{width:"100%", justifyContent:"center"}}>
                      <Icon name="folder" size={12}/>
                      <span>Connect data folder</span>
                    </button>
                  </div>
                  <div style={{fontSize:10, color:"var(--smoke)", lineHeight:1.5, marginTop:6, padding:"6px 2px"}}>
                    PC内の任意のフォルダにデータファイルを置くと、Chrome / Edge / Brave など複数のブラウザから同じデータにアクセスできます。
                  </div>
                </>
              ) : (
                <>
                  <div className="twk-row" style={{display:"flex", alignItems:"center", gap:10, padding:"8px 10px", background:"rgba(245,197,24,0.08)", border:"1px solid rgba(245,197,24,0.24)", borderRadius:5}}>
                    <Icon name="folder" size={14} color="var(--gold)"/>
                    <div style={{flex:1, minWidth:0, fontSize:11, color:"var(--ivory)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                      <div style={{fontFamily:"var(--f-mono)", fontSize:9, color:"var(--gold)", letterSpacing:"0.12em"}}>CONNECTED</div>
                      <div title={folder.name}>{folder.name}</div>
                    </div>
                  </div>
                  <div className="twk-row">
                    <button className="btn ghost sm" onClick={doDisconnectFolder} style={{width:"100%", justifyContent:"center"}}>
                      <span>Disconnect</span>
                    </button>
                  </div>
                  <div style={{fontSize:10, color:"var(--smoke)", lineHeight:1.5, marginTop:6, padding:"6px 2px"}}>
                    別のブラウザから開く時は、もう一度「Connect data folder」を押して<strong style={{color:"var(--ivory)"}}>同じフォルダ</strong>を選択してください。
                  </div>
                </>
              )}
            </>
          ) : (
            <div style={{padding:"10px 12px", background:"rgba(91,155,213,0.08)", border:"1px solid rgba(91,155,213,0.24)", borderRadius:5, fontSize:11, color:"var(--ash)", lineHeight:1.5}}>
              データはサーバに自動保存され、どの端末・ブラウザからも同じデータにアクセスできます。フォルダ同期は不要です。
            </div>
          )}
        </TweakSection>
        <TweakSection label="Data">
          <div className="twk-row">
            <button className="btn ghost sm" onClick={doExport} style={{width:"100%", justifyContent:"center"}}>
              <Icon name="download" size={12}/>
              <span>Export backup (JSON)</span>
            </button>
          </div>
          <div className="twk-row">
            <button className="btn ghost sm" onClick={triggerImport} style={{width:"100%", justifyContent:"center"}}>
              <Icon name="copy" size={12}/>
              <span>Import backup</span>
            </button>
          </div>
          <div className="twk-row">
            <button className="btn ghost sm" onClick={doReset} style={{width:"100%", justifyContent:"center", color:"var(--crimson-hi)", borderColor:"rgba(196,30,58,0.32)"}}>
              <Icon name="refresh" size={12}/>
              <span>Reset to demo data</span>
            </button>
          </div>
          <div style={{fontSize:10, color:"var(--smoke)", lineHeight:1.5, marginTop:6, padding:"6px 2px"}}>
            データは自動的にこのブラウザに保存されます。エクスポートでJSONバックアップを取得、別PCではインポートで復元できます。
          </div>
        </TweakSection>
      </TweaksPanel>
    </>
  );
};

ReactDOM.createRoot(document.getElementById("root")).render(
  <AuthGate><App /></AuthGate>
);

